import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { OnlineZahlungDialog } from "./components/OnlineZahlungDialog";
import { MahnstufeEinsWidget } from './components/MahnstufeEinsWidget';
import { FremdSicherungen } from './components/FremdSicherungen';
import "@/App.css";
import "@/theme-codana.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { TeilstornoButton, TeilstornoListe } from "./components/TeilstornoButton";
import { ERechnungButtons } from "./components/ERechnungButtons";
import { MahnregelnPanel } from "./components/MahnregelnPanel";
import { MahnsperreBox } from "./components/MahnsperreBox";
import Eingangsrechnungen from "./pages/Eingangsrechnungen";
import Rechnungsbuecher from "./pages/Rechnungsbuecher";
import Kontoauszuege from "./pages/Kontoauszuege";
import Betriebsferien from "./pages/Betriebsferien";
import ERechnungVersand from "./pages/ERechnungVersand";
import { RechnungsadresseFormular, EinwilligungRechnung, EinwilligungEinrichtung } from "./pages/ERechnungPublic";
import QMCockpit from "./pages/QMCockpit";
import Kurstermine from "./pages/Kurstermine";
import VertriebAuswertung from "./pages/VertriebAuswertung";
import { Toaster, toast } from "sonner";
import { 
  LayoutDashboard, Building2, Users, GraduationCap, BookOpen, 
  ClipboardCheck, Award, ListTodo, Menu, X, Search, Plus, 
  ChevronDown, ChevronUp, ArrowUpDown, Download, Pencil, Trash2, Eye, FileText, Upload,
  Ticket, AlertTriangle, Calendar as CalendarIcon, Play, CheckCircle,
  Circle, ShieldCheck, FileDown, CreditCard, CircleDollarSign,
  Check, CircleX, RefreshCcw, ChevronLeft, ChevronRight, RefreshCw,
  Phone, Mail, User, Target, DollarSign, Briefcase, Clock, Euro, LogOut, UserPlus,
  BarChart3, TrendingUp, ShoppingCart, Package, MessageSquare, XCircle, Edit, Save,
  MapPin, LayoutGrid, List, Copy, Database, Loader2, MessageCircle, FileSignature, MoreHorizontal, SlidersHorizontal,
  Sunrise, Moon, Laptop
} from "lucide-react";

// Auth Context
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/components/LoginPage";

import BGKursEinstellungen from "@/BGKursEinstellungen";
import AnwesenheitNeu from "@/AnwesenheitNeu";
import AZAVMappe from "@/components/AZAVMappe";
import AZAVOrdner from "@/components/AZAVOrdner";
import AZAVDokumente from "@/components/AZAVDokumente";
import AZAVAntragsWizard from "@/components/AZAVAntragsWizard";
import CockpitVorschau from "@/pages/CockpitVorschau";
import DesignBeispiele from "@/pages/DesignBeispiele";
import Aenderungsprotokoll from "@/pages/Aenderungsprotokoll";
import { AutoTodos } from "@/pages/AufgabenAutomatisch";
import { DokumentWaechter } from "@/components/audit/DokumentWaechter";
import { Zahlungsvorschau } from "@/components/finanzen/Zahlungsvorschau";
import { DozentNachtragBlock } from "@/components/audit/DozentNachtragBlock";
import { ZertifikatDruckliste } from "@/components/audit/ZertifikatDruckliste";
import { GeburtsdatumInput, istUngueltig } from "@/components/common/GeburtsdatumInput";
import VerbleibsbefragungAuswertung from "@/components/VerbleibsbefragungAuswertung";
import Personalakten from "@/components/Personalakten";
import Urlaubsplanung from "@/components/Urlaubsplanung";
import FertigOhneZertifikat from "@/components/FertigOhneZertifikat";
import PflegeeinrichtungDetail from "@/components/PflegeeinrichtungDetail";
import { OriginalDruckOffen } from "@/components/OriginalDruckOffen";
import { BestandenOhnePruefung } from "@/components/BestandenOhnePruefung";
import { ZertifikatBelegCheck } from "@/components/ZertifikatBelegCheck";
import { PraktikumNachweise } from "@/components/PraktikumNachweise";
import { AzavReparaturen } from "@/components/AzavReparaturen";
import { EinrichtungBelegstatus } from "@/components/EinrichtungBelegstatus";
import { UnterschriftenVorBeginn } from "@/components/UnterschriftenVorBeginn";
import SalesUeberblick from "@/components/SalesUeberblick";
import SignPage from "@/components/SignPage";
import TerminBuchung, { TerminStorno } from "@/pages/TerminBuchung";
import Termine from "@/pages/Termine";
import KlassenbuchLuecken from "@/pages/KlassenbuchLuecken";
import EditorialGallery from "@/components/EditorialGallery";

// Importierte gemeinsame Komponenten
import { AZAVAmpel, Sidebar, StatusBadge } from "@/components/common";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import React from "react";

// Error Boundary Komponente
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ error, errorInfo });
    console.error("React Error Boundary caught:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full">
            <h2 className="text-xl font-bold text-red-600 mb-4">Ein Fehler ist aufgetreten</h2>
            <p className="text-gray-600 mb-4">
              Bitte laden Sie die Seite neu. Wenn das Problem weiterhin besteht, kontaktieren Sie den Support.
            </p>
            <details className="bg-gray-50 p-3 rounded text-sm">
              <summary className="cursor-pointer font-medium">Technische Details</summary>
              <pre className="mt-2 text-xs overflow-auto max-h-40 text-red-500">
                {this.state.error && this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
            <button 
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              onClick={() => window.location.reload()}
            >
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Import TeilnehmerDetail Komponente
import TeilnehmerDetail from "@/components/TeilnehmerDetail";
import RechnungenTnZuordnung from "@/components/RechnungenTnZuordnung";
import Buchhaltung from "@/components/Buchhaltung";
import Interessenten from "@/components/Interessenten";
import VertriebsCockpit from "@/components/VertriebsCockpit";
import DozentenView from "@/components/DozentenView";
import KlassenbuchNeu from "@/KlassenbuchNeu";
import Finanzuebersicht from "@/Finanzuebersicht";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// ==================== WHATSAPP HELPERS ====================
// Normalisiert eine (deutsche) Telefonnummer für wa.me: nur Ziffern, ohne führende 0/00, mit Landesvorwahl
const normalizePhoneForWhatsApp = (raw) => {
  if (!raw) return "";
  const s = String(raw).trim();
  // Alle Nicht-Ziffern-Zeichen entfernen, dabei führendes + merken
  const hasPlus = s.startsWith("+");
  let digits = s.replace(/\D/g, "");
  if (!digits) return "";
  if (hasPlus) return digits; // bereits internationale Notation
  if (digits.startsWith("00")) return digits.slice(2); // 00 49… → 49…
  if (digits.startsWith("0")) return "49" + digits.slice(1); // 0170… → 49170…
  if (digits.startsWith("49")) return digits; // schon mit Ländervorwahl
  // Fallback: als DE-Nummer behandeln
  return "49" + digits;
};

const DEFAULT_WA_TEMPLATE = `Hallo {ansprechpartner},

Jeany hier von Dickies-Campus 

Kurze Nachricht — ihr habt euch bei uns den Delegationsatlas heruntergeladen. Ich hab da 4 Wochen dran gearbeitet und stelle ihn kostenlos zur Verfügung, weil er der Branche wirklich helfen soll.

Falls euch die Arbeit gefallen hat: wir bilden auch selbst aus (Praxisanleitung, Fachtherapeut Wunde HKP, PDL, Behandlungspflege LG1 + LG2, Palliativbegleitung, §43b Betreuungskraft). Mit ATLAS20 bekommt ihr 20 % auf die erste Buchung.

Unsere Kurse im Überblick: https://dickies-helden.de/#kurse

Sagt einfach Bescheid, wenn was für euch dabei ist.

LG Jeany`;

const WA_TEMPLATE_LS_KEY = "wa_template_delegationsatlas_v1";

const getSavedWaTemplate = () => {
  try {
    return localStorage.getItem(WA_TEMPLATE_LS_KEY) || DEFAULT_WA_TEMPLATE;
  } catch {
    return DEFAULT_WA_TEMPLATE;
  }
};

const renderWaMessage = (template, pe) => {
  const anspr = (pe?.ansprechpartner_name || "").trim();
  // Nur Vorname verwenden, wenn Name mit Anrede/Titel beginnt → nimm letztes Wort
  const anrede = anspr ? anspr.split(/\s+/).slice(0, 1).join(" ") : "zusammen";
  const einrichtung = pe?.einrichtungsname || "";
  return String(template || "")
    .replaceAll("{ansprechpartner}", anrede || "zusammen")
    .replaceAll("{einrichtung}", einrichtung);
};

// Reusable WhatsApp Dialog
const WhatsAppSenderDialog = ({ open, onOpenChange, pe, onSent }) => {
  const [message, setMessage] = useState("");
  const [templateDirty, setTemplateDirty] = useState(false);
  const [tracking, setTracking] = useState(false);

  useEffect(() => {
    if (open && pe) {
      const tmpl = getSavedWaTemplate();
      setMessage(renderWaMessage(tmpl, pe));
      setTemplateDirty(false);
    }
  }, [open, pe]);

  const phone = normalizePhoneForWhatsApp(pe?.ansprechpartner_telefon);
  const hasPhone = Boolean(phone);

  const openWhatsApp = async () => {
    if (!hasPhone) {
      toast.error("Keine Telefonnummer hinterlegt");
      return;
    }
    // Erst Server informieren (damit die Liste sofort den Versand-Marker zeigt)
    setTracking(true);
    try {
      if (pe?.id) {
        await axios.post(`${API}/pflegeeinrichtungen/${pe.id}/whatsapp-sent`, { nachricht: message });
      }
    } catch (e) {
      // Nicht blockieren wenn Tracking fehlschlägt — WhatsApp trotzdem öffnen
      console.warn("WhatsApp-Tracking fehlgeschlagen:", e);
    } finally {
      setTracking(false);
    }
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    if (onSent) onSent(pe);
    onOpenChange(false);
  };

  const saveAsTemplate = () => {
    try {
      // Speichere die aktuelle Nachricht als Vorlage — dabei Name/Einrichtung wieder auf Platzhalter zurücksetzen
      let tmpl = message;
      const anspr = (pe?.ansprechpartner_name || "").trim();
      const vor = anspr ? anspr.split(/\s+/).slice(0, 1).join(" ") : "";
      if (vor) tmpl = tmpl.replaceAll(vor, "{ansprechpartner}");
      if (pe?.einrichtungsname) tmpl = tmpl.replaceAll(pe.einrichtungsname, "{einrichtung}");
      localStorage.setItem(WA_TEMPLATE_LS_KEY, tmpl);
      toast.success("Vorlage gespeichert", { description: "Beim nächsten Öffnen wird diese Nachricht vorausgefüllt." });
      setTemplateDirty(false);
    } catch (e) {
      toast.error("Vorlage konnte nicht gespeichert werden");
    }
  };

  const resetToDefault = () => {
    try { localStorage.removeItem(WA_TEMPLATE_LS_KEY); } catch (_e) { /* ignore */ }
    setMessage(renderWaMessage(DEFAULT_WA_TEMPLATE, pe));
    setTemplateDirty(false);
    toast.info("Standard-Vorlage wiederhergestellt");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="whatsapp-sender-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <MessageCircle size={18} className="text-green-600" />
            WhatsApp-Nachricht senden
          </DialogTitle>
          <DialogDescription>
            Nachricht anpassen und in WhatsApp öffnen. Vorlage wird lokal gespeichert.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-800">{pe?.ansprechpartner_name || "—"}</p>
                <p className="text-xs text-gray-600">{pe?.einrichtungsname || ""}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Nummer</p>
                <p className="font-mono text-sm text-gray-800">{pe?.ansprechpartner_telefon || "—"}</p>
                {hasPhone && <p className="font-mono text-[10px] text-gray-500">→ +{phone}</p>}
              </div>
            </div>
          </div>

          {pe?.whatsapp_last_sent_at && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="whatsapp-already-sent-hint">
              <div className="flex items-center gap-2 font-semibold">
                <MessageCircle size={12} />
                Bereits {pe.whatsapp_sent_count || 1}× kontaktiert
              </div>
              <p className="mt-0.5">
                Zuletzt am {new Date(pe.whatsapp_last_sent_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {pe.whatsapp_last_sent_by ? ` von ${pe.whatsapp_last_sent_by}` : ''}.
              </p>
            </div>
          )}

          <div>
            <Label className="text-xs">Nachricht</Label>
            <Textarea
              value={message}
              onChange={(e) => { setMessage(e.target.value); setTemplateDirty(true); }}
              rows={10}
              className="font-normal text-sm"
              data-testid="whatsapp-message-textarea"
            />
            <p className="mt-1 text-[11px] text-gray-500">
              Platzhalter (nur beim Speichern als Vorlage): <code>{"{ansprechpartner}"}</code>, <code>{"{einrichtung}"}</code>
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={resetToDefault}
              data-testid="whatsapp-reset-template-btn"
            >
              Standard
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={saveAsTemplate}
              disabled={!templateDirty}
              data-testid="whatsapp-save-template-btn"
            >
              Als Vorlage speichern
            </Button>
          </div>
          <Button
            type="button"
            onClick={openWhatsApp}
            disabled={!hasPhone || tracking}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="whatsapp-open-btn"
          >
            {tracking ? (
              <><Loader2 size={16} className="mr-1.5 animate-spin" /> Öffne…</>
            ) : (
              <><MessageCircle size={16} className="mr-1.5" /> {pe?.whatsapp_last_sent_at ? "Erneut in WhatsApp öffnen" : "In WhatsApp öffnen"}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, loading, hasAccess } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#b8a99a]"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !hasAccess(allowedRoles)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// ==================== KALENDER ====================
// Feste Farben pro Terminart
const TERMIN_FARBEN = {
  akquise: "#3B82F6",       // Blau
  kurs: "#22C55E",          // Grün
  pruefung: "#F97316",      // Orange
  wiedervorlage: "#6B7280", // Grau (Standard)
  praktikum: "#A855F7",     // Lila
  azav_frist: "#EF4444",    // Rot
  zertifikat: "#EAB308",    // Gold
  intern: "#14B8A6",        // Türkis
  ausser_haus: "#EC4899",   // Pink
  meeting: "#1E40AF"        // Dunkelblau
};

// Vertriebsmitarbeiter-Farben für Wiedervorlagen
const VM_FARBEN = {
  "105a7626-aa3f-4645-8f0e-68ae31909f96": "#3B82F6", // Jean Dickie - Blau
  "8692bf55-2baa-4008-9145-7773b8d80edb": "#EC4899", // Katharina Blatsas - Pink
};

// Hilfsfunktion für Termin-Farbe (berücksichtigt VM bei Wiedervorlagen)
const getTerminFarbe = (eintrag) => {
  if (eintrag.terminart === "wiedervorlage" && eintrag.vertriebsmitarbeiter_id) {
    return VM_FARBEN[eintrag.vertriebsmitarbeiter_id] || TERMIN_FARBEN.wiedervorlage;
  }
  return TERMIN_FARBEN[eintrag.terminart] || "#6B7280";
};

const TERMINART_LABELS = {
  akquise: "Akquise",
  kurs: "Kurs",
  pruefung: "Prüfung",
  wiedervorlage: "Wiedervorlage",
  praktikum: "Praktikum",
  azav_frist: "AZAV-Frist",
  zertifikat: "Zertifikat",
  intern: "Intern",
  ausser_haus: "Außer Haus",
  meeting: "Meeting"
};

const Kalender = () => {
  const navigate = useNavigate();
  const [eintraege, setEintraege] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ansicht, setAnsicht] = useState("woche"); // tag, woche, monat
  const [aktuellesDatum, setAktuellesDatum] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [pflegeeinrichtungen, setPflegeeinrichtungen] = useState([]);
  const [teilnehmerListe, setTeilnehmerListe] = useState([]);
  const [kurseListe, setKurseListe] = useState([]);
  
  // Filter
  const [filterTerminart, setFilterTerminart] = useState("all");
  const [filterStatus, setFilterStatus] = useState("geplant");
  const [filterAzav, setFilterAzav] = useState("all");
  
  const [formData, setFormData] = useState({
    titel: "",
    terminart: "akquise",
    datum: "",
    uhrzeit_von: "",
    uhrzeit_bis: "",
    bezug_typ: "",
    bezug_id: "",
    status: "geplant",
    notiz: "",
    azav_relevant: false
  });

  // Datum-Hilfsfunktionen
  const getWochenTage = (datum) => {
    const wochenStart = new Date(datum);
    const tag = wochenStart.getDay();
    const diff = wochenStart.getDate() - tag + (tag === 0 ? -6 : 1);
    wochenStart.setDate(diff);
    
    const tage = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(wochenStart);
      d.setDate(wochenStart.getDate() + i);
      tage.push(d);
    }
    return tage;
  };

  const getMonatsTage = (datum) => {
    const jahr = datum.getFullYear();
    const monat = datum.getMonth();
    const ersterTag = new Date(jahr, monat, 1);
    const letzterTag = new Date(jahr, monat + 1, 0);
    
    const tage = [];
    // Fülle vorherige Tage
    const startTag = ersterTag.getDay() === 0 ? 6 : ersterTag.getDay() - 1;
    for (let i = startTag; i > 0; i--) {
      const d = new Date(ersterTag);
      d.setDate(ersterTag.getDate() - i);
      tage.push({ date: d, isCurrentMonth: false });
    }
    // Aktuelle Monatstage
    for (let i = 1; i <= letzterTag.getDate(); i++) {
      tage.push({ date: new Date(jahr, monat, i), isCurrentMonth: true });
    }
    // Fülle nachfolgende Tage
    const endeTage = 42 - tage.length;
    for (let i = 1; i <= endeTage; i++) {
      const d = new Date(letzterTag);
      d.setDate(letzterTag.getDate() + i);
      tage.push({ date: d, isCurrentMonth: false });
    }
    return tage;
  };

  // Formatiert Datum OHNE Zeitzonen-Konvertierung (lokale Zeit)
  const formatDatum = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const getDatumBereich = () => {
    if (ansicht === "tag") {
      return { von: formatDatum(aktuellesDatum), bis: formatDatum(aktuellesDatum) };
    } else if (ansicht === "woche") {
      const tage = getWochenTage(aktuellesDatum);
      return { von: formatDatum(tage[0]), bis: formatDatum(tage[6]) };
    } else {
      const jahr = aktuellesDatum.getFullYear();
      const monat = aktuellesDatum.getMonth();
      const erster = new Date(jahr, monat, 1);
      const letzter = new Date(jahr, monat + 1, 0);
      return { von: formatDatum(erster), bis: formatDatum(letzter) };
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { von, bis } = getDatumBereich();
      const params = new URLSearchParams();
      params.append("datum_von", von);
      params.append("datum_bis", bis);
      if (filterTerminart !== "all") params.append("terminart", filterTerminart);
      if (filterStatus !== "all") {
        params.append("status", filterStatus);
        params.append("nur_geplant", "false");
      } else {
        params.append("nur_geplant", "false");
      }
      if (filterAzav !== "all") params.append("azav_relevant", filterAzav === "ja");
      
      const [kalenderRes, peRes, tnRes, massnahmenRes] = await Promise.all([
        axios.get(`${API}/kalender?${params}`),
        axios.get(`${API}/pflegeeinrichtungen`),
        axios.get(`${API}/teilnehmer`),
        axios.get(`${API}/massnahmen`)
      ]);
      
      setEintraege(kalenderRes.data);
      setPflegeeinrichtungen(peRes.data);
      setTeilnehmerListe(tnRes.data);
      setKurseListe(massnahmenRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden des Kalenders");
    } finally {
      setLoading(false);
    }
  }, [ansicht, aktuellesDatum, filterTerminart, filterStatus, filterAzav]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    try {
      const response = await axios.post(`${API}/kalender/sync`);
      toast.success(`Kalender synchronisiert: ${response.data.eintraege_erstellt} neue Einträge`);
      fetchData();
    } catch (error) {
      toast.error("Fehler bei der Synchronisation");
    }
  };

  const handleSubmit = async () => {
    try {
      // Bereite Daten vor - leere Strings zu null konvertieren
      const submitData = {
        ...formData,
        bezug_typ: formData.bezug_typ || null,
        bezug_id: formData.bezug_id || null,
        uhrzeit_von: formData.uhrzeit_von || null,
        uhrzeit_bis: formData.uhrzeit_bis || null,
        notiz: formData.notiz || ""
      };
      
      if (editItem) {
        await axios.put(`${API}/kalender/${editItem.id}`, submitData);
        toast.success("Termin aktualisiert");
      } else {
        await axios.post(`${API}/kalender`, submitData);
        toast.success("Termin erstellt");
      }
      setDialogOpen(false);
      setEditItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error("Kalender save error:", error);
      toast.error("Fehler beim Speichern");
    }
  };

  const handleStatusToggle = async (eintrag) => {
    try {
      const neuerStatus = eintrag.status === "geplant" ? "erledigt" : "geplant";
      await axios.put(`${API}/kalender/${eintrag.id}/status?status=${neuerStatus}`);
      toast.success(`Status auf "${neuerStatus}" geändert`);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Ändern des Status");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Termin löschen?")) {
      try {
        await axios.delete(`${API}/kalender/${id}`);
        toast.success("Termin gelöscht");
        fetchData();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      titel: item.titel,
      terminart: item.terminart,
      datum: item.datum,
      uhrzeit_von: item.uhrzeit_von || "",
      uhrzeit_bis: item.uhrzeit_bis || "",
      bezug_typ: item.bezug_typ || "",
      bezug_id: item.bezug_id || "",
      status: item.status,
      notiz: item.notiz || "",
      azav_relevant: item.azav_relevant || false
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      titel: "",
      terminart: "akquise",
      datum: formatDatum(new Date()),
      uhrzeit_von: "",
      uhrzeit_bis: "",
      bezug_typ: "",
      bezug_id: "",
      status: "geplant",
      notiz: "",
      azav_relevant: false
    });
  };

  const navigateToBezug = (eintrag) => {
    if (!eintrag.bezug_typ || !eintrag.bezug_id) return;
    if (eintrag.bezug_typ === "pflegeeinrichtung") {
      // Direkt zum Vertrieb navigieren mit Kunden-ID als URL-Parameter
      navigate(`/vertrieb?pe=${eintrag.bezug_id}`);
    }
    else if (eintrag.bezug_typ === "teilnehmer") navigate("/teilnehmer");
    else if (eintrag.bezug_typ === "kurs" || eintrag.bezug_typ === "massnahme") navigate("/massnahmen");
  };

  const getEintraegeForDatum = (datum) => {
    const datumStr = formatDatum(datum);
    return eintraege.filter(e => e.datum === datumStr);
  };

  const navigieren = (richtung) => {
    const neuDatum = new Date(aktuellesDatum);
    if (ansicht === "tag") {
      neuDatum.setDate(neuDatum.getDate() + richtung);
    } else if (ansicht === "woche") {
      neuDatum.setDate(neuDatum.getDate() + (richtung * 7));
    } else {
      neuDatum.setMonth(neuDatum.getMonth() + richtung);
    }
    setAktuellesDatum(neuDatum);
  };

  const heute = () => setAktuellesDatum(new Date());

  const wochenTage = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const istHeute = (datum) => formatDatum(datum) === formatDatum(new Date());

  return (
    <div className="page-container" data-testid="kalender-page">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Kalender</h1>
      </div>
      
      {/* Action Buttons */}
      <div className="flex gap-2 mb-4">
        <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus size={16} className="mr-2" />
          Neuer Termin
        </Button>
        <Button variant="outline" onClick={handleSync}>
          <RefreshCcw size={16} className="mr-2" />
          Synchronisieren
        </Button>
      </div>

      {/* Filter und Navigation */}
      <Card className="card-custom mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center justify-between">
            {/* Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigieren(-1)}>
                <ChevronLeft size={16} />
              </Button>
              <Button variant="outline" size="sm" onClick={heute}>Heute</Button>
              <Button variant="outline" size="sm" onClick={() => navigieren(1)}>
                <ChevronRight size={16} />
              </Button>
              <span className="font-medium ml-2">
                {ansicht === "monat" 
                  ? aktuellesDatum.toLocaleString('de-DE', { month: 'long', year: 'numeric' })
                  : ansicht === "woche"
                  ? `KW ${Math.ceil((aktuellesDatum.getDate() - aktuellesDatum.getDay() + 1) / 7)} - ${aktuellesDatum.toLocaleString('de-DE', { month: 'long', year: 'numeric' })}`
                  : aktuellesDatum.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                }
              </span>
            </div>

            {/* Ansicht */}
            <div className="flex gap-1">
              <Button variant={ansicht === "tag" ? "default" : "outline"} size="sm" onClick={() => setAnsicht("tag")}>Tag</Button>
              <Button variant={ansicht === "woche" ? "default" : "outline"} size="sm" onClick={() => setAnsicht("woche")}>Woche</Button>
              <Button variant={ansicht === "monat" ? "default" : "outline"} size="sm" onClick={() => setAnsicht("monat")}>Monat</Button>
            </div>

            {/* Filter */}
            <div className="flex gap-2">
              <Select value={filterTerminart} onValueChange={setFilterTerminart}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Terminart" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Arten</SelectItem>
                  <SelectItem value="akquise">Akquise</SelectItem>
                  <SelectItem value="kurs">Kurs</SelectItem>
                  <SelectItem value="pruefung">Prüfung</SelectItem>
                  <SelectItem value="wiedervorlage">Wiedervorlage</SelectItem>
                  <SelectItem value="praktikum">Praktikum</SelectItem>
                  <SelectItem value="azav_frist">AZAV-Frist</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="geplant">Geplant</SelectItem>
                  <SelectItem value="erledigt">Erledigt</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterAzav} onValueChange={setFilterAzav}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="AZAV" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="ja">Nur AZAV</SelectItem>
                  <SelectItem value="nein">Ohne AZAV</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Farblegende */}
      <div className="flex flex-wrap gap-3 mb-4">
        {Object.entries(TERMINART_LABELS).map(([key, label]) => (
          <div key={key} className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: TERMIN_FARBEN[key] }}></div>
            <span className="text-sm text-[#78716c]">{label}</span>
          </div>
        ))}
      </div>

      {/* Kalender-Ansicht */}
      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="loading-spinner"></div>
            </div>
          ) : ansicht === "monat" ? (
            // Monatsansicht
            <div className="grid grid-cols-7">
              {wochenTage.map(tag => (
                <div key={tag} className="p-2 text-center font-medium text-sm border-b bg-[#FFFFFF]">{tag}</div>
              ))}
              {getMonatsTage(aktuellesDatum).map((tagObj, idx) => {
                const tagesEintraege = getEintraegeForDatum(tagObj.date);
                return (
                  <div 
                    key={idx} 
                    className={`min-h-[100px] p-1 border-b border-r ${!tagObj.isCurrentMonth ? 'bg-gray-50' : ''} ${istHeute(tagObj.date) ? 'bg-blue-50' : ''}`}
                  >
                    <div className={`text-sm font-medium mb-1 ${!tagObj.isCurrentMonth ? 'text-gray-400' : ''}`}>
                      {tagObj.date.getDate()}
                    </div>
                    <div className="space-y-0.5">
                      {tagesEintraege.slice(0, 3).map(e => (
                        <div 
                          key={e.id}
                          className={`text-xs p-1 rounded truncate cursor-pointer hover:opacity-80 ${e.status === 'erledigt' ? 'opacity-50' : ''}`}
                          style={{ backgroundColor: getTerminFarbe(e), color: 'white' }}
                          onClick={() => handleEdit(e)}
                          onDoubleClick={() => e.bezug_id && navigateToBezug(e)}
                          title={`${e.titel}${e.bezug_name ? ` - ${e.bezug_name}` : ''}\n(Doppelklick: zur Einrichtung)`}
                        >
                          {e.titel}
                        </div>
                      ))}
                      {tagesEintraege.length > 3 && (
                        <div className="text-xs text-[#78716c]">+{tagesEintraege.length - 3} weitere</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : ansicht === "woche" ? (
            // Wochenansicht
            <div>
              <div className="grid grid-cols-7 border-b">
                {getWochenTage(aktuellesDatum).map((tag, idx) => (
                  <div key={idx} className={`p-3 text-center border-r ${istHeute(tag) ? 'bg-blue-50' : 'bg-[#FFFFFF]'}`}>
                    <div className="text-sm text-[#78716c]">{wochenTage[idx]}</div>
                    <div className={`text-lg font-medium ${istHeute(tag) ? 'text-blue-600' : ''}`}>{tag.getDate()}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 min-h-[400px]">
                {getWochenTage(aktuellesDatum).map((tag, idx) => {
                  const tagesEintraege = getEintraegeForDatum(tag);
                  return (
                    <div key={idx} className="border-r p-2 space-y-1">
                      {tagesEintraege.map(e => (
                        <div 
                          key={e.id}
                          className={`text-xs p-2 rounded cursor-pointer hover:opacity-80 ${e.status === 'erledigt' ? 'opacity-50 line-through' : ''}`}
                          style={{ backgroundColor: getTerminFarbe(e), color: 'white' }}
                          onClick={() => handleEdit(e)}
                        >
                          <div className="font-medium truncate">{e.titel}</div>
                          {e.uhrzeit_von && <div className="opacity-80">{e.uhrzeit_von}</div>}
                          {e.bezug_name && (
                            <div 
                              className="opacity-80 truncate hover:underline hover:opacity-100"
                              onClick={(ev) => { ev.stopPropagation(); navigateToBezug(e); }}
                              title="Klicken um zur Einrichtung zu gehen"
                            >
                              → {e.bezug_name}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            // Tagesansicht
            <div className="p-4">
              <div className={`text-center mb-4 p-3 rounded ${istHeute(aktuellesDatum) ? 'bg-blue-50' : 'bg-[#FFFFFF]'}`}>
                <div className="text-2xl font-semibold">{aktuellesDatum.toLocaleDateString('de-DE', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
              </div>
              {getEintraegeForDatum(aktuellesDatum).length === 0 ? (
                <div className="text-center py-8 text-[#78716c]">
                  <CalendarIcon size={48} className="mx-auto mb-2 opacity-30" />
                  <p>Keine Termine an diesem Tag</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {getEintraegeForDatum(aktuellesDatum).map(e => (
                    <div 
                      key={e.id}
                      className={`p-4 rounded-lg border-l-4 bg-white shadow-sm ${e.status === 'erledigt' ? 'opacity-60' : ''}`}
                      style={{ borderLeftColor: getTerminFarbe(e) }}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${e.status === 'erledigt' ? 'line-through' : ''}`}>{e.titel}</span>
                            <Badge style={{ backgroundColor: getTerminFarbe(e) }} className="text-white">
                              {TERMINART_LABELS[e.terminart]}
                            </Badge>
                            {e.azav_relevant && <Badge variant="destructive">AZAV</Badge>}
                          </div>
                          {e.uhrzeit_von && (
                            <p className="text-sm text-[#78716c] mt-1">
                              {e.uhrzeit_von}{e.uhrzeit_bis ? ` - ${e.uhrzeit_bis}` : ''}
                            </p>
                          )}
                          {e.bezug_name && (
                            <p 
                              className="text-sm text-blue-600 cursor-pointer hover:underline mt-1"
                              onClick={() => navigateToBezug(e)}
                            >
                              → {e.bezug_name}
                            </p>
                          )}
                          {e.notiz && <p className="text-sm text-[#78716c] mt-2">{e.notiz}</p>}
                        </div>
                        <div className="flex gap-1">
                          <button className="action-btn" onClick={() => handleStatusToggle(e)} title={e.status === 'geplant' ? 'Als erledigt markieren' : 'Als geplant markieren'}>
                            {e.status === 'geplant' ? <CheckCircle size={16} className="text-emerald-600" /> : <Circle size={16} />}
                          </button>
                          <button className="action-btn" onClick={() => handleEdit(e)}><Pencil size={16} /></button>
                          <button className="action-btn delete" onClick={() => handleDelete(e.id)}><Trash2 size={16} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>
              {editItem ? "Termin bearbeiten" : "Neuer Termin"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Titel *</Label>
              <Input
                value={formData.titel}
                onChange={(e) => setFormData({...formData, titel: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Terminart *</Label>
                <Select value={formData.terminart} onValueChange={(v) => setFormData({...formData, terminart: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="akquise">Akquise</SelectItem>
                    <SelectItem value="kurs">Kurs</SelectItem>
                    <SelectItem value="pruefung">Prüfung</SelectItem>
                    <SelectItem value="wiedervorlage">Wiedervorlage</SelectItem>
                    <SelectItem value="praktikum">Praktikum</SelectItem>
                    <SelectItem value="azav_frist">AZAV-Frist</SelectItem>
                    <SelectItem value="zertifikat">Zertifikat</SelectItem>
                    <SelectItem value="intern">Intern</SelectItem>
                    <SelectItem value="ausser_haus">Außer Haus</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Datum *</Label>
                <Input
                  type="date"
                  value={formData.datum}
                  onChange={(e) => setFormData({...formData, datum: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Uhrzeit von</Label>
                <Input
                  type="time"
                  value={formData.uhrzeit_von}
                  onChange={(e) => setFormData({...formData, uhrzeit_von: e.target.value})}
                />
              </div>
              <div>
                <Label>Uhrzeit bis</Label>
                <Input
                  type="time"
                  value={formData.uhrzeit_bis}
                  onChange={(e) => setFormData({...formData, uhrzeit_bis: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bezugstyp</Label>
                <Select value={formData.bezug_typ} onValueChange={(v) => setFormData({...formData, bezug_typ: v, bezug_id: ""})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pflegeeinrichtung">Pflegeeinrichtung</SelectItem>
                    <SelectItem value="teilnehmer">Teilnehmer</SelectItem>
                    <SelectItem value="kurs">Kurs</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bezugsobjekt</Label>
                <Select 
                  value={formData.bezug_id} 
                  onValueChange={(v) => setFormData({...formData, bezug_id: v})}
                  disabled={!formData.bezug_typ}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.bezug_typ === "pflegeeinrichtung" && pflegeeinrichtungen.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.einrichtungsname}</SelectItem>
                    ))}
                    {formData.bezug_typ === "teilnehmer" && teilnehmerListe.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.vorname} {t.nachname}</SelectItem>
                    ))}
                    {formData.bezug_typ === "kurs" && kurseListe.map(k => (
                      <SelectItem key={k.id} value={k.id}>{k.kursname}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Notiz</Label>
              <Textarea
                value={formData.notiz}
                onChange={(e) => setFormData({...formData, notiz: e.target.value})}
                rows={2}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="azav_relevant"
                checked={formData.azav_relevant}
                onChange={(e) => setFormData({...formData, azav_relevant: e.target.checked})}
                className="rounded"
              />
              <Label htmlFor="azav_relevant" className="cursor-pointer">AZAV-relevant</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={handleSubmit}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== VERTRIEB ====================
const AKQUISE_STATUS_LABELS = {
  neu: "Neu",
  kontaktiert: "Kontaktiert",
  interessent: "Interessent",
  angebot_gesendet: "Angebot erstellt",
  kunde: "Kunde",
  abgesagt: "Abgesagt"
};

const AKQUISE_STATUS_FARBEN = {
  neu: "#3B82F6",
  kontaktiert: "#F59E0B",
  interessent: "#14B8A6",  // Türkis
  angebot_gesendet: "#8B5CF6",
  kunde: "#22C55E",
  abgesagt: "#EF4444"
};

// Kurse mit Preisen für automatische Berechnung
// Zentrale Kursliste - wird aus Backend geladen, hier als Fallback
const VERTRIEB_KURSE = [
  { id: "ambulanter_pflegeassistent", name: "Ambulanter Pflegeassistent", preis: 2178, ue: 300, bg_faehig: true, praktikum: "3 Monate", massnahmenummer: "364 1045 2026" },
  { id: "berufsspezifisches_deutsch", name: "Berufsspezifisches Deutsch für Pflegehelfer", preis: 1900, ue: 156 },
  { id: "gerontopsychiatrie", name: "Gerontopsychiatrie", preis: 0, ue: 420 },
  { id: "inklusionsassistenz", name: "Inklusionsassistenz", preis: 944, ue: 80 },
  { id: "palliativ_care", name: "Palliativ Care", preis: 3490, ue: 120 },
  { id: "pflegehilfskraft_betreuungsassistent", name: "Pflegehilfskraft & Betreuungsassistent (§§43b, 53b, 53c)", preis: 3114.54, ue: 429, bg_faehig: true, praktikum: "1 Woche", massnahmenummer: "364 1232 2024" },
  { id: "pflegehelfer_qn2", name: "Pflegehilfskraft — Qualifikationsniveau 2 (QN2)", preis: 0, ue: 429 },
  { id: "alltagsbegleiter_betreuungskraft", name: "Alltagsbegleiter / Betreuungskraft (kleiner Schein)", preis: 990, ue: 40 },
  { id: "praxisanleitung", name: "Praxisanleitung", preis: 2499, ue: 300 },
  { id: "lg3_lg4", name: "Weiterqualifikation LG3 & LG4", preis: 490, ue: 120 },
  { id: "pflegeassistent_lg1_lg2", name: "Pflegeassistent LG1 & LG2", preis: 1360, ue: 208, praktikum: "3 Monate" },
  { id: "pflegeassistent_lg1_lg2_bg", name: "Pflegeassistent LG1 & LG2 (BG)", preis: 1495.52, ue: 208, bg_faehig: true, praktikum: "3 Monate", massnahmenummer: "364 1013 2026" },
  { id: "zusatzqualifikation_wunden", name: "Zusatzqualifikation chron. Wunden (Pflegefachkräfte)", preis: 1250, ue: 84 },
  { id: "wundexperte", name: "Wundexperte", preis: 1690, ue: 84, praktikum: "16h Hospitation" },
  { id: "wundtherapeut", name: "Fachtherapeut Wunde nach HKP-Richtlinien", preis: 2890, ue: 120 },
  { id: "pflegetherapeut_wunde", name: "Pflegetherapeut Wunde nach HKP-Richtlinien", preis: 1450, ue: 128 },
  { id: "expertenkurs_intensivpflege", name: "Expertenkurs Außerklinische Intensivpflege", preis: 1990, ue: 200 },
  { id: "rezertifizierung_betreuungskraft", name: "Rezertifizierung Betreuungskraft", preis: 250, ue: 16 },
  { id: "rezertifizierung_praxisanleiter", name: "Rezertifizierung Praxisanleiter", preis: 620, ue: 24 },
  { id: "pflegedienstleitung", name: "Pflegedienstleitung (PDL)", preis: 4690, ue: 720 },
  { id: "intensivpflege", name: "Basiskurs Intensivpflege §132l", preis: 0, ue: 120 },
  { id: "pflegeberater", name: "Pflegeberater §7a SGB XI", preis: 0, ue: 480 },
  { id: "qualitaetsbeauftragter", name: "Qualitätsbeauftragter in der Pflege", preis: 0, ue: 160 },
  { id: "fortbildungspaket_klein", name: "Fortbildungspaket klein", preis: 1200 },
  { id: "fortbildungspaket_gross", name: "Fortbildungspaket groß", preis: 1900 },
  { id: "sonstiges", name: "Sonstiges (individueller Betrag)", preis: 0, ue: 0, custom: true },
];

// ==================== SAMMELMAIL KOMPONENTE ====================
const MAIL_VORLAGEN = {
  fortbildungspakete: {
    betreff: "Neu: Unsere Fortbildungspakete für Ihre Einrichtung ",
    nachricht: `wir freuen uns, Ihnen unsere neuen Jahres-Fortbildungspakete vorzustellen!

Ab sofort bieten wir zwei maßgeschneiderte Pakete an, die Ihre jährlichen Fortbildungspflichten vollständig abdecken:

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
JAHRESPAKET PFLEGE KLEIN – 1.200 €
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Das kompakte Paket für die grundlegenden Fortbildungspflichten.

✓ Hygiene und Infektionsprävention (8 UE)
✓ Haftung, Verantwortung und Delegation (8 UE)

Ideal für: Ambulante Pflegedienste, stationäre Einrichtungen, Pflegepersonal aller Qualifikationsstufen

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
JAHRESPAKET PFLEGE GROSS – 1.900 €
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Das umfassende Paket inkl. Pflichtfortbildungen für Betreuungskräfte & Praxisanleiter.

Pflegepersonal (32 UE):
✓ Hygiene und Infektionsprävention (8 UE)
✓ Haftung, Verantwortung und Delegation (8 UE)
✓ Gewaltprävention und Deeskalation (8 UE)
✓ Kommunikation mit Angehörigen und im Team (8 UE)

Betreuungskräfte nach § 43b SGB XI:
✓ Pflichtfortbildung Betreuungskraft (16 UE)

Praxisanleiter nach PflAPrV:
✓ Pflichtfortbildung Praxisanleitung (24 UE)

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Ihre Vorteile:
• Vollständige Erfüllung aller Fortbildungspflichten
• Erhöhte Prüfsicherheit bei MD-Prüfungen
• Einheitlicher Jahresnachweis
• Planungssicherheit für Ihr Team

Gerne erstellen wir Ihnen ein individuelles Angebot. Kontaktieren Sie uns einfach!

Mit freundlichen Grüßen
Ihr Team der Dickies-Campus GmbH

--
Tel: 0211-29273331
Web: www.dickies-helden.de`
  },
  rabatt_50_prozent: {
    betreff: "Schön, dass wir uns kennenlernen durften! ",
    nachricht: `vielen Dank für das nette Telefonat!

Als Dankeschön für Ihr Interesse möchten wir Ihnen ein exklusives Willkommensangebot machen:

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
50% RABATT AUF IHREN ERSTEN KURS
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

Ob Pflegehilfskraft, Betreuungsassistent oder Praxisanleitung – wir bilden Ihre Mitarbeiter praxisnah und zertifiziert aus.

Unsere Angebote:
✓ Pflegehilfskraft & Betreuungsassistent (528 UE)
✓ Behandlungspflege LG1 & LG2 (200 UE)
✓ Gerontopsychiatrie für Fachkräfte (420 UE)
✓ Pflegedienstleitung PDL (720 UE)
✓ Praxisanleitung (300 UE)
✓ Pflichtfortbildungen für Betreuungskräfte (16 UE)

Bei Fragen stehe ich Ihnen gerne zur Verfügung!

Herzliche Grüße
Ihr Team der Dickies-Campus GmbH

--
Tel: 0211-29273331
Web: www.dickies-helden.de`
  }
};

const SammelmailTab = ({ pflegeeinrichtungen }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [betreff, setBetreff] = useState("");
  const [nachricht, setNachricht] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("alle");
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [result, setResult] = useState(null);
  
  // Vorlage laden
  const loadVorlage = (key) => {
    const vorlage = MAIL_VORLAGEN[key];
    if (vorlage) {
      setBetreff(vorlage.betreff);
      setNachricht(vorlage.nachricht);
      toast.success("Vorlage geladen");
    }
  };
  
  // Gefilterte Einrichtungen
  const filteredPE = useMemo(() => {
    return pflegeeinrichtungen.filter(pe => {
      // Nur mit E-Mail
      if (!pe.ansprechpartner_email) return false;
      
      // Suche
      if (search && !pe.einrichtungsname?.toLowerCase().includes(search.toLowerCase()) &&
          !pe.ort?.toLowerCase().includes(search.toLowerCase())) {
        return false;
      }
      
      // Status-Filter
      if (statusFilter === "kunden" && pe.akquise_status !== "kunde") return false;
      if (statusFilter === "interessenten" && pe.akquise_status !== "interessent" && pe.akquise_status !== "angebot_gesendet") return false;
      if (statusFilter === "neu" && pe.akquise_status !== "neu" && pe.akquise_status !== "kontaktiert") return false;
      
      return true;
    });
  }, [pflegeeinrichtungen, search, statusFilter]);
  
  // Alle auswählen / abwählen
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredPE.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredPE.map(pe => pe.id));
    }
  };
  
  // Einzelne Auswahl
  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };
  
  // Ausgewählte Einrichtungen
  const selectedPE = pflegeeinrichtungen.filter(pe => selectedIds.includes(pe.id));
  
  // Sammelmail senden
  const sendSammelmail = async () => {
    if (selectedPE.length === 0) {
      toast.error("Bitte wählen Sie mindestens einen Empfänger aus");
      return;
    }
    if (!betreff.trim()) {
      toast.error("Bitte geben Sie einen Betreff ein");
      return;
    }
    if (!nachricht.trim()) {
      toast.error("Bitte geben Sie eine Nachricht ein");
      return;
    }
    
    setSending(true);
    try {
      const empfaenger = selectedPE.map(pe => ({
        id: pe.id,
        email: pe.ansprechpartner_email,
        name: pe.einrichtungsname
      }));
      
      const response = await axios.post(`${API}/email/sammelmail`, {
        empfaenger,
        betreff,
        nachricht
      });
      
      setResult(response.data);
      toast.success(`${response.data.results?.gesendet || 0} E-Mails gesendet!`);
      setShowPreview(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Versenden");
    } finally {
      setSending(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <Card className="card-custom">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="text-violet-600" size={24} />
            Sammelmail an Pflegeeinrichtungen
          </CardTitle>
          <p className="text-sm text-gray-500">
            Wählen Sie Empfänger aus und schreiben Sie eine personalisierte Nachricht.
            Jede E-Mail beginnt automatisch mit "Sehr geehrtes Team von [Einrichtungsname]..."
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Filter & Suche */}
          <div className="flex gap-4 items-center flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <Input
                placeholder="Einrichtung suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="alle">Alle mit E-Mail</SelectItem>
                <SelectItem value="kunden">Nur Kunden</SelectItem>
                <SelectItem value="interessenten">Interessenten</SelectItem>
                <SelectItem value="neu">Neue Kontakte</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={toggleSelectAll}>
              {selectedIds.length === filteredPE.length && filteredPE.length > 0 
                ? "Alle abwählen" 
                : `Alle auswählen (${filteredPE.length})`}
            </Button>
          </div>
          
          {/* Empfänger-Liste */}
          <div className="border rounded-lg max-h-[300px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 sticky top-0">
                  <TableHead className="w-[60px] text-center">
                    <div className="flex items-center justify-center">
                      <Checkbox
                        checked={selectedIds.length === filteredPE.length && filteredPE.length > 0}
                        onCheckedChange={toggleSelectAll}
                        className="h-5 w-5"
                      />
                    </div>
                  </TableHead>
                  <TableHead>Einrichtung</TableHead>
                  <TableHead>Ort</TableHead>
                  <TableHead>E-Mail</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPE.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-gray-500">
                      Keine Einrichtungen mit E-Mail gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPE.map(pe => (
                    <TableRow 
                      key={pe.id} 
                      className={`cursor-pointer hover:bg-gray-50 ${selectedIds.includes(pe.id) ? 'bg-violet-100 border-l-4 border-l-violet-500' : ''}`}
                      onClick={() => toggleSelect(pe.id)}
                    >
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={selectedIds.includes(pe.id)}
                            onCheckedChange={() => toggleSelect(pe.id)}
                            className="h-5 w-5"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{pe.einrichtungsname}</TableCell>
                      <TableCell className="text-gray-600">{pe.ort || "-"}</TableCell>
                      <TableCell className="text-sm text-blue-600">{pe.ansprechpartner_email}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {pe.akquise_status === "kunde" ? "Kunde" : 
                           pe.akquise_status === "interessent" ? "Interessent" :
                           pe.akquise_status === "angebot_gesendet" ? "Angebot" :
                           pe.akquise_status === "kontaktiert" ? "Kontaktiert" : "Neu"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          {/* Ausgewählt-Anzeige */}
          {selectedIds.length > 0 && (
            <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg">
              <p className="text-sm font-medium text-violet-700">
                ✓ {selectedIds.length} Empfänger ausgewählt
              </p>
            </div>
          )}
          
          {/* E-Mail Formular */}
          <div className="space-y-4 pt-4 border-t">
            
            {/* Vorlagen-Auswahl */}
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                <FileText size={16} className="text-emerald-600" /> Vorgefertigte Vorlagen
              </p>
              <div className="flex gap-2 flex-wrap">
                <Button 
                  variant="outline" 
                  className="bg-white border-emerald-300 hover:bg-emerald-100"
                  onClick={() => loadVorlage('fortbildungspakete')}
                >
                  Fortbildungspakete (Klein & Groß)
                </Button>
                <Button 
                  variant="outline" 
                  className="bg-white border-amber-300 hover:bg-amber-100"
                  onClick={() => loadVorlage('rabatt_50_prozent')}
                >
                  50% Rabatt-Angebot
                </Button>
              </div>
            </div>
            
            <div>
              <Label className="font-medium">Betreff *</Label>
              <Input
                placeholder="z.B. Neue Fortbildungspakete bei Dickies-Campus"
                value={betreff}
                onChange={(e) => setBetreff(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="font-medium">Nachricht *</Label>
              <p className="text-xs text-gray-500 mb-1">
                Die E-Mail beginnt automatisch mit: "Sehr geehrtes Team von [Einrichtungsname],"
              </p>
              <Textarea
                placeholder="Ihre Nachricht hier eingeben..."
                value={nachricht}
                onChange={(e) => setNachricht(e.target.value)}
                className="mt-1 min-h-[200px]"
              />
            </div>
          </div>
          
          {/* Vorschau & Senden */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setShowPreview(true)}
              disabled={!betreff || !nachricht || selectedIds.length === 0}
            >
              <Eye size={16} className="mr-2" />
              Vorschau
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={sendSammelmail}
              disabled={sending || !betreff || !nachricht || selectedIds.length === 0}
            >
              {sending ? (
                <>Wird gesendet...</>
              ) : (
                <>
                  <Mail size={16} className="mr-2" />
                  {selectedIds.length} E-Mail(s) senden
                </>
              )}
            </Button>
          </div>
          
          {/* Ergebnis */}
          {result && (
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="font-semibold text-emerald-700 mb-2">
                {result.results?.gesendet || 0} E-Mails erfolgreich gesendet
              </p>
              {result.results?.fehler > 0 && (
                <p className="text-sm text-red-600">
                  {result.results.fehler} Fehler aufgetreten
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Vorschau-Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>E-Mail Vorschau</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Betreff:</p>
              <p className="font-semibold">{betreff}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 mb-1">Nachricht (Beispiel für erste Einrichtung):</p>
              <div className="whitespace-pre-wrap text-sm">
                <p className="font-medium">Sehr geehrtes Team von {selectedPE[0]?.einrichtungsname || "[Einrichtungsname]"},</p>
                <p className="mt-2">{nachricht}</p>
              </div>
            </div>
            <div className="p-3 bg-violet-50 rounded-lg">
              <p className="text-sm text-violet-700">
                Diese E-Mail wird an <strong>{selectedIds.length}</strong> Empfänger gesendet.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Abbrechen
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={() => { setShowPreview(false); sendSammelmail(); }}
              disabled={sending}
            >
              <Mail size={16} className="mr-2" />
              Jetzt senden
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Vertrieb = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, canManageProvisionen } = useAuth(); // Für Berechtigungsprüfung (Markus Dickie für Provisionen)
  const [activeTab, setActiveTab] = useState("pipeline");
  const [uebersicht, setUebersicht] = useState(null);
  const [kontakte, setKontakte] = useState([]);
  const [provisionen, setProvisionen] = useState([]);
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [pflegeeinrichtungen, setPflegeeinrichtungen] = useState([]);
  const [vertriebsStatistik, setVertriebsStatistik] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState("");
  const [filterVM, setFilterVM] = useState("all");
  const [selectedPE, setSelectedPE] = useState(null);
  const [showPEDialog, setShowPEDialog] = useState(false);
  const [peHistorie, setPeHistorie] = useState([]);
  const [peHistorieLoading, setPeHistorieLoading] = useState(false);
  const [neueNotizPE, setNeueNotizPE] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProvision, setSelectedProvision] = useState(null); // NEU: Für Provisions-Detail
  const [filterNurPflegedienste, setFilterNurPflegedienste] = useState(true); // Filter für echte Pflegedienste
  const [pipelineStatus, setPipelineStatus] = useState("neu"); // Aktiver Status-Tab in der Pipeline
  const [sortierung, setSortierung] = useState("name"); // name, ort, datum
  const [ansichtModus, setAnsichtModus] = useState("karten"); // karten oder liste
  const [gruppierung, setGruppierung] = useState("keine"); // keine, stadt
  const [selectedIds, setSelectedIds] = useState([]); // Mehrfachauswahl
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [anzeigeLimit, setAnzeigeLimit] = useState(100); // NEU: Limit für Anzeige, kann erhöht werden
  
  // NEU: Follow-Up Email Dialog
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailPE, setEmailPE] = useState(null);
  const [emailText, setEmailText] = useState(""); // Editierbarer Email-Text
  const [emailSubject, setEmailSubject] = useState(""); // Editierbarer Betreff
  const [verfuegbareAngebote, setVerfuegbareAngebote] = useState([]); // Angebote dieser PE
  const [selectedAngebotId, setSelectedAngebotId] = useState(""); // Optionaler PDF-Anhang
  const [sendingEmail, setSendingEmail] = useState(false);

  // Kontakt-Bearbeitung im PE-Dialog
  const [editingKontakt, setEditingKontakt] = useState(false);
  const [peKontaktForm, setPeKontaktForm] = useState({
    ansprechpartner_name: "",
    ansprechpartner_telefon: "",
    ansprechpartner_email: "",
    ort: "",
  });
  const [savingKontakt, setSavingKontakt] = useState(false);

  // WhatsApp-Dialog
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waTarget, setWaTarget] = useState(null);

  const startKontaktEdit = (pe) => {
    setPeKontaktForm({
      ansprechpartner_name: pe?.ansprechpartner_name || "",
      ansprechpartner_telefon: pe?.ansprechpartner_telefon || "",
      ansprechpartner_email: pe?.ansprechpartner_email || "",
      ort: pe?.ort || "",
    });
    setEditingKontakt(true);
  };

  const saveKontakt = async () => {
    if (!selectedPE) return;
    setSavingKontakt(true);
    try {
      const res = await axios.patch(`${API}/pflegeeinrichtungen/${selectedPE.id}/kontakt`, peKontaktForm);
      // Lokal aktualisieren
      setSelectedPE(res.data);
      setPflegeeinrichtungen(prev => prev.map(p => (p.id === selectedPE.id ? res.data : p)));
      toast.success("Kontaktdaten aktualisiert");
      setEditingKontakt(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSavingKontakt(false);
    }
  };
  
  // Follow-Up Email Vorlage
  const getFollowUpEmail = (pe) => {
    const einrichtungsName = pe?.einrichtungsname || pe?.name || "euer Team";
    return `Liebes Team von ${einrichtungsName},

vielen Dank für das angenehme Telefonat.

Wie besprochen unterstützen wir euch bei Weiterbildungen so, dass sie wirklich in euren Arbeitsalltag passen. Ihr legt die Zeiten fest. Wir richten uns danach. Eure Mitarbeitenden bleiben im Dienst und müssen keine Freizeit opfern.

Wir arbeiten flexibel. Wir denken mit. Wir passen Inhalte und Abläufe an euer Team an. Ihr bekommt keine starren Konzepte, sondern Lösungen, die funktionieren.

Wenn ihr mögt, besprechen wir im nächsten Schritt konkret, was ihr braucht und wie wir das umsetzen. Sagt mir einfach, was für euch gut passt.

Viele Grüße
Jean Dickie
Dickies-Campus
0162 2572545`;
  };

  // Angebots-Email Vorlage
  const getAngebotsEmail = (pe) => {
    const einrichtungsName = pe?.einrichtungsname || pe?.name || "euer Team";
    return `Liebes Team von ${einrichtungsName},

wie besprochen sende ich euch hiermit unser Angebot.

Bei Fragen stehe ich euch jederzeit zur Verfügung.

Viele Grüße
Jean Dickie
Dickies-Campus
0162 2572545`;
  };

  const openFollowUpEmail = (pe, isAngebot = false) => {
    setEmailPE(pe);
    setSelectedAngebotId("");
    if (isAngebot) {
      setEmailText(getAngebotsEmail(pe));
      setEmailSubject("Angebot - Dickies-Campus");
    } else {
      setEmailText(getFollowUpEmail(pe));
      setEmailSubject("Weiterbildungen für Ihr Team - Dickies-Campus");
    }
    // Verfügbare Angebote dieser PE laden
    if (pe?.id) {
      axios.get(`${API}/angebote?pe_id=${pe.id}`).then((res) => {
        setVerfuegbareAngebote(res.data || []);
        // Bei isAngebot: neuestes Angebot automatisch vorauswählen
        if (isAngebot && res.data && res.data.length > 0) {
          setSelectedAngebotId(res.data[0].id);
        }
      }).catch(() => setVerfuegbareAngebote([]));
    } else {
      setVerfuegbareAngebote([]);
    }
    setShowEmailDialog(true);
  };

  // Helper um Email-Adresse aus PE zu holen (verschiedene Feldnamen)
  const getPEEmail = (pe) => {
    return pe?.ansprechpartner_email || pe?.email || pe?.kontakt_email || pe?.e_mail || "";
  };

  const copyEmailToClipboard = () => {
    navigator.clipboard.writeText(emailText);
    toast.success("Email in Zwischenablage kopiert!");
  };

  const openEmailClient = () => {
    const email = getPEEmail(emailPE);
    const subject = encodeURIComponent(emailSubject);
    const body = encodeURIComponent(emailText);
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
    setShowEmailDialog(false);
  };

  const sendEmailDirect = async () => {
    const toEmail = getPEEmail(emailPE);
    if (!toEmail) {
      toast.error("Keine Email-Adresse hinterlegt!");
      return;
    }
    
    setSendingEmail(true);
    try {
      const response = await axios.post(`${API}/email/send`, {
        to_email: toEmail,
        to_name: emailPE?.einrichtungsname || emailPE?.name,
        subject: emailSubject,
        body: emailText,
        pe_id: emailPE?.id,
        angebot_id: selectedAngebotId || null,
      });
      
      const hinweis = response.data?.anhang ? ` (Anhang: ${response.data.anhang})` : "";
      toast.success(`Email an ${toEmail} gesendet!${hinweis}`);
      setShowEmailDialog(false);
      
      // Status auf "kontaktiert" setzen falls noch "neu"
      if (emailPE?.vertriebsstatus === "neu") {
        handleStatusChange(emailPE.id, "kontaktiert");
      }
    } catch (error) {
      console.error("Email Fehler:", error);
      toast.error(error.response?.data?.detail || "Email konnte nicht gesendet werden");
    } finally {
      setSendingEmail(false);
    }
  };
  
  // Prüft ob ein Name wie ein Pflegedienst aussieht
  const istWahrscheinlichPflegedienst = (name) => {
    if (!name) return false;
    const n = name.toLowerCase();
    const pflegeKeywords = ['pflege', 'care', 'senior', 'intensiv', 'ambulant', 'heim', 'gmbh', 'gbr', 'ug', 'betreu', 'dienst', 'hilfe', 'wg ', 'residenz', 'haus ', 'living', 'vita', 'med', 'sana', 'cura', 'plus', 'team'];
    return pflegeKeywords.some(kw => n.includes(kw));
  };

  // Mehrfachauswahl Toggle
  const toggleSelection = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  // Alle auswählen/abwählen
  const toggleSelectAll = (ids) => {
    if (ids.every(id => selectedIds.includes(id))) {
      setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedIds(prev => [...new Set([...prev, ...ids])]);
    }
  };

  // Bulk Status-Änderung
  const handleBulkStatusChange = async (newStatus) => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${selectedIds.length} Einrichtungen auf "${AKQUISE_STATUS_LABELS[newStatus]}" setzen?`)) return;
    
    try {
      await Promise.all(selectedIds.map(id => 
        axios.put(`${API}/pflegeeinrichtungen/${id}/akquise-status`, { akquise_status: newStatus })
      ));
      toast.success(`${selectedIds.length} Einträge aktualisiert`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error("Fehler bei Massenänderung");
    }
  };

  // Bulk Löschen
  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`${selectedIds.length} Einrichtungen wirklich LÖSCHEN? Dies kann nicht rückgängig gemacht werden!`)) return;
    
    try {
      await Promise.all(selectedIds.map(id => axios.delete(`${API}/pflegeeinrichtungen/${id}`)));
      toast.success(`${selectedIds.length} Einträge gelöscht`);
      setSelectedIds([]);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  // NEU: Online-Bestellung State (WooCommerce)
  const [onlineBestellung, setOnlineBestellung] = useState({
    kunde_name: "",
    kurs_name: "",
    umsatzbetrag: 0,
    anzahl_teilnehmer: 1,
    bestelldatum: new Date().toISOString().split('T')[0],
    externe_rechnungsnummer: "",
    vertriebsmitarbeiter_id: null,
    notiz: "",
    // NEU: Ratenzahlung
    zahlungsart: "einmalzahlung",
    anzahl_raten: 1,
    bereits_bezahlt: false
  });
  
  // Bestellungsform State (für automatische Berechnung) - MULTI KURS
  const [bestellungForm, setBestellungForm] = useState({
    kurse: [], // Array of {kursId, anzahl}
    gesamtumsatz: 0,
    vertriebsmitarbeiter_id: "",
    notiz: "",
    benachrichtigung_verwaltung: true, // Standard: Verwaltung benachrichtigen
    rabatt_art: "keiner", // keiner, prozent, fixbetrag
    rabatt_hoehe: 0,
    eigene_schulung: "", // Für manuelle Schulungseingabe
    eigene_schulung_preis: 0,
    eigene_schulung_anzahl: 1, // NEU: Anzahl für eigene Schulung
    eigene_schulung_rabatt_art: "keiner", // NEU: keiner, prozent, fixbetrag
    eigene_schulung_rabatt_wert: 0, // NEU
    eigene_schulung_ist_geschenk: false, // NEU
    angebot_text: "" // Eigener Text für Angebot-PDF
  });
  
  const [kontaktForm, setKontaktForm] = useState({
    pflegeeinrichtung_id: "",
    vertriebsmitarbeiter_id: "",
    kontaktart: "akquise",
    kontaktkanal: "telefon",
    kontaktdatum: new Date().toISOString().split('T')[0],
    ansprechpartner: "",
    gespraechsinhalt: "",
    ergebnis: "",
    naechster_schritt: "",
    naechster_kontakt_am: "",
    erledigt: false
  });

  const [provisionForm, setProvisionForm] = useState({
    pflegeeinrichtung_id: "",
    vertriebsmitarbeiter_id: "",
    bezug: "erstabschluss",
    massnahme_oder_kurs: "",
    umsatzbetrag: "",
    datum: new Date().toISOString().split('T')[0],
    // NEU: Rabatt-Felder für manuelle Bearbeitung
    rabatt_art: "keiner",
    rabatt_hoehe: 0,
    rabatt_betrag: 0,
    provisions_netto: null
  });

  const [mitarbeiterForm, setMitarbeiterForm] = useState({
    name: "",
    kuerzel: "",
    provision_neukunde_prozent: 20,
    aktiv: true
  });

  // Automatische Berechnung bei Kursauswahl - MULTI KURS
  const handleKursToggle = (kursId) => {
    setBestellungForm(prev => {
      const exists = prev.kurse.find(k => k.kursId === kursId);
      let newKurse;
      if (exists) {
        newKurse = prev.kurse.filter(k => k.kursId !== kursId);
      } else {
        // NEU: Kurs mit Rabatt-Feldern hinzufügen
        newKurse = [...prev.kurse, { 
          kursId, 
          anzahl: 1,
          rabatt_art: "keiner", // keiner, prozent, fixbetrag, geschenk
          rabatt_wert: 0,
          ist_geschenk: false
        }];
      }
      // Recalculate total (mit Rabatten)
      const total = calculateKurseTotal(newKurse, getEigeneSchulungData(prev));
      return { ...prev, kurse: newKurse, gesamtumsatz: total };
    });
  };

  // Berechne Gesamtpreis aller Kurse inkl. individueller Rabatte
  const calculateKurseTotal = (kurse, eigeneSchulung = null) => {
    let total = kurse.reduce((sum, k) => {
      const kursInfo = VERTRIEB_KURSE.find(vk => vk.id === k.kursId);
      if (!kursInfo) return sum;
      
      const originalPreis = kursInfo.preis * k.anzahl;
      
      if (k.ist_geschenk) {
        return sum; // Geschenk = 0€
      } else if (k.rabatt_art === "prozent" && k.rabatt_wert > 0) {
        return sum + originalPreis * (1 - k.rabatt_wert / 100);
      } else if (k.rabatt_art === "fixbetrag" && k.rabatt_wert > 0) {
        return sum + Math.max(0, originalPreis - (k.rabatt_wert * k.anzahl));
      }
      return sum + originalPreis;
    }, 0);
    
    // Eigene Schulung hinzufügen falls vorhanden
    if (eigeneSchulung && eigeneSchulung.name && eigeneSchulung.preis > 0) {
      const originalPreis = eigeneSchulung.preis * eigeneSchulung.anzahl;
      if (eigeneSchulung.ist_geschenk) {
        // Geschenk = 0€
      } else if (eigeneSchulung.rabatt_art === "prozent" && eigeneSchulung.rabatt_wert > 0) {
        total += originalPreis * (1 - eigeneSchulung.rabatt_wert / 100);
      } else if (eigeneSchulung.rabatt_art === "fixbetrag" && eigeneSchulung.rabatt_wert > 0) {
        total += Math.max(0, originalPreis - (eigeneSchulung.rabatt_wert * eigeneSchulung.anzahl));
      } else {
        total += originalPreis;
      }
    }
    
    return total;
  };

  // Anzahl für einen Kurs ändern
  const handleKursAnzahlChange = (kursId, anzahl) => {
    setBestellungForm(prev => {
      const newKurse = prev.kurse.map(k => 
        k.kursId === kursId ? { ...k, anzahl: parseInt(anzahl) || 1 } : k
      );
      const total = calculateKurseTotal(newKurse, getEigeneSchulungData(prev));
      return { ...prev, kurse: newKurse, gesamtumsatz: total };
    });
  };

  // NEU: Rabatt für einen Kurs ändern
  // WICHTIG: Bei Prozent-Rabatt max 20%, da Provision auch 20% ist
  const handleKursRabattChange = (kursId, rabatt_art, rabatt_wert = 0) => {
    // Validierung: Prozent-Rabatt maximal 20%
    if (rabatt_art === "prozent" && rabatt_wert > 20) {
      toast.error("Rabatt darf maximal 20% sein (Provision = 20%)");
      rabatt_wert = 20;
    }
    
    setBestellungForm(prev => {
      const newKurse = prev.kurse.map(k => {
        if (k.kursId === kursId) {
          const ist_geschenk = rabatt_art === "geschenk";
          return { 
            ...k, 
            rabatt_art: ist_geschenk ? "keiner" : rabatt_art,
            rabatt_wert: ist_geschenk ? 0 : Math.min(rabatt_wert, rabatt_art === "prozent" ? 20 : rabatt_wert),
            ist_geschenk
          };
        }
        return k;
      });
      const total = calculateKurseTotal(newKurse, getEigeneSchulungData(prev));
      return { ...prev, kurse: newKurse, gesamtumsatz: total };
    });
  };

  // Helper: Eigene Schulung Daten für Berechnung
  const getEigeneSchulungData = (form) => ({
    name: form.eigene_schulung,
    preis: form.eigene_schulung_preis,
    anzahl: form.eigene_schulung_anzahl || 1,
    rabatt_art: form.eigene_schulung_rabatt_art || "keiner",
    rabatt_wert: form.eigene_schulung_rabatt_wert || 0,
    ist_geschenk: form.eigene_schulung_ist_geschenk || false
  });

  // NEU: Eigene Schulung ändern mit Neuberechnung
  const handleEigeneSchulungChange = (field, value) => {
    setBestellungForm(prev => {
      const newForm = { ...prev, [field]: value };
      const eigeneSchulung = getEigeneSchulungData(newForm);
      const total = calculateKurseTotal(newForm.kurse, eigeneSchulung);
      return { ...newForm, gesamtumsatz: total };
    });
  };

  // NEU: Rabatt für eigene Schulung setzen
  // WICHTIG: Bei Prozent-Rabatt max 20%, da Provision auch 20% ist
  const handleEigeneSchulungRabattChange = (rabatt_art, rabatt_wert = 0) => {
    // Validierung: Prozent-Rabatt maximal 20%
    if (rabatt_art === "prozent" && rabatt_wert > 20) {
      toast.error("Rabatt darf maximal 20% sein (Provision = 20%)");
      rabatt_wert = 20;
    }
    
    setBestellungForm(prev => {
      const ist_geschenk = rabatt_art === "geschenk";
      const newForm = {
        ...prev,
        eigene_schulung_rabatt_art: ist_geschenk ? "keiner" : rabatt_art,
        eigene_schulung_rabatt_wert: ist_geschenk ? 0 : Math.min(rabatt_wert, rabatt_art === "prozent" ? 20 : rabatt_wert),
        eigene_schulung_ist_geschenk: ist_geschenk
      };
      const eigeneSchulung = getEigeneSchulungData(newForm);
      const total = calculateKurseTotal(newForm.kurse, eigeneSchulung);
      return { ...newForm, gesamtumsatz: total };
    });
  };

  // Akquise-Status ändern - Dialog bleibt offen!
  const handleStatusChange = async (peId, newStatus) => {
    try {
      await axios.put(`${API}/pflegeeinrichtungen/${peId}/akquise-status`, { 
        akquise_status: newStatus 
      });
      toast.success(`Status: ${AKQUISE_STATUS_LABELS[newStatus]}`);
      setSelectedPE(prev => ({...prev, akquise_status: newStatus}));
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Ändern des Status");
    }
  };

  const handleDeletePE = async (peId) => {
    try {
      await axios.delete(`${API}/pflegeeinrichtungen/${peId}`);
      toast.success("Eintrag gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handlePEClick = (pe) => {
    // Reset bestellungForm beim Öffnen des Dialogs
    const defaultVM = mitarbeiter.find(m => m.aktiv && (m.name.includes("Katharina") || m.name.includes("Jean")));
    setBestellungForm({
      kurse: [],
      gesamtumsatz: 0,
      vertriebsmitarbeiter_id: defaultVM?.id || "",
      notiz: "",
      benachrichtigung_verwaltung: true
    });
    setSelectedPE(pe);
    setShowPEDialog(true);
    setNeueNotizPE(""); // Reset neue Notiz
    // Historie für PE laden
    fetchPeHistorie(pe.id);
  };

  const fetchPeHistorie = async (peId) => {
    try {
      setPeHistorieLoading(true);
      const response = await axios.get(`${API}/aktivitaeten/pflegeeinrichtung/${peId}?limit=10`);
      setPeHistorie(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der PE-Historie");
      setPeHistorie([]);
    } finally {
      setPeHistorieLoading(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterVM !== "all" ? `?vertriebsmitarbeiter_id=${filterVM}` : "";
      const [uebRes, kontRes, provRes, maRes, peRes] = await Promise.all([
        axios.get(`${API}/vertrieb/uebersicht${params}`),
        axios.get(`${API}/kontaktprotokoll${params}`),
        axios.get(`${API}/provisionen${params}`),
        axios.get(`${API}/vertriebsmitarbeiter`),
        axios.get(`${API}/pflegeeinrichtungen`)
      ]);
      setUebersicht(uebRes.data);
      setKontakte(kontRes.data);
      setProvisionen(provRes.data);
      setMitarbeiter(maRes.data);
      setPflegeeinrichtungen(peRes.data);
      
      // Lade Vertriebsstatistik
      try {
        const statsRes = await axios.get(`${API}/vertrieb/statistik`);
        setVertriebsStatistik(statsRes.data);
      } catch (e) {
        console.log("Statistik konnte nicht geladen werden");
      }
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [filterVM]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // URL-Parameter verarbeiten (für direkte Navigation aus Dashboard "Kunde reaktivieren" oder Kalender)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const peIdParam = params.get('pe_id') || params.get('pe'); // Unterstütze beide Parameter
    if (peIdParam && pflegeeinrichtungen.length > 0) {
      const pe = pflegeeinrichtungen.find(p => p.id === peIdParam);
      if (pe) {
        setSelectedPE(pe);
        setShowPEDialog(true);
        fetchPeHistorie(pe.id);
        // URL bereinigen
        navigate('/vertrieb', { replace: true });
      }
    }
  }, [location.search, pflegeeinrichtungen, navigate]);

  // Bestellung erfassen (OHNE Dialog zu schließen)
  const handleBestellungSubmit = async () => {
    if (!bestellungForm.vertriebsmitarbeiter_id) {
      toast.error("Bitte Vertriebsmitarbeiter auswählen");
      return;
    }
    if (bestellungForm.kurse.length === 0 && !bestellungForm.eigene_schulung) {
      toast.error("Bitte mindestens einen Kurs auswählen oder eigene Schulung eingeben");
      return;
    }
    if (bestellungForm.gesamtumsatz <= 0) {
      toast.error("Bitte gültigen Umsatz eingeben");
      return;
    }
    
    try {
      // Kurse als String formatieren
      const kurseText = bestellungForm.kurse.map(k => {
        const kursInfo = VERTRIEB_KURSE.find(vk => vk.id === k.kursId);
        return `${k.anzahl}x ${kursInfo?.name || k.kursId}`;
      }).join(", ");
      
      // Eigene Schulung hinzufügen - MIT Anzahl
      const eigeneAnzahl = bestellungForm.eigene_schulung_anzahl || 1;
      const eigeneSchulungText = bestellungForm.eigene_schulung && bestellungForm.eigene_schulung_preis > 0 
        ? `${eigeneAnzahl}x ${bestellungForm.eigene_schulung} (€${bestellungForm.eigene_schulung_preis})` 
        : "";
      const gesamtText = [kurseText, eigeneSchulungText].filter(Boolean).join(", ");
      
      // Anzahl Teilnehmer berechnen (für jeden Kurs UND eigene Schulung)
      const totalTeilnehmer = bestellungForm.kurse.reduce((sum, k) => sum + k.anzahl, 0) + 
        (bestellungForm.eigene_schulung && bestellungForm.eigene_schulung_preis > 0 ? eigeneAnzahl : 0);
      
      // NEUE LOGIK: Berechne Gesamtrabatt als Prozentsatz
      // Rabatt + Provision darf maximal 20% sein!
      let gesamtOriginalPreis = 0;
      let gesamtRabattBetrag = 0;
      
      // 1. Kurs-Rabatte
      bestellungForm.kurse.forEach(k => {
        const kurs = VERTRIEB_KURSE.find(kk => kk.id === k.kursId);
        if (kurs && k.anzahl > 0) {
          const originalPreis = kurs.preis * k.anzahl;
          gesamtOriginalPreis += originalPreis;
          
          if (k.rabatt_art === "prozent" && k.rabatt_wert > 0) {
            gesamtRabattBetrag += originalPreis * k.rabatt_wert / 100;
          } else if (k.rabatt_art === "fixbetrag" && k.rabatt_wert > 0) {
            gesamtRabattBetrag += k.rabatt_wert * k.anzahl;
          }
        }
      });
      
      // 2. Eigene Schulung Rabatt
      if (bestellungForm.eigene_schulung && bestellungForm.eigene_schulung_preis > 0) {
        const originalPreis = bestellungForm.eigene_schulung_preis * eigeneAnzahl;
        gesamtOriginalPreis += originalPreis;
        
        if (bestellungForm.eigene_schulung_rabatt_art === "prozent" && bestellungForm.eigene_schulung_rabatt_wert > 0) {
          gesamtRabattBetrag += originalPreis * bestellungForm.eigene_schulung_rabatt_wert / 100;
        } else if (bestellungForm.eigene_schulung_rabatt_art === "fixbetrag" && bestellungForm.eigene_schulung_rabatt_wert > 0) {
          gesamtRabattBetrag += bestellungForm.eigene_schulung_rabatt_wert * eigeneAnzahl;
        }
      }
      
      // 3. Globaler Rabatt
      if (bestellungForm.rabatt_art === "prozent" && bestellungForm.rabatt_hoehe > 0) {
        gesamtRabattBetrag += (gesamtOriginalPreis - gesamtRabattBetrag) * bestellungForm.rabatt_hoehe / 100;
      } else if (bestellungForm.rabatt_art === "fixbetrag" && bestellungForm.rabatt_hoehe > 0) {
        gesamtRabattBetrag += bestellungForm.rabatt_hoehe;
      }
      
      // Effektiver Rabatt-Prozentsatz
      const gesamtRabattProzent = gesamtOriginalPreis > 0 ? (gesamtRabattBetrag / gesamtOriginalPreis) * 100 : 0;
      
      // PROVISION = 20% - Rabatt%
      // Bei 15% Rabatt → 5% Provision
      const effektiverProvisionsProzent = Math.max(0, 20 - gesamtRabattProzent);
      const provisionBetrag = gesamtOriginalPreis * effektiverProvisionsProzent / 100;
      
      // Provision wird NICHT mehr automatisch erstellt - nur Markus Dickie kann Provisionen eintragen
      // (Provisionserfassung deaktiviert auf Kundenwunsch)
      
      // Kontaktprotokoll speichern (ohne Provisions-Info)
      const rabattText = gesamtRabattBetrag > 0 ? ` (${gesamtRabattProzent.toFixed(0)}% Rabatt)` : '';
      await axios.post(`${API}/kontaktprotokoll`, {
        pflegeeinrichtung_id: selectedPE.id,
        vertriebsmitarbeiter_id: bestellungForm.vertriebsmitarbeiter_id,
        kontaktart: "akquise",
        kontaktkanal: "telefon",
        kontaktdatum: new Date().toISOString().split('T')[0],
        gespraechsinhalt: `BESTELLUNG: ${gesamtText} - Umsatz: €${gesamtOriginalPreis.toLocaleString()}${rabattText}${bestellungForm.notiz ? ' - ' + bestellungForm.notiz : ''}`,
        erledigt: false, // Offen lassen damit Verwaltung es sieht
        fuer_verwaltung: bestellungForm.benachrichtigung_verwaltung
      });
      
      // Status auf Kunde setzen
      await handleStatusChange(selectedPE.id, "kunde");
      
      // Toast mit korrekter Provision
      const toastMsg = gesamtRabattBetrag > 0 
        ? `Bestellung erfasst!\n${totalTeilnehmer} TN | €${gesamtOriginalPreis.toLocaleString()} Umsatz\n${gesamtRabattProzent.toFixed(0)}% Rabatt → ${effektiverProvisionsProzent.toFixed(0)}% Provision\nIhre Provision: €${provisionBetrag.toLocaleString()}`
        : `Bestellung erfasst!\n${totalTeilnehmer} TN | €${gesamtOriginalPreis.toLocaleString()} Umsatz\nProvision (20%): €${provisionBetrag.toLocaleString()}`;
      toast.success(toastMsg + (bestellungForm.benachrichtigung_verwaltung ? '\nVerwaltung wird benachrichtigt' : ''));
      
      // Form zurücksetzen aber Dialog NICHT schließen
      setBestellungForm(prev => ({
        ...prev,
        kurse: [],
        gesamtumsatz: 0,
        notiz: "",
        rabatt_art: "keiner",
        rabatt_hoehe: 0,
        eigene_schulung: "",
        eigene_schulung_preis: 0,
        eigene_schulung_anzahl: 1,
        eigene_schulung_rabatt_art: "keiner",
        eigene_schulung_rabatt_wert: 0,
        eigene_schulung_ist_geschenk: false
      }));
      
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Erfassen der Bestellung");
    }
  };

  // Angebot als PDF erstellen
  // mode: "download" (Standard) oder "email" → hängt PDF als Anhang an E-Mail an Ansprechpartner
  const handleAngebotErstellen = async (pe, mode = "download") => {
    if (bestellungForm.kurse.length === 0 && !bestellungForm.eigene_schulung) {
      toast.error("Bitte mindestens einen Kurs auswählen oder eigene Schulung eingeben");
      return;
    }
    if (mode === "email" && !pe.ansprechpartner_email) {
      toast.error("Keine E-Mail-Adresse hinterlegt", { description: "Bitte erst Ansprechpartner-E-Mail bei der Pflegeeinrichtung ergänzen." });
      return;
    }
    
    try {
      // Positionen zusammenstellen MIT individuellen Rabatten
      const positionen = [];
      
      // Ausgewählte Kurse mit Rabatt-Info
      bestellungForm.kurse.forEach(k => {
        const kursInfo = VERTRIEB_KURSE.find(vk => vk.id === k.kursId);
        if (kursInfo) {
          positionen.push({
            beschreibung: kursInfo.name,
            menge: k.anzahl,
            einzelpreis: kursInfo.preis,
            // NEU: Individuelle Rabatte pro Position
            rabatt_art: k.rabatt_art || "keiner",
            rabatt_wert: k.rabatt_wert || 0,
            ist_geschenk: k.ist_geschenk || false
          });
        }
      });
      
      // Eigene Schulung hinzufügen falls vorhanden - MIT Anzahl und Rabatt
      if (bestellungForm.eigene_schulung && bestellungForm.eigene_schulung_preis > 0) {
        positionen.push({
          beschreibung: bestellungForm.eigene_schulung,
          menge: bestellungForm.eigene_schulung_anzahl || 1,
          einzelpreis: bestellungForm.eigene_schulung_preis,
          // NEU: Auch Rabatte für eigene Schulung
          rabatt_art: bestellungForm.eigene_schulung_rabatt_art || "keiner",
          rabatt_wert: bestellungForm.eigene_schulung_rabatt_wert || 0,
          ist_geschenk: bestellungForm.eigene_schulung_ist_geschenk || false
        });
      }
      
      // Rabatt berechnen
      const zwischensumme = positionen.reduce((sum, p) => sum + (p.menge * p.einzelpreis), 0);
      let rabattBetrag = 0;
      if (bestellungForm.rabatt_art === "prozent") {
        rabattBetrag = zwischensumme * bestellungForm.rabatt_hoehe / 100;
      } else if (bestellungForm.rabatt_art === "fixbetrag") {
        rabattBetrag = bestellungForm.rabatt_hoehe;
      }
      
      // Angebot-PDF generieren
      const response = await axios.post(`${API}/angebote/pdf`, {
        pflegeeinrichtung_id: pe.id,
        empfaenger_name: pe.einrichtungsname,
        empfaenger_strasse: pe.strasse || "",
        empfaenger_plz: pe.plz || "",
        empfaenger_ort: pe.ort || "",
        ansprechpartner: pe.ansprechpartner_name || "",
        positionen: positionen,
        rabatt_art: bestellungForm.rabatt_art,
        rabatt_hoehe: bestellungForm.rabatt_hoehe,
        rabatt_betrag: rabattBetrag,
        notiz: bestellungForm.notiz,
        angebot_text: bestellungForm.angebot_text || "", // Eigener Text fürs Angebot
        gueltig_bis: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 30 Tage gültig
      }, { responseType: 'blob' });

      const filename = `Angebot_${pe.einrichtungsname.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;

      if (mode === "email") {
        // PDF-Blob → base64 → E-Mail-Anhang
        const b64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(String(reader.result).split(",")[1] || "");
          reader.onerror = reject;
          reader.readAsDataURL(response.data);
        });
        const kurseText = positionen.map(p => `${p.menge}× ${p.beschreibung}`).join("\n  • ");
        const emailBody = `Hallo ${pe.ansprechpartner_name || ""},

anbei erhalten Sie unser Angebot für Ihr Team:

  • ${kurseText}

Das Angebot ist 30 Tage gültig. Bei Fragen erreichen Sie mich jederzeit unter 0211 - 29273330 oder per Antwort auf diese Mail.

Herzliche Grüße
Linda Lüppe
Dickies-Campus GmbH
Neuenhausplatz 73 · 40699 Erkrath
`;
        await axios.post(`${API}/email/send`, {
          to_email: pe.ansprechpartner_email,
          to_name:  pe.ansprechpartner_name || pe.einrichtungsname,
          subject:  `Ihr Angebot von Dickies Campus`,
          body:     emailBody,
          pe_id:    pe.id,
          anhang_filename: filename,
          anhang_data:     b64,
        });
        toast.success(`Angebot per E-Mail an ${pe.ansprechpartner_email} verschickt`);
      } else {
        // PDF herunterladen (Default)
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
      
      // Status auf "Angebot erstellt" setzen
      await handleStatusChange(pe.id, "angebot_gesendet");
      
      // Kontaktprotokoll speichern
      const kurseText = positionen.map(p => `${p.menge}x ${p.beschreibung}`).join(", ");
      await axios.post(`${API}/kontaktprotokoll`, {
        pflegeeinrichtung_id: pe.id,
        vertriebsmitarbeiter_id: bestellungForm.vertriebsmitarbeiter_id || "",
        kontaktart: "akquise",
        kontaktkanal: "email",
        kontaktdatum: new Date().toISOString().split('T')[0],
        gespraechsinhalt: `ANGEBOT ERSTELLT: ${kurseText} - Summe: €${(zwischensumme - rabattBetrag).toLocaleString()}${rabattBetrag > 0 ? ` (inkl. Rabatt €${rabattBetrag.toLocaleString()})` : ''}`,
        erledigt: false
      });
      
      toast.success(`Angebot erstellt und heruntergeladen!`);
      fetchData();
    } catch (error) {
      console.error("Fehler beim Angebot:", error);
      console.error("Fehler Details:", error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || "Unbekannter Fehler";
      toast.error(`Fehler: ${errorMsg}`);
    }
  };

  // Schnelle Notiz speichern (OHNE Dialog zu schließen)
  const handleSchnellNotiz = async (notiz) => {
    if (!notiz.trim()) {
      toast.error("Bitte Notiz eingeben");
      return;
    }
    
    const defaultVM = mitarbeiter.find(m => m.aktiv && (m.name.includes("Katharina") || m.name.includes("Jean")));
    
    try {
      await axios.post(`${API}/kontaktprotokoll`, {
        pflegeeinrichtung_id: selectedPE.id,
        vertriebsmitarbeiter_id: bestellungForm.vertriebsmitarbeiter_id || defaultVM?.id || "",
        kontaktart: "akquise",
        kontaktkanal: "telefon",
        kontaktdatum: new Date().toISOString().split('T')[0],
        gespraechsinhalt: notiz,
        erledigt: true
      });
      toast.success("Notiz gespeichert!");
      fetchData();
      return true;
    } catch (error) {
      toast.error("Fehler beim Speichern");
      return false;
    }
  };

  const handleKontaktSubmit = async () => {
    try {
      await axios.post(`${API}/kontaktprotokoll`, kontaktForm);
      toast.success("Kontakt protokolliert");
      setDialogOpen(false);
      resetKontaktForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleProvisionSubmit = async () => {
    try {
      const umsatz = parseFloat(provisionForm.umsatzbetrag) || 0;
      
      // Berechne Rabatt-Prozent
      let rabattProzent = 0;
      if (provisionForm.rabatt_art === "prozent" && provisionForm.rabatt_hoehe > 0) {
        rabattProzent = parseFloat(provisionForm.rabatt_hoehe);
      } else if (provisionForm.rabatt_art === "fixbetrag" && provisionForm.rabatt_betrag > 0 && umsatz > 0) {
        rabattProzent = (parseFloat(provisionForm.rabatt_betrag) / umsatz) * 100;
      }
      
      // Provision = 20% - Rabatt%
      const effektiverProvisionssatz = Math.max(0, 20 - rabattProzent);
      const provisionsBetrag = umsatz * effektiverProvisionssatz / 100;
      const rabattBetrag = provisionForm.rabatt_art === "prozent" 
        ? umsatz * rabattProzent / 100 
        : parseFloat(provisionForm.rabatt_betrag) || 0;
      
      await axios.post(`${API}/provisionen`, {
        ...provisionForm,
        umsatzbetrag: umsatz,
        provisionssatz: effektiverProvisionssatz,
        rabatt_hoehe: rabattProzent,
        rabatt_betrag: rabattBetrag,
        provisions_netto: provisionsBetrag
      });
      toast.success(`Provision erstellt (${effektiverProvisionssatz.toFixed(0)}%)`);
      setDialogOpen(false);
      resetProvisionForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleMitarbeiterSubmit = async () => {
    try {
      await axios.post(`${API}/vertriebsmitarbeiter`, mitarbeiterForm);
      toast.success("Vertriebsmitarbeiter erstellt");
      setDialogOpen(false);
      resetMitarbeiterForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleToggleKontaktErledigt = async (id, erledigt) => {
    try {
      await axios.put(`${API}/kontaktprotokoll/${id}/erledigt?erledigt=${!erledigt}`);
      toast.success(erledigt ? "Als offen markiert" : "Als erledigt markiert");
      fetchData();
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const handleAbrechnenProvision = async (id, abgerechnet) => {
    try {
      await axios.put(`${API}/provisionen/${id}/abrechnen?abgerechnet=${!abgerechnet}`);
      toast.success(abgerechnet ? "Als offen markiert" : "Abgerechnet");
      fetchData();
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const handleDeleteProvision = async (id) => {
    if (!window.confirm("Provision wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/provisionen/${id}`);
      toast.success("Provision gelöscht");
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const [editingProvisionId, setEditingProvisionId] = useState(null);

  const handleEditProvision = (provision) => {
    setEditingProvisionId(provision.id);
    setProvisionForm({
      pflegeeinrichtung_id: provision.pflegeeinrichtung_id || "",
      vertriebsmitarbeiter_id: provision.vertriebsmitarbeiter_id || "",
      bezug: provision.bezug || "erstabschluss",
      massnahme_oder_kurs: provision.massnahme_oder_kurs || "",
      umsatzbetrag: provision.umsatzbetrag || "",
      datum: provision.datum || new Date().toISOString().split('T')[0],
      anzahl_teilnehmer: provision.anzahl_teilnehmer || 1,
      // NEU: Rabatt-Felder laden
      rabatt_art: provision.rabatt_art || "keiner",
      rabatt_hoehe: provision.rabatt_hoehe || 0,
      rabatt_betrag: provision.rabatt_betrag || 0,
      provisions_netto: provision.provisions_netto || null
    });
    setDialogType("provision");
    setDialogOpen(true);
  };

  const handleSaveProvision = async () => {
    try {
      // Listenpreis (vom User eingegeben)
      const listenpreis = parseFloat(provisionForm.umsatzbetrag) || 0;
      
      // Berechne Rabatt-Betrag
      let rabattBetrag = 0;
      let rabattProzent = 0;
      if (provisionForm.rabatt_art === "prozent" && provisionForm.rabatt_hoehe > 0) {
        rabattProzent = parseFloat(provisionForm.rabatt_hoehe);
        rabattBetrag = listenpreis * rabattProzent / 100;
      } else if (provisionForm.rabatt_art === "fixbetrag" && provisionForm.rabatt_hoehe > 0) {
        rabattBetrag = parseFloat(provisionForm.rabatt_hoehe);
        rabattProzent = listenpreis > 0 ? (rabattBetrag / listenpreis) * 100 : 0;
      }
      
      // Tatsächlicher Umsatz = Listenpreis - Rabatt
      const tatsaechlicherUmsatz = Math.max(0, listenpreis - rabattBetrag);
      
      // Provision = 20% vom tatsächlichen Umsatz
      const provisionNetto = tatsaechlicherUmsatz * 0.20;

      const dataToSend = {
        ...provisionForm,
        umsatzbetrag: tatsaechlicherUmsatz,  // Speichere den tatsächlichen Umsatz
        provisionssatz: 20,
        rabatt_hoehe: rabattProzent,
        rabatt_betrag: rabattBetrag,
        provisions_netto: provisionNetto
      };

      if (editingProvisionId) {
        // Bearbeiten
        await axios.put(`${API}/provisionen/${editingProvisionId}`, dataToSend);
        toast.success(`Provision aktualisiert (Umsatz €${tatsaechlicherUmsatz.toLocaleString()}, Provision €${provisionNetto.toLocaleString()})`);
        setEditingProvisionId(null);
      } else {
        // Neu erstellen
        await axios.post(`${API}/provisionen`, dataToSend);
        toast.success(`Provision erstellt (Umsatz €${tatsaechlicherUmsatz.toLocaleString()}, Provision €${provisionNetto.toLocaleString()})`);
      }
      setDialogOpen(false);
      resetProvisionForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const resetKontaktForm = () => {
    const defaultVM = mitarbeiter.find(m => m.aktiv && (m.name.includes("Katharina") || m.name.includes("Jean")));
    setKontaktForm({
      pflegeeinrichtung_id: "",
      vertriebsmitarbeiter_id: defaultVM?.id || "",
      kontaktart: "akquise",
      kontaktkanal: "telefon",
      kontaktdatum: new Date().toISOString().split('T')[0],
      ansprechpartner: "",
      gespraechsinhalt: "",
      ergebnis: "",
      naechster_schritt: "",
      naechster_kontakt_am: "",
      erledigt: false
    });
  };

  const resetProvisionForm = () => {
    const defaultVM = mitarbeiter.find(m => m.aktiv && (m.name.includes("Katharina") || m.name.includes("Jean")));
    setProvisionForm({
      pflegeeinrichtung_id: "",
      vertriebsmitarbeiter_id: defaultVM?.id || "",
      bezug: "erstabschluss",
      massnahme_oder_kurs: "",
      umsatzbetrag: "",
      datum: new Date().toISOString().split('T')[0],
      rabatt_art: "keiner",
      rabatt_hoehe: 0,
      rabatt_betrag: 0,
      provisions_netto: null
    });
  };

  const resetMitarbeiterForm = () => {
    setMitarbeiterForm({ name: "", kuerzel: "", provision_neukunde_prozent: 20, aktiv: true });
  };

  const openDialog = (type) => {
    setDialogType(type);
    setEditingProvisionId(null); // Reset editing mode
    if (type === "kontakt") resetKontaktForm();
    else if (type === "provision") resetProvisionForm();
    else if (type === "mitarbeiter") resetMitarbeiterForm();
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingProvisionId(null);
  };

  // NEU: Status automatisch korrigieren
  const handleFixStatus = async () => {
    try {
      toast.loading("Status wird korrigiert...");
      const response = await axios.post(`${API}/vertrieb/fix-status`);
      toast.dismiss();
      if (response.data.updated > 0) {
        toast.success(`${response.data.updated} Einrichtungen korrigiert!\n${response.data.details?.slice(0, 5).join('\n') || ''}`);
        fetchData();
      } else {
        toast.success("Alle Status sind korrekt!");
      }
    } catch (error) {
      toast.dismiss();
      toast.error("Fehler beim Korrigieren der Status");
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="page-container" data-testid="vertrieb-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Vertrieb & Akquise</h1>
        <div className="flex gap-2">
          {/* NEU: Status korrigieren Button */}
          <Button 
            variant="outline" 
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={handleFixStatus}
            title="Korrigiert automatisch den Status basierend auf Teilnehmern und Rechnungen"
          >
            <RefreshCw size={16} className="mr-2" />
            Status korrigieren
          </Button>
          {/* NEU: Excel-Export der gesamten Pipeline */}
          <Button 
            variant="outline" 
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            data-testid="btn-pipeline-excel"
            onClick={async () => {
              const t = toast.loading("Excel wird erzeugt...");
              try {
                const vmParam = filterVM && filterVM !== 'all' ? `?vertriebsmitarbeiter_id=${filterVM}` : '';
                const res = await axios.get(`${API}/vertrieb/pipeline-excel${vmParam}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data]));
                const a = document.createElement('a');
                a.href = url;
                a.setAttribute('download', `vertriebspipeline_${new Date().toISOString().slice(0,10)}.xlsx`);
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Excel heruntergeladen", { id: t });
              } catch (err) {
                toast.error("Export fehlgeschlagen", { id: t });
              }
            }}
            title="Komplette Pipeline mit allen Status, Notizen und letztem Kontakt als Excel"
          >
            <Download size={16} className="mr-2" />
            Excel-Export
          </Button>
          <Select value={filterVM} onValueChange={setFilterVM}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Alle Mitarbeiter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Mitarbeiter</SelectItem>
              {mitarbeiter.filter(m => m.aktiv).map(m => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={() => openDialog("kontakt")}>
            <Plus size={16} className="mr-2" />
            Neuer Kontakt
          </Button>
        </div>
      </div>

      {/* Übersicht Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card className="card-custom">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Target className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-semibold">{uebersicht?.pipeline_counts?.neu || 0}</p>
                <p className="text-sm text-[#78716c]">Neue Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-custom cursor-pointer hover:shadow-lg hover:border-orange-300 transition-all" onClick={() => setActiveTab("kontakte")}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Phone className="text-orange-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-semibold">{uebersicht?.kontaktierte_count || 0}</p>
                <p className="text-sm text-[#78716c]">Kontaktiert</p>
                <p className="text-xs text-orange-600">Klicken für Details →</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-custom">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Building2 className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-semibold">{uebersicht?.neukunden_count || 0}</p>
                <p className="text-sm text-[#78716c]">Neukunden</p>
              </div>
            </div>
          </CardContent>
        </Card>
        {canManageProvisionen() && (
          <Card className="card-custom cursor-pointer hover:shadow-lg hover:border-amber-300 transition-all" onClick={() => setActiveTab("provisionen")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <DollarSign className="text-amber-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-semibold">€{(uebersicht?.provisionen?.offen || 0).toLocaleString()}</p>
                  <p className="text-sm text-[#78716c]">Provision offen</p>
                  <p className="text-xs text-amber-600 mt-1">Klicken für Details →</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {canManageProvisionen() && (
          <Card className="card-custom cursor-pointer hover:shadow-lg hover:border-purple-300 transition-all" onClick={() => setActiveTab("provisionen")}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Briefcase className="text-purple-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-semibold">€{(uebersicht?.provisionen?.abgerechnet || 0).toLocaleString()}</p>
                  <p className="text-sm text-[#78716c]">Provision abgerechnet</p>
                  <p className="text-xs text-purple-600 mt-1">Klicken für Details →</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        {/* Meine Provision - für Vertriebsmitarbeiter (nicht Markus) */}
        {!canManageProvisionen() && (() => {
          // Finde den Vertriebsmitarbeiter anhand des User-Namens
          const meineVM = mitarbeiter.find(m => m.name === user?.name);
          if (!meineVM) return null;
          const meineProvisionen = provisionen.filter(p => p.vertriebsmitarbeiter_id === meineVM.id);
          const meineOffen = meineProvisionen.filter(p => !p.abgerechnet).reduce((sum, p) => sum + (p.provisions_netto ?? p.provisionsbetrag ?? 0), 0);
          const meineAbgerechnet = meineProvisionen.filter(p => p.abgerechnet).reduce((sum, p) => sum + (p.provisions_netto ?? p.provisionsbetrag ?? 0), 0);
          return (
            <Card className="card-custom bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 col-span-1 md:col-span-2">
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Euro className="text-amber-600" size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-800">Meine Provision</p>
                    <div className="flex gap-4 mt-1">
                      <div>
                        <p className="text-xs text-amber-600">Offen</p>
                        <p className="text-lg font-bold text-amber-700">€{meineOffen.toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600">Ausgezahlt</p>
                        <p className="text-lg font-bold text-emerald-700">€{meineAbgerechnet.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Liste der Provisionen mit Kundennamen */}
                {meineProvisionen.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-200 max-h-40 overflow-y-auto">
                    <p className="text-xs text-amber-700 font-medium mb-2">Details:</p>
                    {meineProvisionen.slice(0, 10).map(p => (
                      <div key={p.id} className="flex justify-between items-center text-sm py-1 border-b border-amber-100 last:border-0">
                        <div className="flex-1">
                          <span className="font-medium text-gray-700">{p.kunde_name || p.pflegeeinrichtung_name || "Unbekannt"}</span>
                          <span className="text-xs text-gray-500 ml-2">{p.massnahme_oder_kurs}</span>
                        </div>
                        <div className="text-right">
                          <span className={`font-semibold ${p.abgerechnet ? 'text-emerald-600' : 'text-amber-600'}`}>
                            €{(p.provisions_netto ?? p.provisionsbetrag ?? 0).toLocaleString()}
                          </span>
                          <span className="text-xs text-gray-400 ml-1">
                            {p.abgerechnet ? '✓' : '○'}
                          </span>
                        </div>
                      </div>
                    ))}
                    {meineProvisionen.length > 10 && (
                      <p className="text-xs text-amber-600 mt-2">+ {meineProvisionen.length - 10} weitere...</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 items-center flex-wrap">
        <Button variant={activeTab === "pipeline" ? "default" : "outline"} onClick={() => setActiveTab("pipeline")}>
          <Target size={16} className="mr-2" />Pipeline
        </Button>
        <Button variant={activeTab === "kontakte" ? "default" : "outline"} onClick={() => setActiveTab("kontakte")}>
          <Phone size={16} className="mr-2" />Kontakte
        </Button>
        {canManageProvisionen() && (
          <Button variant={activeTab === "provisionen" ? "default" : "outline"} onClick={() => setActiveTab("provisionen")}>
            <Euro size={16} className="mr-2" />Provisionen
          </Button>
        )}
        <Button variant={activeTab === "online" ? "default" : "outline"} onClick={() => setActiveTab("online")}>
          <ShoppingCart size={16} className="mr-2" />Online-Shop
        </Button>
        <Button variant={activeTab === "mitarbeiter" ? "default" : "outline"} onClick={() => setActiveTab("mitarbeiter")}>
          <Users size={16} className="mr-2" />Mitarbeiter
        </Button>
        <Button variant={activeTab === "statistik" ? "default" : "outline"} onClick={() => setActiveTab("statistik")}>
          <BarChart3 size={16} className="mr-2" />Statistik
        </Button>
        <Button variant={activeTab === "sammelmail" ? "default" : "outline"} onClick={() => setActiveTab("sammelmail")} className="bg-violet-50 border-violet-200 text-violet-700 hover:bg-violet-100">
          <Mail size={16} className="mr-2" />Sammelmail
        </Button>
        
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={() => navigate('/pflegeeinrichtungen')}>
            <Building2 size={16} className="mr-2" />
            Alle Einrichtungen
          </Button>
        </div>
      </div>

      {/* Pipeline */}
      {activeTab === "pipeline" && (
        <div className="space-y-6">
          {/* Moderne Status-Tabs */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-2">
            <div className="flex gap-1 overflow-x-auto">
              {["neu", "kontaktiert", "interessent", "angebot_gesendet", "kunde", "abgesagt"].map(status => {
                const count = (uebersicht?.pipeline?.[status] || []).filter(pe => 
                  !filterNurPflegedienste || istWahrscheinlichPflegedienst(pe.einrichtungsname)
                ).length;
                const isActive = pipelineStatus === status;
                return (
                  <button
                    key={status}
                    onClick={() => setPipelineStatus(status)}
                    className={`flex items-center gap-3 px-5 py-3 rounded-xl font-medium transition-all whitespace-nowrap ${
                      isActive 
                        ? 'bg-gradient-to-r from-[#6d665b] to-[#8a8279] text-white shadow-lg' 
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span 
                      className={`w-3 h-3 rounded-full ${isActive ? 'bg-white/80' : ''}`}
                      style={{ backgroundColor: isActive ? undefined : AKQUISE_STATUS_FARBEN[status] }}
                    />
                    <span>{AKQUISE_STATUS_LABELS[status]}</span>
                    <span className={`px-2.5 py-0.5 rounded-full text-sm font-semibold ${
                      isActive ? 'bg-white/20' : 'bg-gray-200'
                    }`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Suchleiste & Filter - ERWEITERT */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="relative flex-1 min-w-[280px]">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <Input
                  type="text"
                  placeholder="Nach Name, Ort oder Ansprechpartner suchen..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-12 h-12 text-base rounded-xl border-gray-200 focus:border-[#6d665b] focus:ring-[#6d665b]"
                  data-testid="vertrieb-search-input"
                />
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
              
              <Select value={sortierung} onValueChange={setSortierung}>
                <SelectTrigger className="w-[160px] h-12 rounded-xl">
                  <SelectValue placeholder="Sortierung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">A-Z Name</SelectItem>
                  <SelectItem value="ort">Nach Stadt</SelectItem>
                  <SelectItem value="datum">Neueste zuerst</SelectItem>
                </SelectContent>
              </Select>

              <Select value={gruppierung} onValueChange={setGruppierung}>
                <SelectTrigger className="w-[160px] h-12 rounded-xl">
                  <SelectValue placeholder="Gruppierung" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keine">Keine Gruppierung</SelectItem>
                  <SelectItem value="stadt">Nach Stadt</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={filterNurPflegedienste}
                    onChange={(e) => setFilterNurPflegedienste(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-[#6d665b] focus:ring-[#6d665b]"
                  />
                  <span className="text-sm font-medium text-gray-700">Nur Pflegedienste</span>
                </label>
              </div>

              <div className="flex bg-gray-100 rounded-xl p-1">
                <button 
                  onClick={() => setAnsichtModus("karten")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    ansichtModus === "karten" ? "bg-white shadow-sm text-[#6d665b]" : "text-gray-500"
                  }`}
                >
                  <LayoutGrid size={18} />
                </button>
                <button 
                  onClick={() => setAnsichtModus("liste")}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    ansichtModus === "liste" ? "bg-white shadow-sm text-[#6d665b]" : "text-gray-500"
                  }`}
                >
                  <List size={18} />
                </button>
              </div>
            </div>
          </div>
          
          {/* Einträge-Anzeige */}
          {(() => {
            const allePE = (uebersicht?.pipeline?.[pipelineStatus] || [])
              .filter(pe => {
                if (filterNurPflegedienste && !istWahrscheinlichPflegedienst(pe.einrichtungsname)) return false;
                if (!searchTerm) return true;
                const search = searchTerm.toLowerCase();
                return (
                  pe.einrichtungsname?.toLowerCase().includes(search) ||
                  pe.ort?.toLowerCase().includes(search) ||
                  pe.ansprechpartner_name?.toLowerCase().includes(search)
                );
              })
              .sort((a, b) => {
                if (sortierung === "name") return (a.einrichtungsname || "").localeCompare(b.einrichtungsname || "");
                if (sortierung === "ort") return (a.ort || "").localeCompare(b.ort || "");
                if (sortierung === "datum") return (b.created_at || "").localeCompare(a.created_at || "");
                return 0;
              });

            // Gruppierung nach Stadt
            const gruppiertePE = gruppierung === "stadt" 
              ? allePE.reduce((acc, pe) => {
                  const stadt = pe.ort || "Ohne Ort";
                  if (!acc[stadt]) acc[stadt] = [];
                  acc[stadt].push(pe);
                  return acc;
                }, {})
              : { "": allePE };

            const stadtGruppen = Object.keys(gruppiertePE).sort();
            const alleIds = allePE.slice(0, anzeigeLimit).map(pe => pe.id);
            
            return (
              <div>
                {/* Info-Leiste mit Bulk-Aktionen */}
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-gray-500">
                      <span className="font-semibold text-gray-700">{allePE.length}</span> Einrichtungen in <span className="font-medium">{AKQUISE_STATUS_LABELS[pipelineStatus]}</span>
                      {searchTerm && <span> • Suche: "{searchTerm}"</span>}
                    </p>
                    
                    {/* Mehrfachauswahl aktivieren */}
                    <Button 
                      variant={selectedIds.length > 0 ? "default" : "outline"}
                      size="sm"
                      className="h-9 rounded-lg"
                      onClick={() => {
                        if (selectedIds.length > 0) {
                          setSelectedIds([]);
                        } else {
                          toggleSelectAll(alleIds);
                        }
                      }}
                    >
                      {selectedIds.length > 0 ? (
                        <><XCircle size={14} className="mr-2" />{selectedIds.length} ausgewählt</>
                      ) : (
                        <><CheckCircle size={14} className="mr-2" />Auswählen</>
                      )}
                    </Button>
                  </div>

                  {/* Bulk-Aktionen wenn ausgewählt */}
                  {selectedIds.length > 0 && (
                    <div className="flex items-center gap-2 bg-[#6d665b]/5 px-4 py-2 rounded-xl">
                      <span className="text-sm font-medium text-[#6d665b] mr-2">Aktion für {selectedIds.length}:</span>
                      {pipelineStatus === "neu" && (
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleBulkStatusChange("kontaktiert")}>
                          <Phone size={12} className="mr-1" /> Kontaktiert
                        </Button>
                      )}
                      {(pipelineStatus === "kontaktiert" || pipelineStatus === "interessent") && (
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleBulkStatusChange("angebot_gesendet")}>
                          <FileText size={12} className="mr-1" /> Angebot
                        </Button>
                      )}
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleBulkStatusChange("kunde")}>
                        <CheckCircle size={12} className="mr-1" /> Kunde
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => handleBulkStatusChange("abgesagt")}>
                        <XCircle size={12} className="mr-1" /> Absage
                      </Button>
                      <Button size="sm" variant="destructive" className="h-8 text-xs" onClick={handleBulkDelete}>
                        <Trash2 size={12} className="mr-1" /> Löschen
                      </Button>
                    </div>
                  )}
                </div>

                {/* Karten-Ansicht */}
                {ansichtModus === "karten" && (
                  <div className="space-y-6">
                    {stadtGruppen.map(stadt => (
                      <div key={stadt || "alle"}>
                        {/* Stadt-Header bei Gruppierung */}
                        {gruppierung === "stadt" && stadt && (
                          <div className="flex items-center gap-3 mb-4 pb-2 border-b border-gray-200">
                            <MapPin size={18} className="text-[#6d665b]" />
                            <h3 className="font-semibold text-lg text-gray-800">{stadt}</h3>
                            <Badge variant="secondary">{gruppiertePE[stadt].length}</Badge>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                          {(gruppiertePE[stadt] || []).slice(0, anzeigeLimit).map(pe => (
                            <div 
                              key={pe.id} 
                              className={`bg-white rounded-2xl shadow-sm border overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200 group relative ${
                                selectedIds.includes(pe.id) ? 'border-[#6d665b] ring-2 ring-[#6d665b]/20' : 'border-gray-100 hover:border-[#6d665b]/30'
                              }`}
                              onClick={() => selectedIds.length > 0 ? toggleSelection(pe.id) : handlePEClick({...pe, akquise_status: pipelineStatus})}
                            >
                              {/* Checkbox bei Mehrfachauswahl */}
                              {selectedIds.length > 0 && (
                                <div className="absolute top-3 right-3 z-10">
                                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                    selectedIds.includes(pe.id) 
                                      ? 'bg-[#6d665b] border-[#6d665b]' 
                                      : 'bg-white border-gray-300'
                                  }`}>
                                    {selectedIds.includes(pe.id) && <Check size={14} className="text-white" />}
                                  </div>
                                </div>
                              )}
                              
                              {/* Farbiger Header */}
                              <div 
                                className="h-2"
                                style={{ backgroundColor: AKQUISE_STATUS_FARBEN[pipelineStatus] }}
                              />
                              
                              <div className="p-5">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <h3 className="font-semibold text-gray-900 text-base line-clamp-2 flex-1">
                                    {pe.einrichtungsname}
                                  </h3>
                                  <button
                                    type="button"
                                    data-testid={`hot-lead-toggle-${pe.id}`}
                                    title={pe.hot_lead ? "Hot-Lead-Markierung entfernen" : "Als Hot-Lead markieren"}
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      const next = !pe.hot_lead;
                                      try {
                                        await axios.put(`${API}/pflegeeinrichtungen/${pe.id}/hot-lead`, { hot_lead: next });
                                        fetchData();
                                        toast.success(next ? "Hot-Lead markiert" : "Markierung entfernt");
                                      } catch {
                                        toast.error("Aktion fehlgeschlagen");
                                      }
                                    }}
                                    className={`text-2xl leading-none transition-all hover:scale-110 ${
                                      pe.hot_lead ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'
                                    }`}
                                  >
                                    {pe.hot_lead ? '' : ''}
                                  </button>
                                </div>
                                
                                <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                                  <MapPin size={14} />
                                  <span>{pe.ort || "Kein Ort"}</span>
                                </div>
                                
                                {pe.ansprechpartner_name && (
                                  <div className="flex items-center gap-2 text-gray-500 text-sm mb-3">
                                    <User size={14} />
                                    <span>{pe.ansprechpartner_name}</span>
                                  </div>
                                )}
                                
                                {/* NEU: Letzter Kontakt */}
                                {pe.letzter_kontakt_am && (
                                  <div className="flex items-center gap-2 text-gray-500 text-xs mb-2">
                                    <Clock size={12} />
                                    <span>Letzter Kontakt: {new Date(pe.letzter_kontakt_am).toLocaleDateString('de-DE')}</span>
                                  </div>
                                )}
                                
                                {/* NEU: Teilnehmer-Anzahl anzeigen */}
                                {pe.teilnehmer_count > 0 && (
                                  <div className="flex items-center gap-2 text-emerald-600 text-sm mb-3 font-medium">
                                    <Users size={14} />
                                    <span>{pe.teilnehmer_count} Teilnehmer</span>
                                    {pe.akquise_status !== "kunde" && (
                                      <Badge className="bg-red-100 text-red-700 border-0 text-xs">Status falsch!</Badge>
                                    )}
                                  </div>
                                )}
                                
                                {pe.ist_neukunde && (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-0 mb-3">Neukunde</Badge>
                                )}
                                
                                {/* Fortbildungspaket Badge */}
                                {pe.fortbildungspaket && (
                                  <Badge className={`border-0 mb-3 ml-1 ${pe.fortbildungspaket === 'klein' ? 'bg-blue-100 text-blue-700' : 'bg-violet-100 text-violet-700'}`}>
                                    {pe.fortbildungspaket === 'klein' ? 'Klein' : 'Groß'}
                                    {pe.fortbildungspaket_start && (
                                      <span className="ml-1 opacity-75">
                                        bis {(() => {
                                          const start = new Date(pe.fortbildungspaket_start);
                                          const ende = new Date(start);
                                          ende.setFullYear(ende.getFullYear() + 1);
                                          return ende.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});
                                        })()}
                                      </span>
                                    )}
                                  </Badge>
                                )}
                                
                                {/* Aktionen - nur wenn keine Mehrfachauswahl */}
                                {selectedIds.length === 0 && (
                                  <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-100">
                                    {pipelineStatus === "neu" && (
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        className="text-xs h-8 rounded-lg border-amber-200 text-amber-700 hover:bg-amber-50"
                                        onClick={(e) => { e.stopPropagation(); handleStatusChange(pe.id, "kontaktiert"); }}
                                      >
                                        <Phone size={12} className="mr-1" /> Kontaktiert
                                      </Button>
                                    )}
                                    {(pipelineStatus === "kontaktiert" || pipelineStatus === "angebot_gesendet") && (
                                      <>
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          className="text-xs h-8 rounded-lg border-blue-200 text-blue-700 hover:bg-blue-50"
                                          onClick={(e) => { e.stopPropagation(); openFollowUpEmail(pe, false); }}
                                        >
                                          <Mail size={12} className="mr-1" /> Follow-Up
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          className="text-xs h-8 rounded-lg border-purple-200 text-purple-700 hover:bg-purple-50"
                                          onClick={(e) => { e.stopPropagation(); openFollowUpEmail(pe, true); }}
                                        >
                                          <Mail size={12} className="mr-1" /> Angebot senden
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="outline"
                                          className="text-xs h-8 rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                          onClick={(e) => { e.stopPropagation(); handleStatusChange(pe.id, "kunde"); }}
                                        >
                                          <CheckCircle size={12} className="mr-1" /> Kunde
                                        </Button>
                                      </>
                                    )}
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      className="text-xs h-8 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-600 ml-auto"
                                      onClick={(e) => { 
                                        e.stopPropagation(); 
                                        if (window.confirm(`"${pe.einrichtungsname}" wirklich löschen?`)) {
                                          handleDeletePE(pe.id);
                                        }
                                      }}
                                    >
                                      <Trash2 size={12} />
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Listen-Ansicht */}
                {ansichtModus === "liste" && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-100">
                        <tr>
                          {selectedIds.length > 0 && (
                            <th className="w-12 px-4 py-4">
                              <input 
                                type="checkbox" 
                                checked={alleIds.every(id => selectedIds.includes(id))}
                                onChange={() => toggleSelectAll(alleIds)}
                                className="w-5 h-5 rounded border-gray-300 text-[#6d665b] focus:ring-[#6d665b]"
                              />
                            </th>
                          )}
                          <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Einrichtung</th>
                          <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Ort</th>
                          <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Ansprechpartner</th>
                          <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">Aktionen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allePE.slice(0, anzeigeLimit).map((pe, idx) => (
                          <tr 
                            key={pe.id} 
                            className={`border-b border-gray-50 hover:bg-[#FFFFFF] cursor-pointer transition-colors ${
                              selectedIds.includes(pe.id) ? 'bg-[#6d665b]/5' : idx % 2 === 0 ? '' : 'bg-gray-50/30'
                            }`}
                            onClick={() => selectedIds.length > 0 ? toggleSelection(pe.id) : handlePEClick({...pe, akquise_status: pipelineStatus})}
                          >
                            {selectedIds.length > 0 && (
                              <td className="w-12 px-4 py-4" onClick={(e) => { e.stopPropagation(); toggleSelection(pe.id); }}>
                                <input 
                                  type="checkbox" 
                                  checked={selectedIds.includes(pe.id)}
                                  onChange={() => {}}
                                  className="w-5 h-5 rounded border-gray-300 text-[#6d665b] focus:ring-[#6d665b]"
                                />
                              </td>
                            )}
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div 
                                  className="w-2 h-8 rounded-full"
                                  style={{ backgroundColor: AKQUISE_STATUS_FARBEN[pipelineStatus] }}
                                />
                                <div>
                                  <p className="font-medium text-gray-900">{pe.einrichtungsname}</p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {pe.ist_neukunde && (
                                      <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">Neukunde</Badge>
                                    )}
                                    {pe.teilnehmer_count > 0 && (
                                      <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">
                                        {pe.teilnehmer_count} TN
                                      </Badge>
                                    )}
                                    {(pe.tn_anzahl_aktuelles_jahr > 0) && (
                                      <Badge className="bg-amber-100 text-amber-800 border-0 text-xs" title={`${pe.tn_anzahl_aktuelles_jahr} Teilnehmer im aktuellen Jahr`}>
                                        {pe.tn_anzahl_aktuelles_jahr}/{new Date().getFullYear()}
                                      </Badge>
                                    )}
                                    {pe.letzte_bestellung_am && (
                                      <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 text-xs" title="Letzte Bestellung (aus Teilnehmer-Kursstart)">
                                        {pe.letzte_bestellung_am}
                                      </Badge>
                                    )}
                                    {pe.teilnehmer_count > 0 && pe.akquise_status !== "kunde" && (
                                      <Badge className="bg-red-100 text-red-700 border-0 text-xs"></Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-gray-600">{pe.ort || "-"}</td>
                            <td className="px-6 py-4 text-gray-600">{pe.ansprechpartner_name || "-"}</td>
                            <td className="px-6 py-4">
                              <div className="flex justify-end gap-2">
                                {pipelineStatus === "neu" && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    className="h-8 text-amber-600 hover:bg-amber-50"
                                    onClick={(e) => { e.stopPropagation(); handleStatusChange(pe.id, "kontaktiert"); }}
                                  >
                                    <Phone size={14} />
                                  </Button>
                                )}
                                {(pipelineStatus === "kontaktiert" || pipelineStatus === "angebot_gesendet") && (
                                  <>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      className="h-8 text-blue-600 hover:bg-blue-50"
                                      onClick={(e) => { e.stopPropagation(); openFollowUpEmail(pe, false); }}
                                      title="Follow-Up Email"
                                    >
                                      <Mail size={14} />
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      className="h-8 text-purple-600 hover:bg-purple-50"
                                      onClick={(e) => { e.stopPropagation(); openFollowUpEmail(pe, true); }}
                                      title="Angebot senden"
                                    >
                                      <Mail size={14} />
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="ghost"
                                      className="h-8 text-emerald-600 hover:bg-emerald-50"
                                      onClick={(e) => { e.stopPropagation(); handleStatusChange(pe.id, "kunde"); }}
                                    >
                                      <CheckCircle size={14} />
                                    </Button>
                                  </>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="h-8 text-red-500 hover:bg-red-50"
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    if (window.confirm(`"${pe.einrichtungsname}" wirklich löschen?`)) {
                                      handleDeletePE(pe.id);
                                    }
                                  }}
                                >
                                  <Trash2 size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {allePE.length === 0 && (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-16 text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Search size={32} className="text-gray-400" />
                    </div>
                    <p className="text-gray-500 text-lg">
                      {searchTerm ? `Keine Treffer für "${searchTerm}"` : "Keine Einträge in dieser Kategorie"}
                    </p>
                  </div>
                )}

                {allePE.length > anzeigeLimit && (
                  <div className="text-center mt-4 space-y-2">
                    <p className="text-gray-500">
                      Zeige {Math.min(anzeigeLimit, allePE.length)} von {allePE.length} Einträgen
                    </p>
                    <div className="flex gap-2 justify-center">
                      <Button 
                        variant="outline"
                        onClick={() => setAnzeigeLimit(prev => prev + 100)}
                        className="text-sm"
                      >
                        +100 mehr laden
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setAnzeigeLimit(allePE.length)}
                        className="text-sm"
                      >
                        Alle {allePE.length} anzeigen
                      </Button>
                    </div>
                  </div>
                )}
                
                {allePE.length > 0 && allePE.length <= anzeigeLimit && allePE.length > 100 && (
                  <p className="text-center text-gray-500 mt-4">
                    Zeige alle {allePE.length} Einträge
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* VERBESSERTER PE Detail Dialog */}
      <Dialog open={showPEDialog} onOpenChange={(open) => { setShowPEDialog(open); if (!open) setEditingKontakt(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Building2 size={24} className="text-[#D9775A]" />
              {selectedPE?.einrichtungsname}
              <Badge 
                className="ml-2"
                style={{ 
                  backgroundColor: `${AKQUISE_STATUS_FARBEN[selectedPE?.akquise_status]}20`,
                  color: AKQUISE_STATUS_FARBEN[selectedPE?.akquise_status]
                }}
              >
                {AKQUISE_STATUS_LABELS[selectedPE?.akquise_status]}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          {selectedPE && (
            <div className="space-y-5">
              {/* Kontaktdaten */}
              {!editingKontakt ? (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-gray-700">Kontaktdaten</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                      data-testid="btn-pe-kontakt-edit"
                      onClick={() => startKontaktEdit(selectedPE)}
                    >
                      <Edit size={12} className="mr-1" /> Bearbeiten
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <p className="text-xs text-gray-500 flex items-center gap-1"><Building2 size={10} /> Ort</p>
                      <p className="font-medium">{selectedPE.ort || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 flex items-center gap-1"><User size={10} /> Ansprechpartner</p>
                      <p className="font-medium">{selectedPE.ansprechpartner_name || "-"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 flex items-center gap-1"><Phone size={10} /> Telefon</p>
                      {selectedPE.ansprechpartner_telefon ? (
                        <div className="flex items-center gap-2 flex-wrap">
                          <a href={`tel:${selectedPE.ansprechpartner_telefon}`} className="text-blue-600 font-medium hover:underline">{selectedPE.ansprechpartner_telefon}</a>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setWaTarget(selectedPE); setWaDialogOpen(true); }}
                            className={`inline-flex items-center gap-1 rounded-full text-white text-[10px] font-semibold px-2 py-0.5 transition-colors ${selectedPE.whatsapp_last_sent_at ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-green-600 hover:bg-green-700'}`}
                            title={selectedPE.whatsapp_last_sent_at ? `Bereits ${selectedPE.whatsapp_sent_count || 1}× gesendet` : 'WhatsApp-Nachricht senden'}
                            data-testid="btn-vertrieb-detail-whatsapp"
                          >
                            <MessageCircle size={10} /> WhatsApp
                          </button>
                        </div>
                      ) : (
                        <p className="font-medium text-gray-400">-</p>
                      )}
                      {selectedPE.whatsapp_last_sent_at && (
                        <p className="text-[10px] text-emerald-700 mt-0.5" data-testid="pe-detail-wa-hint">
                          ✓ WA {new Date(selectedPE.whatsapp_last_sent_at).toLocaleDateString('de-DE')}
                          {(selectedPE.whatsapp_sent_count || 0) > 1 && ` (${selectedPE.whatsapp_sent_count}×)`}
                          {selectedPE.whatsapp_last_sent_by ? ` · ${selectedPE.whatsapp_last_sent_by}` : ''}
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 flex items-center gap-1"><Mail size={10} /> E-Mail</p>
                      {selectedPE.ansprechpartner_email ? (
                        <a href={`mailto:${selectedPE.ansprechpartner_email}`} className="text-blue-600 font-medium text-sm hover:underline truncate block">{selectedPE.ansprechpartner_email}</a>
                      ) : (
                        <p className="font-medium text-gray-400">-</p>
                      )}
                    </div>
                    {selectedPE.naechste_wiedervorlage && (
                      <div>
                        <p className="text-xs text-gray-500 flex items-center gap-1"><CalendarIcon size={10} /> Wiedervorlage</p>
                        <p className="font-medium text-amber-600">{selectedPE.naechste_wiedervorlage}</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-semibold text-emerald-800">Kontaktdaten bearbeiten</p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        data-testid="btn-pe-kontakt-cancel"
                        onClick={() => setEditingKontakt(false)}
                        disabled={savingKontakt}
                      >
                        Abbrechen
                      </Button>
                      <Button
                        size="sm"
                        className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="btn-pe-kontakt-save"
                        onClick={saveKontakt}
                        disabled={savingKontakt}
                      >
                        {savingKontakt ? "Speichern..." : "Speichern"}
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Ort</Label>
                      <Input
                        data-testid="input-pe-ort"
                        value={peKontaktForm.ort}
                        onChange={(e) => setPeKontaktForm({ ...peKontaktForm, ort: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Ansprechpartner</Label>
                      <Input
                        data-testid="input-pe-ansprechpartner"
                        value={peKontaktForm.ansprechpartner_name}
                        onChange={(e) => setPeKontaktForm({ ...peKontaktForm, ansprechpartner_name: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Telefon</Label>
                      <Input
                        data-testid="input-pe-telefon"
                        type="tel"
                        value={peKontaktForm.ansprechpartner_telefon}
                        onChange={(e) => setPeKontaktForm({ ...peKontaktForm, ansprechpartner_telefon: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">E-Mail</Label>
                      <Input
                        data-testid="input-pe-email"
                        type="email"
                        value={peKontaktForm.ansprechpartner_email}
                        onChange={(e) => setPeKontaktForm({ ...peKontaktForm, ansprechpartner_email: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Vertriebsmitarbeiter Zuweisung */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <User size={16} className="text-emerald-600" /> Vertriebsmitarbeiter
                </p>
                {(() => {
                  const istJean = user?.email === 'jean@dickies-helden.de';
                  const hatVMZugewiesen = !!selectedPE.vertriebsmitarbeiter_id;
                  const vmWurdeVonJeanGesetzt = selectedPE.vm_gesetzt_von === 'jean@dickies-helden.de';
                  
                  // Kann ändern wenn:
                  // 1. Noch kein VM zugewiesen, ODER
                  // 2. User ist Jean (Chef kann alles ändern)
                  const kannAendern = !hatVMZugewiesen || istJean;
                  
                  return (
                    <div className="flex gap-3 items-center flex-wrap">
                      <Select 
                        value={selectedPE.vertriebsmitarbeiter_id || "none"} 
                        onValueChange={async (v) => {
                          if (!kannAendern) return;
                          const newVmId = v === "none" ? null : v;
                          try {
                            const response = await axios.put(`${API}/pflegeeinrichtungen/${selectedPE.id}/vertriebsmitarbeiter`, {
                              vertriebsmitarbeiter_id: newVmId,
                              gesetzt_von: user?.email
                            });
                            const vmName = mitarbeiter.find(vm => vm.id === newVmId)?.name || "Keiner";
                            toast.success(`Vertriebsmitarbeiter: ${vmName}`);
                            setSelectedPE(response.data);
                            fetchData();
                          } catch (error) {
                            console.error("Fehler beim Speichern:", error);
                            toast.error("Fehler beim Speichern");
                          }
                        }}
                        disabled={!kannAendern}
                      >
                        <SelectTrigger className={`w-[250px] ${kannAendern ? 'bg-white' : 'bg-gray-100 cursor-not-allowed'}`}>
                          <SelectValue placeholder="Nicht zugewiesen" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Nicht zugewiesen</SelectItem>
                          {mitarbeiter.filter(vm => vm.id).map(vm => (
                            <SelectItem key={vm.id} value={vm.id}>{vm.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-gray-500">
                        {selectedPE.vertriebsmitarbeiter_id 
                          ? `Zugewiesen an: ${mitarbeiter.find(vm => vm.id === selectedPE.vertriebsmitarbeiter_id)?.name || 'Unbekannt'}`
                          : 'Noch nicht zugewiesen'
                        }
                      </span>
                      {hatVMZugewiesen && !istJean && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded">
                          Nur Jean kann ändern
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
              
              {/* Bestehende Notizen aus Pflegeeinrichtung + Neue hinzufügen */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <FileText size={16} className="text-blue-600" /> Interne Notizen (Pflegeeinrichtung)
                </p>
                
                {/* Bestehende Notizen */}
                {selectedPE.interne_notizen && (
                  <div className="bg-white rounded p-2 mb-3 max-h-32 overflow-y-auto">
                    <pre className="text-sm text-gray-700 whitespace-pre-wrap font-sans">{selectedPE.interne_notizen}</pre>
                  </div>
                )}
                
                {/* Neue Notiz hinzufügen */}
                <div className="space-y-2">
                  <Textarea
                    placeholder="Neue Notiz hinzufügen..."
                    value={neueNotizPE}
                    onChange={(e) => setNeueNotizPE(e.target.value)}
                    className="min-h-[50px] text-sm"
                  />
                  {neueNotizPE.trim() && (
                    <div className="flex justify-end gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setNeueNotizPE("")}
                      >
                        Abbrechen
                      </Button>
                      <Button 
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={async () => {
                          try {
                            const heute = new Date().toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit', 
                              year: 'numeric'
                            });
                            const benutzer = user?.name || 'Unbekannt';
                            const neueNotiz = `[${heute} - ${benutzer}] ${neueNotizPE.trim()}`;
                            const alleNotizen = selectedPE.interne_notizen 
                              ? `${neueNotiz}\n\n${selectedPE.interne_notizen}`
                              : neueNotiz;
                            
                            await axios.put(`${API}/pflegeeinrichtungen/${selectedPE.id}`, {
                              ...selectedPE,
                              interne_notizen: alleNotizen
                            });
                            setNeueNotizPE("");
                            // Update local state
                            setSelectedPE(prev => ({...prev, interne_notizen: alleNotizen}));
                            fetchPeHistorie(selectedPE.id);
                            toast.success("Notiz hinzugefügt");
                          } catch (error) {
                            toast.error("Fehler beim Speichern");
                          }
                        }}
                      >
                        + Notiz hinzufügen
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Letzte Kontaktprotokolle / Gesprächsnotizen */}
              {(() => {
                const peKontakte = kontakte.filter(k => k.pflegeeinrichtung_id === selectedPE.id).slice(0, 5);
                if (peKontakte.length === 0) return null;
                return (
                  <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                      <MessageSquare size={16} className="text-purple-600" /> Letzte Gespräche ({peKontakte.length})
                    </p>
                    <div className="space-y-2 max-h-[150px] overflow-y-auto">
                      {peKontakte.map((k, idx) => (
                        <div key={idx} className="text-sm p-2 bg-white rounded border border-purple-100">
                          <div className="flex justify-between text-xs text-gray-500 mb-1">
                            <span>{k.kontaktdatum || 'Kein Datum'}</span>
                            <span>{k.mitarbeiter_name || k.kontaktkanal || ''}</span>
                          </div>
                          <p className="text-gray-700">{k.gespraechsinhalt || k.notizen || '-'}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* Status ändern */}
              <div className="p-4 bg-white border rounded-lg">
                <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Target size={16} className="text-blue-600" /> Status ändern
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    {key: "neu", label: "Neu", icon: ""},
                    {key: "kontaktiert", label: "Kontaktiert", icon: ""},
                    {key: "interessent", label: "Interessent", icon: ""},
                    {key: "angebot_gesendet", label: "Angebot erstellt", icon: ""},
                    {key: "kunde", label: "Kunde", icon: ""},
                    {key: "abgesagt", label: "Abgesagt", icon: ""}
                  ].map(s => (
                    <button
                      key={s.key}
                      className={`px-4 py-2 text-sm rounded-lg border-2 transition-all flex items-center gap-2 ${
                        selectedPE.akquise_status === s.key 
                          ? 'border-gray-800 font-bold shadow-md scale-105' 
                          : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
                      }`}
                      style={{ borderLeftColor: AKQUISE_STATUS_FARBEN[s.key], borderLeftWidth: '4px' }}
                      onClick={() => handleStatusChange(selectedPE.id, s.key)}
                    >
                      <span>{s.icon}</span> {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Wiedervorlage setzen */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <CalendarIcon size={16} className="text-blue-600" /> Wiedervorlage
                </p>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Nächste Wiedervorlage</Label>
                    <Input
                      type="date"
                      id="vertrieb-wiedervorlage"
                      defaultValue={selectedPE.naechste_wiedervorlage || ""}
                      className="bg-white"
                    />
                  </div>
                  <Button
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    onClick={async () => {
                      const input = document.getElementById('vertrieb-wiedervorlage');
                      try {
                        await axios.put(`${API}/pflegeeinrichtungen/${selectedPE.id}`, {
                          ...selectedPE,
                          naechste_wiedervorlage: input.value
                        });
                        toast.success("Wiedervorlage gespeichert");
                        fetchData();
                        // Update selectedPE
                        setSelectedPE(prev => ({...prev, naechste_wiedervorlage: input.value}));
                      } catch (error) {
                        toast.error("Fehler beim Speichern");
                      }
                    }}
                  >
                    Speichern
                  </Button>
                  {selectedPE.naechste_wiedervorlage && (
                    <Button
                      variant="outline"
                      className="text-red-500 border-red-300 hover:bg-red-50"
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/pflegeeinrichtungen/${selectedPE.id}`, {
                            ...selectedPE,
                            naechste_wiedervorlage: ""
                          });
                          toast.success("Wiedervorlage gelöscht");
                          fetchData();
                          setSelectedPE(prev => ({...prev, naechste_wiedervorlage: ""}));
                          document.getElementById('vertrieb-wiedervorlage').value = "";
                        } catch (error) {
                          toast.error("Fehler beim Löschen");
                        }
                      }}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
                {selectedPE.naechste_wiedervorlage && (
                  <p className="text-xs text-blue-600 mt-2">
                    Aktuelle Wiedervorlage: {new Date(selectedPE.naechste_wiedervorlage).toLocaleDateString('de-DE')}
                  </p>
                )}
              </div>
              
              {/* Kaltakquise Follow-up E-Mail */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Mail size={16} className="text-emerald-600" /> Kaltakquise Follow-up
                </p>
                <p className="text-xs text-gray-600 mb-3">
                  Sendet automatisch eine personalisierte E-Mail mit 50% Rabatt-Angebot für den ersten Kurs.
                </p>
                {selectedPE.ansprechpartner_email ? (
                  <Button
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/email/kaltakquise-followup`, {
                          pe_id: selectedPE.id,
                          ansprechpartner_name: selectedPE.ansprechpartner_name || "Interessent",
                          ansprechpartner_email: selectedPE.ansprechpartner_email,
                          einrichtungsname: selectedPE.einrichtungsname
                        });
                        toast.success(`E-Mail gesendet an ${selectedPE.ansprechpartner_email}`);
                      } catch (error) {
                        toast.error(error.response?.data?.detail || "Fehler beim Senden der E-Mail");
                      }
                    }}
                  >
                    <Mail size={16} className="mr-2" />
                    Follow-up E-Mail an {selectedPE.ansprechpartner_name || selectedPE.ansprechpartner_email} senden
                  </Button>
                ) : (
                  <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                    Keine E-Mail-Adresse hinterlegt. Bitte zuerst eine E-Mail eintragen.
                  </p>
                )}
              </div>
              
              {/* Fortbildungspaket */}
              <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
                <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Package size={16} className="text-violet-600" /> Fortbildungspaket (Jahrespaket)
                </p>
                <div className="space-y-3">
                  <div className="flex gap-3 items-center flex-wrap">
                    {[
                      {value: null, label: "Kein Paket", color: "gray"},
                      {value: "klein", label: "Klein (€1.200)", color: "blue"},
                      {value: "gross", label: "Groß (€1.900)", color: "emerald"}
                    ].map(option => (
                      <button
                        key={option.value || "none"}
                        className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                          selectedPE.fortbildungspaket === option.value 
                            ? `border-${option.color}-500 bg-${option.color}-100 font-bold` 
                            : 'border-gray-200 hover:border-gray-400 bg-white'
                        }`}
                        onClick={async () => {
                          try {
                            const updateData = {
                              ...selectedPE,
                              fortbildungspaket: option.value,
                              fortbildungspaket_start: option.value ? (selectedPE.fortbildungspaket_start || new Date().toISOString().split('T')[0]) : null
                            };
                            await axios.put(`${API}/pflegeeinrichtungen/${selectedPE.id}`, updateData);
                            setSelectedPE(prev => ({
                              ...prev, 
                              fortbildungspaket: option.value,
                              fortbildungspaket_start: updateData.fortbildungspaket_start
                            }));
                            fetchData();
                            toast.success(option.value ? `Paket "${option.label}" gesetzt` : "Paket entfernt");
                          } catch (error) {
                            toast.error("Fehler beim Speichern");
                          }
                        }}
                      >
                        {selectedPE.fortbildungspaket === option.value && "✓ "}{option.label}
                      </button>
                    ))}
                  </div>
                  
                  {selectedPE.fortbildungspaket && (
                    <div className="flex gap-4 items-end bg-white p-3 rounded-lg border border-violet-200">
                      <div className="flex-1">
                        <Label className="text-xs">Gültig ab</Label>
                        <Input
                          type="date"
                          value={selectedPE.fortbildungspaket_start || ""}
                          className="bg-white"
                          onChange={async (e) => {
                            const newDate = e.target.value;
                            try {
                              await axios.put(`${API}/pflegeeinrichtungen/${selectedPE.id}`, {
                                ...selectedPE,
                                fortbildungspaket_start: newDate
                              });
                              setSelectedPE(prev => ({...prev, fortbildungspaket_start: newDate}));
                              fetchData();
                              toast.success("Paket-Startdatum aktualisiert");
                            } catch (error) {
                              toast.error("Fehler beim Speichern");
                            }
                          }}
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs">Läuft bis (automatisch +1 Jahr)</Label>
                        <div className="h-10 flex items-center px-3 bg-gray-100 rounded-md border text-sm font-medium">
                          {selectedPE.fortbildungspaket_start 
                            ? (() => {
                                const start = new Date(selectedPE.fortbildungspaket_start);
                                const ende = new Date(start);
                                ende.setFullYear(ende.getFullYear() + 1);
                                return ende.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});
                              })()
                            : "-"
                          }
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Schnelle Notiz */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <FileText size={16} className="text-amber-600" /> Gesprächsnotiz
                </p>
                <div className="flex gap-2">
                  <Textarea 
                    className="flex-1 bg-white" 
                    rows={2}
                    placeholder="Was wurde besprochen? (Enter zum Speichern)"
                    id="dialog-notiz"
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        const success = await handleSchnellNotiz(e.target.value);
                        if (success) e.target.value = "";
                      }
                    }}
                  />
                  <Button
                    className="bg-amber-500 hover:bg-amber-600 text-white"
                    onClick={async () => {
                      const input = document.getElementById('dialog-notiz');
                      const success = await handleSchnellNotiz(input.value);
                      if (success) input.value = "";
                    }}
                  >
                    Speichern
                  </Button>
                </div>
              </div>
              
              {/* VERBESSERTE Bestellung erfassen - MULTI KURS */}
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <p className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <Euro size={16} className="text-emerald-600" /> Bestellung erfassen (Mehrfachauswahl möglich)
                </p>
                
                <div className="space-y-4">
                  {/* Kurse auswählen - mit Anzahl-Spinner und INDIVIDUALEM RABATT */}
                  <div>
                    <Label className="text-xs font-medium mb-2 block">Kurse / Maßnahmen auswählen</Label>
                    <div className="grid grid-cols-1 gap-2 max-h-[280px] overflow-y-auto bg-white p-3 rounded-lg border">
                      {VERTRIEB_KURSE.map(k => {
                        const selected = bestellungForm.kurse.find(bk => bk.kursId === k.id);
                        const anzahl = selected ? selected.anzahl : 0;
                        const istAusgewaehlt = anzahl > 0;
                        
                        // Rabatt-Berechnung für Anzeige
                        let rabattAnzeige = null;
                        let effektiverPreis = k.preis;
                        if (selected) {
                          if (selected.ist_geschenk) {
                            effektiverPreis = 0;
                            rabattAnzeige = <span className="text-pink-600 font-bold text-xs">GESCHENK</span>;
                          } else if (selected.rabatt_art === "prozent" && selected.rabatt_wert > 0) {
                            effektiverPreis = k.preis * (1 - selected.rabatt_wert / 100);
                            rabattAnzeige = <span className="text-orange-600 font-bold text-xs">-{selected.rabatt_wert}%</span>;
                          } else if (selected.rabatt_art === "fixbetrag" && selected.rabatt_wert > 0) {
                            effektiverPreis = Math.max(0, k.preis - selected.rabatt_wert);
                            rabattAnzeige = <span className="text-orange-600 font-bold text-xs">-€{selected.rabatt_wert}</span>;
                          }
                        }
                        
                        return (
                          <div key={k.id} className={`rounded transition-colors ${istAusgewaehlt ? 'bg-emerald-50 border border-emerald-300' : 'hover:bg-gray-50 border border-transparent'}`}>
                            {/* Haupt-Zeile */}
                            <div 
                              className="flex items-center justify-between p-2 cursor-pointer"
                              onClick={() => {
                                if (anzahl === 0) {
                                  handleKursToggle(k.id);
                                } else {
                                  handleKursAnzahlChange(k.id, anzahl + 1);
                                }
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <div 
                                  className={`w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer ${istAusgewaehlt ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 hover:border-emerald-400'}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleKursToggle(k.id);
                                  }}
                                >
                                  {istAusgewaehlt && <Check size={14} className="text-white" />}
                                </div>
                                <span className="text-sm">{k.name}</span>
                                {rabattAnzeige}
                              </div>
                              <div className="flex items-center gap-3">
                                {selected?.ist_geschenk || (selected?.rabatt_wert > 0) ? (
                                  <div className="flex items-center gap-1">
                                    <span className="text-gray-400 line-through text-sm">€{k.preis.toLocaleString()}</span>
                                    <span className="text-emerald-600 font-bold">€{effektiverPreis.toLocaleString()}</span>
                                  </div>
                                ) : (
                                  <span className="text-emerald-600 font-bold">€{k.preis.toLocaleString()}</span>
                                )}
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (anzahl > 0) {
                                        if (anzahl === 1) {
                                          handleKursToggle(k.id);
                                        } else {
                                          handleKursAnzahlChange(k.id, anzahl - 1);
                                        }
                                      }
                                    }}
                                  >
                                    -
                                  </Button>
                                  <span className="w-8 text-center font-bold">{anzahl}</span>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 w-7 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (anzahl === 0) {
                                        handleKursToggle(k.id);
                                      } else {
                                        handleKursAnzahlChange(k.id, anzahl + 1);
                                      }
                                    }}
                                  >
                                    +
                                  </Button>
                                </div>
                              </div>
                            </div>
                            
                            {/* Rabatt-Zeile - nur wenn Kurs ausgewählt */}
                            {istAusgewaehlt && (
                              <div className="px-2 pb-2 pt-1 border-t border-emerald-200 flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-gray-500">Rabatt:</span>
                                <div className="flex items-center gap-1">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={!selected.ist_geschenk && selected.rabatt_art === "keiner" ? "default" : "outline"}
                                    className="h-6 px-2 text-xs"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleKursRabattChange(k.id, "keiner", 0);
                                    }}
                                  >
                                    Keiner
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={selected.ist_geschenk ? "default" : "outline"}
                                    className={`h-6 px-2 text-xs ${selected.ist_geschenk ? 'bg-pink-500 hover:bg-pink-600' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleKursRabattChange(k.id, "geschenk");
                                    }}
                                  >
                                    Geschenk
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={!selected.ist_geschenk && selected.rabatt_art === "prozent" ? "default" : "outline"}
                                    className={`h-6 px-2 text-xs ${!selected.ist_geschenk && selected.rabatt_art === "prozent" ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleKursRabattChange(k.id, "prozent", selected.rabatt_wert || 10);
                                    }}
                                  >
                                    % Rabatt
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={!selected.ist_geschenk && selected.rabatt_art === "fixbetrag" ? "default" : "outline"}
                                    className={`h-6 px-2 text-xs ${!selected.ist_geschenk && selected.rabatt_art === "fixbetrag" ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleKursRabattChange(k.id, "fixbetrag", selected.rabatt_wert || 100);
                                    }}
                                  >
                                    € Rabatt
                                  </Button>
                                </div>
                                {/* Eingabefeld für Rabattwert */}
                                {!selected.ist_geschenk && selected.rabatt_art !== "keiner" && (
                                  <Input
                                    type="number"
                                    className="w-20 h-6 text-xs"
                                    value={selected.rabatt_wert || ""}
                                    placeholder={selected.rabatt_art === "prozent" ? "max 20%" : "€"}
                                    max={selected.rabatt_art === "prozent" ? 20 : undefined}
                                    onClick={(e) => e.stopPropagation()}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      const val = parseFloat(e.target.value) || 0;
                                      handleKursRabattChange(k.id, selected.rabatt_art, val);
                                    }}
                                  />
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Eigene Schulung hinzufügen - ERWEITERT mit Anzahl, Rabatt und Geschenk */}
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <Label className="text-xs font-medium mb-2 block">Eigene Schulung / Position hinzufügen</Label>
                    <div className="flex gap-2 mb-2">
                      <Input 
                        value={bestellungForm.eigene_schulung}
                        onChange={(e) => handleEigeneSchulungChange('eigene_schulung', e.target.value)}
                        placeholder="z.B. Inhouse-Schulung, Sonderkurs..."
                        className="flex-1 bg-white"
                      />
                      <Input 
                        type="number"
                        value={bestellungForm.eigene_schulung_preis || ""}
                        onChange={(e) => handleEigeneSchulungChange('eigene_schulung_preis', parseFloat(e.target.value) || 0)}
                        placeholder="Einzelpreis €"
                        className="w-28 bg-white"
                      />
                      {/* Anzahl */}
                      <div className="flex items-center gap-1 bg-white border rounded-md px-2">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => handleEigeneSchulungChange('eigene_schulung_anzahl', Math.max(1, (bestellungForm.eigene_schulung_anzahl || 1) - 1))}
                        >-</Button>
                        <span className="w-6 text-center font-medium">{bestellungForm.eigene_schulung_anzahl || 1}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => handleEigeneSchulungChange('eigene_schulung_anzahl', (bestellungForm.eigene_schulung_anzahl || 1) + 1)}
                        >+</Button>
                      </div>
                    </div>
                    {/* Rabatt-Optionen für eigene Schulung */}
                    {bestellungForm.eigene_schulung && bestellungForm.eigene_schulung_preis > 0 && (
                      <div className="flex gap-2 items-center flex-wrap mt-2 pt-2 border-t border-purple-200">
                        <span className="text-xs text-purple-700">Rabatt:</span>
                        <div className="flex gap-1">
                          <Button 
                            size="sm"
                            variant={!bestellungForm.eigene_schulung_ist_geschenk && bestellungForm.eigene_schulung_rabatt_art === "keiner" ? "default" : "outline"}
                            className={`h-6 px-2 text-xs ${!bestellungForm.eigene_schulung_ist_geschenk && bestellungForm.eigene_schulung_rabatt_art === "keiner" ? 'bg-gray-500' : ''}`}
                            onClick={() => handleEigeneSchulungRabattChange("keiner")}
                          >
                            Keiner
                          </Button>
                          <Button 
                            size="sm"
                            variant={bestellungForm.eigene_schulung_ist_geschenk ? "default" : "outline"}
                            className={`h-6 px-2 text-xs ${bestellungForm.eigene_schulung_ist_geschenk ? 'bg-pink-500 hover:bg-pink-600' : ''}`}
                            onClick={() => handleEigeneSchulungRabattChange("geschenk")}
                          >
                            Geschenk
                          </Button>
                          <Button 
                            size="sm"
                            variant={!bestellungForm.eigene_schulung_ist_geschenk && bestellungForm.eigene_schulung_rabatt_art === "prozent" ? "default" : "outline"}
                            className={`h-6 px-2 text-xs ${!bestellungForm.eigene_schulung_ist_geschenk && bestellungForm.eigene_schulung_rabatt_art === "prozent" ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                            onClick={() => handleEigeneSchulungRabattChange("prozent", bestellungForm.eigene_schulung_rabatt_wert || 10)}
                          >
                            % Rabatt
                          </Button>
                          <Button 
                            size="sm"
                            variant={!bestellungForm.eigene_schulung_ist_geschenk && bestellungForm.eigene_schulung_rabatt_art === "fixbetrag" ? "default" : "outline"}
                            className={`h-6 px-2 text-xs ${!bestellungForm.eigene_schulung_ist_geschenk && bestellungForm.eigene_schulung_rabatt_art === "fixbetrag" ? 'bg-amber-600 hover:bg-amber-700' : ''}`}
                            onClick={() => handleEigeneSchulungRabattChange("fixbetrag", bestellungForm.eigene_schulung_rabatt_wert || 100)}
                          >
                            € Rabatt
                          </Button>
                        </div>
                        {/* Eingabefeld für Rabattwert */}
                        {!bestellungForm.eigene_schulung_ist_geschenk && bestellungForm.eigene_schulung_rabatt_art !== "keiner" && (
                          <Input
                            type="number"
                            className="w-20 h-6 text-xs bg-white"
                            value={bestellungForm.eigene_schulung_rabatt_wert || ""}
                            placeholder={bestellungForm.eigene_schulung_rabatt_art === "prozent" ? "max 20%" : "€"}
                            max={bestellungForm.eigene_schulung_rabatt_art === "prozent" ? 20 : undefined}
                            onChange={(e) => handleEigeneSchulungRabattChange(bestellungForm.eigene_schulung_rabatt_art, parseFloat(e.target.value) || 0)}
                          />
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Rabatt */}
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label className="text-xs font-medium mb-2 block">Rabatt</Label>
                    <div className="flex gap-2 items-center">
                      <Select 
                        value={bestellungForm.rabatt_art} 
                        onValueChange={(v) => setBestellungForm(prev => ({...prev, rabatt_art: v, rabatt_hoehe: 0}))}
                      >
                        <SelectTrigger className="w-40 bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="keiner">Kein Rabatt</SelectItem>
                          <SelectItem value="prozent">Prozent %</SelectItem>
                          <SelectItem value="fixbetrag">Fixbetrag €</SelectItem>
                        </SelectContent>
                      </Select>
                      {bestellungForm.rabatt_art !== "keiner" && (
                        <Input 
                          type="number"
                          value={bestellungForm.rabatt_hoehe || ""}
                          onChange={(e) => {
                            let val = parseFloat(e.target.value) || 0;
                            // Prozent-Rabatt max 20%
                            if (bestellungForm.rabatt_art === "prozent" && val > 20) {
                              toast.error("Rabatt darf maximal 20% sein (Provision = 20%)");
                              val = 20;
                            }
                            setBestellungForm(prev => ({...prev, rabatt_hoehe: val}));
                          }}
                          placeholder={bestellungForm.rabatt_art === "prozent" ? "max 20%" : "€"}
                          max={bestellungForm.rabatt_art === "prozent" ? 20 : undefined}
                          className="w-24 bg-white"
                        />
                      )}
                      {bestellungForm.rabatt_art !== "keiner" && bestellungForm.rabatt_hoehe > 0 && (
                        <span className="text-amber-700 font-medium">
                          = -{bestellungForm.rabatt_art === "prozent" 
                            ? `${bestellungForm.rabatt_hoehe}%` 
                            : `€${bestellungForm.rabatt_hoehe.toLocaleString()}`}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Vertriebsmitarbeiter */}
                  <div>
                    <Label className="text-xs font-medium">Vertriebsmitarbeiter *</Label>
                    <Select 
                      value={bestellungForm.vertriebsmitarbeiter_id} 
                      onValueChange={(v) => setBestellungForm(prev => ({...prev, vertriebsmitarbeiter_id: v}))}
                    >
                      <SelectTrigger className="bg-white">
                        <SelectValue placeholder="Auswählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {mitarbeiter.filter(m => m.aktiv).map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Berechnungsergebnis - MIT individuellen Rabatten und eigene Schulung */}
                  {(bestellungForm.kurse.length > 0 || (bestellungForm.eigene_schulung && bestellungForm.eigene_schulung_preis > 0)) && (
                    <div className="p-4 bg-white rounded-lg border-2 border-emerald-300">
                      <div className="space-y-2 mb-3">
                        {bestellungForm.kurse.map(k => {
                          const kursInfo = VERTRIEB_KURSE.find(vk => vk.id === k.kursId);
                          const originalPreis = (kursInfo?.preis || 0) * k.anzahl;
                          
                          // Effektiven Preis berechnen
                          let effektiverPreis = originalPreis;
                          let rabattInfo = null;
                          if (k.ist_geschenk) {
                            effektiverPreis = 0;
                            rabattInfo = <span className="text-pink-600 text-xs ml-2">Geschenk</span>;
                          } else if (k.rabatt_art === "prozent" && k.rabatt_wert > 0) {
                            effektiverPreis = originalPreis * (1 - k.rabatt_wert / 100);
                            rabattInfo = <span className="text-orange-600 text-xs ml-2">-{k.rabatt_wert}%</span>;
                          } else if (k.rabatt_art === "fixbetrag" && k.rabatt_wert > 0) {
                            effektiverPreis = Math.max(0, originalPreis - (k.rabatt_wert * k.anzahl));
                            rabattInfo = <span className="text-orange-600 text-xs ml-2">-€{k.rabatt_wert * k.anzahl}</span>;
                          }
                          
                          return (
                            <div key={k.kursId} className="flex justify-between text-sm">
                              <span>{k.anzahl}x {kursInfo?.name}{rabattInfo}</span>
                              <span className="font-mono">
                                {effektiverPreis !== originalPreis && (
                                  <span className="text-gray-400 line-through mr-2">€{originalPreis.toLocaleString()}</span>
                                )}
                                €{effektiverPreis.toLocaleString()}
                              </span>
                            </div>
                          );
                        })}
                        {/* Eigene Schulung in Zusammenfassung anzeigen */}
                        {bestellungForm.eigene_schulung && bestellungForm.eigene_schulung_preis > 0 && (() => {
                          const anzahl = bestellungForm.eigene_schulung_anzahl || 1;
                          const originalPreis = bestellungForm.eigene_schulung_preis * anzahl;
                          let effektiverPreis = originalPreis;
                          let rabattInfo = null;
                          
                          if (bestellungForm.eigene_schulung_ist_geschenk) {
                            effektiverPreis = 0;
                            rabattInfo = <span className="text-pink-600 text-xs ml-2">Geschenk</span>;
                          } else if (bestellungForm.eigene_schulung_rabatt_art === "prozent" && bestellungForm.eigene_schulung_rabatt_wert > 0) {
                            effektiverPreis = originalPreis * (1 - bestellungForm.eigene_schulung_rabatt_wert / 100);
                            rabattInfo = <span className="text-orange-600 text-xs ml-2">-{bestellungForm.eigene_schulung_rabatt_wert}%</span>;
                          } else if (bestellungForm.eigene_schulung_rabatt_art === "fixbetrag" && bestellungForm.eigene_schulung_rabatt_wert > 0) {
                            effektiverPreis = Math.max(0, originalPreis - (bestellungForm.eigene_schulung_rabatt_wert * anzahl));
                            rabattInfo = <span className="text-orange-600 text-xs ml-2">-€{bestellungForm.eigene_schulung_rabatt_wert * anzahl}</span>;
                          }
                          
                          return (
                            <div className="flex justify-between text-sm text-purple-700 bg-purple-50 p-1 rounded">
                              <span>{anzahl}x {bestellungForm.eigene_schulung}{rabattInfo}</span>
                              <span className="font-mono">
                                {effektiverPreis !== originalPreis && (
                                  <span className="text-gray-400 line-through mr-2">€{originalPreis.toLocaleString()}</span>
                                )}
                                €{effektiverPreis.toLocaleString()}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      {(() => {
                        // Gesamtumsatz ist bereits mit individuellen Rabatten berechnet
                        const brutto = bestellungForm.gesamtumsatz;
                        
                        // Berechne den GESAMTEN Rabatt (in Prozent vom Originalpreis)
                        // 1. Individuelle Kurs-Rabatte
                        let gesamtRabattProzent = 0;
                        let gesamtOriginalPreis = 0;
                        let gesamtRabattBetrag = 0;
                        
                        bestellungForm.kurse.forEach(k => {
                          const kurs = VERTRIEB_KURSE.find(kk => kk.id === k.kursId);
                          if (kurs && k.anzahl > 0) {
                            const originalPreis = kurs.preis * k.anzahl;
                            gesamtOriginalPreis += originalPreis;
                            
                            if (k.rabatt_art === "prozent" && k.rabatt_wert > 0) {
                              gesamtRabattBetrag += originalPreis * k.rabatt_wert / 100;
                            } else if (k.rabatt_art === "fixbetrag" && k.rabatt_wert > 0) {
                              gesamtRabattBetrag += k.rabatt_wert * k.anzahl;
                            }
                          }
                        });
                        
                        // 2. Eigene Schulung Rabatt
                        if (bestellungForm.eigene_schulung && bestellungForm.eigene_schulung_preis > 0) {
                          const anzahl = bestellungForm.eigene_schulung_anzahl || 1;
                          const originalPreis = bestellungForm.eigene_schulung_preis * anzahl;
                          gesamtOriginalPreis += originalPreis;
                          
                          if (bestellungForm.eigene_schulung_rabatt_art === "prozent" && bestellungForm.eigene_schulung_rabatt_wert > 0) {
                            gesamtRabattBetrag += originalPreis * bestellungForm.eigene_schulung_rabatt_wert / 100;
                          } else if (bestellungForm.eigene_schulung_rabatt_art === "fixbetrag" && bestellungForm.eigene_schulung_rabatt_wert > 0) {
                            gesamtRabattBetrag += bestellungForm.eigene_schulung_rabatt_wert * anzahl;
                          }
                        }
                        
                        // 3. Zusätzlicher globaler Rabatt
                        if (bestellungForm.rabatt_art === "prozent" && bestellungForm.rabatt_hoehe > 0) {
                          gesamtRabattBetrag += (gesamtOriginalPreis - gesamtRabattBetrag) * bestellungForm.rabatt_hoehe / 100;
                        } else if (bestellungForm.rabatt_art === "fixbetrag" && bestellungForm.rabatt_hoehe > 0) {
                          gesamtRabattBetrag += bestellungForm.rabatt_hoehe;
                        }
                        
                        // Berechne effektiven Rabatt-Prozentsatz
                        gesamtRabattProzent = gesamtOriginalPreis > 0 ? (gesamtRabattBetrag / gesamtOriginalPreis) * 100 : 0;
                        
                        // NEUE LOGIK: Provision = 20% - Rabatt%
                        // Bei 15% Rabatt → 5% Provision
                        // Bei 0% Rabatt → 20% Provision
                        const effektiverProvisionsProzent = Math.max(0, 20 - gesamtRabattProzent);
                        const provisionBetrag = gesamtOriginalPreis * effektiverProvisionsProzent / 100;
                        
                        const netto = gesamtOriginalPreis - gesamtRabattBetrag;
                        
                        return (
                          <div className="pt-3 border-t border-emerald-200">
                            <div className="flex justify-between items-center">
                              <span className="text-emerald-700 font-semibold">Gesamtumsatz (brutto):</span>
                              <span className="text-2xl font-bold text-emerald-700">€{gesamtOriginalPreis.toLocaleString()}</span>
                            </div>
                            {gesamtRabattBetrag > 0 && (
                              <>
                                <div className="flex justify-between items-center mt-1 text-amber-600">
                                  <span>Rabatt gesamt ({gesamtRabattProzent.toFixed(1)}%):</span>
                                  <span className="font-medium">-€{gesamtRabattBetrag.toLocaleString()}</span>
                                </div>
                                <div className="flex justify-between items-center mt-1">
                                  <span className="text-gray-600">Kunde zahlt:</span>
                                  <span className="font-bold text-gray-700">€{netto.toLocaleString()}</span>
                                </div>
                              </>
                            )}
                            <div className="flex justify-between items-center mt-2 pt-2 border-t border-dashed border-emerald-200">
                              <span className="text-emerald-600">
                                Provision ({effektiverProvisionsProzent.toFixed(0)}%{gesamtRabattBetrag > 0 ? ` = 20% - ${gesamtRabattProzent.toFixed(0)}% Rabatt` : ''}):
                              </span>
                              <span className={`font-bold text-lg ${effektiverProvisionsProzent < 20 ? 'text-amber-600' : 'text-emerald-600'}`}>
                                €{provisionBetrag.toLocaleString()}
                              </span>
                            </div>
                            {effektiverProvisionsProzent < 20 && (
                              <div className="text-xs text-amber-600 mt-1">
                                Provision reduziert wegen Rabatt (max. 20% insgesamt)
                              </div>
                            )}
                            <div className="flex justify-between items-center mt-2 text-sm text-gray-500">
                              <span>Teilnehmer gesamt:</span>
                              <span className="font-medium">
                                {bestellungForm.kurse.reduce((sum, k) => sum + k.anzahl, 0) + 
                                 (bestellungForm.eigene_schulung && bestellungForm.eigene_schulung_preis > 0 ? (bestellungForm.eigene_schulung_anzahl || 1) : 0)}
                              </span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  
                  {/* Optionale Notiz */}
                  <div>
                    <Label className="text-xs font-medium">Notiz zur Bestellung (optional)</Label>
                    <Input 
                      value={bestellungForm.notiz}
                      onChange={(e) => setBestellungForm(prev => ({...prev, notiz: e.target.value}))}
                      placeholder="z.B. Starttermin, Besonderheiten..."
                      className="bg-white"
                    />
                  </div>
                  
                  {/* Eigener Text für Angebot */}
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <Label className="text-xs font-medium mb-2 block">Eigener Text für Angebot (optional)</Label>
                    <textarea 
                      value={bestellungForm.angebot_text || ""}
                      onChange={(e) => setBestellungForm(prev => ({...prev, angebot_text: e.target.value}))}
                      placeholder="Zusätzlicher Text der im Angebot erscheinen soll, z.B. Sonderkonditionen, Hinweise..."
                      className="w-full p-2 border rounded-md bg-white text-sm min-h-[80px] resize-y"
                      rows={3}
                    />
                  </div>
                  
                  {/* Verwaltung benachrichtigen */}
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Checkbox 
                      id="benachrichtigung"
                      checked={bestellungForm.benachrichtigung_verwaltung}
                      onCheckedChange={(checked) => setBestellungForm(prev => ({...prev, benachrichtigung_verwaltung: checked}))}
                    />
                    <Label htmlFor="benachrichtigung" className="text-sm cursor-pointer">
                      Verwaltung (Angelina) benachrichtigen für Rechnungsstellung
                    </Label>
                  </div>
                  
                  {/* Submit Buttons */}
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      type="button"
                      className="flex-1 min-w-[180px] bg-purple-600 hover:bg-purple-700 text-white py-6"
                      onClick={() => handleAngebotErstellen(selectedPE, "download")}
                      disabled={bestellungForm.kurse.length === 0 && !bestellungForm.eigene_schulung}
                      data-testid="angebot-download-btn"
                    >
                      <FileText size={20} className="mr-2" />
                      Angebot (PDF laden)
                    </Button>
                    <Button 
                      type="button"
                      className="flex-1 min-w-[180px] bg-indigo-600 hover:bg-indigo-700 text-white py-6"
                      onClick={() => handleAngebotErstellen(selectedPE, "email")}
                      disabled={(bestellungForm.kurse.length === 0 && !bestellungForm.eigene_schulung) || !selectedPE?.ansprechpartner_email}
                      title={!selectedPE?.ansprechpartner_email ? "Bitte erst Ansprechpartner-E-Mail hinterlegen" : `Angebot direkt an ${selectedPE?.ansprechpartner_email} senden`}
                      data-testid="angebot-email-btn"
                    >
                      <Mail size={20} className="mr-2" />
                      Angebot per E-Mail
                    </Button>
                    <Button 
                      type="button"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-6"
                      onClick={handleBestellungSubmit}
                      disabled={bestellungForm.kurse.length === 0 && !bestellungForm.eigene_schulung}
                    >
                      <CheckCircle size={20} className="mr-2" />
                      Als Kunde buchen
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Änderungshistorie */}
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg border-dashed">
                <p className="text-sm font-semibold mb-2 flex items-center gap-2">
                  <FileText size={16} className="text-gray-500" /> Änderungshistorie
                </p>
                {peHistorieLoading ? (
                  <div className="text-center text-gray-400 text-sm py-2">Lade...</div>
                ) : peHistorie.length === 0 ? (
                  <div className="text-center text-gray-400 text-sm py-2">Noch keine Änderungen</div>
                ) : (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {peHistorie.slice(0, 5).map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs border-b pb-2 last:border-0">
                        <span className="text-gray-400 whitespace-nowrap">
                          {item.datum_formatiert || new Date(item.datum).toLocaleDateString("de-DE")}
                        </span>
                        <span className="text-gray-600 flex-1">
                          {item.benutzer && <span className="font-medium">{item.benutzer}: </span>}
                          {item.details || `${item.aktion}`}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Footer Buttons */}
              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setShowPEDialog(false)}
                >
                  Schließen
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowPEDialog(false);
                    navigate(`/pflegeeinrichtungen`);
                  }}
                >
                  Alle Details ansehen →
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Follow-Up Email Dialog */}
      <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="text-blue-600" />
              Email senden
            </DialogTitle>
            <DialogDescription>
              {emailPE?.einrichtungsname || emailPE?.name || "Pflegeeinrichtung"}
              {getPEEmail(emailPE) && <span className="ml-2 text-blue-600">({getPEEmail(emailPE)})</span>}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Betreff */}
            <div>
              <Label className="text-sm font-medium">Betreff</Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Betreff eingeben..."
              />
            </div>
            
            {/* Email-Text editierbar */}
            <div>
              <Label className="text-sm font-medium">Nachricht</Label>
              <Textarea
                value={emailText}
                onChange={(e) => setEmailText(e.target.value)}
                className="min-h-[300px] font-sans text-sm"
                placeholder="Email-Text eingeben..."
              />
            </div>
            
            {/* Vorlagen-Buttons */}
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setEmailText(getFollowUpEmail(emailPE));
                  setEmailSubject("Weiterbildungen für Ihr Team - Dickies-Campus");
                }}
              >
                Follow-Up Vorlage
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  setEmailText(getAngebotsEmail(emailPE));
                  setEmailSubject("Angebot - Dickies-Campus");
                  if (verfuegbareAngebote.length > 0 && !selectedAngebotId) {
                    setSelectedAngebotId(verfuegbareAngebote[0].id);
                  }
                }}
              >
                Angebot Vorlage
              </Button>
            </div>

            {/* Angebots-PDF Anhang Auswahl */}
            {verfuegbareAngebote.length > 0 && (
              <div className="border rounded-lg p-3 bg-emerald-50 border-emerald-200">
                <Label className="text-sm font-medium flex items-center gap-2 text-emerald-900">
                  Angebots-PDF anhängen <span className="text-xs text-emerald-700 font-normal">(optional)</span>
                </Label>
                <select
                  className="w-full mt-2 px-3 py-2 border border-emerald-300 rounded-md bg-white text-sm"
                  value={selectedAngebotId}
                  onChange={(e) => setSelectedAngebotId(e.target.value)}
                  data-testid="email-angebot-select"
                >
                  <option value="">– Kein Anhang –</option>
                  {verfuegbareAngebote.map(a => (
                    <option key={a.id} value={a.id}>
                      {a.angebotsnummer || a.id.slice(0,8)} · {a.kurs_name || a.titel || "Angebot"} · {a.created_at ? new Date(a.created_at).toLocaleDateString("de-DE") : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Senden Buttons */}
            <div className="flex gap-3 flex-wrap pt-2 border-t">
              {getPEEmail(emailPE) && (
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={sendEmailDirect}
                  disabled={sendingEmail}
                  data-testid="email-send-direct-btn"
                >
                  <Mail size={16} className="mr-2" />
                  {sendingEmail ? "Sende..." : (selectedAngebotId ? "Mit PDF senden" : "Direkt senden")}
                </Button>
              )}
              <Button 
                variant="outline"
                className="flex-1"
                onClick={copyEmailToClipboard}
              >
                <Copy size={16} className="mr-2" />
                Kopieren
              </Button>
              {getPEEmail(emailPE) && (
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={openEmailClient}
                >
                  Email-Programm
                </Button>
              )}
            </div>
            
            {!getPEEmail(emailPE) && (
              <p className="text-sm text-red-500 bg-red-50 p-2 rounded">
                Keine Email-Adresse hinterlegt! Bitte erst Email bei der Pflegeeinrichtung eintragen.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Kontakte Tab */}
      {activeTab === "kontakte" && (
        <Card className="card-custom">
          <CardHeader className="pb-2">
            <div className="flex justify-between items-center">
              <CardTitle className="text-lg">Kontaktprotokoll</CardTitle>
              <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={() => openDialog("kontakt")}>
                <Plus size={16} className="mr-2" />
                Neuer Kontakt
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead>Datum</TableHead>
                  <TableHead>Einrichtung</TableHead>
                  <TableHead>Vertrieb</TableHead>
                  <TableHead>Art</TableHead>
                  <TableHead>Kanal</TableHead>
                  <TableHead>Inhalt</TableHead>
                  <TableHead>Nächster Kontakt</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {kontakte.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      Keine Kontakte vorhanden
                    </TableCell>
                  </TableRow>
                ) : kontakte.map(k => (
                  <TableRow key={k.id} className={`${k.erledigt ? "opacity-60 bg-gray-50" : ""} hover:bg-gray-50`}>
                    <TableCell className="font-mono text-sm">{k.kontaktdatum}</TableCell>
                    <TableCell className="font-medium">{k.pflegeeinrichtung_name}</TableCell>
                    <TableCell>{k.vertriebsmitarbeiter_name || "-"}</TableCell>
                    <TableCell>
                      <Badge variant={k.kontaktart === "akquise" ? "default" : "secondary"}>
                        {k.kontaktart === "akquise" ? "Akquise" : "Bestandskunde"}
                      </Badge>
                    </TableCell>
                    <TableCell className="capitalize">{k.kontaktkanal}</TableCell>
                    <TableCell className="max-w-[250px]">
                      <p className="truncate" title={k.gespraechsinhalt}>{k.gespraechsinhalt}</p>
                    </TableCell>
                    <TableCell>{k.naechster_kontakt_am || "-"}</TableCell>
                    <TableCell className="text-center">
                      <button 
                        className="p-2 rounded-lg hover:bg-gray-100 transition-colors" 
                        onClick={() => handleToggleKontaktErledigt(k.id, k.erledigt)}
                        title={k.erledigt ? "Als offen markieren" : "Als erledigt markieren"}
                      >
                        {k.erledigt ? <CheckCircle size={20} className="text-emerald-600" /> : <Circle size={20} className="text-gray-400" />}
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Provisionen Tab - Nur für Markus Dickie sichtbar */}
      {activeTab === "provisionen" && canManageProvisionen() && (
        <div>
          {/* Zusammenfassung pro Mitarbeiter */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            {/* Gesamt */}
            <Card className="p-4 bg-gray-50 border-gray-200">
              <p className="text-sm text-gray-600 font-medium mb-2">Gesamt</p>
              <div className="flex gap-4">
                <div>
                  <p className="text-xs text-amber-600">Offen</p>
                  <p className="text-lg font-bold text-amber-700">€{(uebersicht?.provisionen?.offen || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-emerald-600">Abgerechnet</p>
                  <p className="text-lg font-bold text-emerald-700">€{(uebersicht?.provisionen?.abgerechnet || 0).toLocaleString()}</p>
                </div>
              </div>
            </Card>
            
            {/* Pro Mitarbeiter */}
            {mitarbeiter.map(vm => {
              const vmProvisionen = provisionen.filter(p => p.vertriebsmitarbeiter_id === vm.id);
              // NEU: Nur bezahlte Rechnungen für Provisionsberechnung
              const vmBezahlt = vmProvisionen.filter(p => p.rechnung_bezahlt);
              const vmNichtBezahlt = vmProvisionen.filter(p => !p.rechnung_bezahlt);
              // Verwende provisions_netto falls vorhanden, sonst provisionsbetrag
              const vmProvisionBezahlt = vmBezahlt.reduce((sum, p) => sum + (p.provisions_netto ?? p.provisionsbetrag ?? 0), 0);
              const vmProvisionOffen = vmNichtBezahlt.reduce((sum, p) => sum + (p.provisions_netto ?? p.provisionsbetrag ?? 0), 0);
              const vmAbgerechnet = vmBezahlt.filter(p => p.abgerechnet).reduce((sum, p) => sum + (p.provisions_netto ?? p.provisionsbetrag ?? 0), 0);
              const vmUmsatz = vmProvisionen.reduce((sum, p) => sum + (p.umsatzbetrag || 0), 0);
              
              return (
                <Card key={vm.id} className="p-4 bg-blue-50 border-blue-200">
                  <p className="text-sm text-blue-800 font-medium mb-2">{vm.name}</p>
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <p className="text-xs text-gray-500">Umsatz</p>
                      <p className="text-md font-bold text-gray-700">€{vmUmsatz.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">Unbezahlt</p>
                      <p className="text-md font-bold text-gray-400">€{vmProvisionOffen.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-amber-600">Verdient</p>
                      <p className="text-md font-bold text-amber-700">€{vmProvisionBezahlt.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-xs text-emerald-600">Ausgez.</p>
                      <p className="text-md font-bold text-emerald-700">€{vmAbgerechnet.toLocaleString()}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          
          <div className="flex justify-between items-center mb-4">
            <div className="flex gap-2 items-center flex-wrap">
              <Select value={filterVM} onValueChange={setFilterVM}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Alle Mitarbeiter" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Mitarbeiter</SelectItem>
                  {mitarbeiter.filter(vm => vm.id).map(vm => (
                    <SelectItem key={vm.id} value={vm.id}>{vm.name}</SelectItem>
                  ))}
                  <SelectItem value="none">Ohne Zuordnung</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Alle offenen abrechnen Button */}
              {provisionen.filter(p => !p.abgerechnet).length > 0 && (
                <Button 
                  variant="outline" 
                  className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                  onClick={async () => {
                    const offene = provisionen.filter(p => !p.abgerechnet);
                    if (window.confirm(`Möchten Sie alle ${offene.length} offenen Provisionen als abgerechnet markieren?`)) {
                      for (const p of offene) {
                        await axios.put(`${API}/provisionen/${p.id}/abrechnen?abgerechnet=true`);
                      }
                      toast.success(`${offene.length} Provisionen abgerechnet`);
                      fetchData();
                    }
                  }}
                >
                  <CheckCircle size={16} className="mr-2" />
                  Alle abrechnen ({provisionen.filter(p => !p.abgerechnet).length})
                </Button>
              )}
              
              {/* EINMALIG: Alle Provisionen zurücksetzen - NUR für Markus Dickie */}
              {provisionen.length > 0 && (
                <Button 
                  variant="outline" 
                  className="border-red-500 text-red-600 hover:bg-red-50"
                  onClick={async () => {
                    if (window.confirm(`ACHTUNG: Möchten Sie wirklich ALLE ${provisionen.length} Provisionen auf 0€ setzen und als abgerechnet markieren?\n\nDiese Aktion kann NICHT rückgängig gemacht werden!`)) {
                      if (window.confirm(`LETZTE WARNUNG: Alle bestehenden Provisionsbeträge werden auf 0€ gesetzt. Fortfahren?`)) {
                        try {
                          const response = await axios.post(`${API}/provisionen/alle-abrechnen`);
                          toast.success(response.data.message || "Alle Provisionen zurückgesetzt");
                          fetchData();
                        } catch (error) {
                          toast.error("Fehler beim Zurücksetzen");
                        }
                      }
                    }
                  }}
                >
                  <RefreshCw size={16} className="mr-2" />
                  Alle auf 0€ setzen
                </Button>
              )}
            </div>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={() => openDialog("provision")}>
              <Plus size={16} className="mr-2" />
              Neue Provision
            </Button>
          </div>
          <Card className="card-custom">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#FFFFFF]">
                    <TableHead>Datum</TableHead>
                    <TableHead>Einrichtung/Kunde</TableHead>
                    <TableHead>Vertrieb</TableHead>
                    <TableHead>Kurs/Maßnahme</TableHead>
                    <TableHead className="text-right">Umsatz</TableHead>
                    <TableHead className="text-right">Provision (20%)</TableHead>
                    <TableHead className="text-center">Abgerechnet</TableHead>
                    <TableHead className="text-center">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {provisionen.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                        Keine Provisionen vorhanden
                      </TableCell>
                    </TableRow>
                  ) : provisionen
                    .filter(p => filterVM === "all" || 
                      (filterVM === "none" ? !p.vertriebsmitarbeiter_id : p.vertriebsmitarbeiter_id === filterVM))
                    .map(p => (
                    <TableRow 
                      key={p.id} 
                      className={`${p.abgerechnet ? "opacity-60 bg-gray-50" : ""} hover:bg-blue-50 cursor-pointer transition-colors`}
                      onClick={() => setSelectedProvision(p)}
                    >
                      <TableCell className="font-mono text-sm">{p.datum}</TableCell>
                      <TableCell className="font-medium">
                        {p.kunde_name || p.pflegeeinrichtung_name || "-"}
                        {p.quelle === "woocommerce" && (
                          <Badge variant="outline" className="ml-2 text-purple-600 border-purple-300">Online</Badge>
                        )}
                      </TableCell>
                      <TableCell>{p.vertriebsmitarbeiter_name || "-"}</TableCell>
                      <TableCell>
                        <span className="text-sm">{p.massnahme_oder_kurs || "-"}</span>
                        <Badge variant={p.bezug === "erstabschluss" || p.bezug === "neukunde" ? "default" : "secondary"} className="ml-2">
                          {p.bezug === "erstabschluss" || p.bezug === "neukunde" ? "Neu" : "Folge"}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-right">€{(p.umsatzbetrag || 0).toLocaleString()}</TableCell>
                      <TableCell className={`font-mono font-semibold text-right ${p.rechnung_bezahlt ? 'text-emerald-600' : 'text-gray-400'}`}>
                        €{(p.provisions_netto ?? p.provisionsbetrag ?? 0).toLocaleString()}
                        {p.provisionssatz && p.provisionssatz < 20 && (
                          <span className="text-xs block text-orange-500">({p.provisionssatz.toFixed(0)}%)</span>
                        )}
                        {!p.rechnung_bezahlt && <span className="text-xs block text-gray-400">(unbezahlt)</span>}
                      </TableCell>
                      <TableCell className="text-center">
                        <button 
                          className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAbrechnenProvision(p.id, p.abgerechnet);
                          }}
                          title={p.abgerechnet ? "Als offen markieren" : "Als abgerechnet markieren"}
                        >
                          {p.abgerechnet ? <CheckCircle size={20} className="text-emerald-600" /> : <Circle size={20} className="text-gray-400 hover:text-emerald-500" />}
                        </button>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <button 
                            className="p-2 rounded-lg hover:bg-blue-50 text-blue-500 hover:text-blue-700 transition-colors" 
                            onClick={() => handleEditProvision(p)}
                            title="Provision bearbeiten"
                          >
                            <Pencil size={18} />
                          </button>
                          <button 
                            className="p-2 rounded-lg hover:bg-red-50 text-red-500 hover:text-red-700 transition-colors" 
                            onClick={() => handleDeleteProvision(p.id)}
                            title="Provision löschen"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
          
          {/* Provisions-Detail Dialog */}
          <Dialog open={!!selectedProvision} onOpenChange={() => setSelectedProvision(null)}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Euro size={20} className="text-emerald-600" />
                  Provisions-Details
                </DialogTitle>
              </DialogHeader>
              {selectedProvision && (
                <div className="space-y-4">
                  {/* Kunde/Einrichtung */}
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium mb-1">Kunde / Einrichtung</p>
                    <p className="text-lg font-bold text-blue-800">
                      {selectedProvision.kunde_name || selectedProvision.pflegeeinrichtung_name || "Unbekannt"}
                    </p>
                    {selectedProvision.quelle && (
                      <Badge variant="outline" className="mt-2">
                        {selectedProvision.quelle === "woocommerce" ? "Online-Bestellung" : 
                         selectedProvision.quelle === "vertrieb" ? "Vertrieb" : 
                         selectedProvision.quelle === "manuell" ? "Manuell erfasst" : selectedProvision.quelle}
                      </Badge>
                    )}
                  </div>
                  
                  {/* Details Grid */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Datum</p>
                      <p className="font-medium">{selectedProvision.datum}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Vertriebsmitarbeiter</p>
                      <p className="font-medium">{selectedProvision.vertriebsmitarbeiter_name || "Kein VM"}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Kurs / Maßnahme</p>
                      <p className="font-medium">{selectedProvision.massnahme_oder_kurs || "-"}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-500">Bezug</p>
                      <p className="font-medium">
                        {selectedProvision.bezug === "erstabschluss" || selectedProvision.bezug === "neukunde" 
                          ? "Neukunde" : "Folgegeschäft"}
                      </p>
                    </div>
                  </div>
                  
                  {/* Beträge */}
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Umsatz</span>
                      <span className="font-bold text-lg">€{(selectedProvision.umsatzbetrag || 0).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Provisionssatz</span>
                      <span className="font-medium">{selectedProvision.provisionssatz || 20}%</span>
                    </div>
                    {selectedProvision.rabatt_hoehe > 0 && (
                      <div className="flex justify-between items-center mb-2 text-orange-600">
                        <span>Rabatt</span>
                        <span className="font-medium">-{selectedProvision.rabatt_hoehe?.toFixed(0) || 0}%</span>
                      </div>
                    )}
                    <div className="border-t border-emerald-300 pt-2 mt-2 flex justify-between items-center">
                      <span className="text-emerald-700 font-medium">Provision</span>
                      <span className="font-bold text-xl text-emerald-700">€{(selectedProvision.provisions_netto ?? selectedProvision.provisionsbetrag ?? 0).toLocaleString()}</span>
                    </div>
                  </div>
                  
                  {/* Status */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">Status</span>
                    <Badge className={selectedProvision.abgerechnet ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                      {selectedProvision.abgerechnet ? "Abgerechnet" : "Offen"}
                    </Badge>
                  </div>
                  
                  {/* Notizen */}
                  {selectedProvision.notiz && (
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-xs text-amber-600 font-medium mb-1">Notiz</p>
                      <p className="text-sm">{selectedProvision.notiz}</p>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelectedProvision(null)}>Schließen</Button>
                <Button 
                  className="bg-blue-500 hover:bg-blue-600"
                  onClick={() => {
                    handleEditProvision(selectedProvision);
                    setSelectedProvision(null);
                  }}
                >
                  <Pencil size={16} className="mr-2" /> Bearbeiten
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Online-Shop / WooCommerce Tab */}
      {activeTab === "online" && (
        <div>
          <Card className="card-custom mb-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart size={20} />
                Online-Bestellung erfassen (WooCommerce)
              </CardTitle>
              <CardDescription>
                Bestellungen aus dem Online-Shop erfassen - werden automatisch in Umsatz & Provision eingerechnet
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Kundenname *</Label>
                  <Input
                    placeholder="z.B. Max Mustermann"
                    value={onlineBestellung?.kunde_name || ""}
                    onChange={(e) => setOnlineBestellung({...onlineBestellung, kunde_name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Kurs/Produkt *</Label>
                  <Select 
                    value={onlineBestellung?.kurs_name || ""} 
                    onValueChange={(v) => {
                      const kurs = VERTRIEB_KURSE.find(k => k.name === v);
                      setOnlineBestellung({
                        ...onlineBestellung, 
                        kurs_name: v,
                        umsatzbetrag: kurs ? kurs.preis : onlineBestellung?.umsatzbetrag || 0
                      });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kurs wählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {VERTRIEB_KURSE.map(k => (
                        <SelectItem key={k.id} value={k.name}>{k.name} (€{k.preis})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Umsatzbetrag (€) *</Label>
                  <Input
                    type="number"
                    value={onlineBestellung?.umsatzbetrag || ""}
                    onChange={(e) => setOnlineBestellung({...onlineBestellung, umsatzbetrag: parseFloat(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Anzahl TN</Label>
                  <Input
                    type="number"
                    min="1"
                    value={onlineBestellung?.anzahl_teilnehmer || 1}
                    onChange={(e) => setOnlineBestellung({...onlineBestellung, anzahl_teilnehmer: parseInt(e.target.value) || 1})}
                  />
                </div>
                <div>
                  <Label>Bestelldatum *</Label>
                  <Input
                    type="date"
                    value={onlineBestellung?.bestelldatum || new Date().toISOString().split('T')[0]}
                    onChange={(e) => setOnlineBestellung({...onlineBestellung, bestelldatum: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Externe Rechnungsnr. (WooCommerce)</Label>
                  <Input
                    placeholder="z.B. WC-12345"
                    value={onlineBestellung?.externe_rechnungsnummer || ""}
                    onChange={(e) => setOnlineBestellung({...onlineBestellung, externe_rechnungsnummer: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Vertriebsmitarbeiter</Label>
                  <Select 
                    value={onlineBestellung?.vertriebsmitarbeiter_id || "none"} 
                    onValueChange={(v) => setOnlineBestellung({...onlineBestellung, vertriebsmitarbeiter_id: v === "none" ? null : v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Optional..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Kein Mitarbeiter (Direktverkauf)</SelectItem>
                      {mitarbeiter.filter(vm => vm.id).map(vm => (
                        <SelectItem key={vm.id} value={vm.id}>{vm.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Notiz</Label>
                  <Input
                    placeholder="z.B. Bezahlt via PayPal"
                    value={onlineBestellung?.notiz || ""}
                    onChange={(e) => setOnlineBestellung({...onlineBestellung, notiz: e.target.value})}
                  />
                </div>
                
                {/* NEU: Zahlungsart */}
                <div className="col-span-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="text-sm font-medium mb-2 block">Zahlungsart</Label>
                  <div className="flex gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="zahlungsart"
                        checked={onlineBestellung?.zahlungsart !== "ratenzahlung"}
                        onChange={() => setOnlineBestellung({...onlineBestellung, zahlungsart: "einmalzahlung", anzahl_raten: 1})}
                        className="w-4 h-4"
                      />
                      <span>Einmalzahlung</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="zahlungsart"
                        checked={onlineBestellung?.zahlungsart === "ratenzahlung"}
                        onChange={() => setOnlineBestellung({...onlineBestellung, zahlungsart: "ratenzahlung", anzahl_raten: 2, bereits_bezahlt: false})}
                        className="w-4 h-4"
                      />
                      <span>Ratenzahlung</span>
                    </label>
                  </div>
                  
                  {onlineBestellung?.zahlungsart === "ratenzahlung" && (
                    <div className="mt-3 p-3 bg-white rounded-lg border">
                      <div className="flex items-center gap-3 mb-3">
                        <Label className="text-sm">Anzahl Raten:</Label>
                        <Select 
                          value={String(onlineBestellung?.anzahl_raten || 2)}
                          onValueChange={(v) => setOnlineBestellung({...onlineBestellung, anzahl_raten: parseInt(v)})}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2">2 Raten</SelectItem>
                            <SelectItem value="3">3 Raten</SelectItem>
                            <SelectItem value="4">4 Raten</SelectItem>
                            <SelectItem value="5">5 Raten</SelectItem>
                            <SelectItem value="6">6 Raten</SelectItem>
                            <SelectItem value="7">7 Raten</SelectItem>
                            <SelectItem value="8">8 Raten</SelectItem>
                            <SelectItem value="9">9 Raten</SelectItem>
                            <SelectItem value="10">10 Raten</SelectItem>
                            <SelectItem value="11">11 Raten</SelectItem>
                            <SelectItem value="12">12 Raten</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {onlineBestellung?.umsatzbetrag > 0 && (
                        <div className="text-sm text-gray-600">
                          <p className="font-medium">Ratenübersicht:</p>
                          {Array.from({length: onlineBestellung?.anzahl_raten || 2}).map((_, i) => {
                            const rateBetrag = (onlineBestellung.umsatzbetrag / (onlineBestellung?.anzahl_raten || 2)).toFixed(2);
                            const faellig = new Date(onlineBestellung?.bestelldatum || new Date());
                            faellig.setMonth(faellig.getMonth() + i + 1);
                            return (
                              <div key={i} className="flex justify-between py-1 border-b border-gray-100 last:border-0">
                                <span>Rate {i + 1} (fällig {faellig.toLocaleDateString('de-DE')})</span>
                                <span className="font-medium">€{rateBetrag}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Bereits bezahlt - nur bei Einmalzahlung */}
                {onlineBestellung?.zahlungsart !== "ratenzahlung" && (
                  <div className="flex items-center gap-2 col-span-2">
                    <input
                      type="checkbox"
                      id="bereits_bezahlt"
                      checked={onlineBestellung?.bereits_bezahlt || false}
                      onChange={(e) => setOnlineBestellung({...onlineBestellung, bereits_bezahlt: e.target.checked})}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <Label htmlFor="bereits_bezahlt" className="cursor-pointer">Bereits bezahlt</Label>
                  </div>
                )}
              </div>
              
              {/* Vorschau */}
              {onlineBestellung?.umsatzbetrag > 0 && (
                <div className={`mt-4 p-4 ${onlineBestellung?.vertriebsmitarbeiter_id ? 'bg-emerald-50 border-emerald-200' : 'bg-blue-50 border-blue-200'} border rounded-lg`}>
                  <div className="flex justify-between items-center">
                    <div>
                      {onlineBestellung?.vertriebsmitarbeiter_id ? (
                        <>
                          <p className="text-sm text-emerald-700">Provision (20%):</p>
                          <p className="text-2xl font-bold text-emerald-600">€{(onlineBestellung.umsatzbetrag * 0.20).toFixed(2)}</p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-blue-700">Direktverkauf (keine Provision)</p>
                          <p className="text-2xl font-bold text-blue-600">Umsatz: €{parseFloat(onlineBestellung.umsatzbetrag).toFixed(2)}</p>
                        </>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {onlineBestellung?.isEditing && (
                        <Button 
                          variant="outline"
                          onClick={() => {
                            setOnlineBestellung({
                              kunde_name: "",
                              kurs_name: "",
                              umsatzbetrag: 0,
                              anzahl_teilnehmer: 1,
                              bestelldatum: new Date().toISOString().split('T')[0],
                              externe_rechnungsnummer: "",
                              vertriebsmitarbeiter_id: null,
                              notiz: "",
                              bereits_bezahlt: false
                            });
                          }}
                        >
                          Abbrechen
                        </Button>
                      )}
                      <Button 
                        className={`${onlineBestellung?.vertriebsmitarbeiter_id ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                        onClick={async () => {
                          if (!onlineBestellung?.kunde_name || !onlineBestellung?.kurs_name || !onlineBestellung?.umsatzbetrag) {
                            toast.error("Bitte alle Pflichtfelder ausfüllen");
                            return;
                          }
                          try {
                            if (onlineBestellung?.isEditing && onlineBestellung?.id) {
                              // Update existing
                              await axios.put(`${API}/online-bestellungen/${onlineBestellung.id}`, {
                                kunde_name: onlineBestellung.kunde_name,
                                kurs_name: onlineBestellung.kurs_name,
                                umsatzbetrag: onlineBestellung.umsatzbetrag,
                                anzahl_teilnehmer: onlineBestellung.anzahl_teilnehmer,
                                bestelldatum: onlineBestellung.bestelldatum,
                                externe_rechnungsnummer: onlineBestellung.externe_rechnungsnummer,
                                vertriebsmitarbeiter_id: onlineBestellung.vertriebsmitarbeiter_id,
                                notiz: onlineBestellung.notiz,
                                bereits_bezahlt: onlineBestellung.bereits_bezahlt,
                                zahlungsart: onlineBestellung.zahlungsart || "einmalzahlung",
                                anzahl_raten: onlineBestellung.anzahl_raten || 1
                              });
                              toast.success("Bestellung aktualisiert!");
                            } else {
                              // Create new
                              await axios.post(`${API}/externe-bestellungen`, {
                                ...onlineBestellung,
                                bestelldatum: onlineBestellung.bestelldatum || new Date().toISOString().split('T')[0],
                                zahlungsart: onlineBestellung.zahlungsart || "einmalzahlung",
                                anzahl_raten: onlineBestellung.anzahl_raten || 1
                              });
                              const msg = onlineBestellung?.zahlungsart === "ratenzahlung" 
                                ? `Ratenzahlung erfasst (${onlineBestellung.anzahl_raten} Raten)`
                                : onlineBestellung?.vertriebsmitarbeiter_id 
                                  ? "Online-Bestellung erfasst & verprovisioniert!" 
                                  : "Direktverkauf erfasst (keine Provision)";
                              toast.success(msg);
                            }
                            setOnlineBestellung({
                              kunde_name: "",
                              kurs_name: "",
                              umsatzbetrag: 0,
                              anzahl_teilnehmer: 1,
                              bestelldatum: new Date().toISOString().split('T')[0],
                              externe_rechnungsnummer: "",
                              vertriebsmitarbeiter_id: null,
                              notiz: "",
                              bereits_bezahlt: false,
                              zahlungsart: "einmalzahlung",
                              anzahl_raten: 1
                            });
                            fetchData();
                          } catch (err) {
                            toast.error("Fehler beim Speichern");
                          }
                        }}
                      >
                        {onlineBestellung?.isEditing ? (
                          <><Save size={16} className="mr-2" /> Änderungen speichern</>
                        ) : (
                          <><Plus size={16} className="mr-2" /> Bestellung erfassen</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          
          {/* Liste der Online-Bestellungen */}
          <Card className="card-custom">
            <CardHeader>
              <CardTitle>Erfasste Online-Bestellungen</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum</TableHead>
                    <TableHead>Kunde</TableHead>
                    <TableHead>Kurs</TableHead>
                    <TableHead className="text-right">Umsatz</TableHead>
                    <TableHead className="text-right">Provision</TableHead>
                    <TableHead>Ext. Rechnung</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {provisionen.filter(p => p.quelle === "woocommerce" || p.manuell_erfasst).map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{p.datum}</TableCell>
                      <TableCell className="font-medium">{p.kunde_name || "-"}</TableCell>
                      <TableCell>{p.massnahme_oder_kurs || "-"}</TableCell>
                      <TableCell className="text-right">€{p.umsatzbetrag?.toLocaleString()}</TableCell>
                      <TableCell className={`text-right font-medium ${p.provisionsbetrag > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                        {p.provisionsbetrag > 0 ? `€${p.provisionsbetrag?.toLocaleString()}` : 'Keine'}
                      </TableCell>
                      <TableCell>{p.externe_rechnungsnummer || "-"}</TableCell>
                      <TableCell>
                        <Badge variant={p.bereits_bezahlt ? "success" : "outline"} className={p.bereits_bezahlt ? "bg-emerald-100 text-emerald-700" : ""}>
                          {p.bereits_bezahlt ? "Bezahlt" : "Offen"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                              setOnlineBestellung({
                                id: p.id,
                                kunde_name: p.kunde_name || "",
                                kurs_name: p.massnahme_oder_kurs || "",
                                umsatzbetrag: p.umsatzbetrag || 0,
                                anzahl_teilnehmer: p.anzahl_teilnehmer || 1,
                                bestelldatum: p.datum || "",
                                externe_rechnungsnummer: p.externe_rechnungsnummer || "",
                                vertriebsmitarbeiter_id: p.vertriebsmitarbeiter_id || null,
                                notiz: p.notiz || "",
                                bereits_bezahlt: p.bereits_bezahlt || false,
                                zahlungsart: p.zahlungsart || "einmalzahlung",
                                anzahl_raten: p.anzahl_raten || 1,
                                isEditing: true
                              });
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                          >
                            <Edit size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700"
                            onClick={async () => {
                              if (window.confirm(`Bestellung ${p.externe_rechnungsnummer || ''} von ${p.kunde_name} wirklich löschen?`)) {
                                try {
                                  await axios.delete(`${API}/provisionen/${p.id}`);
                                  toast.success("Bestellung gelöscht");
                                  fetchData();
                                } catch (err) {
                                  toast.error("Fehler beim Löschen");
                                }
                              }
                            }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Mitarbeiter Tab */}
      {activeTab === "mitarbeiter" && (
        <div>
          <div className="flex justify-end mb-4">
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={() => openDialog("mitarbeiter")}>
              <UserPlus size={16} className="mr-2" />
              Neuer Mitarbeiter
            </Button>
          </div>
          <Card className="card-custom">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#FFFFFF]">
                    <TableHead>Name</TableHead>
                    <TableHead>Kürzel</TableHead>
                    <TableHead>Provision %</TableHead>
                    <TableHead className="text-right">Unbezahlt (€)</TableHead>
                    <TableHead className="text-right">Ausgezahlt (€)</TableHead>
                    <TableHead className="text-right">Verdient (€)</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mitarbeiter.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                        Keine Mitarbeiter vorhanden
                      </TableCell>
                    </TableRow>
                  ) : mitarbeiter.map(m => {
                    // Berechne Provisionen für diesen Mitarbeiter
                    const maProvs = provisionen.filter(p => p.vertriebsmitarbeiter_id === m.id);
                    // NEU: Nur bezahlte Rechnungen für Provisionsberechnung
                    const bezahlteProvs = maProvs.filter(p => p.rechnung_bezahlt);
                    // Verwende provisions_netto falls vorhanden, sonst provisionsbetrag
                    const provVerdient = bezahlteProvs.reduce((sum, p) => sum + (p.provisions_netto ?? p.provisionsbetrag ?? 0), 0);
                    const provAusgezahlt = bezahlteProvs.filter(p => p.abgerechnet).reduce((sum, p) => sum + (p.provisions_netto ?? p.provisionsbetrag ?? 0), 0);
                    const provUnbezahlt = maProvs.filter(p => !p.rechnung_bezahlt).reduce((sum, p) => sum + (p.provisions_netto ?? p.provisionsbetrag ?? 0), 0);
                    
                    return (
                    <TableRow key={m.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">{m.name}</TableCell>
                      <TableCell className="font-mono">{m.kuerzel}</TableCell>
                      <TableCell>20%</TableCell>
                      <TableCell className="text-right font-mono text-gray-400">
                        {provUnbezahlt > 0 ? `€${provUnbezahlt.toLocaleString()}` : '-'}
                        {provUnbezahlt > 0 && <span className="text-xs block">(unbezahlt)</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-emerald-600">
                        {provAusgezahlt > 0 ? `€${provAusgezahlt.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-amber-600">
                        {provVerdient > 0 ? `€${provVerdient.toLocaleString()}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={m.aktiv ? "default" : "secondary"} className={m.aktiv ? "bg-emerald-100 text-emerald-700" : ""}>
                          {m.aktiv ? "Aktiv" : "Inaktiv"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Statistik Tab */}
      {activeTab === "statistik" && (
        <div className="space-y-6">
          <Card className="card-custom">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <BarChart3 className="text-blue-600" size={20} />
                Vertriebsleistung pro Mitarbeiter (monatlich)
              </h3>
              
              {vertriebsStatistik.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Noch keine Kontakte oder Bestellungen erfasst</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FFFFFF]">
                      <TableHead>Monat</TableHead>
                      <TableHead>Mitarbeiter</TableHead>
                      <TableHead className="text-right">Kontakte (Leads)</TableHead>
                      <TableHead className="text-right">Neukunden</TableHead>
                      <TableHead className="text-right">Conversion</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vertriebsStatistik.map((stat, idx) => (
                      <TableRow key={idx} className="hover:bg-gray-50">
                        <TableCell className="font-mono">{stat.monat}</TableCell>
                        <TableCell className="font-medium">{stat.vertriebsmitarbeiter_name}</TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-blue-600">{stat.leads}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-mono text-emerald-600 font-semibold">{stat.kunden}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge className={stat.conversion_rate >= 20 ? "bg-emerald-100 text-emerald-700" : stat.conversion_rate >= 10 ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-700"}>
                            {stat.conversion_rate}%
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm">
                <p className="text-blue-700">
                  <strong>Hinweis:</strong> Kontakte werden aus dem Kontaktprotokoll gezählt. 
                  Neukunden sind Erstabschlüsse (Provisionen mit Bezug "Erstabschluss").
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sammelmail */}
      {activeTab === "sammelmail" && (
        <SammelmailTab pflegeeinrichtungen={pflegeeinrichtungen} />
      )}

      {/* Dialoge für neue Einträge */}
      <Dialog open={dialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>
              {dialogType === "kontakt" ? "Neuer Kontakt" : 
               dialogType === "provision" ? (editingProvisionId ? "Provision bearbeiten" : "Neue Provision") : 
               "Neuer Vertriebsmitarbeiter"}
            </DialogTitle>
          </DialogHeader>
          
          {dialogType === "kontakt" && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pflegeeinrichtung *</Label>
                  <Select value={kontaktForm.pflegeeinrichtung_id} onValueChange={(v) => setKontaktForm({...kontaktForm, pflegeeinrichtung_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Auswählen" /></SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {pflegeeinrichtungen.slice(0, 200).map(pe => (
                        <SelectItem key={pe.id} value={pe.id}>{pe.einrichtungsname}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Vertriebsmitarbeiter *</Label>
                  <Select value={kontaktForm.vertriebsmitarbeiter_id} onValueChange={(v) => setKontaktForm({...kontaktForm, vertriebsmitarbeiter_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Auswählen" /></SelectTrigger>
                    <SelectContent>
                      {mitarbeiter.filter(m => m.aktiv).map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Kontaktart</Label>
                  <Select value={kontaktForm.kontaktart} onValueChange={(v) => setKontaktForm({...kontaktForm, kontaktart: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="akquise">Akquise</SelectItem>
                      <SelectItem value="bestandskunde">Bestandskunde</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Kontaktkanal</Label>
                  <Select value={kontaktForm.kontaktkanal} onValueChange={(v) => setKontaktForm({...kontaktForm, kontaktkanal: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="telefon">Telefon</SelectItem>
                      <SelectItem value="email">E-Mail</SelectItem>
                      <SelectItem value="persoenlich">Persönlich</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Kontaktdatum *</Label>
                  <Input type="date" value={kontaktForm.kontaktdatum} onChange={(e) => setKontaktForm({...kontaktForm, kontaktdatum: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Ansprechpartner</Label>
                <Input value={kontaktForm.ansprechpartner} onChange={(e) => setKontaktForm({...kontaktForm, ansprechpartner: e.target.value})} />
              </div>
              <div>
                <Label>Gesprächsinhalt *</Label>
                <Textarea rows={3} value={kontaktForm.gespraechsinhalt} onChange={(e) => setKontaktForm({...kontaktForm, gespraechsinhalt: e.target.value})} />
              </div>
              <div>
                <Label>Ergebnis</Label>
                <Input value={kontaktForm.ergebnis} onChange={(e) => setKontaktForm({...kontaktForm, ergebnis: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Nächster Schritt</Label>
                  <Input value={kontaktForm.naechster_schritt} onChange={(e) => setKontaktForm({...kontaktForm, naechster_schritt: e.target.value})} />
                </div>
                <div>
                  <Label>Nächster Kontakt am</Label>
                  <Input type="date" value={kontaktForm.naechster_kontakt_am} onChange={(e) => setKontaktForm({...kontaktForm, naechster_kontakt_am: e.target.value})} />
                </div>
              </div>
            </div>
          )}

          {dialogType === "provision" && (
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Pflegeeinrichtung *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-between font-normal">
                        {provisionForm.pflegeeinrichtung_id 
                          ? pflegeeinrichtungen.find(pe => pe.id === provisionForm.pflegeeinrichtung_id)?.einrichtungsname || "Auswählen..."
                          : "Einrichtung suchen..."}
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[350px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Name eingeben..." />
                        <CommandList>
                          <CommandEmpty>Keine Einrichtung gefunden</CommandEmpty>
                          <CommandGroup className="max-h-[250px] overflow-y-auto">
                            {pflegeeinrichtungen.map(pe => (
                              <CommandItem 
                                key={pe.id} 
                                value={pe.einrichtungsname}
                                onSelect={() => setProvisionForm({...provisionForm, pflegeeinrichtung_id: pe.id})}
                              >
                                <Check className={`mr-2 h-4 w-4 ${provisionForm.pflegeeinrichtung_id === pe.id ? "opacity-100" : "opacity-0"}`} />
                                <span className="truncate">{pe.einrichtungsname}</span>
                                {pe.ort && <span className="ml-2 text-xs text-gray-400">{pe.ort}</span>}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Vertriebsmitarbeiter *</Label>
                  <Select value={provisionForm.vertriebsmitarbeiter_id} onValueChange={(v) => setProvisionForm({...provisionForm, vertriebsmitarbeiter_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Auswählen" /></SelectTrigger>
                    <SelectContent>
                      {mitarbeiter.filter(m => m.aktiv).map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Kurs-Schnellauswahl */}
              <div>
                <Label>Kurs auswählen (mit automatischem Preis)</Label>
                <Select onValueChange={(kursName) => {
                  const kurs = VERTRIEB_KURSE.find(k => k.name === kursName);
                  if (kurs) {
                    setProvisionForm({...provisionForm, massnahme_oder_kurs: kursName, umsatzbetrag: kurs.preis.toString()});
                  }
                }}>
                  <SelectTrigger><SelectValue placeholder="Kurs für automatischen Preis..." /></SelectTrigger>
                  <SelectContent>
                    {VERTRIEB_KURSE.map(k => (
                      <SelectItem key={k.name} value={k.name}>
                        {k.name} - €{k.preis.toLocaleString()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bezug</Label>
                  <Select value={provisionForm.bezug} onValueChange={(v) => setProvisionForm({...provisionForm, bezug: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="erstabschluss">Erstabschluss</SelectItem>
                      <SelectItem value="folgegeschaeft">Folgegeschäft</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Datum</Label>
                  <Input type="date" value={provisionForm.datum} onChange={(e) => setProvisionForm({...provisionForm, datum: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Maßnahme / Kurs</Label>
                <Input value={provisionForm.massnahme_oder_kurs} onChange={(e) => setProvisionForm({...provisionForm, massnahme_oder_kurs: e.target.value})} placeholder="z.B. 2x Pflegedienstleitung PDL" />
              </div>
              <div>
                <Label>Umsatzbetrag (€) *</Label>
                <Input type="number" value={provisionForm.umsatzbetrag} onChange={(e) => setProvisionForm({...provisionForm, umsatzbetrag: e.target.value})} placeholder="z.B. 5000" />
              </div>
              
              {/* NEU: Rabatt-Bereich für manuelle Eingabe */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <Label className="text-sm font-medium mb-2 block">Rabatt (wird von Provision abgezogen)</Label>
                <div className="flex gap-2 items-center">
                  <Select 
                    value={provisionForm.rabatt_art} 
                    onValueChange={(v) => setProvisionForm({...provisionForm, rabatt_art: v, rabatt_hoehe: 0, rabatt_betrag: 0})}
                  >
                    <SelectTrigger className="w-40 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keiner">Kein Rabatt</SelectItem>
                      <SelectItem value="prozent">Prozent %</SelectItem>
                      <SelectItem value="fixbetrag">Fixbetrag €</SelectItem>
                    </SelectContent>
                  </Select>
                  {provisionForm.rabatt_art !== "keiner" && (
                    <Input 
                      type="number"
                      value={provisionForm.rabatt_hoehe || ""}
                      onChange={(e) => {
                        let val = parseFloat(e.target.value) || 0;
                        if (provisionForm.rabatt_art === "prozent" && val > 20) {
                          toast.error("Rabatt darf maximal 20% sein (Provision = 20%)");
                          val = 20;
                        }
                        setProvisionForm({...provisionForm, rabatt_hoehe: val});
                      }}
                      placeholder={provisionForm.rabatt_art === "prozent" ? "max 20%" : "€"}
                      max={provisionForm.rabatt_art === "prozent" ? 20 : undefined}
                      className="w-24 bg-white"
                    />
                  )}
                </div>
              </div>
              
              {/* Berechnungsanzeige mit Rabatt */}
              {(() => {
                const listenpreis = parseFloat(provisionForm.umsatzbetrag) || 0;
                let rabattBetrag = 0;
                if (provisionForm.rabatt_art === "prozent" && provisionForm.rabatt_hoehe > 0) {
                  rabattBetrag = listenpreis * provisionForm.rabatt_hoehe / 100;
                } else if (provisionForm.rabatt_art === "fixbetrag" && provisionForm.rabatt_hoehe > 0) {
                  rabattBetrag = provisionForm.rabatt_hoehe;
                }
                const tatsaechlicherUmsatz = Math.max(0, listenpreis - rabattBetrag);
                const provision = tatsaechlicherUmsatz * 0.20;
                
                return (
                  <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                    {rabattBetrag > 0 ? (
                      <>
                        <div className="flex justify-between items-center text-gray-500">
                          <span>Listenpreis:</span>
                          <span className="line-through">€{listenpreis.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center mt-1 text-red-500">
                          <span className="text-sm">Rabatt:</span>
                          <span className="font-medium">-€{rabattBetrag.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-emerald-300">
                          <span className="text-emerald-700 font-semibold">Umsatz:</span>
                          <span className="text-xl font-bold text-emerald-700">€{tatsaechlicherUmsatz.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-emerald-300">
                          <span className="text-emerald-700 font-semibold">Provision (20%):</span>
                          <span className="text-2xl font-bold text-emerald-700">€{provision.toLocaleString()}</span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-emerald-700">Umsatz:</span>
                          <span className="text-xl font-bold text-emerald-700">€{listenpreis.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center mt-2 pt-2 border-t border-emerald-300">
                          <span className="text-emerald-700 font-semibold">Provision (20%):</span>
                          <span className="text-2xl font-bold text-emerald-700">€{provision.toLocaleString()}</span>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {dialogType === "mitarbeiter" && (
            <div className="grid gap-4 py-4">
              <div>
                <Label>Name *</Label>
                <Input value={mitarbeiterForm.name} onChange={(e) => setMitarbeiterForm({...mitarbeiterForm, name: e.target.value})} placeholder="Max Müller" />
              </div>
              <div>
                <Label>Kürzel *</Label>
                <Input value={mitarbeiterForm.kuerzel} onChange={(e) => setMitarbeiterForm({...mitarbeiterForm, kuerzel: e.target.value})} placeholder="MM" maxLength={3} />
              </div>
              <div>
                <Label>Provision Neukunde (%)</Label>
                <Input type="number" value={mitarbeiterForm.provision_neukunde_prozent} onChange={(e) => setMitarbeiterForm({...mitarbeiterForm, provision_neukunde_prozent: parseInt(e.target.value)})} disabled />
                <p className="text-xs text-[#78716c] mt-1">Fix 20% für alle Mitarbeiter</p>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox 
                  id="aktiv" 
                  checked={mitarbeiterForm.aktiv} 
                  onCheckedChange={(checked) => setMitarbeiterForm({...mitarbeiterForm, aktiv: checked})} 
                />
                <Label htmlFor="aktiv">Aktiv</Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Abbrechen</Button>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={dialogType === "kontakt" ? handleKontaktSubmit : dialogType === "provision" ? handleSaveProvision : handleMitarbeiterSubmit}>
              {editingProvisionId && dialogType === "provision" ? "Aktualisieren" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp-Sender Dialog */}
      <WhatsAppSenderDialog
        open={waDialogOpen}
        onOpenChange={setWaDialogOpen}
        pe={waTarget}
        onSent={async (peUpdated) => {
          // Nach dem Versand: aktualisierte PE aus Backend nachladen und Selected-PE refresh
          try {
            const res = await axios.get(`${API}/pflegeeinrichtungen/${peUpdated.id}`);
            setSelectedPE(res.data);
            setPflegeeinrichtungen(prev => prev.map(p => (p.id === res.data.id ? res.data : p)));
          } catch (_e) { /* still fine */ }
          toast.success("WhatsApp gesendet & vermerkt");
        }}
      />
    </div>
  );
};

// ==================== DASHBOARD ====================
// Dashboard-Widget: Mahnwesen-Vorschläge (überfällige Rechnungen mit vorgeschlagener Stufe)
const MahnwesenVorschlaegeWidget = ({ role }) => {
  const [state, setState] = React.useState({ loading: true, count: 0, vorschlaege: [] });
  const [zeigeRegeln, setZeigeRegeln] = React.useState(false);
  const load = React.useCallback(() => {
    setState(s => ({ ...s, loading: true }));
    axios.get(`${API}/mahnwesen/vorschlaege`)
      .then(res => setState({ loading: false, count: res.data.count || 0, vorschlaege: res.data.vorschlaege || [] }))
      .catch(() => setState({ loading: false, count: 0, vorschlaege: [] }));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const stufeLabel = (stufe) => {
    if (stufe === -1) return { label: "Inkasso vorbereiten", color: "#8B1F16", bg: "#F6E4DF" };
    if (stufe === 0)  return { label: "Zahlungserinnerung", color: "#6E866F", bg: "#E9EFE7" };
    if (stufe === 1)  return { label: "1. Mahnung",         color: "#E07856", bg: "#FBEBE2" };
    if (stufe === 2)  return { label: "2. Mahnung",         color: "#C2452F", bg: "#F6E4DF" };
    return { label: "—", color: "#8E7B71", bg: "#F1E8DF" };
  };

  const eur = (v) => `${(Math.round(v * 100) / 100).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

  return (
    <div className="mt-8 rounded-lg border border-[#E0D6CA] bg-white p-5" data-testid="mahnwesen-vorschlaege-widget">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.24em] font-semibold" style={{ color: "#E07856" }}>
            Debitorenübersicht
          </div>
          <h3 className="text-xl font-serif text-[#3D3835] mt-0.5" style={{ fontFamily: "'Fraunces', Georgia, serif", letterSpacing: "-0.005em" }}>
            Fällige Mahnungen
          </h3>
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => setZeigeRegeln(z => !z)}
            className="text-xs uppercase tracking-wider text-[#8E7B71] hover:text-[#3D3835] transition-colors"
            data-testid="mahnregeln-toggle"
          >
            {zeigeRegeln ? "Regeln ausblenden" : "Regeln je Kundentyp"}
          </button>
          <button
            onClick={load}
            className="text-xs uppercase tracking-wider text-[#8E7B71] hover:text-[#3D3835] transition-colors"
            data-testid="mahnwesen-vorschlaege-reload"
          >
            Aktualisieren
          </button>
        </div>
      </div>
      {zeigeRegeln && <MahnregelnPanel role={role} onChanged={load} />}

      {state.loading ? (
        <div className="text-sm text-[#8E7B71] italic py-4">Prüfe offene Forderungen…</div>
      ) : state.count === 0 ? (
        <div className="rounded-md bg-[#E9EFE7] border border-[#D3DFD1] p-4 text-sm text-[#4F6650]">
          Keine überfälligen Rechnungen — alles im grünen Bereich.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-[#8E7B71] border-b border-[#E0D6CA]">
                <th className="py-2 pr-3 font-semibold">Rechnung</th>
                <th className="py-2 pr-3 font-semibold">Empfänger</th>
                <th className="py-2 pr-3 font-semibold text-right">Offen</th>
                <th className="py-2 pr-3 font-semibold text-right">Überfällig</th>
                <th className="py-2 pr-3 font-semibold">Vorschlag</th>
                <th className="py-2 pr-3 font-semibold">Versand</th>
                <th className="py-2 pl-2 font-semibold">Grund</th>
              </tr>
            </thead>
            <tbody>
              {state.vorschlaege.map((v) => {
                const s = stufeLabel(v.naechste_stufe);
                return (
                  <tr key={v.rechnung_id} className="border-b border-[#F1E8DF] last:border-0" data-testid={`mahnwesen-row-${v.rechnungsnummer}`}>
                    <td className="py-2 pr-3 font-mono text-[13px] text-[#3D3835]">{v.rechnungsnummer}</td>
                    <td className="py-2 pr-3 text-[#3D3835]">{v.empfaenger_name}{v.kundentyp && <div className="text-[10px] text-[#8E7B71]">{v.kundentyp}</div>}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-[#3D3835]">{eur(v.offener_betrag)}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-[#C2452F] font-semibold">{v.tage_ueberfaellig} T</td>
                    <td className="py-2 pr-3">
                      <span
                        className="text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wider"
                        style={{ color: s.color, background: s.bg, border: `0.5px solid ${s.color}55` }}
                      >
                        {s.label}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-[11px] whitespace-nowrap" data-testid={`mahnwesen-versand-${v.rechnungsnummer}`}>
                      {v.heute_versandtag ? <span className="text-[#4F6650] font-semibold">heute</span> : <span className="text-[#8E7B71]">ab {new Date(v.versand_ab).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}</span>}
                    </td>
                    <td className="py-2 pl-2 text-[11px] text-[#8E7B71] italic">{v.begruendung}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


const Dashboard = () => {
  const { user: dashUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todoText, setTodoText] = useState("");
  const [todoZustaendig, setTodoZustaendig] = useState("");
  const [todoFaellig, setTodoFaellig] = useState("");
  const [todoPE, setTodoPE] = useState(null); // Verknüpfte Pflegeeinrichtung
  const [todoTN, setTodoTN] = useState(null); // Verknüpfter Teilnehmer
  const [syncing, setSyncing] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  // Team-Board: Antworten/Kommentare pro Todo
  const [replyText, setReplyText] = useState({});          // { [todoId]: "text..." }
  const [replyAutor, setReplyAutor] = useState("");         // Shared über alle Todos
  const [openReplies, setOpenReplies] = useState({});       // { [todoId]: true } - Antwort-Form eingeblendet
  const [expandedReplies, setExpandedReplies] = useState({}); // Alle Antworten anzeigen
  const [fortbildungspakete, setFortbildungspakete] = useState(null); // NEU: Paket-Übersicht
  const [cashflowAmpel, setCashflowAmpel] = useState(null); // DEPRECATED — Card aus Dashboard entfernt, State bleibt für evtl. spätere Reaktivierung
  const [backupHealth, setBackupHealth] = useState(null); // NEU: Backup-Health-Ampel
  const [waStats, setWaStats] = useState(null); // WhatsApp-Versand-Statistik
  const [drDrill, setDrDrill] = useState(null); // NEU: DR-Drill-Status
  const navigate = useNavigate();
  
  // State für Bestellung hinzufügen
  const [showBestellungDialog, setShowBestellungDialog] = useState(false);
  const [neueBestellung, setNeueBestellung] = useState({
    kunde: "",
    kurs: "",
    datum: new Date().toISOString().split('T')[0],
    betrag: ""
  });
  
  // Meilenstein-Celebration State
  const [meilensteinPopup, setMeilensteinPopup] = useState(null);
  const [gezeighteMeilensteine, setGezeighteMeilensteine] = useState(() => {
    // Aus localStorage laden - MIT aktuellem Monat/Jahr
    const currentMonthKey = `dickies_meilensteine_${new Date().getFullYear()}_${new Date().getMonth() + 1}`;
    const saved = localStorage.getItem(currentMonthKey);
    
    // Alte Monats-Daten löschen
    const oldKeys = Object.keys(localStorage).filter(k => k.startsWith('dickies_meilensteine_') && k !== currentMonthKey);
    oldKeys.forEach(k => localStorage.removeItem(k));
    
    return saved ? JSON.parse(saved) : [];
  });
  
  // State für Kunden-Bearbeitungs-Dialog
  const [selectedReaktivierungKunde, setSelectedReaktivierungKunde] = useState(null);
  const [kundeWiedervorlage, setKundeWiedervorlage] = useState("");
  const [kundeNotiz, setKundeNotiz] = useState("");
  
  // Aktueller Monat für Storage-Key
  const currentMonthKey = `dickies_meilensteine_${new Date().getFullYear()}_${new Date().getMonth() + 1}`;
  
  // Meilenstein-Definitionen (pro MONAT!)
  const MEILENSTEINE = [];

  // Kunden für Reaktivierung öffnen
  const openKundeDialog = async (kunde) => {
    setSelectedReaktivierungKunde(kunde);
    // Default: 7 Tage in der Zukunft
    setKundeWiedervorlage(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
    setKundeNotiz("");
  };

  // Kunden speichern und Dialog schließen
  const saveKundeReaktivierung = async () => {
    if (!selectedReaktivierungKunde) return;
    
    try {
      await axios.patch(`${API}/pflegeeinrichtungen/${selectedReaktivierungKunde.id}/wiedervorlage?datum=${kundeWiedervorlage}`);
      
      // Optional: Notiz speichern
      if (kundeNotiz.trim()) {
        await axios.post(`${API}/kontaktprotokoll`, {
          pflegeeinrichtung_id: selectedReaktivierungKunde.id,
          typ: "notiz",
          inhalt: kundeNotiz,
          datum: new Date().toISOString().split('T')[0]
        });
      }
      
      toast.success(`${selectedReaktivierungKunde.name}: Wiedervorlage am ${new Date(kundeWiedervorlage).toLocaleDateString('de-DE')} gesetzt`);
      setSelectedReaktivierungKunde(null);
      
      // Dashboard sofort aktualisieren - mit kurzer Verzögerung für DB-Konsistenz
      setTimeout(async () => {
        await fetchDashboard();
      }, 100);
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  // Kunde als "erledigt" markieren (ohne WV)
  const markKundeErledigt = async () => {
    if (!selectedReaktivierungKunde) return;
    
    try {
      // Setze WV auf morgen - damit er aus der Liste verschwindet
      const morgen = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      await axios.patch(`${API}/pflegeeinrichtungen/${selectedReaktivierungKunde.id}/wiedervorlage?datum=${morgen}`);
      toast.success(`${selectedReaktivierungKunde.name} erledigt - WV auf morgen gesetzt`);
      setSelectedReaktivierungKunde(null);
      
      // Dashboard sofort aktualisieren - mit kurzer Verzögerung für DB-Konsistenz
      setTimeout(async () => {
        await fetchDashboard();
      }, 100);
    } catch (error) {
      toast.error("Fehler");
    }
  };

  const [topKunden, setTopKunden] = useState(null);
  const [reaktivierungList, setReaktivierungList] = useState(null);

  const fetchDashboard = async () => {
    try {
      const [dashboardRes, paketeRes, ampelRes, backupHealthRes, drDrillRes, waStatsRes, topKundenRes, reaktRes] = await Promise.all([
        axios.get(`${API}/dashboard`),
        axios.get(`${API}/pflegeeinrichtungen/fortbildungspakete`),
        axios.get(`${API}/dashboard/cashflow-ampel`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/backup-health`).catch(() => ({ data: null })),
        axios.get(`${API}/backup/dr-drill/status`).catch(() => ({ data: null })),
        axios.get(`${API}/dashboard/whatsapp-stats`).catch(() => ({ data: null })),
        axios.get(`${API}/pflegeeinrichtungen/top-kunden?limit=10`).catch(() => ({ data: null })),
        axios.get(`${API}/pflegeeinrichtungen/reaktivierung-faellig?tage=90&limit=10`).catch(() => ({ data: null })),
      ]);
      setData(dashboardRes.data);
      setFortbildungspakete(paketeRes.data);
      setCashflowAmpel(ampelRes.data);
      setBackupHealth(backupHealthRes.data);
      setDrDrill(drDrillRes.data);
      setWaStats(waStatsRes.data);
      setTopKunden(topKundenRes.data);
      setReaktivierungList(reaktRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden des Dashboards");
    } finally {
      setLoading(false);
    }
  };

  // Dashboard neu laden wenn Seite fokussiert wird (z.B. nach Bearbeitung einer Pflegeeinrichtung)
  useEffect(() => {
    fetchDashboard();
    
    // Refresh Dashboard wenn Tab/Fenster wieder fokussiert wird
    const handleFocus = () => {
      fetchDashboard();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);
  
  // Meilenstein-Check wenn Daten geladen werden
  useEffect(() => {
    if (data?.statistiken?.umsatz_monat) {
      const aktuellerUmsatz = data.statistiken.umsatz_monat;
      
      // Prüfe ob ein neuer Meilenstein erreicht wurde
      for (const meilenstein of MEILENSTEINE) {
        if (aktuellerUmsatz >= meilenstein.betrag && !gezeighteMeilensteine.includes(meilenstein.betrag)) {
          // Neuer Meilenstein erreicht!
          setMeilensteinPopup(meilenstein);
          
          // Als gezeigt markieren (mit aktuellem Monat-Key)
          const neueListe = [...gezeighteMeilensteine, meilenstein.betrag];
          setGezeighteMeilensteine(neueListe);
          localStorage.setItem(currentMonthKey, JSON.stringify(neueListe));
          
          break; // Nur einen Meilenstein gleichzeitig zeigen
        }
      }
    }
  }, [data?.statistiken?.umsatz_monat, currentMonthKey]);

  const handleAddBestellung = async () => {
    if (!neueBestellung.kunde.trim()) {
      toast.error("Bitte Kunde eingeben");
      return;
    }
    try {
      // Provision wird NICHT mehr automatisch erstellt - nur Markus Dickie kann Provisionen eintragen
      // Erstelle nur einen Kontakt-Eintrag statt Provision
      toast.success("Bestellung notiert (Provision muss manuell eingetragen werden)");
      setShowBestellungDialog(false);
      setNeueBestellung({ kunde: "", kurs: "", datum: new Date().toISOString().split('T')[0], betrag: "" });
      fetchDashboard();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleAddTodo = async () => {
    if (!todoText.trim()) {
      toast.error("Bitte Aufgabe eingeben");
      return;
    }
    try {
      await axios.post(`${API}/todos`, {
        aufgabe: todoText,
        zustaendig: todoZustaendig,
        faelligkeitsdatum: todoFaellig,
        pflegeeinrichtung_id: todoPE?.id || null,
        pflegeeinrichtung_name: todoPE?.einrichtungsname || null,
        teilnehmer_id: todoTN?.id || null,
        teilnehmer_name: todoTN ? `${todoTN.vorname || ''} ${todoTN.nachname || ''}`.trim() : null
      });
      setTodoText("");
      setTodoZustaendig("");
      setTodoFaellig("");
      setTodoPE(null);
      setTodoTN(null);
      fetchDashboard();
      toast.success("Aufgabe hinzugefügt");
    } catch (error) {
      toast.error("Fehler beim Erstellen");
    }
  };

  // Team-Board: Antwort/Kommentar unter einer Aufgabe hinzufügen
  const handleAddReply = async (todoId) => {
    const text = (replyText[todoId] || "").trim();
    if (!text) {
      toast.error("Bitte Antwort-Text eingeben");
      return;
    }
    const autor = replyAutor.trim() || "Team";
    try {
      await axios.post(`${API}/todos/${todoId}/kommentar`, { text, autor });
      setReplyText(prev => ({ ...prev, [todoId]: "" }));
      // Antworten offen lassen und erweitert anzeigen
      setExpandedReplies(prev => ({ ...prev, [todoId]: true }));
      fetchDashboard();
    } catch (error) {
      toast.error("Fehler beim Senden der Antwort");
    }
  };

  const handleDeleteReply = async (todoId, kommentarId) => {
    if (!confirm("Antwort wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/todos/${todoId}/kommentar/${kommentarId}`);
      fetchDashboard();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleToggleTodo = async (todo) => {
    try {
      await axios.put(`${API}/todos/${todo.id}`, {
        ...todo,
        erledigt: !todo.erledigt,
        pflegeeinrichtung_id: todo.pflegeeinrichtung_id,
        pflegeeinrichtung_name: todo.pflegeeinrichtung_name,
        teilnehmer_id: todo.teilnehmer_id,
        teilnehmer_name: todo.teilnehmer_name
      });
      fetchDashboard();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteTodo = async (todoId) => {
    try {
      await axios.delete(`${API}/todos/${todoId}`);
      fetchDashboard();
      toast.success("Aufgabe gelöscht");
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleEditTodo = (todo) => {
    setEditingTodo(todo);
    setTodoText(todo.aufgabe);
    setTodoZustaendig(todo.zustaendig || "");
    setTodoFaellig(todo.faelligkeitsdatum || "");
    setTodoPE(todo.pflegeeinrichtung_id ? { id: todo.pflegeeinrichtung_id, einrichtungsname: todo.pflegeeinrichtung_name } : null);
    setTodoTN(todo.teilnehmer_id ? { id: todo.teilnehmer_id, vorname: todo.teilnehmer_name?.split(' ')[0] || '', nachname: todo.teilnehmer_name?.split(' ').slice(1).join(' ') || '' } : null);
  };

  const handleUpdateTodo = async () => {
    if (!todoText.trim() || !editingTodo) return;
    try {
      await axios.put(`${API}/todos/${editingTodo.id}`, {
        ...editingTodo,
        aufgabe: todoText,
        zustaendig: todoZustaendig,
        faelligkeitsdatum: todoFaellig,
        pflegeeinrichtung_id: todoPE?.id || null,
        pflegeeinrichtung_name: todoPE?.einrichtungsname || null,
        teilnehmer_id: todoTN?.id || null,
        teilnehmer_name: todoTN ? `${todoTN.vorname || ''} ${todoTN.nachname || ''}`.trim() : null
      });
      setEditingTodo(null);
      setTodoText("");
      setTodoZustaendig("");
      setTodoFaellig("");
      setTodoPE(null);
      setTodoTN(null);
      fetchDashboard();
      toast.success("Aufgabe aktualisiert");
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const cancelEditTodo = () => {
    setEditingTodo(null);
    setTodoText("");
    setTodoZustaendig("");
    setTodoFaellig("");
    setTodoPE(null);
    setTodoTN(null);
  };

  const handleNotionSync = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/notion/sync`);
      const stats = response.data.stats;
      toast.success(`Notion-Sync abgeschlossen: ${stats.created} erstellt, ${stats.updated} aktualisiert`);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Notion-Sync");
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="dashboard">
      {dashUser?.role !== "dozent" && <UnterschriftenVorBeginn />}
      <DozentNachtragBlock />
      {dashUser?.role !== "dozent" && <DokumentWaechter rolle={dashUser?.role} />}
      {["gf", "verwaltung"].includes(dashUser?.role) && <Zahlungsvorschau />}
      <div className="dashboard-hero-bg">
      <div className="dashboard-greeting">
        <div>
          <h1>
            Hallo <span className="accent">{(dashUser?.name || "").split(" ")[0]}</span>, willkommen zurück.
          </h1>
          <p className="sub">
            {data?.statistiken?.aktueller_monat || new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
            {data?.statistiken?.echte_kunden > 0 && (
              <> · {data.statistiken.echte_kunden} aktive Kunden · {data?.statistiken?.aktuell_im_kurs || 0} Teilnehmer im Kurs</>
            )}
          </p>
        </div>
        {/* Backup-Button — nur für Markus Dickie (GF) */}
        <button
          onClick={async () => {
            const includeFull = window.confirm(
              "Backup-Variante wählen:\n\n" +
              "[OK] = LIGHT-Backup (empfohlen, schnell, ~MB)\n" +
              "       Stammdaten + Metadaten · OHNE PDF-Binärdateien\n\n" +
              "[Abbrechen] = FULL-Backup (komplett, kann mehrere hundert MB werden)\n" +
              "       Alles inkl. PDF-Anhänge und Scans"
            );
            const mode = includeFull ? '' : '?include_pdfs=true';
            const modeLabel = includeFull ? 'LIGHT' : 'FULL';
            const t = toast.loading(`${modeLabel}-Backup wird erstellt — bitte einen Moment...`);
            try {
              const res = await axios.get(`${API}/admin/backup/download${mode}`, { 
                responseType: 'blob', 
                timeout: 600000 // 10 min für große Backups
              });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/gzip' }));
              const a = document.createElement('a');
              a.href = url;
              const ts = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
              a.setAttribute('download', `dickies_campus_backup_${modeLabel.toLowerCase()}_${ts}.json.gz`);
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
              const sizeMB = (res.data.size / 1024 / 1024).toFixed(2);
              toast.success(`${modeLabel}-Backup heruntergeladen (${sizeMB} MB)`, { id: t });
            } catch (err) {
              let detail = 'Backup fehlgeschlagen';
              if (err.response?.data instanceof Blob) {
                try { const txt = await err.response.data.text(); const j = JSON.parse(txt); detail = j.detail || detail; } catch (e) {}
              } else if (err.message) {
                detail = err.message;
              }
              toast.error(detail, { id: t });
            }
          }}
          className="btn-hero-backup"
          data-testid="btn-download-backup"
          title="Komplettes Datenbank-Backup auf Ihren PC herunterladen (Disaster-Recovery)"
        >
          <Database size={16} /> Backup auf PC sichern
        </button>
      </div>
      
      {/* Motivations-Banner */}
      {data?.motivation_message && (
        <Card className={`mb-6 border-2 ${
          data.motivation_message.type === 'gold' 
            ? 'bg-gradient-to-r from-yellow-100 to-amber-100 border-yellow-400' 
            : data.motivation_message.type === 'wellness'
            ? 'bg-gradient-to-r from-teal-50 to-cyan-100 border-teal-400'
            : data.motivation_message.type === 'wine'
            ? 'bg-gradient-to-r from-pink-50 to-purple-100 border-purple-400'
            : data.motivation_message.type === 'pizza' 
            ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-orange-300' 
            : data.motivation_message.type === 'success'
            ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-300'
            : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300'
        }`}>
          <CardContent className="p-4">
            <p className="text-lg font-semibold text-center">{data.motivation_message.text}</p>
          </CardContent>
        </Card>
      )}
      
      {/* Haupt-Statistiken */}
      <div className="dashboard-section-title">Heute im Überblick</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Echte Kunden — Salbei/Sage */}
        <div className="kpi-tile" style={{'--tile-tint': '#e8efe5', '--tile-border-hover': '#c8d6c0'}} onClick={() => navigate('/pflegeeinrichtungen')}>
          <div className="kpi-tile-icon" style={{background: '#e8efe5'}}>
            <Building2 style={{color: '#6b7e5e'}} size={22} />
          </div>
          <p className="kpi-tile-value">
            {data?.statistiken?.echte_kunden || 0}
          </p>
          <p className="kpi-tile-label">Aktive Kunden</p>
          <p className="kpi-tile-meta">Pflegeeinrichtungen</p>
        </div>
        
        {/* Aktuell im Kurs — warmes Sand-Beige */}
        <div className="kpi-tile" style={{'--tile-tint': '#efe6d8', '--tile-border-hover': '#d4c4a8'}} onClick={() => navigate('/teilnehmer')}>
          <div className="kpi-tile-icon" style={{background: '#efe6d8'}}>
            <GraduationCap style={{color: '#8c7656'}} size={22} />
          </div>
          <p className="kpi-tile-value">
            {data?.statistiken?.aktuell_im_kurs || 0}
          </p>
          <p className="kpi-tile-label">Aktuell im Kurs</p>
          <p className="kpi-tile-meta">Teilnehmer</p>
        </div>
        
        {/* Neue Teilnehmer — warmes Mauve / dezent gedeckt */}
        <div className="kpi-tile" style={{'--tile-tint': '#ede0db', '--tile-border-hover': '#cdb6ad', cursor: 'default'}}>
          <div className="kpi-tile-icon" style={{background: '#ede0db'}}>
            <UserPlus style={{color: '#9a6d5e'}} size={22} />
          </div>
          <p className="kpi-tile-value">
            +{data?.statistiken?.neue_teilnehmer_monat || 0}
          </p>
          <p className="kpi-tile-label">Neue im Monat</p>
          <p className="kpi-tile-meta">Anmeldungen</p>
        </div>
        
        {/* Umsatz mit Meilenstein-Roadmap — Brand-Pfirsich */}
        <div className="kpi-tile" style={{'--tile-tint': '#F6F1E7', '--tile-border-hover': '#D9775A', gridColumn: 'span 1'}} onClick={() => navigate('/rechnungen')}>
          <div className="kpi-tile-icon" style={{background: '#F6F1E7'}}>
            <Euro style={{color: '#d96b48'}} size={22} />
          </div>
          <p className="kpi-tile-value" style={{color: '#a8482e'}}>
            {(data?.statistiken?.umsatz_monat || 0).toLocaleString('de-DE')}€
          </p>
          <p className="kpi-tile-label">Umsatz Monat</p>
          {/* Meilenstein-Roadmap — alle 4 Phasen sichtbar */}
          {(() => {
            const umsatz = data?.statistiken?.umsatz_monat || 0;
            const naechster = MEILENSTEINE.find(m => m.betrag > umsatz);
            const erreichteSumme = MEILENSTEINE.filter(m => m.betrag <= umsatz).length;
            const totalProzent = naechster
              ? Math.min(100, Math.round((umsatz / MEILENSTEINE[MEILENSTEINE.length - 1].betrag) * 100))
              : 100;
            return (
              <div className="mt-3.5">
                <div className="flex items-center justify-between text-[10px] uppercase tracking-wider mb-1.5 font-medium" style={{color: '#A0958B'}}>
                  <span>{erreichteSumme}/{MEILENSTEINE.length} Phasen</span>
                  {naechster && (
                    <span className="font-semibold normal-case tracking-normal" style={{color: '#d96b48'}}>
                      Bis {naechster.emoji}: {(naechster.betrag - umsatz).toLocaleString('de-DE')}€
                    </span>
                  )}
                  {!naechster && <span className="font-bold" style={{color: '#6b7e5e'}}>ALLE</span>}
                </div>
                <div className="relative w-full h-2.5 rounded-full overflow-hidden" style={{background: '#f1e9dd'}}>
                  {/* Brand-Gradient von Pfirsich → Sand → Mokka */}
                  <div
                    className="absolute inset-y-0 left-0 transition-all duration-1000"
                    style={{
                      width: `${totalProzent}%`,
                      background: 'linear-gradient(90deg, #D9775A 0%, #C2603F 35%, #c8a878 70%, #8c7656 100%)'
                    }}
                  />
                  {/* Phasen-Marker */}
                  {MEILENSTEINE.map((m, idx) => {
                    const pos = (m.betrag / MEILENSTEINE[MEILENSTEINE.length - 1].betrag) * 100;
                    const erreicht = umsatz >= m.betrag;
                    return (
                      <div
                        key={m.betrag}
                        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2"
                        style={{ left: `${pos}%` }}
                      >
                        <div
                          className="w-3 h-3 rounded-full border-2 transition-all"
                          style={{
                            background: erreicht ? '#fefcf9' : '#e8ddcd',
                            borderColor: erreicht ? '#D9775A' : '#c8b9a8',
                            boxShadow: erreicht ? '0 1px 2px rgba(86,80,75,0.15)' : 'none'
                          }}
                          title={`${m.titel} — ${m.betrag.toLocaleString('de-DE')}€`}
                        />
                      </div>
                    );
                  })}
                </div>
                {/* Phasen-Labels darunter */}
                <div className="relative h-5 mt-1">
                  {MEILENSTEINE.map((m, idx) => {
                    const pos = (m.betrag / MEILENSTEINE[MEILENSTEINE.length - 1].betrag) * 100;
                    const erreicht = umsatz >= m.betrag;
                    const isNext = !erreicht && naechster && m.betrag === naechster.betrag;
                    return (
                      <div
                        key={m.betrag}
                        className="absolute -translate-x-1/2 text-center"
                        style={{ left: `${pos}%` }}
                      >
                        <div
                          className={`text-base leading-none transition-all ${
                            erreicht ? 'grayscale-0 opacity-100' : 'grayscale opacity-50'
                          } ${isNext ? '!opacity-100 !grayscale-0 animate-pulse' : ''}`}
                          title={`${(m.betrag / 1000).toFixed(0)}K — ${m.titel}`}
                        >
                          {m.emoji}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="dashboard-section-title">Aktivität & Compliance</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* HOT LEADS — auf User-Wunsch vom Dashboard entfernt (Datenqualität unzureichend). */}

        {/* WHATSAPP-VERSAND — Statistik der manuellen WhatsApp-Kontakte */}
        {waStats && waStats.total > 0 && (
          <Card
            className="card-custom border-l-4 border-l-emerald-500 bg-emerald-50/30 cursor-pointer hover:shadow-md transition-all"
            onClick={() => navigate('/pflegeeinrichtungen')}
            data-testid="dashboard-whatsapp-card"
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between" style={{fontFamily: 'Manrope, sans-serif'}}>
                <span className="flex items-center gap-2">
                  <MessageCircle size={18} className="text-emerald-600" />
                  WhatsApp-Akquise
                  <span className="text-sm font-normal text-gray-500">({waStats.total} gesamt)</span>
                </span>
                {waStats.heute > 0 && (
                  <Badge className="bg-emerald-100 text-emerald-700 border-0 text-xs">
                    {waStats.heute} heute
                  </Badge>
                )}
              </CardTitle>
              <p className="text-xs text-[#78716c]">
                Klick → zu den Pflegeeinrichtungen
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="rounded-lg bg-white/70 p-2.5 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">Woche</p>
                  <p className="text-xl font-semibold text-emerald-700" data-testid="wa-stat-week">{waStats.diese_woche}</p>
                  <p className="text-[10px] text-gray-500">{waStats.empfaenger_woche_unique} Empfänger</p>
                </div>
                <div className="rounded-lg bg-white/70 p-2.5 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">Monat</p>
                  <p className="text-xl font-semibold text-emerald-700" data-testid="wa-stat-month">{waStats.dieser_monat}</p>
                </div>
                <div className="rounded-lg bg-white/70 p-2.5 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-gray-500">Gesamt</p>
                  <p className="text-xl font-semibold text-emerald-700" data-testid="wa-stat-total">{waStats.total}</p>
                </div>
              </div>

              {waStats.letzte_kontakte && waStats.letzte_kontakte.length > 0 && (
                <div className="space-y-1.5">
                  <p className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Zuletzt kontaktiert</p>
                  {waStats.letzte_kontakte.slice(0, 4).map((k, idx) => {
                    const dt = new Date(k.gesendet_am);
                    const heute = new Date();
                    const diffMs = heute - dt;
                    const diffHours = Math.floor(diffMs / 3600000);
                    const diffDays = Math.floor(diffMs / 86400000);
                    const relLabel = diffHours < 1 ? 'gerade eben'
                      : diffHours < 24 ? `vor ${diffHours}h`
                      : diffDays < 30 ? `vor ${diffDays}T`
                      : dt.toLocaleDateString('de-DE');
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between gap-2 p-1.5 bg-white/60 rounded text-xs"
                        onClick={(e) => { e.stopPropagation(); navigate(`/pflegeeinrichtungen?peId=${k.pe_id}`); }}
                        data-testid={`wa-recent-${k.pe_id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate" title={k.einrichtungsname}>{k.einrichtungsname}</p>
                          <p className="text-[10px] text-gray-500 truncate">
                            {k.ansprechpartner || '—'}
                            {k.count > 1 && ` · ${k.count}× kontaktiert`}
                          </p>
                        </div>
                        <span className="text-[10px] text-emerald-700 whitespace-nowrap font-medium">{relLabel}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {waStats.top_absender && waStats.top_absender.length > 1 && (
                <div className="mt-3 pt-2 border-t border-emerald-100 flex items-center gap-1.5 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wide text-gray-500">Team:</span>
                  {waStats.top_absender.map((a) => (
                    <span key={a.name} className="text-[10px] bg-white/70 rounded px-1.5 py-0.5 text-gray-700">
                      {a.name} <span className="text-emerald-700 font-semibold">{a.count}</span>
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CASH-FLOW-AMPEL — auf User-Wunsch entfernt (Berechnung war nicht verlässlich).
            Die vollständige Übersicht steht weiter unter /finanzuebersicht zur Verfügung. */}
        
        {/* BACKUP-HEALTH-AMPEL — Disaster Recovery Status */}
        {backupHealth && (
          <Card 
            className={`card-custom border-l-4 cursor-pointer hover:shadow-md transition-all ${
              backupHealth.ampel === 'rot' ? 'border-l-red-500 bg-red-50/40' :
              backupHealth.ampel === 'gelb' ? 'border-l-amber-500 bg-amber-50/40' :
              'border-l-emerald-500 bg-emerald-50/30'
            }`}
            onClick={() => navigate('/datensicherung-wartung')}
            data-testid="backup-health-card"
            title="Klick → System → Datensicherung & Wartung"
          >
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center justify-between" style={{fontFamily: 'Manrope, sans-serif'}}>
                <span className="flex items-center gap-2">
                  <span className={`inline-flex w-3 h-3 rounded-full ${
                    backupHealth.ampel === 'rot' ? 'bg-red-500 animate-pulse' :
                    backupHealth.ampel === 'gelb' ? 'bg-amber-500' :
                    'bg-emerald-500'
                  }`}></span>
                  Backup-Status
                </span>
                <Badge className={`text-xs border-0 ${
                  backupHealth.ampel === 'rot' ? 'bg-red-100 text-red-700' :
                  backupHealth.ampel === 'gelb' ? 'bg-amber-100 text-amber-700' :
                  'bg-emerald-100 text-emerald-700'
                }`}>
                  {backupHealth.label}
                </Badge>
              </CardTitle>
              <p className="text-xs text-[#78716c]">
                Disaster-Recovery: Synology + E-Mail-Backup
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {/* Synology */}
                <div className="flex items-center justify-between p-2 bg-white/70 rounded" data-testid="backup-health-synology">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex w-2.5 h-2.5 rounded-full ${
                      backupHealth.synology.ampel === 'rot' ? 'bg-red-500' :
                      backupHealth.synology.ampel === 'gelb' ? 'bg-amber-500' :
                      backupHealth.synology.ampel === 'grau' ? 'bg-gray-400' :
                      'bg-emerald-500'
                    }`}></span>
                    <span className="text-sm font-medium">Synology</span>
                  </div>
                  <span className="text-xs text-gray-600">{backupHealth.synology.label}</span>
                </div>
                {/* E-Mail */}
                <div className="flex items-center justify-between p-2 bg-white/70 rounded" data-testid="backup-health-email">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex w-2.5 h-2.5 rounded-full ${
                      backupHealth.email.ampel === 'rot' ? 'bg-red-500' :
                      backupHealth.email.ampel === 'gelb' ? 'bg-amber-500' :
                      backupHealth.email.ampel === 'grau' ? 'bg-gray-400' :
                      'bg-emerald-500'
                    }`}></span>
                    <span className="text-sm font-medium">Wochen-E-Mail</span>
                  </div>
                  <span className="text-xs text-gray-600">{backupHealth.email.label}</span>
                </div>
                {/* Hinweis wenn keiner konfiguriert */}
                {!backupHealth.synology.configured && !backupHealth.email.configured && (
                  <div className="p-2 text-xs text-red-700 bg-red-100/60 rounded border border-red-200">
                    Die Daten liegen sicher in der Datenbank. Eine zusätzliche Kopie außerhalb (Synology oder Wochen-E-Mail) ist noch nicht eingerichtet: System → Datensicherung & Wartung.
                  </div>
                )}
                
                {/* DR-Drill-Bericht (AZAV-Audit-Nachweis) */}
                {drDrill?.available && drDrill?.report && (
                  <div className={`mt-2 p-2 rounded border text-xs ${
                    drDrill.report.failed === 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                    'bg-amber-50 border-amber-200 text-amber-800'
                  }`} data-testid="dr-drill-summary">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium">
                        DR-Drill: {drDrill.report.overall_status} ({drDrill.report.passed}/{drDrill.report.total})
                      </span>
                      <span className="text-[10px] opacity-75">
                        {drDrill.last_run_at && new Date(drDrill.last_run_at).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                    <p className="text-[10px] mt-1 opacity-80">
                      Monatlicher Selbsttest der Backup-Kette (AZAV-Audit-Nachweis)
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* FORTBILDUNGSPAKETE - Ablauf-Warnung */}
        {fortbildungspakete && (fortbildungspakete.statistik?.bald_auslaufend > 0 || fortbildungspakete.statistik?.abgelaufen > 0) && (
          <Card className="card-custom border-l-4 border-l-violet-400">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily: 'Manrope, sans-serif'}}>
                <Package size={20} className="text-violet-500" />
                Fortbildungspakete
              </CardTitle>
              <p className="text-xs text-[#78716c]">
                {fortbildungspakete.statistik?.gesamt || 0} Einrichtungen mit Paket 
                ({fortbildungspakete.statistik?.klein || 0}x Klein, {fortbildungspakete.statistik?.gross || 0}x Groß)
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Abgelaufene Pakete */}
                {fortbildungspakete.bereits_abgelaufen?.length > 0 && (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                    <p className="text-sm font-semibold text-red-700 mb-2">
                      {fortbildungspakete.bereits_abgelaufen.length} Paket(e) abgelaufen!
                    </p>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {fortbildungspakete.bereits_abgelaufen.slice(0, 5).map(pe => (
                        <div key={pe.id} className="text-xs flex justify-between">
                          <span className="truncate max-w-[200px]">{pe.einrichtungsname}</span>
                          <span className="text-red-600 font-medium">
                            {pe.paket === 'klein' ? 'Klein' : 'Groß'} - abgelaufen am {new Date(pe.paket_ende).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Bald auslaufende Pakete */}
                {fortbildungspakete.bald_auslaufend?.length > 0 && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm font-semibold text-amber-700 mb-2">
                      {fortbildungspakete.bald_auslaufend.length} Paket(e) laufen in 30 Tagen aus
                    </p>
                    <div className="space-y-1 max-h-24 overflow-y-auto">
                      {fortbildungspakete.bald_auslaufend.slice(0, 5).map(pe => (
                        <div key={pe.id} className="text-xs flex justify-between">
                          <span className="truncate max-w-[200px]">{pe.einrichtungsname}</span>
                          <span className="text-amber-600 font-medium">
                            {pe.paket === 'klein' ? 'Klein' : 'Groß'} bis {new Date(pe.paket_ende).toLocaleDateString('de-DE')}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* TODO-Liste → Team-Board (volle Breite, mehr Platz, mit Antworten) */}
        <Card className="card-custom col-span-full lg:col-span-3">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily: 'Manrope, sans-serif'}}>
                <ListTodo size={20} className="text-[#6d665b]" />
                Team-Board — Aufgaben & Antworten
              </CardTitle>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="reply-autor-input"
                  placeholder="Dein Name (für Antworten)…"
                  value={replyAutor}
                  onChange={(e) => setReplyAutor(e.target.value)}
                  className="text-xs h-8 w-[200px]"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Neue Aufgabe hinzufügen / bearbeiten */}
            <div className="mb-4 p-3 bg-[#FFFFFF] rounded-lg border border-dashed border-gray-300">
              <div className="grid grid-cols-1 gap-2">
                {editingTodo && (
                  <div className="text-xs text-blue-600 font-medium mb-1">
                    Aufgabe bearbeiten
                  </div>
                )}
                <Input
                  placeholder="Was muss erledigt werden?"
                  value={todoText}
                  onChange={(e) => setTodoText(e.target.value)}
                  className="text-sm"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="Wer? (z.B. Angelina)"
                    value={todoZustaendig}
                    onChange={(e) => setTodoZustaendig(e.target.value)}
                    className="text-sm"
                  />
                  <Input
                    type="date"
                    value={todoFaellig}
                    onChange={(e) => setTodoFaellig(e.target.value)}
                    className="text-sm"
                  />
                </div>
                
                {/* Verknüpfungen - Suchbare Dropdowns */}
                <div className="grid grid-cols-2 gap-2">
                  {/* Pflegeeinrichtung Suche */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="justify-start text-sm h-9 font-normal">
                        {todoPE ? (
                          <span className="truncate flex items-center gap-1">
                            <Building2 size={12} className="text-orange-500" />
                            {todoPE.einrichtungsname?.substring(0, 25)}{todoPE.einrichtungsname?.length > 25 ? '...' : ''}
                          </span>
                        ) : (
                          <span className="text-gray-400">Pflegeeinrichtung...</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Pflegeeinrichtung suchen..." />
                        <CommandList>
                          <CommandEmpty>Keine Ergebnisse</CommandEmpty>
                          <CommandGroup>
                            <CommandItem onSelect={() => setTodoPE(null)}>
                              <span className="text-gray-400">Keine Auswahl</span>
                            </CommandItem>
                            {data?.pflegeeinrichtungen?.map(pe => (
                              <CommandItem 
                                key={pe.id} 
                                value={pe.einrichtungsname}
                                onSelect={() => setTodoPE(pe)}
                              >
                                <Building2 size={14} className="mr-2 text-orange-500" />
                                {pe.einrichtungsname}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  
                  {/* Teilnehmer Suche */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="justify-start text-sm h-9 font-normal">
                        {todoTN ? (
                          <span className="truncate flex items-center gap-1">
                            <Users size={12} className="text-blue-500" />
                            {todoTN.vorname} {todoTN.nachname}
                          </span>
                        ) : (
                          <span className="text-gray-400">Teilnehmer...</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Teilnehmer suchen..." />
                        <CommandList>
                          <CommandEmpty>Keine Ergebnisse</CommandEmpty>
                          <CommandGroup>
                            <CommandItem onSelect={() => setTodoTN(null)}>
                              <span className="text-gray-400">Keine Auswahl</span>
                            </CommandItem>
                            {data?.teilnehmer?.map(tn => (
                              <CommandItem 
                                key={tn.id} 
                                value={`${tn.vorname} ${tn.nachname}`}
                                onSelect={() => setTodoTN(tn)}
                              >
                                <Users size={14} className="mr-2 text-blue-500" />
                                {tn.vorname} {tn.nachname}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                
                {/* Ausgewählte Verknüpfungen anzeigen */}
                {(todoPE || todoTN) && (
                  <div className="flex flex-wrap gap-2 text-xs">
                    {todoPE && (
                      <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded-full flex items-center gap-1">
                        <Building2 size={12} /> {todoPE.einrichtungsname}
                        <button onClick={() => setTodoPE(null)} className="ml-1 hover:text-red-500">×</button>
                      </span>
                    )}
                    {todoTN && (
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full flex items-center gap-1">
                        <Users size={12} /> {todoTN.vorname} {todoTN.nachname}
                        <button onClick={() => setTodoTN(null)} className="ml-1 hover:text-red-500">×</button>
                      </span>
                    )}
                  </div>
                )}
                
                {editingTodo ? (
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={handleUpdateTodo}
                      disabled={!todoText.trim()}
                      className="bg-blue-500 hover:bg-blue-600 text-white flex-1"
                    >
                      Speichern
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={cancelEditTodo}
                      variant="outline"
                    >
                      Abbrechen
                    </Button>
                  </div>
                ) : (
                  <Button 
                    size="sm" 
                    onClick={handleAddTodo}
                    disabled={!todoText.trim()}
                    className="bg-[#D9775A] hover:bg-[#C2603F] text-white"
                  >
                    <Plus size={14} className="mr-1" /> Aufgabe hinzufügen
                  </Button>
                )}
              </div>
            </div>
            
            {/* Aufgaben-Liste */}
            {data?.todos?.length > 0 ? (
              <div className="space-y-2 max-h-[600px] overflow-y-auto pr-1">
                {data.todos.filter(t => !t.erledigt).map((todo) => {
                  // Farbcodierung basierend auf Zuständigkeit
                  const isJean = todo.zustaendig?.toLowerCase().includes('jean');
                  const isKatharina = todo.zustaendig?.toLowerCase().includes('katharina');
                  const borderColor = isJean ? 'border-l-4 border-l-blue-500' : isKatharina ? 'border-l-4 border-l-pink-500' : '';
                  const bgColor = isJean ? 'bg-blue-50' : isKatharina ? 'bg-pink-50' : 'bg-white';
                  
                  return (
                  <div key={todo.id} className={`rounded-lg border ${borderColor} ${bgColor} overflow-hidden`}>
                    {/* Aufgaben-Zeile (kompakt) */}
                    <div className="flex items-center gap-2 px-3 py-2 hover:bg-black/[0.02] cursor-pointer" onClick={() => handleEditTodo(todo)}>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleToggleTodo(todo); }}
                        className="w-4 h-4 rounded border-2 border-gray-300 flex items-center justify-center hover:border-emerald-500 shrink-0"
                      >
                        {todo.erledigt && <Check size={10} className="text-emerald-500" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium leading-tight ${todo.erledigt ? 'line-through text-gray-400' : ''}`}>
                          {todo.aufgabe}
                        </p>
                        <div className="flex flex-wrap gap-x-2 text-[11px] text-stone-500 mt-0.5">
                          {todo.zustaendig && <span className={isJean ? 'text-blue-600' : isKatharina ? 'text-pink-600' : ''}>{todo.zustaendig}</span>}
                          {todo.faelligkeitsdatum && <span>{todo.faelligkeitsdatum}</span>}
                          {todo.pflegeeinrichtung_name && <span className="text-orange-600">{todo.pflegeeinrichtung_name}</span>}
                          {todo.teilnehmer_name && <span className="text-purple-600">· {todo.teilnehmer_name}</span>}
                        </div>
                      </div>
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteTodo(todo.id); }}
                        className="text-gray-300 hover:text-red-500 shrink-0"
                        title="Aufgabe löschen"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>

                    {/* Antworten-Thread — IMMER sichtbar wenn vorhanden */}
                    {todo.kommentare?.length > 0 && (
                      <div className="px-3 pt-1 pb-2 bg-white/60 border-t border-black/5" data-testid={`todo-replies-${todo.id}`}>
                        <div className="space-y-1.5 pl-5">
                          {todo.kommentare.map((k) => (
                            <div key={k.id} className="flex items-start gap-1.5 group">
                              <div className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">
                                {(k.autor || "?").substring(0, 1).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <span className="text-xs font-semibold text-stone-700">{k.autor}</span>
                                <span className="text-[10px] text-stone-400 ml-1.5">
                                  {k.erstellt_am ? new Date(k.erstellt_am).toLocaleString('de-DE', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'}) : ''}
                                </span>
                                <p className="text-xs text-stone-800 whitespace-pre-wrap break-words leading-snug">{k.text}</p>
                              </div>
                              <button
                                onClick={() => handleDeleteReply(todo.id, k.id)}
                                className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 mt-0.5 shrink-0"
                                title="Antwort löschen"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Antwort-Eingabe — kompakt, immer sichtbar */}
                    <div className="flex items-center gap-1.5 px-3 py-1.5 bg-white/80 border-t border-black/5" data-testid={`todo-reply-form-${todo.id}`}>
                      <div className="w-5 h-5 rounded-full bg-stone-300 text-white flex items-center justify-center text-[10px] font-bold shrink-0">
                        {(replyAutor || "?").substring(0, 1).toUpperCase()}
                      </div>
                      <input
                        data-testid={`todo-reply-input-${todo.id}`}
                        type="text"
                        placeholder="Antwort schreiben…"
                        value={replyText[todo.id] || ""}
                        onChange={(e) => setReplyText(prev => ({ ...prev, [todo.id]: e.target.value }))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleAddReply(todo.id);
                          }
                        }}
                        className="flex-1 text-xs bg-transparent border-0 border-b border-stone-200 focus:border-blue-400 focus:outline-none px-1 py-0.5"
                      />
                      {(replyText[todo.id] || "").trim() && (
                        <button
                          data-testid={`todo-reply-send-${todo.id}`}
                          onClick={() => handleAddReply(todo.id)}
                          className="text-blue-500 hover:text-blue-700 text-xs font-semibold px-2"
                        >
                          Senden
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
                {/* Erledigte Aufgaben mit wann/wer */}
                {data.todos.filter(t => t.erledigt).length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-emerald-600 font-medium mb-2">
                      ✓ {data.todos.filter(t => t.erledigt).length} Aufgaben erledigt
                    </p>
                    <div className="space-y-1 max-h-[150px] overflow-y-auto">
                      {data.todos.filter(t => t.erledigt).map((todo) => (
                        <div key={todo.id} className="flex items-center gap-2 text-xs text-gray-400">
                          <Check size={12} className="text-emerald-500" />
                          <span className="line-through truncate flex-1">{todo.aufgabe}</span>
                          {todo.erledigt_am && (
                            <span className="text-[10px] whitespace-nowrap">
                              {todo.erledigt_am}{todo.erledigt_von ? ` (${todo.erledigt_von})` : ''}
                            </span>
                          )}
                          <button 
                            onClick={() => handleDeleteTodo(todo.id)}
                            className="text-gray-300 hover:text-red-500"
                          >
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[#78716c] text-sm text-center py-4">Keine offenen Aufgaben - super! </p>
            )}
          </CardContent>
        </Card>

        {/* Dialog für neue Bestellung */}
        <Dialog open={showBestellungDialog} onOpenChange={setShowBestellungDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>
                Bestellung hinzufügen
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Kunde / Name *</Label>
                <Input 
                  value={neueBestellung.kunde} 
                  onChange={(e) => setNeueBestellung({...neueBestellung, kunde: e.target.value})}
                  placeholder="z.B. Max Mustermann oder Pflegedienst XY"
                />
              </div>
              <div>
                <Label>Kurs / Maßnahme</Label>
                <Input 
                  value={neueBestellung.kurs} 
                  onChange={(e) => setNeueBestellung({...neueBestellung, kurs: e.target.value})}
                  placeholder="z.B. LG1 & LG2"
                />
              </div>
              <div>
                <Label>Datum</Label>
                <Input 
                  type="date"
                  value={neueBestellung.datum} 
                  onChange={(e) => setNeueBestellung({...neueBestellung, datum: e.target.value})}
                />
              </div>
              <div>
                <Label>Betrag (€)</Label>
                <Input 
                  type="number"
                  value={neueBestellung.betrag} 
                  onChange={(e) => setNeueBestellung({...neueBestellung, betrag: e.target.value})}
                  placeholder="z.B. 1360"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBestellungDialog(false)}>Abbrechen</Button>
              <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={handleAddBestellung}>
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Nächste Prüfungen, Laufende Maßnahmen und Offene Ratenzahlungen wurden
            entfernt — Platz wird jetzt vom Team-Board unten eingenommen (siehe TeamBoard). */}
      </div>

      {/* Teilnehmer nach Finanzierungsart */}
      <div className="mt-6">
        <Card className="card-custom">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg" style={{fontFamily: 'Manrope, sans-serif'}}>
              Teilnehmer nach Finanzierungsart
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-[#FFFFFF] rounded-lg text-center">
                <p className="text-2xl font-semibold text-[#6d665b]">{data?.statistiken?.teilnehmer_selbstzahler || 0}</p>
                <p className="text-sm text-[#78716c]">Selbstzahler</p>
              </div>
              <div className="p-4 bg-[#FFFFFF] rounded-lg text-center">
                <p className="text-2xl font-semibold text-[#6d665b]">{data?.statistiken?.teilnehmer_einrichtung || 0}</p>
                <p className="text-sm text-[#78716c]">Einrichtung</p>
              </div>
              <div className="p-4 bg-[#FFFFFF] rounded-lg text-center">
                <p className="text-2xl font-semibold text-[#6d665b]">{data?.statistiken?.teilnehmer_bildungsgutschein || 0}</p>
                <p className="text-sm text-[#78716c]">Bildungsgutschein</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ratenzahlungen Übersicht */}
      <div className="mt-6">
        <Card className="card-custom">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2" style={{fontFamily: 'Manrope, sans-serif'}}>
              <CircleDollarSign size={20} className="text-[#6d665b]" />
              Ratenzahlungen Übersicht
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-red-50 rounded-lg text-center cursor-pointer hover:bg-red-100" onClick={() => navigate('/ratenzahlung')}>
                <p className="text-2xl font-semibold text-red-600">{data?.statistiken?.ratenzahlungen_offen || 0}</p>
                <p className="text-sm text-red-700">Offen</p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg text-center cursor-pointer hover:bg-amber-100" onClick={() => navigate('/ratenzahlung')}>
                <p className="text-2xl font-semibold text-amber-600">{data?.statistiken?.ratenzahlungen_teilweise || 0}</p>
                <p className="text-sm text-amber-700">Teilweise bezahlt</p>
              </div>
              <div className="p-4 bg-emerald-50 rounded-lg text-center cursor-pointer hover:bg-emerald-100" onClick={() => navigate('/ratenzahlung')}>
                <p className="text-2xl font-semibold text-emerald-600">{data?.statistiken?.ratenzahlungen_vollstaendig || 0}</p>
                <p className="text-sm text-emerald-700">Vollständig</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Meilenstein-Celebration Popup */}
      {meilensteinPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-300">
          <div className={`bg-gradient-to-br ${meilensteinPopup.farbe} p-1 rounded-3xl shadow-2xl transform animate-in zoom-in duration-500`}>
            <div className="bg-white rounded-3xl p-8 text-center max-w-md">
              <div className="text-8xl mb-4 animate-bounce">{meilensteinPopup.emoji}</div>
              <h2 className="text-3xl font-bold mb-3" style={{fontFamily: 'Manrope, sans-serif'}}>
                {meilensteinPopup.titel}
              </h2>
              <p className="text-xl text-gray-700 mb-6">
                {meilensteinPopup.nachricht}
              </p>
              <div className="mb-6 p-4 bg-gradient-to-r from-amber-100 to-yellow-100 rounded-xl">
                <p className="text-sm text-amber-700">Monatsumsatz {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</p>
                <p className="text-4xl font-bold text-amber-600">
                  {(data?.statistiken?.umsatz_monat || 0).toLocaleString('de-DE')}€
                </p>
              </div>
              <Button 
                className={`bg-gradient-to-r ${meilensteinPopup.farbe} text-white px-8 py-6 text-lg rounded-full hover:opacity-90 transition-opacity`}
                onClick={() => setMeilensteinPopup(null)}
              >
                Weiter feiern!
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog für Kunden-Reaktivierung */}
      <Dialog open={!!selectedReaktivierungKunde} onOpenChange={() => setSelectedReaktivierungKunde(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone size={20} className="text-orange-500" />
              Kunde kontaktieren
            </DialogTitle>
            <DialogDescription>
              {selectedReaktivierungKunde?.name}
            </DialogDescription>
          </DialogHeader>
          
          {selectedReaktivierungKunde && (
            <div className="space-y-4">
              {/* Kunden-Info */}
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <p className="font-semibold">{selectedReaktivierungKunde.name}</p>
                <p className="text-sm text-gray-600">{selectedReaktivierungKunde.ort}</p>
                {selectedReaktivierungKunde.kontaktperson && (
                  <p className="text-sm text-orange-700 mt-1">
                    {selectedReaktivierungKunde.kontaktperson}
                  </p>
                )}
                <div className="mt-2 flex gap-4 text-xs text-gray-500">
                  {selectedReaktivierungKunde.letzte_bestellung && (
                    <span>Letzte Bestellung: {selectedReaktivierungKunde.letzte_bestellung}</span>
                  )}
                  {selectedReaktivierungKunde.tage_seit && (
                    <span className="text-orange-600 font-medium">{selectedReaktivierungKunde.tage_seit} Tage her</span>
                  )}
                </div>
              </div>
              
              {/* Wiedervorlage */}
              <div>
                <Label className="text-sm font-medium">Nächste Wiedervorlage</Label>
                <Input
                  type="date"
                  value={kundeWiedervorlage}
                  onChange={(e) => setKundeWiedervorlage(e.target.value)}
                  className="mt-1"
                />
                <div className="flex gap-2 mt-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs"
                    onClick={() => setKundeWiedervorlage(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                  >
                    +7 Tage
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs"
                    onClick={() => setKundeWiedervorlage(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                  >
                    +14 Tage
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs"
                    onClick={() => setKundeWiedervorlage(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                  >
                    +30 Tage
                  </Button>
                </div>
              </div>
              
              {/* Notiz */}
              <div>
                <Label className="text-sm font-medium">Gesprächsnotiz (optional)</Label>
                <textarea
                  value={kundeNotiz}
                  onChange={(e) => setKundeNotiz(e.target.value)}
                  placeholder="Was wurde besprochen?"
                  className="w-full mt-1 p-2 border rounded-md text-sm min-h-[80px]"
                />
              </div>
            </div>
          )}
          
          <DialogFooter className="flex gap-2 sm:gap-2">
            <Button 
              variant="outline" 
              onClick={markKundeErledigt}
              className="text-gray-600"
            >
              Erledigt (ohne WV)
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                navigate(`/vertrieb?pe_id=${selectedReaktivierungKunde?.id}`);
                setSelectedReaktivierungKunde(null);
              }}
            >
              Zur Vertriebsseite
            </Button>
            <Button 
              onClick={saveKundeReaktivierung}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >
              Speichern & Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditorialGallery />

      {dashUser?.role !== "dozent" && <MahnstufeEinsWidget />}
      {dashUser?.role === "gf" && <FremdSicherungen />}
      {dashUser?.role !== "dozent" && <MahnwesenVorschlaegeWidget role={dashUser?.role} />}

      </div>{/* end dashboard-hero-bg */}
    </div>
  );
};

// ==================== PIPELINE-KACHELN für Pflegeeinrichtungen ====================
// Zeigt Anzahl Einrichtungen je Akquise-Status als klickbare Filter-Kacheln.
// Klick auf Kachel setzt den akquise_status-Filter der Pflegeeinrichtungen-Liste.
// Zeigt Gesamt-Counts unabhängig vom aktiven Filter (extra Fetch, ohne Filter).
const PIPELINE_STAGES = [
  { key: "neu",             label: "Neu",              color: "#9A3412", bg: "#FBEDE7" },
  { key: "kontaktiert",     label: "Kontaktiert",      color: "#c26a1f", bg: "#f5e0cf" },
  { key: "interessent",     label: "Interessent",      color: "#4a6b8a", bg: "#dde8f5" },
  { key: "angebot_gesendet",label: "Angebot gesendet", color: "#6b4a8a", bg: "#e6dcf5" },
  { key: "kunde",           label: "Kunde",            color: "#4a6b2b", bg: "#e0efd9" },
  { key: "abgesagt",        label: "Abgesagt",         color: "#8a2b2b", bg: "#f5d9d9" },
];

// ==================== KALENDER-ANSICHT für Pflegeeinrichtungen ====================
// Zeigt alle Einrichtungen mit gesetzter `naechste_wiedervorlage` als Termine in einem Monatskalender.
// Klick auf Termin öffnet Detail-View.
const PflegeKalenderView = ({ data, onOpenPE }) => {
  const [monatOffset, setMonatOffset] = useState(0); // 0 = aktueller Monat, -1 = Vormonat, +1 = Folgemonat
  const heute = new Date();
  const ref = new Date(heute.getFullYear(), heute.getMonth() + monatOffset, 1);
  const jahr = ref.getFullYear();
  const monat = ref.getMonth(); // 0-11
  const tageImMonat = new Date(jahr, monat + 1, 0).getDate();
  const erstesWochentag = (new Date(jahr, monat, 1).getDay() + 6) % 7; // Mo=0..So=6
  const monatsName = ref.toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  // Termine gruppieren nach YYYY-MM-DD
  const termine = {};
  (data || []).forEach((pe) => {
    if (!pe.naechste_wiedervorlage) return;
    const key = pe.naechste_wiedervorlage; // Format YYYY-MM-DD erwartet
    if (!termine[key]) termine[key] = [];
    termine[key].push(pe);
  });

  // Farbe pro Vertriebsmitarbeiter (deterministisch via Hash)
  const vmFarben = ["#9A3412", "#4a6b2b", "#4a6b8a", "#6b4a8a", "#c26a1f", "#8a2b2b", "#2b8a6b", "#5a4a35"];
  const farbeFuerVM = (vmId) => {
    if (!vmId) return "#78716c";
    let h = 0;
    for (let i = 0; i < String(vmId).length; i++) h = (h * 31 + String(vmId).charCodeAt(i)) & 0xffffffff;
    return vmFarben[Math.abs(h) % vmFarben.length];
  };

  const heuteIso = new Date().toISOString().split("T")[0];
  const isVergangenheit = (iso) => iso < heuteIso;

  // Grid-Zellen: leere Felder vor Monatsbeginn + Tage
  const zellen = [];
  for (let i = 0; i < erstesWochentag; i++) zellen.push(null);
  for (let d = 1; d <= tageImMonat; d++) {
    const iso = `${jahr}-${String(monat + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    zellen.push({ tag: d, iso, termine: termine[iso] || [] });
  }

  const gesamtTermine = Object.values(termine).flat().length;

  return (
    <div data-testid="pflege-kalender-view">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <button onClick={() => setMonatOffset(monatOffset - 1)} className="px-3 py-1.5 bg-white border border-[#d6c4a5] rounded-md text-sm hover:bg-[#F7F5F2]" data-testid="cal-prev">
            ◀ Vormonat
          </button>
          <h3 className="text-lg font-semibold text-[#2a2118] capitalize min-w-[180px] text-center" style={{ fontFamily: "Manrope, sans-serif" }}>
            {monatsName}
          </h3>
          <button onClick={() => setMonatOffset(monatOffset + 1)} className="px-3 py-1.5 bg-white border border-[#d6c4a5] rounded-md text-sm hover:bg-[#F7F5F2]" data-testid="cal-next">
            Folgemonat ▶
          </button>
          {monatOffset !== 0 && (
            <button onClick={() => setMonatOffset(0)} className="px-3 py-1.5 bg-[#FBEDE7] border border-[#9A3412] rounded-md text-sm text-[#9A3412] hover:bg-[#EBC9B9]" data-testid="cal-heute">
              Heute
            </button>
          )}
        </div>
        <div className="text-xs text-[#78716c]">
          {gesamtTermine} Wiedervorlagen im gesamten Datenbestand
        </div>
      </div>

      {/* Wochentag-Header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"].map((w) => (
          <div key={w} className="text-center text-xs font-semibold uppercase tracking-wider text-[#78716c] py-2">
            {w}
          </div>
        ))}
      </div>

      {/* Kalender-Grid */}
      <div className="grid grid-cols-7 gap-1">
        {zellen.map((zelle, idx) => {
          if (!zelle) return <div key={`empty-${idx}`} />;
          const istHeute = zelle.iso === heuteIso;
          const istPast = isVergangenheit(zelle.iso);
          return (
            <div
              key={zelle.iso}
              className={`min-h-[110px] rounded-md border p-1.5 ${
                istHeute
                  ? "border-[#9A3412] bg-[#FBEDE7]"
                  : istPast
                  ? "border-[#F6E3DA] bg-[#F7F5F2] opacity-70"
                  : "border-[#F6E3DA] bg-white"
              }`}
              data-testid={`cal-day-${zelle.iso}`}
            >
              <div className={`text-xs font-semibold mb-1 ${istHeute ? "text-[#9A3412]" : "text-[#5a4a35]"}`}>
                {zelle.tag}
                {istPast && zelle.termine.length > 0 && (
                  <span className="ml-1 text-[10px] text-red-600 font-bold" title="Überfällig">!</span>
                )}
              </div>
              <div className="space-y-0.5">
                {zelle.termine.slice(0, 4).map((pe) => (
                  <button
                    key={pe.id}
                    type="button"
                    onClick={() => onOpenPE(pe.id)}
                    className="w-full text-left px-1.5 py-0.5 rounded text-white text-[10px] truncate hover:opacity-90 transition-opacity block"
                    style={{ backgroundColor: farbeFuerVM(pe.vertriebsmitarbeiter_id) }}
                    title={`${pe.einrichtungsname}${pe.ansprechpartner_name ? ` · ${pe.ansprechpartner_name}` : ""}`}
                    data-testid={`cal-termin-${pe.id}`}
                  >
                    {pe.einrichtungsname}
                  </button>
                ))}
                {zelle.termine.length > 4 && (
                  <div className="text-[10px] text-[#78716c] px-1">+{zelle.termine.length - 4} weitere</div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {gesamtTermine === 0 && (
        <div className="text-center text-sm text-[#78716c] italic py-8">
          Keine Wiedervorlagen für {monatsName} — Wiedervorlagen können in der Einrichtungs-Detail-Ansicht gesetzt werden.
        </div>
      )}
    </div>
  );
};

// ==================== PIPELINE-KANBAN-ANSICHT für Pflegeeinrichtungen ====================
// Zeigt alle Einrichtungen als Karten in Spalten je nach akquise_status.
// Klick auf Karte öffnet Detail-View. Drag & Drop verschiebt Karten zwischen Spalten
// und aktualisiert automatisch den akquise_status (via PUT /pflegeeinrichtungen/{id}/akquise-status).
const PflegePipelineKanbanView = ({ data, onOpenPE }) => {
  const stagesInList = PIPELINE_STAGES;
  const [dragOverStage, setDragOverStage] = useState(null);
  const [localOverrides, setLocalOverrides] = useState({}); // {peId: newStatus} — Optimistic UI

  // Gruppieren nach Status (mit Optimistic Overrides)
  const gruppiert = {};
  stagesInList.forEach((s) => { gruppiert[s.key] = []; });
  (data || []).forEach((pe) => {
    const effektiverStatus = localOverrides[pe.id] || pe.akquise_status || "neu";
    if (gruppiert[effektiverStatus]) {
      gruppiert[effektiverStatus].push(pe);
    } else {
      gruppiert["neu"].push(pe);
    }
  });

  const handleDragStart = (e, pe) => {
    e.dataTransfer.setData("pe_id", pe.id);
    e.dataTransfer.setData("pe_current_status", pe.akquise_status || "neu");
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverStage(stageKey);
  };

  const handleDragLeave = () => setDragOverStage(null);

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    setDragOverStage(null);
    const peId = e.dataTransfer.getData("pe_id");
    const currentStatus = e.dataTransfer.getData("pe_current_status");
    if (!peId || currentStatus === targetStage) return;

    // Bestätigung nur wenn Ziel = "kunde" (Neukunden-Markierung wird gesetzt) oder "abgesagt"
    if (targetStage === "kunde") {
      const targetPe = (data || []).find((p) => p.id === peId);
      const name = targetPe?.einrichtungsname || "diese Einrichtung";
      if (!window.confirm(`„${name}" als Neukunde markieren?\n\nDamit wird automatisch das heutige Datum als Neukunden-Datum gesetzt.`)) {
        return;
      }
    }

    // Optimistic Update
    setLocalOverrides((prev) => ({ ...prev, [peId]: targetStage }));
    try {
      await axios.put(`${API}/pflegeeinrichtungen/${peId}/akquise-status`, {
        akquise_status: targetStage,
      });
      const stageLabel = stagesInList.find((s) => s.key === targetStage)?.label || targetStage;
      toast.success(`Status geändert: ${stageLabel}`);
    } catch (err) {
      // Rollback bei Fehler
      setLocalOverrides((prev) => {
        const neu = { ...prev };
        delete neu[peId];
        return neu;
      });
      toast.error("Statuswechsel fehlgeschlagen — bitte Seite neu laden");
      console.error(err);
    }
  };

  return (
    <div data-testid="pflege-pipeline-kanban" className="overflow-x-auto">
      <p className="text-xs text-[#78716c] italic mb-2 px-1">
        Tipp: Karten per Drag &amp; Drop in eine andere Spalte ziehen — der Akquise-Status wird automatisch aktualisiert.
      </p>
      <div className="flex gap-3 min-w-max pb-3" style={{ minWidth: "1200px" }}>
        {stagesInList.map((stage) => {
          const isDragOver = dragOverStage === stage.key;
          return (
          <div
            key={stage.key}
            className={`w-[220px] flex-shrink-0 rounded-lg border-2 p-2 transition-all ${
              isDragOver ? "ring-4 ring-offset-1" : ""
            }`}
            style={{
              backgroundColor: stage.bg,
              borderColor: isDragOver ? stage.color : stage.color + "33",
              ringColor: stage.color,
            }}
            data-testid={`kanban-col-${stage.key}`}
            onDragOver={(e) => handleDragOver(e, stage.key)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, stage.key)}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: stage.color }}>
                {stage.label}
              </span>
              <span className="text-xs font-bold" style={{ color: stage.color }}>
                {gruppiert[stage.key].length}
              </span>
            </div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {gruppiert[stage.key].map((pe) => (
                <div
                  key={pe.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, pe)}
                  onClick={() => onOpenPE(pe.id)}
                  className="cursor-grab active:cursor-grabbing bg-white border border-[#F6E3DA] rounded-md p-2.5 hover:border-[#9A3412] hover:shadow-md transition-all select-none"
                  data-testid={`kanban-card-${pe.id}`}
                  title="Klicken zum Öffnen · Ziehen zum Verschieben"
                >
                  <div className="font-medium text-[#2a2118] text-sm truncate">{pe.einrichtungsname}</div>
                  {(pe.ansprechpartner_name || pe.ort) && (
                    <div className="text-xs text-[#78716c] mt-0.5 truncate">
                      {pe.ansprechpartner_name || ""}
                      {pe.ansprechpartner_name && pe.ort ? " · " : ""}
                      {pe.ort || ""}
                    </div>
                  )}
                  <div className="flex items-center gap-1 mt-1 flex-wrap">
                    {pe.naechste_wiedervorlage && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FBEDE7] text-[#9A3412] border border-[#EBC9B9]">
                        WV {pe.naechste_wiedervorlage}
                      </span>
                    )}
                    {pe.hot_lead && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Hot</span>
                    )}
                  </div>
                </div>
              ))}
              {gruppiert[stage.key].length === 0 && (
                <div className={`text-xs italic px-1 py-3 text-center rounded ${
                  isDragOver ? "bg-white/50 border-2 border-dashed" : ""
                }`} style={{ color: stage.color, opacity: 0.6, borderColor: isDragOver ? stage.color : "transparent" }}>
                  {isDragOver ? "Hier ablegen" : "Keine Einträge"}
                </div>
              )}
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

const PflegePipelineKacheln = ({ aktiverFilter, onFilterChange }) => {
  const [counts, setCounts] = useState({});
  const [gesamt, setGesamt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // Alle PE ohne Filter holen, im Frontend zählen
        // (Backend-Aggregation als Optimierung möglich, aber für <5000 Records ok)
        const res = await axios.get(`${API}/pflegeeinrichtungen?limit=10000`);
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || []);
        const c = {};
        for (const pe of list) {
          const s = pe.akquise_status || "neu";
          c[s] = (c[s] || 0) + 1;
        }
        setCounts(c);
        setGesamt(list.length);
      } catch (err) {
        // still, nicht laut werden
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5" data-testid="pipeline-kacheln">
      {/* "Alle" Kachel */}
      <button
        type="button"
        onClick={() => onFilterChange("all")}
        className={`text-left rounded-lg p-4 border-2 transition-all hover:shadow-md ${
          aktiverFilter === "all"
            ? "border-[#9A3412] bg-[#F7F5F2] shadow-md"
            : "border-[#F6E3DA] bg-white hover:border-[#d6c4a5]"
        }`}
        data-testid="kachel-alle"
      >
        <div className="text-2xl font-bold text-[#2a2118]">{gesamt}</div>
        <div className="text-xs text-[#78716c] uppercase tracking-wider mt-1">Alle</div>
      </button>

      {PIPELINE_STAGES.map((s) => {
        const isActive = aktiverFilter === s.key;
        const count = counts[s.key] || 0;
        return (
          <button
            key={s.key}
            type="button"
            onClick={() => onFilterChange(s.key)}
            className={`text-left rounded-lg p-4 border-2 transition-all hover:shadow-md ${
              isActive
                ? "shadow-md border-2"
                : "border-transparent hover:shadow-sm"
            }`}
            style={{
              backgroundColor: s.bg,
              borderColor: isActive ? s.color : "transparent",
            }}
            data-testid={`kachel-${s.key}`}
          >
            <div className="text-2xl font-bold" style={{ color: s.color }}>{count}</div>
            <div className="text-xs uppercase tracking-wider mt-1" style={{ color: s.color, opacity: 0.85 }}>
              {s.label}
            </div>
          </button>
        );
      })}
    </div>
  );
};

// ==================== PFLEGEEINRICHTUNGEN ====================
const Pflegeeinrichtungen = () => {
  const { user } = useAuth(); // Für Berechtigungsprüfung
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [vertriebsmitarbeiter, setVertriebsmitarbeiter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [akquiseFilter, setAkquiseFilter] = useState("all");
  // Ansichts-Modus für /pflegeeinrichtungen: "liste" (Standard) | "pipeline" | "kalender"
  const [viewMode, setViewMode] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("view");
    return ["cockpit", "liste", "pipeline", "kalender"].includes(p) ? p : "liste";
  });
  const [mdNoteFilter, setMdNoteFilter] = useState("all");
  const [paketFilter, setPaketFilter] = useState("all"); // NEU: Fortbildungspaket-Filter
  const [ortFilter, setOrtFilter] = useState("");
  const [plzRegionFilter, setPlzRegionFilter] = useState("all");
  const [sortField, setSortField] = useState("letzte_bestellung_am");
  const [sortDir, setSortDir] = useState("desc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [zugehoerigeKursteilnehmer, setZugehoerigeKursteilnehmer] = useState([]);
  // Tag-System: Filter + Lead-Import
  const [tagFilter, setTagFilter] = useState("all");
  const [availableTags, setAvailableTags] = useState([]);
  const [importLeadsOpen, setImportLeadsOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddText, setQuickAddText] = useState("");
  const [quickAddSaving, setQuickAddSaving] = useState(false);
  const [atlasMailOpen, setAtlasMailOpen] = useState(false);
  const [atlasTestEmail, setAtlasTestEmail] = useState("linda@dickies-helden.de");
  const [atlasSending, setAtlasSending] = useState(false);
  const [atlasRecipients, setAtlasRecipients] = useState(null);
  // Sommer-Aktion 2026 Kampagne (Bestandskunden · 25% Rabatt)
  const [sommerMailOpen, setSommerMailOpen] = useState(false);
  const [sommerTestEmail, setSommerTestEmail] = useState("linda@dickies-helden.de");
  const [sommerSending, setSommerSending] = useState(false);
  const [sommerData, setSommerData] = useState(null); // { total, freemail_count, already_sent_count, empfaenger[] }
  const [sommerSelected, setSommerSelected] = useState(new Set()); // Set<id>
  const [sommerSearch, setSommerSearch] = useState("");
  const [importFile, setImportFile] = useState(null);
  const [importTag, setImportTag] = useState("Neuhaus");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [zugehoerigeRechnungen, setZugehoerigeRechnungen] = useState([]);
  const [loadingTeilnehmer, setLoadingTeilnehmer] = useState(false);
  const [loadingRechnungen, setLoadingRechnungen] = useState(false);
  // WhatsApp-Dialog (manueller Versand pro Lead)
  const [waDialogOpen, setWaDialogOpen] = useState(false);
  const [waTarget, setWaTarget] = useState(null);
  const location = useLocation();
  const [formData, setFormData] = useState({
    einrichtungsname: "",
    strasse: "",
    plz: "",
    ort: "",
    ansprechpartner_name: "",
    ansprechpartner_email: "",
    ansprechpartner_telefon: "",
    status: "interessent",
    interne_notizen: "",
    naechste_wiedervorlage: "",
    // Vertriebsfelder
    vertriebsmitarbeiter_id: "",
    akquise_status: "neu",
    // MD-Prüfungsnote
    md_pruefungsnote: "",
    // Fortbildungspaket
    fortbildungspaket: null,
    fortbildungspaket_start: ""
  });

  // PLZ-Regionen (Bundesländer-Näherung)
  const PLZ_REGIONEN = {
    "0": "Sachsen, Thüringen, Brandenburg",
    "1": "Berlin, Brandenburg, Mecklenburg-Vorpommern",
    "2": "Hamburg, Schleswig-Holstein, Niedersachsen (Nord)",
    "3": "Niedersachsen, Bremen",
    "4": "Nordrhein-Westfalen (Nord)",
    "5": "Nordrhein-Westfalen (Süd), Rheinland-Pfalz",
    "6": "Hessen, Rheinland-Pfalz, Saarland",
    "7": "Baden-Württemberg (Nord)",
    "8": "Bayern (Süd), Baden-Württemberg (Süd)",
    "9": "Bayern (Nord)"
  };

  // Einzigartige Städte für Filter
  const uniqueOrte = useMemo(() => {
    const orte = [...new Set(data.map(e => e.ort).filter(Boolean))].sort();
    return orte;
  }, [data]);

  // Tags aus Daten extrahieren (live, basiert auf geladenen Pflegeeinrichtungen)
  useEffect(() => {
    const tagSet = new Set();
    data.forEach(pe => (pe.tags || []).forEach(t => t && tagSet.add(t)));
    setAvailableTags([...tagSet].sort());
  }, [data]);

  // URL-Parameter verarbeiten (für direkte Navigation zu einer Einrichtung)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const idParam = params.get('id');
    if (idParam && data.length > 0) {
      const einrichtung = data.find(e => e.id === idParam);
      if (einrichtung) {
        handleEdit(einrichtung);
        // URL bereinigen
        navigate('/pflegeeinrichtungen', { replace: true });
      }
    }
  }, [location.search, data]);

  // Sortierung
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const getSortedData = () => {
    // Zuerst lokale Filter anwenden
    let filtered = [...data];
    
    // Ort-Filter
    if (ortFilter) {
      filtered = filtered.filter(pe => pe.ort === ortFilter);
    }
    
    // PLZ-Region Filter
    if (plzRegionFilter !== "all") {
      filtered = filtered.filter(pe => pe.plz && pe.plz.startsWith(plzRegionFilter));
    }
    
    // Fortbildungspaket-Filter
    if (paketFilter !== "all") {
      if (paketFilter === "mit_paket") {
        filtered = filtered.filter(pe => pe.fortbildungspaket === "klein" || pe.fortbildungspaket === "gross");
      } else if (paketFilter === "ohne_paket") {
        filtered = filtered.filter(pe => !pe.fortbildungspaket);
      } else {
        filtered = filtered.filter(pe => pe.fortbildungspaket === paketFilter);
      }
    }

    // Tag-Filter (z.B. "Neuhaus")
    if (tagFilter !== "all") {
      if (tagFilter === "__none__") {
        filtered = filtered.filter(pe => !pe.tags || pe.tags.length === 0);
      } else {
        filtered = filtered.filter(pe => Array.isArray(pe.tags) && pe.tags.includes(tagFilter));
      }
    }
    
    // Sortieren
    return filtered.sort((a, b) => {
      // Sonderfall: Datum "letzte_bestellung_am" – None immer ans Ende
      if (sortField === "letzte_bestellung_am") {
        const dA = a.letzte_bestellung_am || "";
        const dB = b.letzte_bestellung_am || "";
        if (!dA && !dB) return 0;
        if (!dA) return 1;   // leer immer nach unten
        if (!dB) return -1;
        return sortDir === "asc" ? dA.localeCompare(dB) : dB.localeCompare(dA);
      }
      // Sonderfall: Kunde ja/nein
      if (sortField === "ist_kunde") {
        const kA = a.akquise_status === "kunde" ? 1 : 0;
        const kB = b.akquise_status === "kunde" ? 1 : 0;
        if (kA === kB) {
          // Sekundärsort: letzte Bestellung DESC
          const dA = a.letzte_bestellung_am || "";
          const dB = b.letzte_bestellung_am || "";
          return dB.localeCompare(dA);
        }
        return sortDir === "asc" ? kA - kB : kB - kA;
      }
      let valA = (a[sortField] || "").toString().toLowerCase();
      let valB = (b[sortField] || "").toString().toLowerCase();
      if (sortDir === "asc") {
        return valA < valB ? -1 : valA > valB ? 1 : 0;
      } else {
        return valA > valB ? -1 : valA < valB ? 1 : 0;
      }
    });
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="ml-1 opacity-30" />;
    return sortDir === "asc" ? 
      <ChevronUp size={14} className="ml-1" /> : 
      <ChevronDown size={14} className="ml-1" />;
  };

  // VM-Liste nur 1x laden (ändert sich nicht je Suche/Filter)
  const fetchVM = useCallback(async () => {
    try {
      const vmRes = await axios.get(`${API}/vertriebsmitarbeiter`);
      setVertriebsmitarbeiter(vmRes.data);
    } catch (e) {
      console.error("VM-Liste konnte nicht geladen werden", e);
    }
  }, []);

  // Aktuelle Filter-Werte als Ref (für fetchData() ohne Argument nach Save/Delete)
  const filtersRef = useRef({ search: "", statusFilter: "all", akquiseFilter: "all", mdNoteFilter: "all" });
  useEffect(() => {
    filtersRef.current = { search, statusFilter, akquiseFilter, mdNoteFilter };
  }, [search, statusFilter, akquiseFilter, mdNoteFilter]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const f = filtersRef.current;
      const params = new URLSearchParams();
      const q = (f.search || "").trim();
      if (q) params.append("search", q);
      if (f.statusFilter !== "all") params.append("status", f.statusFilter);
      const peRes = await axios.get(`${API}/pflegeeinrichtungen?${params}`);
      let filtered = peRes.data;
      if (f.akquiseFilter !== "all") {
        filtered = filtered.filter(pe => pe.akquise_status === f.akquiseFilter);
      }
      // MD-Note Filter - berücksichtigt sowohl Text- als auch numerische Werte
      if (f.mdNoteFilter !== "all") {
        if (f.mdNoteFilter === "keine") {
          filtered = filtered.filter(pe => !pe.md_pruefungsnote);
        } else {
          const noteRanges = {
            "sehr gut": (n) => n && (n === "sehr gut" || (parseFloat(n) >= 1 && parseFloat(n) <= 1.5)),
            "gut": (n) => n && (n === "gut" || (parseFloat(n) > 1.5 && parseFloat(n) <= 2.5)),
            "befriedigend": (n) => n && (n === "befriedigend" || (parseFloat(n) > 2.5 && parseFloat(n) <= 3.5)),
            "ausreichend": (n) => n && (n === "ausreichend" || (parseFloat(n) > 3.5 && parseFloat(n) <= 4.5)),
            "mangelhaft": (n) => n && (n === "mangelhaft" || (parseFloat(n) > 4.5))
          };
          const matchFn = noteRanges[f.mdNoteFilter];
          if (matchFn) {
            filtered = filtered.filter(pe => matchFn(pe.md_pruefungsnote));
          }
        }
      }
      setData(filtered);
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, []);

  // VM einmalig laden
  useEffect(() => {
    fetchVM();
  }, [fetchVM]);

  // Debounced Reload bei Filter-/Such-Änderung (300ms)
  useEffect(() => {
    const handle = setTimeout(() => {
      fetchData();
    }, search ? 300 : 0);
    return () => clearTimeout(handle);
  }, [search, statusFilter, akquiseFilter, mdNoteFilter, fetchData]);

  // Hilfsfunktion: MD-Note in Text und Farbe umwandeln
  const getMdNoteInfo = (note) => {
    if (!note) return null;
    
    // Wenn es bereits Text ist
    const textColors = {
      "sehr gut": { label: "sehr gut", color: "#22C55E" },
      "gut": { label: "gut", color: "#84CC16" },
      "befriedigend": { label: "befriedigend", color: "#F59E0B" },
      "ausreichend": { label: "ausreichend", color: "#F97316" },
      "mangelhaft": { label: "mangelhaft", color: "#EF4444" }
    };
    
    if (textColors[note]) return textColors[note];
    
    // Numerisch -> Text konvertieren
    const num = parseFloat(note);
    if (isNaN(num)) return { label: note, color: "#6B7280" };
    
    if (num <= 1.5) return { label: `${note} (sehr gut)`, color: "#22C55E" };
    if (num <= 2.5) return { label: `${note} (gut)`, color: "#84CC16" };
    if (num <= 3.5) return { label: `${note} (befriedigend)`, color: "#F59E0B" };
    if (num <= 4.5) return { label: `${note} (ausreichend)`, color: "#F97316" };
    return { label: `${note} (mangelhaft)`, color: "#EF4444" };
  };

  const getVMName = (id) => {
    const vm = vertriebsmitarbeiter.find(v => v.id === id);
    return vm ? vm.name : "-";
  };

  const handleSubmit = async () => {
    try {
      if (editItem) {
        await axios.put(`${API}/pflegeeinrichtungen/${editItem.id}`, formData);
        toast.success("Einrichtung aktualisiert");
      } else {
        await axios.post(`${API}/pflegeeinrichtungen`, formData);
        toast.success("Einrichtung erstellt");
      }
      setDialogOpen(false);
      setEditItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/pflegeeinrichtungen/${id}`);
        toast.success("Einrichtung gelöscht");
        fetchData();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = async (item) => {
    setEditItem(item);
    setFormData({
      einrichtungsname: item.einrichtungsname,
      strasse: item.strasse,
      plz: item.plz,
      ort: item.ort,
      ansprechpartner_name: item.ansprechpartner_name,
      ansprechpartner_email: item.ansprechpartner_email,
      ansprechpartner_telefon: item.ansprechpartner_telefon,
      status: item.status,
      interne_notizen: item.interne_notizen || "",
      naechste_wiedervorlage: item.naechste_wiedervorlage || "",
      vertriebsmitarbeiter_id: item.vertriebsmitarbeiter_id || "",
      akquise_status: item.akquise_status || "neu",
      md_pruefungsnote: item.md_pruefungsnote || "",
      fortbildungspaket: item.fortbildungspaket || null,
      fortbildungspaket_start: item.fortbildungspaket_start || ""
    });
    setDialogOpen(true);
    
    // Lade zugehörige Kursteilnehmer
    setLoadingTeilnehmer(true);
    try {
      const res = await axios.get(`${API}/pflegeeinrichtungen/${item.id}/teilnehmer`);
      setZugehoerigeKursteilnehmer(res.data);
    } catch (error) {
      console.error("Fehler beim Laden der Teilnehmer:", error);
      setZugehoerigeKursteilnehmer([]);
    } finally {
      setLoadingTeilnehmer(false);
    }
    
    // Lade zugehörige Rechnungen
    setLoadingRechnungen(true);
    try {
      const res = await axios.get(`${API}/rechnungen`);
      const rechnungenDerEinrichtung = res.data.filter(r => r.pflegeeinrichtung_id === item.id);
      setZugehoerigeRechnungen(rechnungenDerEinrichtung);
    } catch (error) {
      console.error("Fehler beim Laden der Rechnungen:", error);
      setZugehoerigeRechnungen([]);
    } finally {
      setLoadingRechnungen(false);
    }
  };

  const resetForm = () => {
    setFormData({
      einrichtungsname: "",
      strasse: "",
      plz: "",
      ort: "",
      ansprechpartner_name: "",
      ansprechpartner_email: "",
      ansprechpartner_telefon: "",
      status: "interessent",
      interne_notizen: "",
      naechste_wiedervorlage: "",
      vertriebsmitarbeiter_id: "",
      akquise_status: "neu",
      md_pruefungsnote: "",
      fortbildungspaket: null,
      fortbildungspaket_start: ""
    });
    setZugehoerigeKursteilnehmer([]);
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/export/pflegeeinrichtungen`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'pflegeeinrichtungen.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export erfolgreich");
    } catch (error) {
      toast.error("Fehler beim Export");
    }
  };

  return (
    <div className="page-container" data-testid="pflegeeinrichtungen-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Pflegeeinrichtungen</h1>
      </div>

      {/* Pipeline-Kacheln — Klick filtert die Liste nach Akquise-Status */}
      <PflegePipelineKacheln
        aktiverFilter={akquiseFilter}
        onFilterChange={setAkquiseFilter}
      />
      
      {/* Toolbar: Only primary actions + kebab menu */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button
          className="bg-[#D9775A] hover:bg-[#C2603F] text-white"
          onClick={() => { resetForm(); setDialogOpen(true); }}
          data-testid="add-einrichtung-btn"
        >
          <Plus size={16} className="mr-2" />
          Neue Einrichtung
        </Button>
        <Button
          variant="outline"
          onClick={() => setImportLeadsOpen(true)}
          data-testid="import-leads-btn"
          title="CSV mit Leads (z.B. aus GoHighLevel-Funnel) importieren – optional mit Tag versehen"
        >
          <Plus size={16} className="mr-2" />
          Leads importieren
        </Button>
        <Button
          variant="outline"
          onClick={() => setQuickAddOpen(true)}
          data-testid="quick-add-lead-btn"
          title="Schnelles Anlegen: Beliebigen Text (Name, Firma, Email, Tel, Notiz) reinkopieren – wird automatisch geparst."
        >
          <Plus size={16} className="mr-2" />
          Lead schnell anlegen
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" data-testid="pe-more-menu-btn">
              <MoreHorizontal size={16} className="mr-2" />
              Mehr
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>Import &amp; Export</DropdownMenuLabel>
            <DropdownMenuItem onClick={handleExport} data-testid="menu-export">
              <Download size={14} className="mr-2" /> Export (Excel)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => document.getElementById('delegations-atlas-file-input')?.click()} data-testid="menu-delegations-atlas">
              <Plus size={14} className="mr-2" /> Delegations-Atlas Import
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Kampagnen</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => {
                setAtlasMailOpen(true);
                setAtlasRecipients(null);
                axios.post(`${API}/pflegeeinrichtungen/bulk-email-delegations-atlas`,
                  { dry_run: true }, { timeout: 30000 })
                  .then(r => setAtlasRecipients(r.data))
                  .catch(e => setAtlasRecipients({
                    error: e?.response?.data?.detail || e?.message || "Verbindung zum Server fehlgeschlagen",
                    recipient_count: 0,
                  }));
              }}
              data-testid="menu-atlas-mail"
            >
              <Mail size={14} className="mr-2" /> Atlas-Kampagne senden
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                setSommerMailOpen(true);
                setSommerData(null);
                setSommerSelected(new Set());
                setSommerSearch("");
                axios.get(`${API}/pflegeeinrichtungen/sommeraktion-empfaenger`, { timeout: 30000 })
                  .then(r => {
                    setSommerData(r.data);
                    const preselect = new Set(
                      (r.data?.empfaenger || [])
                        .filter(e => !e.already_sent)
                        .map(e => e.id)
                    );
                    setSommerSelected(preselect);
                  })
                  .catch(e => setSommerData({
                    error: e?.response?.data?.detail || e?.message || "Verbindung zum Server fehlgeschlagen",
                    empfaenger: [],
                  }));
              }}
              data-testid="menu-sommer-mail"
            >
              <Mail size={14} className="mr-2" /> Sommer-Aktion senden
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Datenpflege</DropdownMenuLabel>
            <DropdownMenuItem
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/pflegeeinrichtungen/assign-vertrieb/preview`);
                  const d = res.data;
                  const msg = `VORSCHAU Vertrieb-Zuweisung:\n\n` +
                    `Einrichtungen mit Teilnehmern: ${d.einrichtungen_mit_teilnehmern}\n\n` +
                    `Wird Jean Dickie zugewiesen: ${d.zusammenfassung.neu_jean}\n` +
                    `Wird Katharina zugewiesen: ${d.zusammenfassung.neu_katharina}\n` +
                    `Bereits zugewiesen: ${d.zusammenfassung.bereits_zugewiesen}\n\n` +
                    (d.vorschau.wird_jean_zugewiesen.length > 0 ? `→ Jean: ${d.vorschau.wird_jean_zugewiesen.slice(0,10).join(", ")}${d.vorschau.wird_jean_zugewiesen.length > 10 ? "..." : ""}\n` : "") +
                    (d.vorschau.wird_katharina_zugewiesen.length > 0 ? `→ Katharina: ${d.vorschau.wird_katharina_zugewiesen.join(", ")}\n` : "");
                  alert(msg);
                } catch (e) {
                  toast.error("Fehler bei Vorschau");
                }
              }}
              data-testid="menu-preview-vertrieb"
            >
              <Eye size={14} className="mr-2" /> Vertrieb-Zuweisung Vorschau
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                if (!window.confirm("Vertriebsmitarbeiter zuweisen?\n\n• Einrichtungen mit Teilnehmern → Jean Dickie\n• Ausnahmen (Flamingo, Weissenberg, germeda, Mitmenschen) → Katharina Blatsas\n\nDies kann nicht rückgängig gemacht werden!")) return;
                try {
                  const res = await axios.post(`${API}/pflegeeinrichtungen/assign-vertrieb`);
                  toast.success(`${res.data.jean_zugewiesen} → Jean Dickie, ${res.data.katharina_zugewiesen} → Katharina`);
                  fetchData();
                } catch (e) {
                  toast.error("Fehler bei Zuweisung");
                }
              }}
              data-testid="menu-assign-vertrieb"
            >
              <User size={14} className="mr-2" /> Vertrieb zuweisen
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/pflegeeinrichtungen/duplikate`);
                  const d = res.data;
                  if (d.duplikat_gruppen === 0) {
                    toast.success("Keine Duplikate gefunden!");
                  } else {
                    const names = Object.keys(d.duplikate).slice(0, 5).join(", ");
                    alert(`${d.duplikat_gruppen} potenzielle Duplikat-Gruppen gefunden!\n\nBeispiele: ${names}...`);
                  }
                } catch (e) {
                  toast.error("Fehler bei Duplikat-Prüfung");
                }
              }}
              data-testid="menu-check-duplicates"
            >
              <AlertTriangle size={14} className="mr-2" /> Duplikate prüfen
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={async () => {
                if (!window.confirm("Kunden-Klassifikation neu berechnen?\n\n• Einrichtungen MIT Teilnehmern → 'Kunde' (aktiv)\n• Nur Kontakt-Historie → 'Kontaktiert' (warm)\n• Nichts → 'Neu' (kalt)\n\nZusätzlich wird 'Letzte Bestellung' und TN-Anzahl 2026 gespeichert.")) return;
                try {
                  const res = await axios.post(`${API}/pflegeeinrichtungen/recompute-kunden-klassifikation`);
                  toast.success(`${res.data.total_einrichtungen} Einrichtungen neu klassifiziert`, {
                    description: `${res.data.aktive_kunden} Kunden | ${res.data.warme_kontakte} Warm | ${res.data.kalt_akquise} Kalt`,
                    duration: 8000,
                  });
                  fetchData();
                } catch (e) {
                  toast.error("Fehler bei Klassifikation");
                }
              }}
              data-testid="menu-fix-status"
            >
              <RefreshCw size={14} className="mr-2" /> Kunden-Status aktualisieren
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                try {
                  const jahr = new Date().getFullYear();
                  const res = await axios.get(`${API}/pflegeeinrichtungen/top-kunden?jahr=${jahr}&limit=10`);
                  const lines = (res.data.top_kunden || []).map((k, i) =>
                    `${i + 1}. ${k.einrichtungsname} — ${k.tn_anzahl_im_jahr} TN in ${jahr} (letzte Bestellung: ${k.letzte_bestellung_am || '—'})${k.ort ? ' · ' + k.ort : ''}`
                  ).join("\n");
                  alert(`Top 10 Kunden ${jahr}\n\n${lines || 'Noch keine Bestellungen dieses Jahr'}\n\nGesamt: ${res.data.total} Einrichtungen mit Bestellung in ${jahr}`);
                } catch (e) {
                  toast.error("Fehler beim Laden der Top-Kunden");
                }
              }}
              data-testid="menu-top-kunden"
            >
              <TrendingUp size={14} className="mr-2" /> Top 10 Kunden anzeigen
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Hidden file input for Delegations-Atlas CSV */}
        <input
          id="delegations-atlas-file-input"
          type="file"
          accept=".csv"
          className="hidden"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            e.target.value = "";
            try {
              const fd1 = new FormData();
              fd1.append("file", f);
              const dry = await axios.post(
                `${API}/pflegeeinrichtungen/import-delegations-atlas?dry_run=true`, fd1,
                { headers: { "Content-Type": "multipart/form-data" } }
              );
              const cnt = dry.data?.inserted_count || 0;
              const dupCnt = dry.data?.duplicate_count || 0;
              if (cnt === 0) {
                toast.info(`Keine neuen Leads (${dupCnt} Duplikate erkannt).`);
                return;
              }
              if (!window.confirm(
                `Delegations-Atlas Import:\n\n` +
                `  • ${cnt} neue Leads\n` +
                `  • ${dupCnt} Duplikate (werden übersprungen)\n\n` +
                `Anlegen mit Hot-Lead=Ja + Tag "Delegations-Atlas"?`
              )) return;
              const fd2 = new FormData();
              fd2.append("file", f);
              const live = await axios.post(
                `${API}/pflegeeinrichtungen/import-delegations-atlas`, fd2,
                { headers: { "Content-Type": "multipart/form-data" } }
              );
              toast.success(
                `${live.data?.inserted_count || 0} Leads angelegt · ${live.data?.duplicate_count || 0} Duplikate übersprungen`,
                { duration: 8000 }
              );
              if (typeof fetchData === "function") await fetchData();
            } catch (err) {
              toast.error("Import fehlgeschlagen",
                { description: err?.response?.data?.detail || err.message });
            }
          }}
        />
      </div>

      {/* Filters */}
      <Card className="card-custom mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Suche */}
            <div className="flex-1 min-w-[240px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716c]" size={18} />
              <Input
                placeholder="Suchen nach Name, Ort, Ansprechpartner..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-input"
              />
            </div>

            {/* Filter-Popover (bündelt alle Sekundär-Filter) */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" data-testid="filter-popover-btn">
                  <SlidersHorizontal size={16} className="mr-2" />
                  Filter
                  {(ortFilter || plzRegionFilter !== "all" || statusFilter !== "all" || akquiseFilter !== "all" || mdNoteFilter !== "all" || paketFilter !== "all" || tagFilter !== "all") && (
                    <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[20px] rounded-full bg-[#D9775A] text-white text-[10px] font-semibold px-1.5">
                      {[ortFilter, plzRegionFilter !== "all", statusFilter !== "all", akquiseFilter !== "all", mdNoteFilter !== "all", paketFilter !== "all", tagFilter !== "all"].filter(Boolean).length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[360px] p-4" align="end">
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-[#78716c] mb-1 block">Stadt</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-between font-normal">
                          {ortFilter || "Alle Städte"}
                          <Search size={14} className="ml-2 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0">
                        <Command>
                          <CommandInput placeholder="Stadt suchen..." />
                          <CommandList>
                            <CommandEmpty>Keine Stadt gefunden</CommandEmpty>
                            <CommandGroup>
                              <CommandItem onSelect={() => setOrtFilter("")}>
                                <Check className={`mr-2 h-4 w-4 ${!ortFilter ? "opacity-100" : "opacity-0"}`} />
                                Alle Städte
                              </CommandItem>
                              {uniqueOrte.slice(0, 100).map(ort => (
                                <CommandItem key={ort} value={ort} onSelect={() => setOrtFilter(ort)}>
                                  <Check className={`mr-2 h-4 w-4 ${ortFilter === ort ? "opacity-100" : "opacity-0"}`} />
                                  {ort}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label className="text-xs text-[#78716c] mb-1 block">PLZ-Region / Bundesland</Label>
                    <Select value={plzRegionFilter} onValueChange={setPlzRegionFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Region" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Regionen</SelectItem>
                        {Object.entries(PLZ_REGIONEN).map(([plz, region]) => (
                          <SelectItem key={plz} value={plz}>PLZ {plz}xxxx – {region}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-[#78716c] mb-1 block">Status</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger data-testid="status-filter">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Status</SelectItem>
                        <SelectItem value="interessent">Interessent</SelectItem>
                        <SelectItem value="aktiv">Aktiv</SelectItem>
                        <SelectItem value="pausiert">Pausiert</SelectItem>
                        <SelectItem value="abgeschlossen">Abgeschlossen</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-[#78716c] mb-1 block">Akquise-Status</Label>
                    <Select value={akquiseFilter} onValueChange={setAkquiseFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Akquise" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Akquise</SelectItem>
                        <SelectItem value="neu">Neu</SelectItem>
                        <SelectItem value="kontaktiert">Kontaktiert</SelectItem>
                        <SelectItem value="interessent">Interessent</SelectItem>
                        <SelectItem value="angebot_gesendet">Angebot erstellt</SelectItem>
                        <SelectItem value="kunde">Kunde</SelectItem>
                        <SelectItem value="abgesagt">Abgesagt</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-[#78716c] mb-1 block">MD-Prüfnote</Label>
                    <Select value={mdNoteFilter} onValueChange={setMdNoteFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="MD-Note" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle MD-Noten</SelectItem>
                        <SelectItem value="sehr gut">sehr gut</SelectItem>
                        <SelectItem value="gut">gut</SelectItem>
                        <SelectItem value="befriedigend">befriedigend</SelectItem>
                        <SelectItem value="ausreichend">ausreichend</SelectItem>
                        <SelectItem value="mangelhaft">mangelhaft</SelectItem>
                        <SelectItem value="keine">Keine Note</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-[#78716c] mb-1 block">Fortbildungspaket</Label>
                    <Select value={paketFilter} onValueChange={setPaketFilter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Paket" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Pakete</SelectItem>
                        <SelectItem value="mit_paket">Mit Paket</SelectItem>
                        <SelectItem value="klein">Klein (€1.200)</SelectItem>
                        <SelectItem value="gross">Groß (€1.900)</SelectItem>
                        <SelectItem value="ohne_paket">Ohne Paket</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-[#78716c] mb-1 block">Tag / Lead-Quelle</Label>
                    <Select value={tagFilter} onValueChange={setTagFilter}>
                      <SelectTrigger data-testid="tag-filter-select">
                        <SelectValue placeholder="Tag" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle Tags</SelectItem>
                        <SelectItem value="__none__">Ohne Tag</SelectItem>
                        {availableTags.map(t => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="pt-2 border-t">
                    <button
                      className="text-xs text-[#D9775A] hover:text-[#C2603F] font-medium"
                      onClick={() => {
                        setOrtFilter("");
                        setPlzRegionFilter("all");
                        setStatusFilter("all");
                        setAkquiseFilter("all");
                        setMdNoteFilter("all");
                        setPaketFilter("all");
                        setTagFilter("all");
                      }}
                    >
                      Alle Filter zurücksetzen
                    </button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          
          {/* Active Filters Summary */}
          {(ortFilter || plzRegionFilter !== "all" || statusFilter !== "all" || akquiseFilter !== "all" || mdNoteFilter !== "all" || paketFilter !== "all" || tagFilter !== "all") && (
            <div className="flex items-center gap-2 mt-3 pt-3 border-t">
              <span className="text-xs text-gray-500">Aktive Filter:</span>
              {ortFilter && (
                <Badge variant="secondary" className="text-xs cursor-pointer" onClick={() => setOrtFilter("")}>
                  Stadt: {ortFilter} 
                </Badge>
              )}
              {plzRegionFilter !== "all" && (
                <Badge variant="secondary" className="text-xs cursor-pointer" onClick={() => setPlzRegionFilter("all")}>
                  PLZ {plzRegionFilter}xxxx 
                </Badge>
              )}
              {statusFilter !== "all" && (
                <Badge variant="secondary" className="text-xs cursor-pointer" onClick={() => setStatusFilter("all")}>
                  Status: {statusFilter} 
                </Badge>
              )}
              {akquiseFilter !== "all" && (
                <Badge variant="secondary" className="text-xs cursor-pointer" onClick={() => setAkquiseFilter("all")}>
                  Akquise: {akquiseFilter} 
                </Badge>
              )}
              {mdNoteFilter !== "all" && (
                <Badge variant="secondary" className="text-xs cursor-pointer" onClick={() => setMdNoteFilter("all")}>
                  MD-Note: {mdNoteFilter} 
                </Badge>
              )}
              {paketFilter !== "all" && (
                <Badge variant="secondary" className="text-xs cursor-pointer" onClick={() => setPaketFilter("all")}>
                  Paket: {paketFilter === "mit_paket" ? "Mit Paket" : paketFilter === "ohne_paket" ? "Ohne Paket" : paketFilter === "klein" ? "Klein" : "Groß"} 
                </Badge>
              )}
              {tagFilter !== "all" && (
                <Badge variant="secondary" className="text-xs cursor-pointer bg-emerald-50 text-emerald-800" onClick={() => setTagFilter("all")} data-testid="tag-filter-badge">
                  {tagFilter === "__none__" ? "Ohne Tag" : tagFilter} 
                </Badge>
              )}
              <button 
                className="text-xs text-red-500 hover:text-red-700 ml-auto"
                onClick={() => {
                  setOrtFilter("");
                  setPlzRegionFilter("all");
                  setStatusFilter("all");
                  setAkquiseFilter("all");
                  setMdNoteFilter("all");
                  setPaketFilter("all");
                  setTagFilter("all");
                }}
              >
                Alle Filter zurücksetzen
              </button>
            </div>
          )}
        </CardContent>
      </Card>


      {/* Sales-Startseite (Skizze 2): KPIs + Heute-fällig + Neuhaus-Kohorte — nur in Listen-Ansicht */}
      {viewMode === "liste" && (
        <SalesUeberblick data={data} onOpenPE={(peId) => navigate(`/pflegeeinrichtungen/${peId}`)} />
      )}

      {/* View-Umschalter: Cockpit / Liste / Pipeline (Kanban) / Kalender */}
      <div className="flex items-center gap-1 mb-4 border-b border-[#F6E3DA]">
        {[
          { key: "cockpit", label: "Cockpit", testid: "view-cockpit" },
          { key: "liste", label: "Liste", testid: "view-liste" },
          { key: "pipeline", label: "Pipeline (Kanban)", testid: "view-pipeline" },
          { key: "kalender", label: "Kalender", testid: "view-kalender" },
        ].map((v) => (
          <button
            key={v.key}
            onClick={() => setViewMode(v.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              viewMode === v.key
                ? "border-[#9A3412] text-[#9A3412]"
                : "border-transparent text-[#78716c] hover:text-[#5a4a35]"
            }`}
            data-testid={v.testid}
          >
            {v.label}
          </button>
        ))}
      </div>

      {viewMode === "cockpit" && <VertriebsCockpit />}
      {viewMode === "kalender" && <PflegeKalenderView data={data} onOpenPE={(peId) => navigate(`/pflegeeinrichtungen/${peId}`)} />}
      {viewMode === "pipeline" && <PflegePipelineKanbanView data={data} onOpenPE={(peId) => navigate(`/pflegeeinrichtungen/${peId}`)} />}

      {viewMode === "liste" && (
      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
            </div>
          ) : data.length === 0 ? (
            <div className="empty-state">
              <Building2 className="empty-state-icon mx-auto" />
              <p>Keine Einrichtungen gefunden</p>
            </div>
          ) : (
            <Table data-testid="einrichtungen-table">
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead className="w-[240px] cursor-pointer hover:bg-gray-50" onClick={() => handleSort("einrichtungsname")}>
                    <div className="flex items-center">Einrichtung <SortIcon field="einrichtungsname" /></div>
                  </TableHead>
                  <TableHead className="w-[180px] cursor-pointer hover:bg-gray-50" onClick={() => handleSort("ort")}>
                    <div className="flex items-center">Ort <SortIcon field="ort" /></div>
                  </TableHead>
                  <TableHead className="w-[200px] cursor-pointer hover:bg-gray-50" onClick={() => handleSort("ansprechpartner_name")}>
                    <div className="flex items-center">Ansprechpartner <SortIcon field="ansprechpartner_name" /></div>
                  </TableHead>
                  <TableHead className="w-[110px] cursor-pointer hover:bg-gray-50" onClick={() => handleSort("ist_kunde")}>
                    <div className="flex items-center">Kunde <SortIcon field="ist_kunde" /></div>
                  </TableHead>
                  <TableHead className="w-[140px] cursor-pointer hover:bg-gray-50" onClick={() => handleSort("letzte_bestellung_am")}>
                    <div className="flex items-center">Letzte Bestellung <SortIcon field="letzte_bestellung_am" /></div>
                  </TableHead>
                  <TableHead className="w-[130px]">Vertrieb</TableHead>
                  <TableHead className="text-right w-[110px]">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getSortedData().map((item) => {
                  const istKunde = item.akquise_status === "kunde";
                  const letzteBest = item.letzte_bestellung_am
                    ? new Date(item.letzte_bestellung_am).toLocaleDateString('de-DE')
                    : "—";
                  return (
                  <TableRow key={item.id} className="cursor-pointer hover:bg-[#faf9f7]" onClick={() => handleEdit(item)}>
                    <TableCell className="max-w-[240px]">
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); navigate(`/pflegeeinrichtungen/${item.id}`); }}
                        className="text-left font-medium text-[#9A3412] hover:text-[#5a3d1a] hover:underline truncate max-w-full focus:outline-none focus:ring-2 focus:ring-[#EBC9B9] rounded"
                        title={`${item.einrichtungsname} — Detail-Ansicht öffnen`}
                        data-testid={`pe-detail-link-${item.id}`}
                      >
                        {item.einrichtungsname}
                      </button>
                    </TableCell>
                    <TableCell className="max-w-[180px]">
                      <span className="text-sm text-[#57534e] truncate block" title={`${item.strasse || ""}, ${item.plz || ""} ${item.ort || ""}`}>
                        {item.plz || ""} {item.ort || "—"}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[220px]">
                      {(() => {
                        const name = item.ansprechpartner_name || "";
                        const email = item.ansprechpartner_email || "";
                        const tel = item.ansprechpartner_telefon || "";
                        const hasAny = name || email || tel;
                        if (!hasAny) return <span className="text-sm text-[#a8a29e]">—</span>;
                        const line1 = name || (tel || email ? "Zentrale" : "—");
                        const line2 = [tel, email].filter(Boolean).join(" · ");
                        return (
                          <>
                            <p className="text-sm text-[#1c1917] truncate" title={name}>{line1}</p>
                            {line2 && (
                              <p className="text-xs text-[#78716c] truncate" title={line2}>{line2}</p>
                            )}
                          </>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      {istKunde ? (
                        <span
                          className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium"
                          style={{ backgroundColor: "#FDE9E1", color: "#B8410F" }}
                          data-testid={`kunde-badge-${item.id}`}
                        >
                          Kunde
                        </span>
                      ) : (
                        <span className="text-xs text-[#a8a29e]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-[#57534e] tabular-nums">{letzteBest}</TableCell>
                    <TableCell className="text-sm text-[#57534e]">{getVMName(item.vertriebsmitarbeiter_id)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {item.ansprechpartner_telefon && (
                          <button
                            type="button"
                            onClick={() => { setWaTarget(item); setWaDialogOpen(true); }}
                            className="inline-flex items-center justify-center h-7 w-7 rounded-md text-[#78716c] hover:text-[#D9775A] hover:bg-[#fef3ef] transition-colors"
                            title={item.whatsapp_last_sent_at ? `Bereits ${item.whatsapp_sent_count || 1}× per WhatsApp kontaktiert (${new Date(item.whatsapp_last_sent_at).toLocaleDateString('de-DE')}) — erneut senden?` : `WhatsApp senden an ${item.ansprechpartner_name || item.einrichtungsname}`}
                            data-testid={`btn-whatsapp-${item.id}`}
                          >
                            <MessageCircle size={16} />
                          </button>
                        )}
                        <button
                          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-[#78716c] hover:text-red-600 hover:bg-red-50 transition-colors"
                          onClick={() => handleDelete(item.id)}
                          data-testid={`delete-${item.id}`}
                          title="Löschen"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      )}


      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[95vh] flex flex-col bg-white">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>
              {editItem ? "Einrichtung bearbeiten" : "Neue Einrichtung"}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Einrichtungsname *</Label>
                <Input
                  value={formData.einrichtungsname}
                  onChange={(e) => setFormData({...formData, einrichtungsname: e.target.value})}
                  data-testid="input-einrichtungsname"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger data-testid="input-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="interessent">Interessent</SelectItem>
                    <SelectItem value="aktiv">Aktiv</SelectItem>
                    <SelectItem value="pausiert">Pausiert</SelectItem>
                    <SelectItem value="abgeschlossen">Abgeschlossen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Straße *</Label>
              <Input
                value={formData.strasse}
                onChange={(e) => setFormData({...formData, strasse: e.target.value})}
                data-testid="input-strasse"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PLZ *</Label>
                <Input
                  value={formData.plz}
                  onChange={(e) => setFormData({...formData, plz: e.target.value})}
                  data-testid="input-plz"
                />
              </div>
              <div>
                <Label>Ort *</Label>
                <Input
                  value={formData.ort}
                  onChange={(e) => setFormData({...formData, ort: e.target.value})}
                  data-testid="input-ort"
                />
              </div>
            </div>
            <div>
              <Label>MD-Prüfungsnote (AOK)</Label>
              <Select value={formData.md_pruefungsnote || "none"} onValueChange={(v) => setFormData({...formData, md_pruefungsnote: v === "none" ? null : v})}>
                <SelectTrigger data-testid="input-md-note">
                  <SelectValue placeholder="Keine Note" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keine Note</SelectItem>
                  <SelectItem value="sehr gut">sehr gut</SelectItem>
                  <SelectItem value="gut">gut</SelectItem>
                  <SelectItem value="befriedigend">befriedigend</SelectItem>
                  <SelectItem value="ausreichend">ausreichend</SelectItem>
                  <SelectItem value="mangelhaft">mangelhaft</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Ansprechpartner Name *</Label>
                <Input
                  value={formData.ansprechpartner_name}
                  onChange={(e) => setFormData({...formData, ansprechpartner_name: e.target.value})}
                  data-testid="input-ap-name"
                />
              </div>
              <div>
                <Label>E-Mail *</Label>
                <Input
                  type="email"
                  value={formData.ansprechpartner_email}
                  onChange={(e) => setFormData({...formData, ansprechpartner_email: e.target.value})}
                  data-testid="input-ap-email"
                />
              </div>
              <div>
                <Label>Telefon *</Label>
                <Input
                  value={formData.ansprechpartner_telefon}
                  onChange={(e) => setFormData({...formData, ansprechpartner_telefon: e.target.value})}
                  data-testid="input-ap-telefon"
                />
              </div>
            </div>
            
            {/* Vertriebsfelder */}
            <div className="border rounded-lg p-4 bg-blue-50/30 space-y-4">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Target size={16} className="text-blue-600" />
                Vertrieb & Akquise
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vertriebsmitarbeiter</Label>
                  <Select 
                    value={formData.vertriebsmitarbeiter_id || undefined} 
                    onValueChange={(v) => setFormData({...formData, vertriebsmitarbeiter_id: v})}
                    disabled={editItem?.vertriebsmitarbeiter_id && user?.email !== 'jean@dickies-helden.de'}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Auswählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {vertriebsmitarbeiter.filter(v => v.aktiv && v.id).map(vm => (
                        <SelectItem key={vm.id} value={vm.id}>{vm.name} ({vm.kuerzel})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editItem?.vertriebsmitarbeiter_id && user?.email !== 'jean@dickies-helden.de' ? (
                    <p className="text-xs text-amber-600 mt-1">Nur Jean Dickie kann Änderungen vornehmen</p>
                  ) : !editItem?.vertriebsmitarbeiter_id ? (
                    <p className="text-xs text-emerald-600 mt-1">✓ Kann bei Erstkontakt eingetragen werden</p>
                  ) : null}
                </div>
                <div>
                  <Label>Akquise-Status</Label>
                  <Select value={formData.akquise_status} onValueChange={(v) => setFormData({...formData, akquise_status: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="neu">Neu</SelectItem>
                      <SelectItem value="kontaktiert">Kontaktiert</SelectItem>
                      <SelectItem value="interessent">Interessent</SelectItem>
                      <SelectItem value="angebot_gesendet">Angebot erstellt</SelectItem>
                      <SelectItem value="kunde">Kunde</SelectItem>
                      <SelectItem value="abgesagt">Abgesagt</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#78716c] mt-1">Bei "Kunde" wird Neukunde-Status aktiviert</p>
                </div>
              </div>
            </div>

            {/* Fortbildungspaket */}
            <div className="border rounded-lg p-4 bg-violet-50/30 space-y-4">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Package size={16} className="text-violet-600" />
                Fortbildungspaket (Jahrespaket)
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Paket</Label>
                  <Select 
                    value={formData.fortbildungspaket || "keins"} 
                    onValueChange={(v) => setFormData({
                      ...formData, 
                      fortbildungspaket: v === "keins" ? null : v,
                      fortbildungspaket_start: v === "keins" ? "" : (formData.fortbildungspaket_start || new Date().toISOString().split('T')[0])
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Kein Paket" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="keins">Kein Paket</SelectItem>
                      <SelectItem value="klein">Klein (€1.200)</SelectItem>
                      <SelectItem value="gross">Groß (€1.900)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.fortbildungspaket && (
                  <div>
                    <Label>Gültig ab</Label>
                    <Input
                      type="date"
                      value={formData.fortbildungspaket_start || ""}
                      onChange={(e) => setFormData({...formData, fortbildungspaket_start: e.target.value})}
                    />
                    {formData.fortbildungspaket_start && (
                      <p className="text-xs text-violet-600 mt-1">
                        Läuft bis: {(() => {
                          const start = new Date(formData.fortbildungspaket_start);
                          const ende = new Date(start);
                          ende.setFullYear(ende.getFullYear() + 1);
                          return ende.toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'});
                        })()}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Nächste Wiedervorlage</Label>
              <Input
                type="date"
                value={formData.naechste_wiedervorlage}
                onChange={(e) => setFormData({...formData, naechste_wiedervorlage: e.target.value})}
                data-testid="input-wiedervorlage"
              />
            </div>
            <div>
              <Label>Interne Notizen</Label>
              <Textarea
                value={formData.interne_notizen}
                onChange={(e) => setFormData({...formData, interne_notizen: e.target.value})}
                rows={3}
                data-testid="input-notizen"
              />
            </div>
          
          {/* Zugehörige Kursteilnehmer - nur im Edit-Modus */}
          {editItem && (
            <div className="border rounded-lg p-4 bg-orange-50/30 space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <Users size={16} className="text-orange-600" />
                Kursteilnehmer dieser Einrichtung ({zugehoerigeKursteilnehmer.length})
              </h4>
              {loadingTeilnehmer ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin h-5 w-5 border-2 border-orange-500 rounded-full border-t-transparent"></div>
                </div>
              ) : zugehoerigeKursteilnehmer.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">Keine Kursteilnehmer zugeordnet</p>
              ) : (
                <div className="max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Name</TableHead>
                        <TableHead className="text-xs">Kurs</TableHead>
                        <TableHead className="text-xs">Start</TableHead>
                        <TableHead className="text-xs w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {zugehoerigeKursteilnehmer.map(tn => (
                        <TableRow 
                          key={tn.id} 
                          className="cursor-pointer hover:bg-orange-100"
                          onClick={() => {
                            setDialogOpen(false);
                            navigate(`/teilnehmer?id=${tn.id}`);
                          }}
                        >
                          <TableCell className="text-xs py-1">
                            {tn.name || `${tn.vorname || ''} ${tn.nachname || ''}`}
                          </TableCell>
                          <TableCell className="text-xs py-1">{tn.kurs || '-'}</TableCell>
                          <TableCell className="text-xs py-1">{tn.kurs_start || '-'}</TableCell>
                          <TableCell className="text-xs py-1">
                            <button
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  toast.info('Anmeldebestätigung wird erstellt...');
                                  const res = await axios.get(`${API}/pflegeeinrichtungen/${editItem.id}/anmeldebestaetigung/${tn.id}`, { responseType: 'blob' });
                                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `Anmeldebestaetigung_${(tn.nachname || tn.name || 'TN').replace(/\s+/g, '_')}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  toast.success('Anmeldebestätigung heruntergeladen');
                                } catch (err) {
                                  toast.error('Fehler beim Erstellen der Anmeldebestätigung');
                                }
                              }}
                              className="text-blue-500 hover:text-blue-700 p-1"
                              title="Anmeldebestätigung herunterladen"
                              data-testid={`anmeldebestaetigung-btn-${tn.id}`}
                            >
                              <FileText size={14} />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* Rechnungen dieser Einrichtung */}
          {editItem && (
            <div className="border rounded-lg p-4 bg-blue-50/30 space-y-3">
              <h4 className="font-medium text-sm flex items-center gap-2">
                <FileText size={16} className="text-blue-600" />
                Rechnungen dieser Einrichtung ({zugehoerigeRechnungen.length})
              </h4>
              {loadingRechnungen ? (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-500 rounded-full border-t-transparent"></div>
                </div>
              ) : zugehoerigeRechnungen.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-2">Keine Rechnungen vorhanden</p>
              ) : (
                <div className="max-h-48 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Rechnungsnr.</TableHead>
                        <TableHead className="text-xs">Datum</TableHead>
                        <TableHead className="text-xs text-right">Betrag</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {zugehoerigeRechnungen.map(rg => (
                        <TableRow 
                          key={rg.id} 
                          className="cursor-pointer hover:bg-blue-100"
                          onClick={() => {
                            setDialogOpen(false);
                            navigate(`/rechnungen`);
                          }}
                        >
                          <TableCell className="text-xs py-1 font-mono font-medium">{rg.rechnungsnummer}</TableCell>
                          <TableCell className="text-xs py-1">{rg.rechnungsdatum}</TableCell>
                          <TableCell className="text-xs py-1 text-right font-medium">{rg.endbetrag?.toLocaleString('de-DE')} €</TableCell>
                          <TableCell className="text-xs py-1">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              rg.zahlungsstatus === 'bezahlt' ? 'bg-emerald-100 text-emerald-700' :
                              rg.zahlungsstatus === 'teilweise_bezahlt' ? 'bg-yellow-100 text-yellow-700' :
                              rg.zahlungsstatus === 'storniert' ? 'bg-gray-100 text-gray-500' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {rg.zahlungsstatus === 'bezahlt' ? 'Bezahlt' :
                               rg.zahlungsstatus === 'teilweise_bezahlt' ? 'Teilweise' :
                               rg.zahlungsstatus === 'storniert' ? 'Storniert' : 'Offen'}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {/* Summe */}
                  <div className="flex justify-between items-center mt-2 pt-2 border-t text-sm">
                    <span className="text-gray-600">Gesamt:</span>
                    <span className="font-bold text-blue-700">
                      {zugehoerigeRechnungen.filter(r => r.zahlungsstatus !== 'storniert').reduce((sum, r) => sum + (r.endbetrag || 0), 0).toLocaleString('de-DE')} €
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          </div>
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={handleSubmit} data-testid="submit-einrichtung">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lead-Import-Dialog */}
      <Dialog open={importLeadsOpen} onOpenChange={(o) => { setImportLeadsOpen(o); if (!o) { setImportFile(null); setImportResult(null); } }}>
        <DialogContent className="max-w-md" data-testid="import-leads-dialog">
          <DialogHeader>
            <DialogTitle>Leads aus CSV importieren</DialogTitle>
            <DialogDescription>
              Erkannte Spalten (case-insensitive): <strong>Opportunity name / Pflegedienst / Firma</strong>, Contact Name, phone, email, Notes.
              Duplikate (gleicher Firmenname oder E-Mail) werden übersprungen — der Tag wird aber an die bestehenden ergänzt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">CSV-Datei</label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                className="block w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                data-testid="import-file-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700 block mb-1">Tag (z.B. &bdquo;Neuhaus&ldquo;)</label>
              <input
                type="text"
                value={importTag}
                onChange={(e) => setImportTag(e.target.value)}
                placeholder="Neuhaus"
                className="block w-full text-sm border border-gray-300 rounded px-2 py-1.5"
                data-testid="import-tag-input"
              />
              <p className="text-[10px] text-gray-500 mt-1">Wird zu jedem importierten und bei Duplikaten zu bestehenden Datensätzen hinzugefügt. Filtere danach mit dem Tag-Dropdown oben.</p>
            </div>
            {importResult && (
              <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 space-y-0.5">
                <div><strong>{importResult.imported}</strong> neu importiert</div>
                <div><strong>{importResult.tagged_existing}</strong> bestehende mit Tag &bdquo;{importResult.tag_applied}&ldquo; ergänzt</div>
                <div><strong>{importResult.skipped_duplicates}</strong> Duplikate übersprungen</div>
                {importResult.skipped_without_name > 0 && <div>{importResult.skipped_without_name} Zeilen ohne Firmenname übersprungen</div>}
                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="text-amber-800 mt-1">{importResult.errors.length} Fehler: {importResult.errors.slice(0,3).map(e => `Zeile ${e.row}`).join(", ")}</div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportLeadsOpen(false)} data-testid="import-close-btn">
              {importResult ? "Schließen" : "Abbrechen"}
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={!importFile || importing}
              onClick={async () => {
                if (!importFile) return;
                setImporting(true);
                setImportResult(null);
                try {
                  const fd = new FormData();
                  fd.append("file", importFile);
                  fd.append("tag", importTag || "");
                  const res = await axios.post(`${API}/pflegeeinrichtungen/import-leads-csv`, fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                  });
                  setImportResult(res.data);
                  toast.success(`${res.data.imported} neue Leads importiert, ${res.data.tagged_existing} bestehende getaggt`);
                  fetchData();
                } catch (e) {
                  toast.error(`Import fehlgeschlagen: ${e.response?.data?.detail || e.message}`);
                } finally {
                  setImporting(false);
                }
              }}
              data-testid="import-submit-btn"
            >
              {importing ? "Import läuft..." : importResult ? "Erneut importieren" : "Importieren"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick-Add Lead Dialog */}
      <Dialog open={quickAddOpen} onOpenChange={setQuickAddOpen}>
        <DialogContent className="max-w-lg" data-testid="quick-add-dialog">
          <DialogHeader>
            <DialogTitle>Lead schnell anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Beliebiger Text — Name, Firma, E-Mail, Telefon, Rolle, MA-Größe,
              Wiedervorlage-Notiz („Mitte August anrufen"). Wird automatisch geparst.
            </p>
            <textarea
              className="w-full border rounded-md p-3 text-sm font-mono min-h-[180px] focus:outline-none focus:ring-2 focus:ring-purple-400"
              placeholder={"Erik Dörnemann\nDie PflegeProfis\ninfo@die-pflegeprofis.net\n0171 1470842\nInhaber / Geschäftsführung\n10-30 MA\nMitte August anrufen"}
              value={quickAddText}
              onChange={(e) => setQuickAddText(e.target.value)}
              data-testid="quick-add-textarea"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setQuickAddOpen(false); setQuickAddText(""); }}>
              Abbrechen
            </Button>
            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              disabled={!quickAddText.trim() || quickAddSaving}
              onClick={async () => {
                setQuickAddSaving(true);
                try {
                  // Dry-Run zur Vorschau
                  const dry = await axios.post(
                    `${API}/pflegeeinrichtungen/quick-add`,
                    { dry_run: true, text: quickAddText }
                  );
                  const p = dry.data?.parsed || {};
                  const preview = [
                    `Firma: ${p.einrichtungsname || "(leer)"}`,
                    `Name:  ${p.ansprechpartner_name || "(leer)"}`,
                    `Mail:  ${p.ansprechpartner_email || "(leer)"}`,
                    `Tel:   ${p.ansprechpartner_telefon || "(leer)"}`,
                    p.naechste_wiedervorlage ? `Wiedervorlage: ${p.naechste_wiedervorlage}` : "",
                  ].filter(Boolean).join("\n");
                  if (!window.confirm(`Erkannte Daten:\n\n${preview}\n\nJetzt anlegen?`)) {
                    setQuickAddSaving(false);
                    return;
                  }
                  const res = await axios.post(
                    `${API}/pflegeeinrichtungen/quick-add`,
                    { text: quickAddText }
                  );
                  toast.success(`Lead angelegt: ${res.data?.parsed?.einrichtungsname || "OK"}`);
                  setQuickAddOpen(false);
                  setQuickAddText("");
                  fetchData();
                } catch (e) {
                  toast.error("Anlegen fehlgeschlagen",
                    { description: e?.response?.data?.detail || e.message });
                } finally {
                  setQuickAddSaving(false);
                }
              }}
              data-testid="quick-add-submit-btn"
            >
              {quickAddSaving ? "Anlegen..." : "Anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Atlas-Mail-Kampagne Dialog */}
      <Dialog open={atlasMailOpen} onOpenChange={setAtlasMailOpen}>
        <DialogContent className="max-w-xl" data-testid="atlas-mail-dialog">
          <DialogHeader>
            <DialogTitle>Delegations-Atlas Kampagne</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className={atlasRecipients?.error
                ? "bg-red-50 border border-red-300 rounded-md p-3 text-sm text-red-800"
                : "bg-rose-50 border border-rose-200 rounded-md p-3 text-sm"}>
              {atlasRecipients?.error ? (
                <>
                  <div className="font-medium">Empfängerliste konnte nicht geladen werden</div>
                  <div className="text-xs mt-1 break-words">{atlasRecipients.error}</div>
                  <Button size="sm" variant="outline" className="mt-2 h-7 text-xs"
                    onClick={() => {
                      setAtlasRecipients(null);
                      axios.post(`${API}/pflegeeinrichtungen/bulk-email-delegations-atlas`,
                        { dry_run: true }, { timeout: 30000 })
                        .then(r => setAtlasRecipients(r.data))
                        .catch(e => setAtlasRecipients({
                          error: e?.response?.data?.detail || e?.message || "Fehler",
                          recipient_count: 0,
                        }));
                    }}>
                    Neu laden
                  </Button>
                </>
              ) : (
                <>
                  <div><b>Empfänger:</b> {atlasRecipients?.recipient_count ?? "…"} Leads mit Tag „Delegations-Atlas"</div>
                  <div className="text-xs text-slate-600 mt-1">Bereits versendete werden übersprungen (Doppelversand-Schutz).</div>
                </>
              )}
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Absender</div>
              <div className="text-sm text-slate-700">Linda Lüppe (Dickies Campus) &lt;jean@dickies-helden.de&gt;</div>
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Inhalt (Zusammenfassung)</div>
              <ul className="text-xs text-slate-600 list-disc pl-5 space-y-1">
                <li>Danke fürs Runterladen des Delegations-Atlas</li>
                <li>Dickies Campus als AZAV-Bildungszentrum (Editorial-Design im Katalog-Stil)</li>
                <li>17 Weiterbildungen in 4 Lernpfaden (Einstieg / Erfahren / Führung / Pflicht)</li>
                <li>Jeden Montag Kursstart · 20% Rabatt Code <b>ATLAS20</b> auf erste Buchung</li>
                <li>Kontakt: 0211 – 29273330, Mo–Fr 9–17 Uhr</li>
                <li>Anhang: kompletter Kurskatalog 2026 (PDF, 8 Seiten)</li>
              </ul>
            </div>
            <div className="border-t pt-3">
              <div className="text-sm font-medium mb-1">Test-Mail an dich zuerst</div>
              <div className="flex gap-2">
                <input
                  type="email"
                  className="flex-1 border rounded-md px-3 py-2 text-sm"
                  value={atlasTestEmail}
                  onChange={(e) => setAtlasTestEmail(e.target.value)}
                  placeholder="deine@email.de"
                  data-testid="atlas-test-email-input"
                />
                <Button
                  variant="outline"
                  disabled={!atlasTestEmail || atlasSending}
                  onClick={async () => {
                    setAtlasSending(true);
                    try {
                      const r = await axios.post(
                        `${API}/pflegeeinrichtungen/bulk-email-delegations-atlas`,
                        { test_email: atlasTestEmail },
                        { timeout: 60000 }
                      );
                      if (r.data?.sent) toast.success(`Test-Mail an ${atlasTestEmail} verschickt`);
                      else toast.error("Test-Mail fehlgeschlagen", { description: r.data?.error || "" });
                    } catch (e) {
                      toast.error("Fehler", { description: e?.response?.data?.detail || e.message });
                    } finally { setAtlasSending(false); }
                  }}
                  data-testid="atlas-test-send-btn"
                >
                  {atlasSending ? "Sende..." : "Test senden"}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">Prüfe erst die Test-Mail — dann Bulk-Versand starten.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAtlasMailOpen(false)}>Abbrechen</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-700 text-white"
              disabled={atlasSending || !atlasRecipients?.recipient_count || !!atlasRecipients?.error}
              onClick={async () => {
                if (!window.confirm(
                  `Kampagne an ${atlasRecipients?.recipient_count} Empfänger versenden?\n\nDas kann nicht rückgängig gemacht werden.`
                )) return;
                setAtlasSending(true);
                try {
                  const r = await axios.post(
                    `${API}/pflegeeinrichtungen/bulk-email-delegations-atlas`, {},
                    { timeout: 600000 }
                  );
                  toast.success(
                    r.data?.status === "processing"
                      ? `${r.data?.queued_count || 0} Atlas-Mails laufen im Hintergrund · Bestätigung folgt per E-Mail`
                      : `${r.data?.sent_count} Mails verschickt · ${r.data?.failed_count || 0} Fehler`,
                    { duration: 12000 }
                  );
                  setAtlasMailOpen(false);
                } catch (e) {
                  toast.error("Bulk-Versand fehlgeschlagen",
                    { description: e?.response?.data?.detail || e.message });
                } finally { setAtlasSending(false); }
              }}
              data-testid="atlas-bulk-send-btn"
            >
              {atlasSending ? "Sende..." : `An alle senden (${atlasRecipients?.recipient_count ?? "?"})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sommer-Aktion 2026 · Bestandskunden · 25% Rabatt */}
      <Dialog open={sommerMailOpen} onOpenChange={setSommerMailOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="sommer-mail-dialog">
          <DialogHeader>
            <DialogTitle>Sommer-Aktion 2026 — Bestandskunden-Kampagne</DialogTitle>
          </DialogHeader>
          {sommerData?.error ? (
            <div className="bg-red-50 border border-red-300 rounded-md p-3 text-sm text-red-800">
              <div className="font-medium">Empfängerliste konnte nicht geladen werden</div>
              <div className="text-xs mt-1 break-words">{sommerData.error}</div>
            </div>
          ) : !sommerData ? (
            <div className="p-6 text-center text-sm text-slate-500">Empfänger werden geladen…</div>
          ) : (
            <div className="flex flex-col gap-3 overflow-hidden">
              {/* Zusammenfassung */}
              <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm grid grid-cols-2 md:grid-cols-4 gap-3">
                <div><div className="text-xs text-amber-700 uppercase tracking-wider">Kandidaten</div><div className="text-lg font-semibold text-amber-900">{sommerData.total}</div></div>
                <div><div className="text-xs text-amber-700 uppercase tracking-wider">Ausgewählt</div><div className="text-lg font-semibold text-amber-900" data-testid="sommer-selected-count">{sommerSelected.size}</div></div>
                <div><div className="text-xs text-amber-700 uppercase tracking-wider">Freemail-Risiko</div><div className="text-lg font-semibold text-orange-700">{sommerData.freemail_count}</div></div>
                <div><div className="text-xs text-amber-700 uppercase tracking-wider">Bereits verschickt</div><div className="text-lg font-semibold text-slate-500">{sommerData.already_sent_count}</div></div>
              </div>
              {/* Inhalt-Hinweis */}
              <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                <b>Inhalt:</b> Sommer-Aktion · 25 % Rabatt (Code <b>SOMMER25</b>) · gültig bis 31.08.2026 · für alle 17 Weiterbildungen · Kurskatalog als PDF-Anhang · Absender Markus Dickie.
              </div>
              {/* Suche + Bulk-Auswahl */}
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  placeholder="Suche (Name, Einrichtung, E-Mail)…"
                  value={sommerSearch}
                  onChange={(e) => setSommerSearch(e.target.value)}
                  className="flex-1 border rounded-md px-3 py-1.5 text-sm"
                  data-testid="sommer-search-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const all = new Set((sommerData.empfaenger || []).filter(e => !e.already_sent).map(e => e.id));
                    setSommerSelected(all);
                  }}
                  data-testid="sommer-select-all-btn"
                >Alle auswählen</Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSommerSelected(new Set())}
                  data-testid="sommer-select-none-btn"
                >Keine</Button>
              </div>
              {/* Empfängerliste */}
              <div className="flex-1 overflow-y-auto border rounded-md" style={{ maxHeight: "380px" }}>
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="p-2 text-left w-8"></th>
                      <th className="p-2 text-left">Einrichtung</th>
                      <th className="p-2 text-left">Ansprechpartner</th>
                      <th className="p-2 text-left">E-Mail</th>
                      <th className="p-2 text-center w-24">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(sommerData.empfaenger || [])
                      .filter(e => {
                        if (!sommerSearch.trim()) return true;
                        const q = sommerSearch.toLowerCase();
                        return (e.einrichtungsname || "").toLowerCase().includes(q)
                          || (e.ansprechpartner_name || "").toLowerCase().includes(q)
                          || (e.ansprechpartner_email || "").toLowerCase().includes(q);
                      })
                      .map(e => (
                        <tr key={e.id} className={`border-t hover:bg-amber-50 ${e.already_sent ? 'bg-slate-50 text-slate-400' : ''}`}>
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={sommerSelected.has(e.id)}
                              disabled={e.already_sent}
                              onChange={(ev) => {
                                const next = new Set(sommerSelected);
                                if (ev.target.checked) next.add(e.id); else next.delete(e.id);
                                setSommerSelected(next);
                              }}
                              data-testid={`sommer-check-${e.id}`}
                            />
                          </td>
                          <td className="p-2 truncate max-w-[220px]" title={e.einrichtungsname}>{e.einrichtungsname || <span className="text-slate-400">—</span>}</td>
                          <td className="p-2 truncate max-w-[180px]" title={e.ansprechpartner_name}>{e.ansprechpartner_name || <span className="text-slate-400">—</span>}</td>
                          <td className="p-2 truncate max-w-[220px] text-slate-600" title={e.ansprechpartner_email}>{e.ansprechpartner_email}</td>
                          <td className="p-2 text-center">
                            {e.already_sent && <span className="text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">✓ gesendet</span>}
                            {!e.already_sent && e.freemail && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded" title="Freemail-Provider — Zustellrisiko höher">Freemail</span>}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {/* Test-Mail */}
              <div className="border-t pt-3">
                <div className="text-sm font-medium mb-1">Test-Mail an dich zuerst (dringend empfohlen!)</div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    className="flex-1 border rounded-md px-3 py-2 text-sm"
                    value={sommerTestEmail}
                    onChange={(e) => setSommerTestEmail(e.target.value)}
                    placeholder="deine@email.de"
                    data-testid="sommer-test-email-input"
                  />
                  <Button
                    variant="outline"
                    disabled={!sommerTestEmail || sommerSending}
                    onClick={async () => {
                      setSommerSending(true);
                      try {
                        const r = await axios.post(
                          `${API}/pflegeeinrichtungen/bulk-email-sommeraktion`,
                          { test_email: sommerTestEmail },
                          { timeout: 60000 }
                        );
                        if (r.data?.sent) toast.success(`Test-Mail an ${sommerTestEmail} verschickt`);
                        else toast.error("Test-Mail fehlgeschlagen", { description: r.data?.error || "" });
                      } catch (e) {
                        toast.error("Fehler", { description: e?.response?.data?.detail || e.message });
                      } finally { setSommerSending(false); }
                    }}
                    data-testid="sommer-test-send-btn"
                  >
                    {sommerSending ? "Sende..." : "Test senden"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="border-t pt-3 mt-2">
            <Button variant="outline" onClick={() => setSommerMailOpen(false)}>Abbrechen</Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              disabled={sommerSending || sommerSelected.size === 0}
              onClick={async () => {
                const cnt = sommerSelected.size;
                const freemailCnt = (sommerData?.empfaenger || []).filter(e => sommerSelected.has(e.id) && e.freemail).length;
                if (!window.confirm(
                  `Sommer-Aktion an ${cnt} Bestandskunden senden?\n\n` +
                  (freemailCnt > 0 ? `Davon ${freemailCnt} Freemail-Adressen (t-online, gmx, web.de, yahoo, ...). Zustellrate erfahrungsgemäß niedriger — Empfänger sollten Spam-Ordner prüfen.\n\n` : "") +
                  `Das kann nicht rückgängig gemacht werden.`
                )) return;
                setSommerSending(true);
                try {
                  const r = await axios.post(
                    `${API}/pflegeeinrichtungen/bulk-email-sommeraktion`,
                    { recipient_ids: Array.from(sommerSelected), skip_already_sent: true },
                    { timeout: 1200000 }
                  );
                  toast.success(
                    r.data?.status === "processing"
                      ? `${r.data?.queued_count || 0} Sommer-Mails laufen im Hintergrund · Bestätigung folgt per E-Mail`
                      : `${r.data?.sent_count || 0} Mails verschickt · ${r.data?.failed_count || 0} Fehler`,
                    { duration: 12000 }
                  );
                  setSommerMailOpen(false);
                } catch (e) {
                  toast.error("Bulk-Versand fehlgeschlagen",
                    { description: e?.response?.data?.detail || e.message });
                } finally { setSommerSending(false); }
              }}
              data-testid="sommer-bulk-send-btn"
            >
              {sommerSending ? "Sende..." : `An ausgewählte senden (${sommerSelected.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp-Sender Dialog (Delegationsatlas & Co.) */}
      <WhatsAppSenderDialog
        open={waDialogOpen}
        onOpenChange={setWaDialogOpen}
        pe={waTarget}
        onSent={() => { fetchData(); toast.success("WhatsApp gesendet & vermerkt"); }}
      />
    </div>
  );
};

// ==================== TEILNEHMER ====================

// Kursart-Konfiguration (Morgen-/Abendkurs/E-Learning) – zentral definiert,
// damit Badge-Farben, Icons und Labels überall konsistent sind.
// Farbwelt orientiert sich an unserem "Quiet Luxury"-Look: Terra / Espresso / Cream.
const KURSZEIT_OPTIONS = [
  {
    key: "morgenkurs",
    label: "Morgenkurs",
    kurz: "Morgen",
    icon: Sunrise,
    // warmes Terra
    classes: "bg-[#FBEDE7] text-[#9A3412] border-[#EBC9B9]",
  },
  {
    key: "abendkurs",
    label: "Abendkurs",
    kurz: "Abend",
    icon: Moon,
    // dunkles Espresso
    classes: "bg-[#2a2118] text-[#FBEDE7] border-[#3a2f22]",
  },
  {
    key: "elearning",
    label: "E-Learning",
    kurz: "E-Learning",
    icon: Laptop,
    // Cream mit dezentem Rand
    classes: "bg-[#f8f4ec] text-[#5a4a35] border-[#e6d9c1]",
  },
];

// Kleine Anzeige der Kursart-Badges (read-only)
const KurszeitBadges = ({ kurszeiten, size = "sm" }) => {
  const list = Array.isArray(kurszeiten) ? kurszeiten : [];
  if (list.length === 0) return null;
  const isXs = size === "xs";
  return (
    <span className="inline-flex flex-wrap gap-1 align-middle">
      {KURSZEIT_OPTIONS.filter(o => list.includes(o.key)).map(o => {
        const Icon = o.icon;
        return (
          <span
            key={o.key}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-medium ${o.classes} ${isXs ? "text-[10px]" : "text-[11px]"}`}
            title={o.label}
          >
            <Icon size={isXs ? 10 : 11} strokeWidth={2.2} />
            {o.kurz}
          </span>
        );
      })}
    </span>
  );
};

// Quick-Toggle Popover – 3 Checkboxen, direkt in der Liste bedienbar
const KurszeitQuickToggle = ({ teilnehmer, onSaved }) => {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [selected, setSelected] = React.useState(
    Array.isArray(teilnehmer.kurszeiten) ? [...teilnehmer.kurszeiten] : []
  );

  React.useEffect(() => {
    if (open) {
      setSelected(Array.isArray(teilnehmer.kurszeiten) ? [...teilnehmer.kurszeiten] : []);
    }
  }, [open, teilnehmer.kurszeiten]);

  const toggle = (key) => {
    setSelected(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/teilnehmer/${teilnehmer.id}/kurszeiten`, { kurszeiten: selected });
      toast.success("Kursart aktualisiert");
      setOpen(false);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error("Fehler beim Speichern", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const hasBadges = (teilnehmer.kurszeiten || []).length > 0;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center justify-center rounded-md border transition ${hasBadges ? "px-1.5 py-1 border-transparent hover:border-[#e6d9c1] hover:bg-[#faf6ee]" : "w-6 h-6 border-dashed border-gray-300 text-gray-400 hover:text-[#9A3412] hover:border-[#EBC9B9] hover:bg-[#faf6ee]"}`}
          title="Kursart wählen"
          data-testid={`kurszeit-toggle-${teilnehmer.id}`}
        >
          {hasBadges ? (
            <KurszeitBadges kurszeiten={teilnehmer.kurszeiten} size="xs" />
          ) : (
            <Clock size={12} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-56 p-3"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Kursart</div>
        <div className="space-y-2">
          {KURSZEIT_OPTIONS.map(o => {
            const Icon = o.icon;
            const active = selected.includes(o.key);
            return (
              <label
                key={o.key}
                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border ${active ? o.classes : "bg-white border-gray-200 hover:bg-gray-50"}`}
              >
                <Checkbox
                  checked={active}
                  onCheckedChange={() => toggle(o.key)}
                  data-testid={`kurszeit-checkbox-${o.key}-${teilnehmer.id}`}
                />
                <Icon size={13} />
                <span className="text-sm">{o.label}</span>
              </label>
            );
          })}
        </div>
        <div className="mt-3 flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={saving}>
            Abbrechen
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid={`kurszeit-save-${teilnehmer.id}`}>
            {saving ? "..." : "Speichern"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const Teilnehmer = () => {
  const navigate = useNavigate();
  const { user: tnUser } = useAuth();
  const [seite, setSeite] = useState(1);
  const [proSeite, setProSeite] = useState(() => Number(localStorage.getItem("tn_pro_seite")) || 50);
  const [data, setData] = useState([]);
  const [einrichtungen, setEinrichtungen] = useState([]);
  const [massnahmen, setMassnahmen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeTab, setActiveTab] = useState("all"); // all, selbstzahler, einrichtung, bildungsgutschein
  const [einrichtungFilter, setEinrichtungFilter] = useState("all");
  // Filter für Kursart (Morgenkurs / Abendkurs / E-Learning) – Mehrfachauswahl möglich
  const [kurszeitFilter, setKurszeitFilter] = useState([]); // z.B. ["morgenkurs"]
  // BG-Sub-Filter (nur im BG-Tab sichtbar)
  const [bgAzavKursFilter, setBgAzavKursFilter] = useState("all"); // "all" | "lg" | "ambulant" | "pfh" | "ohne"
  const [bgStatusFilter, setBgStatusFilter] = useState("all"); // "all" | "laufend" | "abgeschlossen" | "geplant"
  const [bgZertFilter, setBgZertFilter] = useState("all"); // "all" | "ausgestellt" | "versendet" | "offen"
  const [sortField, setSortField] = useState("kurs_start");
  const [sortDir, setSortDir] = useState("desc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  useEffect(() => { setSeite(1); }, [search, activeTab, einrichtungFilter, kurszeitFilter, bgAzavKursFilter, bgStatusFilter, bgZertFilter, sortField, sortDir]);
  // Detail-Ansicht State
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedTeilnehmerId, setSelectedTeilnehmerId] = useState(null);
  // Pflegeeinrichtung Suche
  const [pflegeeinrichtungSearch, setPflegeeinrichtungSearch] = useState(null);
  // Dialog: Neue Pflegeeinrichtung mit Adresse + Ansprechpartner (statt window.prompt)
  const [newPeDialogOpen, setNewPeDialogOpen] = useState(false);
  const [newPeSaving, setNewPeSaving] = useState(false);
  const [newPeForm, setNewPeForm] = useState({
    einrichtungsname: "", strasse: "", plz: "", ort: "",
    ansprechpartner_name: "", ansprechpartner_email: "", ansprechpartner_telefon: "",
  });

  // Kursdaten-Sync (leere Top-Level `kurs`/`kurs_start` aus `kurse[]` befüllen)
  const [syncKurseOpen, setSyncKurseOpen] = useState(false);
  const [syncKurseLoading, setSyncKurseLoading] = useState(false);
  const [syncKurseResult, setSyncKurseResult] = useState(null); // { changes, would_update_count, ... }
  const [syncKurseApplying, setSyncKurseApplying] = useState(false);

  // Legacy-Zertifikatsnotiz-Cleanup (Ninox-Import-Rest "Zertifikat: 2025/600")
  const [zertCleanupOpen, setZertCleanupOpen] = useState(false);
  const [zertCleanupLoading, setZertCleanupLoading] = useState(false);
  const [zertCleanupResult, setZertCleanupResult] = useState(null);
  const [zertCleanupApplying, setZertCleanupApplying] = useState(false);

  // Bestanden-Flag-Sync in kurse[] (Bugfix für alt erfasste Prüfungen)
  const [bestandenSyncOpen, setBestandenSyncOpen] = useState(false);
  const [bestandenSyncLoading, setBestandenSyncLoading] = useState(false);
  const [bestandenSyncResult, setBestandenSyncResult] = useState(null);
  const [bestandenSyncApplying, setBestandenSyncApplying] = useState(false);

  // Kurs-ID/Name-Mismatch-Fix (z.B. Wundtherapeut mit kurs_id=wundexperte)
  const [kursIdFixOpen, setKursIdFixOpen] = useState(false);
  const [kursIdFixLoading, setKursIdFixLoading] = useState(false);
  const [kursIdFixResult, setKursIdFixResult] = useState(null);
  const [kursIdFixApplying, setKursIdFixApplying] = useState(false);

  const runKursIdFixPreview = async () => {
    setKursIdFixLoading(true);
    setKursIdFixResult(null);
    try {
      const res = await axios.post(`${API}/teilnehmer/detect-kurs-id-mismatch`, { dry_run: true });
      setKursIdFixResult(res.data);
    } catch (e) {
      toast.error("Vorschau fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    } finally {
      setKursIdFixLoading(false);
    }
  };

  const applyKursIdFix = async () => {
    if (!kursIdFixResult || (kursIdFixResult.would_update_count || 0) === 0) {
      toast.info("Nichts zu tun");
      return;
    }
    setKursIdFixApplying(true);
    try {
      const res = await axios.post(`${API}/teilnehmer/detect-kurs-id-mismatch`, { dry_run: false });
      toast.success(`${res.data.updated_count || 0} Teilnehmer korrigiert`, {
        description: "Kurs-ID passt jetzt zum Kurs-Namen — Teilnahmebescheinigung zeigt die korrekten Modul-Inhalte.",
      });
      setKursIdFixOpen(false);
      setKursIdFixResult(null);
      fetchData(search);
    } catch (e) {
      toast.error("Fix fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    } finally {
      setKursIdFixApplying(false);
    }
  };

  const runBestandenSyncPreview = async () => {
    setBestandenSyncLoading(true);
    setBestandenSyncResult(null);
    try {
      const res = await axios.post(`${API}/teilnehmer/sync-bestanden-to-kurse`, { dry_run: true });
      setBestandenSyncResult(res.data);
    } catch (e) {
      toast.error("Vorschau fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBestandenSyncLoading(false);
    }
  };

  const applyBestandenSync = async () => {
    if (!bestandenSyncResult || (bestandenSyncResult.would_update_count || 0) === 0) {
      toast.info("Nichts zu tun");
      return;
    }
    setBestandenSyncApplying(true);
    try {
      const res = await axios.post(`${API}/teilnehmer/sync-bestanden-to-kurse`, { dry_run: false });
      toast.success(`${res.data.updated_count || 0} Teilnehmer aktualisiert`, {
        description: "Bestanden-Flag aus Prüfung in kurse[] gespiegelt — Zertifikat-Druck sollte jetzt funktionieren.",
      });
      setBestandenSyncOpen(false);
      setBestandenSyncResult(null);
      fetchData(search);
    } catch (e) {
      toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    } finally {
      setBestandenSyncApplying(false);
    }
  };

  const runZertCleanupPreview = async () => {
    setZertCleanupLoading(true);
    setZertCleanupResult(null);
    try {
      const res = await axios.post(`${API}/teilnehmer/cleanup-legacy-zert-notes`, { dry_run: true });
      setZertCleanupResult(res.data);
    } catch (e) {
      toast.error("Vorschau fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    } finally {
      setZertCleanupLoading(false);
    }
  };

  const applyZertCleanup = async () => {
    if (!zertCleanupResult || (zertCleanupResult.would_update_count || 0) === 0) {
      toast.info("Nichts zu tun");
      return;
    }
    setZertCleanupApplying(true);
    try {
      const res = await axios.post(`${API}/teilnehmer/cleanup-legacy-zert-notes`, { dry_run: false });
      toast.success(`${res.data.updated_count || 0} Teilnehmer bereinigt`, {
        description: "Legacy-Zertifikatsnotizen aus Ninox entfernt.",
      });
      setZertCleanupOpen(false);
      setZertCleanupResult(null);
      fetchData(search);
    } catch (e) {
      toast.error("Cleanup fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    } finally {
      setZertCleanupApplying(false);
    }
  };
  
  const runSyncKursePreview = async () => {
    setSyncKurseLoading(true);
    setSyncKurseResult(null);
    try {
      const res = await axios.post(`${API}/teilnehmer/sync-kurse-toplevel`, { dry_run: true });
      setSyncKurseResult(res.data);
    } catch (e) {
      toast.error("Vorschau fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSyncKurseLoading(false);
    }
  };

  const applySyncKurse = async () => {
    if (!syncKurseResult || (syncKurseResult.would_update_count || 0) === 0) {
      toast.info("Nichts zu tun");
      return;
    }
    setSyncKurseApplying(true);
    try {
      const res = await axios.post(`${API}/teilnehmer/sync-kurse-toplevel`, { dry_run: false });
      toast.success(`${res.data.updated_count || 0} Teilnehmer aktualisiert`, {
        description: "Kursdaten wurden aus dem Kurse-Array in die Zusammenfassung übernommen.",
      });
      setSyncKurseOpen(false);
      setSyncKurseResult(null);
      // Liste neu laden
      fetchData(search);
    } catch (e) {
      toast.error("Sync fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSyncKurseApplying(false);
    }
  };
  
  // URL Query Parameter für direkten Teilnehmer-Zugriff
  const location = useLocation();
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const teilnehmerId = params.get('id');
    if (teilnehmerId && data.length > 0) {
      setSelectedTeilnehmerId(teilnehmerId);
      setDetailOpen(true);
      // URL bereinigen
      window.history.replaceState({}, '', '/teilnehmer');
    }
  }, [location.search, data]);
  
  // Counts für Tabs
  const tabCounts = {
    all: data.length,
    selbstzahler: data.filter(item => item.finanzierungsart === "selbstzahler").length,
    einrichtung: data.filter(item => item.finanzierungsart === "einrichtung").length,
    bildungsgutschein: data.filter(item => item.finanzierungsart === "bildungsgutschein").length,
  };
  
  // BG-spezifische Hilfsfunktionen
  const isExpiringSoon = (date) => {
    if (!date) return false;
    const ablauf = new Date(date);
    const heute = new Date();
    const diff = (ablauf - heute) / (1000 * 60 * 60 * 24);
    return diff <= 30 && diff >= 0;
  };
  
  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  // Sortierung
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const getSortedData = () => {
    // Erst filtern nach Tab-Filter + Einrichtungs-Filter + Suche
    let filtered = data.filter(item => {
      // Tab-Filter (ersetzt den alten finanzierungFilter)
      if (activeTab !== "all" && item.finanzierungsart !== activeTab) {
        return false;
      }
      
      // Einrichtungs-Filter (nur bei Einrichtung-Tab relevant)
      if (activeTab === "einrichtung" && einrichtungFilter !== "all" && item.pflegeeinrichtung_id !== einrichtungFilter) {
        return false;
      }

      // Kursart-Filter (Morgen/Abend/E-Learning) – Mehrfach → mindestens eine Übereinstimmung
      if (kurszeitFilter.length > 0) {
        const kz = Array.isArray(item.kurszeiten) ? item.kurszeiten : [];
        if (!kurszeitFilter.some(f => kz.includes(f))) {
          return false;
        }
      }
      
      // Suche
      if (search) {
        const searchLower = search.toLowerCase();
        const fullName = `${item.vorname || ""} ${item.nachname || ""} ${item.name || ""}`.toLowerCase();
        const email = (item.email || "").toLowerCase();
        const kurs = (item.kurs || "").toLowerCase();
        const gutscheinNr = (item.bildungsgutschein_nummer || "").toLowerCase();
        if (!fullName.includes(searchLower) && !email.includes(searchLower) && !kurs.includes(searchLower) && !gutscheinNr.includes(searchLower)) {
          return false;
        }
      }
      
      return true;
    });
    
    // Dann sortieren
    return filtered.sort((a, b) => {
      let valA, valB;
      if (sortField === "name") {
        valA = `${a.nachname || ""} ${a.vorname || ""}`.toLowerCase();
        valB = `${b.nachname || ""} ${b.vorname || ""}`.toLowerCase();
      } else if (sortField === "created_at" || sortField === "kurs_start" || sortField === "ablaufdatum") {
        // Datum-Sortierung
        const dateFieldA = sortField === "ablaufdatum" ? a.bildungsgutschein_ablaufdatum : a[sortField];
        const dateFieldB = sortField === "ablaufdatum" ? b.bildungsgutschein_ablaufdatum : b[sortField];
        valA = dateFieldA ? new Date(dateFieldA).getTime() : 0;
        valB = dateFieldB ? new Date(dateFieldB).getTime() : 0;
        if (sortDir === "asc") {
          return valA - valB;
        } else {
          return valB - valA;
        }
      } else {
        valA = (a[sortField] || "").toString().toLowerCase();
        valB = (b[sortField] || "").toString().toLowerCase();
      }
      if (sortDir === "asc") {
        return valA < valB ? -1 : valA > valB ? 1 : 0;
      } else {
        return valA > valB ? -1 : valA < valB ? 1 : 0;
      }
    });
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="ml-1 opacity-30" />;
    return sortDir === "asc" ? 
      <ChevronUp size={14} className="ml-1" /> : 
      <ChevronDown size={14} className="ml-1" />;
  };

  const [formData, setFormData] = useState({
    // Persönliche Daten
    vorname: "",
    nachname: "",
    email: "",
    telefon: "",
    mobil: "",
    geburtsdatum: "",
    geburtsort: "",
    // Adresse
    strasse: "",
    plz: "",
    ort: "",
    // Kurs & Finanzierung
    finanzierungsart: "selbstzahler",
    pflegeeinrichtung_id: "",
    massnahme_id: "",
    kurs: "",
    kurs_start: "",
    kurs_ende: "",
    // Nummern
    kundennummer: "",
    massnahmenummer: "",
    // Bildungsgutschein
    bildungsgutschein_vorhanden: false,
    bildungsgutschein_nummer: "",
    bildungsgutschein_ausstellende_stelle: "",
    bildungsgutschein_ausstellungsdatum: "",
    bildungsgutschein_ablaufdatum: "",
    bildungsgutschein_scan: "",
    // Weitere Felder
    praktikum_nachweis_erbracht: false,
    interne_notizen: "",
    // Prüfungsdaten
    zertifikatsnummer: "",
    gesamtpunkte: "",
    punkte_schriftlich: "",
    punkte_muendlich: "",
    punkte_facharbeit: "",
    pruefung_am: "",
    pruefung_2_am: "",
    bestanden: null
  });

  // Funktion um Detail-Ansicht zu öffnen
  const openDetail = (teilnehmerId) => {
    setSelectedTeilnehmerId(teilnehmerId);
    setDetailOpen(true);
  };

  // Statische Stammdaten (Massnahmen + Einrichtungen) nur 1x laden -
  // ändern sich nicht je Suche und sparen ~250KB pro Keystroke.
  const fetchStammdaten = useCallback(async () => {
    try {
      const [massnahmenRes, einrichtungenRes] = await Promise.all([
        axios.get(`${API}/massnahmen`),
        axios.get(`${API}/pflegeeinrichtungen`)
      ]);
      setMassnahmen(massnahmenRes.data);
      setEinrichtungen(einrichtungenRes.data || []);
    } catch (error) {
      console.error("Fehler beim Laden der Stammdaten:", error);
    }
  }, []);

  // Aktueller Such-Wert als Ref, damit fetchData() ohne Argument
  // (Aufrufe nach Save/Delete) immer den aktuellen Filter nutzt.
  const searchRef = useRef("");
  useEffect(() => { searchRef.current = search; }, [search]);

  const fetchData = useCallback(async (searchValue) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      const q = (searchValue ?? searchRef.current ?? "").trim();
      if (q) params.append("search", q);
      const teilnehmerRes = await axios.get(`${API}/teilnehmer?${params}`);
      setData(teilnehmerRes.data);
    } catch (error) {
      console.error("Fehler beim Laden:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  }, []);

  // Stammdaten 1x beim Mount
  useEffect(() => {
    fetchStammdaten();
  }, [fetchStammdaten]);

  // Debounced Search (300ms) - verhindert API-Call pro Keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      fetchData(search);
    }, search ? 300 : 0);
    return () => clearTimeout(handle);
  }, [search, fetchData]);

  const getEinrichtungName = (id) => {
    if (!id) return "-";
    const e = einrichtungen.find(e => e.id === id);
    return e ? e.einrichtungsname : "-";
  };

  const getMassnahmeName = (id) => {
    if (!id) return "-";
    const m = massnahmen.find(m => m.id === id);
    return m ? m.name : "-";
  };

  const validateForm = () => {
    if (!formData.vorname || !formData.nachname) {
      toast.error("Bitte Vorname und Nachname ausfüllen");
      return false;
    }
    // Geburtsdatum ist optional für einfaches Anlegen
    if (formData.finanzierungsart === "einrichtung" && !formData.pflegeeinrichtung_id) {
      toast.error("Bitte wählen Sie eine Pflegeeinrichtung");
      return false;
    }
    // Bei BG keine Pflichtfelder mehr - nur Warnung
    if (formData.finanzierungsart === "bildungsgutschein") {
      if (!formData.bildungsgutschein_nummer) {
        toast.warning("Hinweis: Bildungsgutschein-Nummer fehlt noch");
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (istUngueltig(formData.geburtsdatum)) {
      toast.error("Geburtsdatum ist unvollständig – bitte als TT.MM.JJJJ eingeben. Nichts gespeichert.");
      return;
    }
    
    try {
      const submitData = { ...formData };
      // NICHT mehr: Bei Selbstzahler keine Einrichtung
      // Jetzt kann jeder Teilnehmer einer Einrichtung zugeordnet werden
      
      // Bildungsgutschein-Felder setzen
      if (submitData.finanzierungsart === "bildungsgutschein") {
        submitData.bildungsgutschein_vorhanden = true;
      }
      
      // Leere Strings zu null konvertieren für Float-Felder (Pydantic erwartet float oder null, nicht "")
      const floatFields = ['punkte_schriftlich', 'punkte_muendlich', 'punkte_facharbeit'];
      floatFields.forEach(field => {
        if (submitData[field] === '' || submitData[field] === null) {
          submitData[field] = null;
        } else if (typeof submitData[field] === 'string') {
          submitData[field] = parseFloat(submitData[field]) || null;
        }
      });
      
      // Boolean-Felder: null oder undefined zu false
      if (submitData.bestanden === null || submitData.bestanden === undefined) {
        submitData.bestanden = false;
      }
      
      if (editItem) {
        await axios.put(`${API}/teilnehmer/${editItem.id}`, submitData);
        toast.success("Teilnehmer aktualisiert");
      } else {
        await axios.post(`${API}/teilnehmer`, submitData);
        toast.success("Teilnehmer erstellt");
      }
      setDialogOpen(false);
      setEditItem(null);
      resetForm();
      await fetchData();
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      // Pydantic ValidationError gibt ein Array zurück - handle das korrekt
      const detail = error.response?.data?.detail;
      let errorMessage = "Fehler beim Speichern";
      if (typeof detail === "string") {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(", ");
      } else if (detail?.msg) {
        errorMessage = detail.msg;
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/teilnehmer/${id}`);
        toast.success("Teilnehmer gelöscht");
        fetchData();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      // Persönliche Daten
      vorname: item.vorname || "",
      nachname: item.nachname || "",
      email: item.email || "",
      telefon: item.telefon || "",
      mobil: item.mobil || "",
      geburtsdatum: item.geburtsdatum || "",
      geburtsort: item.geburtsort || "",
      // Adresse
      strasse: item.strasse || "",
      plz: item.plz || "",
      ort: item.ort || "",
      // Kurs & Finanzierung
      finanzierungsart: item.finanzierungsart || "selbstzahler",
      pflegeeinrichtung_id: item.pflegeeinrichtung_id || "",
      massnahme_id: item.massnahme_id || "",
      kurs: item.kurs || "",
      kurs_start: item.kurs_start || "",
      kurs_ende: item.kurs_ende || "",
      // Nummern
      kundennummer: item.kundennummer || "",
      massnahmenummer: item.massnahmenummer || "",
      // Bildungsgutschein
      bildungsgutschein_vorhanden: item.bildungsgutschein_vorhanden || false,
      bildungsgutschein_nummer: item.bildungsgutschein_nummer || "",
      bildungsgutschein_ausstellende_stelle: item.bildungsgutschein_ausstellende_stelle || "",
      bildungsgutschein_ausstellungsdatum: item.bildungsgutschein_ausstellungsdatum || "",
      bildungsgutschein_ablaufdatum: item.bildungsgutschein_ablaufdatum || "",
      bildungsgutschein_scan: item.bildungsgutschein_scan || "",
      // Weitere Felder
      praktikum_nachweis_erbracht: item.praktikum_nachweis_erbracht || false,
      interne_notizen: item.interne_notizen || "",
      // Prüfungsdaten (wichtig!)
      zertifikatsnummer: item.zertifikatsnummer || "",
      gesamtpunkte: item.gesamtpunkte || "",
      punkte_schriftlich: item.punkte_schriftlich || "",
      punkte_muendlich: item.punkte_muendlich || "",
      punkte_facharbeit: item.punkte_facharbeit || "",
      pruefung_am: item.pruefung_am || "",
      pruefung_2_am: item.pruefung_2_am || "",
      bestanden: item.bestanden || null
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      // Persönliche Daten
      vorname: "",
      nachname: "",
      email: "",
      telefon: "",
      mobil: "",
      geburtsdatum: "",
      geburtsort: "",
      // Adresse
      strasse: "",
      plz: "",
      ort: "",
      // Kurs & Finanzierung
      finanzierungsart: "selbstzahler",
      pflegeeinrichtung_id: "",
      massnahme_id: "",
      kurs: "",
      kurs_start: "",
      kurs_ende: "",
      // Nummern
      kundennummer: "",
      massnahmenummer: "",
      // Bildungsgutschein
      bildungsgutschein_vorhanden: false,
      bildungsgutschein_nummer: "",
      bildungsgutschein_ausstellende_stelle: "",
      bildungsgutschein_ausstellungsdatum: "",
      bildungsgutschein_ablaufdatum: "",
      bildungsgutschein_scan: "",
      // Weitere Felder
      praktikum_nachweis_erbracht: false,
      interne_notizen: "",
      // Prüfungsdaten
      zertifikatsnummer: "",
      gesamtpunkte: "",
      punkte_schriftlich: "",
      punkte_muendlich: "",
      punkte_facharbeit: "",
      pruefung_am: "",
      pruefung_2_am: "",
      bestanden: null
    });
    setPflegeeinrichtungSearch(null);
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Konvertiere zu Base64 für direktes Speichern
    const reader = new FileReader();
    reader.onload = () => {
      setFormData(prev => ({
        ...prev,
        bildungsgutschein_scan: file.name
      }));
      toast.success(`Datei "${file.name}" ausgewählt`);
    };
    reader.readAsDataURL(file);
  };

  const handleExport = async () => {
    try {
      const ids = getSortedData().map((t) => t.id);
      const response = await axios.post(`${API}/export/teilnehmer`, { ids }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'teilnehmer.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Export erfolgreich (${ids.length} Teilnehmende)`);
    } catch (error) {
      toast.error(error.response?.status === 403 ? "Export nur für Geschäftsführung und Verwaltung" : "Fehler beim Export");
    }
  };

  const sortiert = getSortedData();
  const seitenGesamt = Math.max(1, Math.ceil(sortiert.length / proSeite));
  const aktSeite = Math.min(seite, seitenGesamt);
  const seitenDaten = sortiert.slice((aktSeite - 1) * proSeite, aktSeite * proSeite);

  return (
    <div className="page-container" data-testid="teilnehmer-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Teilnehmer</h1>
      </div>
      
      {/* Action Button */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={() => { setEditItem(null); resetForm(); setDialogOpen(true); }} data-testid="btn-new-teilnehmer">
          <Plus size={16} className="mr-2" />
          Neuer Teilnehmer
        </Button>
        {["gf", "verwaltung"].includes(tnUser?.role) && (
          <Button variant="outline" onClick={handleExport} data-testid="btn-export-teilnehmer" title="Exportiert genau die gefilterte Liste">
            <Download size={16} className="mr-2" />
            Excel-Export ({sortiert.length})
          </Button>
        )}
        <Button
          variant="outline"
          className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          onClick={() => { setSyncKurseOpen(true); setSyncKurseResult(null); runSyncKursePreview(); }}
          data-testid="btn-sync-kurse-toplevel"
          title="Fehlende Kursdaten in der Teilnehmer-Übersicht aus dem Detail-Kurse-Array übernehmen"
        >
          <RefreshCw size={16} className="mr-2" />
          Kursdaten synchronisieren
        </Button>
        <Button
          variant="outline"
          className="border-amber-300 text-amber-700 hover:bg-amber-50"
          onClick={() => { setZertCleanupOpen(true); setZertCleanupResult(null); runZertCleanupPreview(); }}
          data-testid="btn-cleanup-zert-notes"
          title="Alte Ninox-Notizen wie ‚Zertifikat: 2025/600' aus interne_notizen entfernen"
        >
          <XCircle size={16} className="mr-2" />
          Alt-Zertifikatsnotizen bereinigen
        </Button>
        <Button
          variant="outline"
          className="border-blue-300 text-blue-700 hover:bg-blue-50"
          onClick={() => { setBestandenSyncOpen(true); setBestandenSyncResult(null); runBestandenSyncPreview(); }}
          data-testid="btn-sync-bestanden-kurse"
          title="Bestanden-Flag aus Prüfung/Top-Level in kurse[]-Array spiegeln (Fix für Zertifikat-Druck-Fehler)"
        >
          <CheckCircle size={16} className="mr-2" />
          Bestanden-Flag reparieren
        </Button>
        <Button
          variant="outline"
          className="border-purple-300 text-purple-700 hover:bg-purple-50"
          onClick={() => { setKursIdFixOpen(true); setKursIdFixResult(null); runKursIdFixPreview(); }}
          data-testid="btn-fix-kurs-id-mismatch"
          title="Findet TN mit falscher kurs_id (z.B. Wundtherapeut mit kurs_id=wundexperte → falsche Bescheinigungs-Inhalte)"
        >
          <AlertTriangle size={16} className="mr-2" />
          Kurs-ID Mismatch fixen
        </Button>
        <Button
          variant="outline"
          className="border-rose-300 text-rose-700 hover:bg-rose-50"
          data-testid="btn-repair-kurs-namen"
          title="Füllt leere Kurs-Namen und Kurs-IDs in kurse[] aus dem Top-Level tn.kurs — Fix für leere Modulübersicht/Teilnahmebescheinigung bei Legacy-TN"
          onClick={async () => {
            try {
              toast.info("Analysiere Kurs-Namen-Lücken...");
              const dry = await axios.post(`${API}/teilnehmer/bulk-repair-kurs-namen?dry_run=true`);
              const c = dry.data.candidates || 0;
              if (c === 0) {
                toast.success("Alle Kurs-Namen sind bereits korrekt gesetzt");
                return;
              }
              if (!window.confirm(`${c} Teilnehmer mit fehlenden Kurs-Namen/IDs in kurse[] gefunden.\n\nJetzt automatisch reparieren?\n(Bereits gesetzte Werte werden NICHT überschrieben)`)) return;
              toast.info(`Repariere ${c} Datensätze...`);
              const app = await axios.post(`${API}/teilnehmer/bulk-repair-kurs-namen?dry_run=false`);
              toast.success(`${app.data.applied || 0} Teilnehmer repariert`, {
                description: "Modulübersicht und Teilnahmebescheinigung sollten jetzt korrekt gefüllt sein.",
              });
              fetchData(search);
            } catch (e) {
              toast.error("Repair fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
            }
          }}
        >
          <RefreshCw size={16} className="mr-2" />
          Kurs-Namen reparieren
        </Button>
        <Button
          variant="outline"
          className="border-amber-400 text-amber-700 hover:bg-amber-50"
          data-testid="btn-bulk-resign-mv"
          title="Regeneriert bereits signierte Maßnahmeverträge mit der KORREKTEN Signatur-Position (Fix 28.07.2026: Unterschrift landete unterhalb des Signaturfelds)"
          onClick={async () => {
            try {
              toast.info("Analysiere signierte Maßnahmeverträge...");
              const dry = await axios.post(`${API}/teilnehmer/bulk-resign-mv`, { dry_run: true, limit: 500 });
              const c = dry.data.total_kandidaten || 0;
              const ok = (dry.data.resigned || []).length;
              const skip = dry.data.skipped || [];
              if (c === 0) {
                toast.warning("Keine signierten MVs gefunden. Prüfe: kategorie='MV' + signed=true in db.teilnehmer_anhaenge", { duration: 8000 });
                return;
              }
              const skipTxt = skip.length > 0
                ? "\n\nÜbersprungen (keine Referenz-Signatur):\n" + skip.slice(0, 8).map(s => `  • ${s.name || s.teilnehmer_id}`).join("\n") + (skip.length > 8 ? `\n  ... (+${skip.length - 8})` : "")
                : "";
              const okTxt = (dry.data.resigned || []).slice(0, 8).map(r => `  • ${r.name}`).join("\n") + ((dry.data.resigned || []).length > 8 ? `\n  ... (+${(dry.data.resigned || []).length - 8})` : "");
              if (!window.confirm(
                `${c} signierte MVs gefunden.\n\n` +
                `✓ ${ok} können neu erzeugt werden:\n${okTxt}\n` +
                `${skipTxt}\n\n` +
                `Jetzt alle mit KORREKTER Signatur-Position neu erzeugen? (Alte PDFs werden ersetzt)`
              )) return;
              toast.info(`Regeneriere ${ok} Maßnahmeverträge...`);
              const app = await axios.post(`${API}/teilnehmer/bulk-resign-mv`, { dry_run: false, limit: 500 });
              const err = (app.data.errors || []);
              if (err.length > 0) {
                toast.error(`${(app.data.resigned || []).length} OK / ${err.length} Fehler`, {
                  description: err.slice(0, 3).map(e => `${e.teilnehmer_id?.slice(0, 8)}: ${e.reason}`).join(" | "),
                  duration: 12000,
                });
              } else {
                toast.success(`${(app.data.resigned || []).length} Maßnahmeverträge neu signiert`, {
                  description: `Bitte TN-Akte neu laden und PDF öffnen — Signatur sitzt jetzt auf der Signatur-Linie.`,
                  duration: 8000,
                });
              }
              fetchData(search);
            } catch (e) {
              toast.error("Bulk-Resign fehlgeschlagen", { description: e?.response?.data?.detail || e.message });
            }
          }}
        >
          <FileSignature size={16} className="mr-2" />
          MV neu signieren
        </Button>
      </div>
      
      {/* Tab Navigation */}
      <div className="teilnehmer-tabs">
        <button
          className={`teilnehmer-tab ${activeTab === "all" ? "active" : ""}`}
          onClick={() => setActiveTab("all")}
        >
          <span className="teilnehmer-tab-label">Alle</span>
          <span className="teilnehmer-tab-count">{tabCounts.all}</span>
        </button>
        <button
          className={`teilnehmer-tab ${activeTab === "selbstzahler" ? "active" : ""}`}
          onClick={() => setActiveTab("selbstzahler")}
        >
          <span className="teilnehmer-tab-label">Selbstzahler</span>
          <span className="teilnehmer-tab-count">{tabCounts.selbstzahler}</span>
        </button>
        <button
          className={`teilnehmer-tab ${activeTab === "einrichtung" ? "active" : ""}`}
          onClick={() => setActiveTab("einrichtung")}
        >
          <span className="teilnehmer-tab-label">Einrichtung</span>
          <span className="teilnehmer-tab-count">{tabCounts.einrichtung}</span>
        </button>
        <button
          className={`teilnehmer-tab ${activeTab === "bildungsgutschein" ? "active" : ""}`}
          onClick={() => setActiveTab("bildungsgutschein")}
        >
          <span className="teilnehmer-tab-label">Bildungsgutschein</span>
          <span className="teilnehmer-tab-count">{tabCounts.bildungsgutschein}</span>
        </button>
      </div>

      {/* Search & Filters */}
      <Card className="card-custom mb-6">
        <CardContent className="p-4">
          <div className="filter-bar">
            <div className="search-box">
              <Search className="search-icon" size={18} />
              <input
                placeholder={activeTab === "bildungsgutschein" ? "Name oder Gutschein-Nr. suchen..." : "Suchen..."}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="search-teilnehmer"
              />
            </div>
            {activeTab === "einrichtung" && (
              <Select value={einrichtungFilter} onValueChange={setEinrichtungFilter}>
                <SelectTrigger className="w-[220px]" data-testid="einrichtung-filter">
                  <SelectValue placeholder="Einrichtung filtern" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle Einrichtungen</SelectItem>
                  {einrichtungen.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.einrichtungsname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {/* Kursart-Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  data-testid="kurszeit-filter-btn"
                >
                  <Clock size={14} />
                  Kursart
                  {kurszeitFilter.length > 0 && (
                    <span className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#9A3412] text-white text-[10px] font-bold">
                      {kurszeitFilter.length}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="start">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Kursart filtern</span>
                  {kurszeitFilter.length > 0 && (
                    <button
                      onClick={() => setKurszeitFilter([])}
                      className="text-[11px] text-gray-500 hover:text-[#9A3412] underline"
                      data-testid="kurszeit-filter-reset"
                    >
                      zurücksetzen
                    </button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {KURSZEIT_OPTIONS.map(o => {
                    const Icon = o.icon;
                    const active = kurszeitFilter.includes(o.key);
                    return (
                      <label
                        key={o.key}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer border ${active ? o.classes : "bg-white border-gray-200 hover:bg-gray-50"}`}
                      >
                        <Checkbox
                          checked={active}
                          onCheckedChange={() => {
                            setKurszeitFilter(prev =>
                              prev.includes(o.key) ? prev.filter(k => k !== o.key) : [...prev, o.key]
                            );
                          }}
                          data-testid={`kurszeit-filter-${o.key}`}
                        />
                        <Icon size={13} />
                        <span className="text-sm">{o.label}</span>
                      </label>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            <span className="text-sm text-gray-500 ml-auto">
              {getSortedData().length} Einträge
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
            </div>
          ) : getSortedData().length === 0 ? (
            <div className="empty-state-modern">
              <div className="icon-wrapper">
                <Users />
              </div>
              <h3>Keine Teilnehmer gefunden</h3>
              <p>
                {search ? "Versuchen Sie eine andere Suche" : `Noch keine ${activeTab === "all" ? "" : activeTab === "selbstzahler" ? "Selbstzahler" : activeTab === "einrichtung" ? "Einrichtungs-Teilnehmer" : "Bildungsgutschein-Teilnehmer"} vorhanden`}
              </p>
            </div>
          ) : (
            <Table data-testid="teilnehmer-table" className="teilnehmer-table">
              <TableHeader>
                <TableRow className="bg-[#faf9f7]">
                  <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort("name")}>
                    <div className="flex items-center">Name <SortIcon field="name" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort("kurs_start")}>
                    <div className="flex items-center">Kurs / Start <SortIcon field="kurs_start" /></div>
                  </TableHead>
                  {activeTab === "all" && (
                    <TableHead>Typ</TableHead>
                  )}
                  {(activeTab === "all" || activeTab === "einrichtung") && (
                    <TableHead>Pflegeeinrichtung</TableHead>
                  )}
                  {activeTab === "bildungsgutschein" && (
                    <>
                      <TableHead>Gutschein-Nr.</TableHead>
                      <TableHead className="cursor-pointer hover:bg-gray-50" onClick={() => handleSort("ablaufdatum")}>
                        <div className="flex items-center">Ablaufdatum <SortIcon field="ablaufdatum" /></div>
                      </TableHead>
                      <TableHead>Scan</TableHead>
                    </>
                  )}
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {seitenDaten.map((item) => (
                  <TableRow key={item.id} className="cursor-pointer" onClick={() => openDetail(item.id)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className={`avatar-circle ${item.finanzierungsart === "selbstzahler" ? "sz" : item.finanzierungsart === "einrichtung" ? "einrichtung" : "bg"}`}>
                          {(item.vorname || "?")[0]}{(item.nachname || "?")[0]}
                        </div>
                        <div>
                          <span className="teilnehmer-name">{item.vorname || ""} {item.nachname || ""}</span>
                          {item.einwilligung_whatsapp_gruppe && (
                            <span
                              className="inline-flex items-center gap-1 ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-100 text-green-700 border border-green-200 align-middle"
                              title="Teilnehmer hat der WhatsApp-Kursgruppe zugestimmt"
                              data-testid={`wa-gruppe-badge-${item.id}`}
                            >
                              <MessageCircle size={10} strokeWidth={2.5} /> WA-Gruppe
                            </span>
                          )}
                          <span className="ml-2 inline-flex align-middle" onClick={(e) => e.stopPropagation()}>
                            <KurszeitQuickToggle
                              teilnehmer={item}
                              onSaved={() => fetchData(search)}
                            />
                          </span>
                          {item.email && <p className="text-xs text-gray-400 truncate max-w-[180px]">{item.email}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        {(() => {
                          // Anzeige-Fallback: Wenn Top-Level `kurs`/`kurs_start` leer sind,
                          // aus kurse[]-Array den aktiven bzw. ersten Eintrag nehmen.
                          // (Dieses Muster tritt auf, wenn ein Kurs nur über das Kurse-Array
                          //  angelegt wurde und nicht die Legacy-Top-Level-Felder syncen.)
                          const kursName = item.kurs || (item.kurse?.[0]?.kurs_name) || "";
                          const kursStart = item.kurs_start || (item.kurse?.[0]?.start) || "";
                          return (
                            <>
                              <p className="font-medium text-sm truncate max-w-[180px]">{kursName || "-"}</p>
                              {kursStart && <p className="text-xs text-gray-400">{kursStart}</p>}
                            </>
                          );
                        })()}
                      </div>
                    </TableCell>
                    {activeTab === "all" && (
                      <TableCell>
                        <span className={`status-pill ${item.finanzierungsart === "selbstzahler" ? "sz" : item.finanzierungsart === "einrichtung" ? "einrichtung" : "bg"}`}>
                          {item.finanzierungsart === "selbstzahler" ? "Selbstzahler" : item.finanzierungsart === "einrichtung" ? "Einrichtung" : "BG"}
                        </span>
                      </TableCell>
                    )}
                    {(activeTab === "all" || activeTab === "einrichtung") && (
                      <TableCell>
                        {item.pflegeeinrichtung_id ? (
                          <button
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline truncate max-w-[200px] text-left"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/pflegeeinrichtungen?id=${item.pflegeeinrichtung_id}`);
                            }}
                          >
                            {getEinrichtungName(item.pflegeeinrichtung_id)}
                          </button>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                    )}
                    {activeTab === "bildungsgutschein" && (
                      <>
                        <TableCell>
                          {item.bildungsgutschein_nummer ? (
                            <span className="gutschein-nummer">{item.bildungsgutschein_nummer}</span>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          {item.bildungsgutschein_ablaufdatum ? (
                            <span className={`status-pill ${isExpired(item.bildungsgutschein_ablaufdatum) ? "abgelaufen" : isExpiringSoon(item.bildungsgutschein_ablaufdatum) ? "bald-ablaufend" : "gueltig"}`}>
                              {item.bildungsgutschein_ablaufdatum}
                            </span>
                          ) : "-"}
                        </TableCell>
                        <TableCell>
                          <span className={`scan-status ${item.bildungsgutschein_scan ? "vorhanden" : "fehlt"}`}>
                            {item.bildungsgutschein_scan ? (
                              <><Check size={14} /> Vorhanden</>
                            ) : (
                              <><CircleX size={14} /> Fehlt</>
                            )}
                          </span>
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      <StatusBadge status={item.status || "aktiv"} />
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <button className="action-btn" onClick={() => openDetail(item.id)} title="Details anzeigen">
                        <Eye size={16} />
                      </button>
                      <button className="action-btn" onClick={() => handleEdit(item)} title="Bearbeiten">
                        <Pencil size={16} />
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(item.id)} title="Löschen">
                        <Trash2 size={16} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!loading && sortiert.length > 0 && (
            <TnSeitenLeiste seite={aktSeite} seitenGesamt={seitenGesamt} gesamt={sortiert.length} proSeite={proSeite}
              onSeite={setSeite} onProSeite={(n) => { setProSeite(n); setSeite(1); localStorage.setItem("tn_pro_seite", String(n)); }} />
          )}
        </CardContent>
      </Card>

      {/* Teilnehmer Detail Dialog */}
      <TeilnehmerDetail
        teilnehmerId={selectedTeilnehmerId}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUpdate={fetchData}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>
              {editItem ? "Teilnehmer bearbeiten" : "Neuer Teilnehmer"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4 max-h-[70vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vorname *</Label>
                <Input
                  value={formData.vorname}
                  onChange={(e) => setFormData({...formData, vorname: e.target.value})}
                  data-testid="input-vorname"
                />
              </div>
              <div>
                <Label>Nachname *</Label>
                <Input
                  value={formData.nachname}
                  onChange={(e) => setFormData({...formData, nachname: e.target.value})}
                  data-testid="input-nachname"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Geburtsdatum</Label>
                <GeburtsdatumInput
                  value={formData.geburtsdatum}
                  onChange={(v) => setFormData({...formData, geburtsdatum: v})}
                />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input
                  value={formData.telefon}
                  onChange={(e) => setFormData({...formData, telefon: e.target.value})}
                  placeholder="z.B. 0151 12345678"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>E-Mail</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="z.B. max@beispiel.de"
                />
              </div>
              <div>
                <Label>Straße, Nr.</Label>
                <Input
                  value={formData.strasse}
                  onChange={(e) => setFormData({...formData, strasse: e.target.value})}
                  placeholder="z.B. Musterstraße 123"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>PLZ</Label>
                <Input
                  value={formData.plz}
                  onChange={(e) => setFormData({...formData, plz: e.target.value})}
                  placeholder="z.B. 40210"
                />
              </div>
              <div>
                <Label>Ort</Label>
                <Input
                  value={formData.ort}
                  onChange={(e) => setFormData({...formData, ort: e.target.value})}
                  placeholder="z.B. Düsseldorf"
                />
              </div>
            </div>

            <hr className="my-2" />
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Finanzierungsart *</Label>
                <Select 
                  value={formData.finanzierungsart} 
                  onValueChange={(v) => setFormData({
                    ...formData, 
                    finanzierungsart: v,
                    pflegeeinrichtung_id: v === "selbstzahler" ? "" : formData.pflegeeinrichtung_id
                  })}
                >
                  <SelectTrigger data-testid="input-finanzierung">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="selbstzahler">Selbstzahler</SelectItem>
                    <SelectItem value="einrichtung">Einrichtung</SelectItem>
                    <SelectItem value="bildungsgutschein">Bildungsgutschein</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Maßnahme/Kurs</Label>
                <Select 
                  value={formData.massnahme_id || "none"} 
                  onValueChange={(v) => {
                    const m = massnahmen.find(x => x.id === v);
                    setFormData({
                      ...formData, 
                      massnahme_id: v === "none" ? "" : v,
                      kurs: m ? m.name : ""
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Maßnahme wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keine Maßnahme</SelectItem>
                    {massnahmen
                      .filter(m => {
                        // Bei Bildungsgutschein nur bestimmte Maßnahmen zeigen
                        if (formData.finanzierungsart === "bildungsgutschein") {
                          const name = m.name.toLowerCase();
                          return (
                            name.includes("pflegehilfskraft") ||
                            name.includes("ambulanter pflegeassistent") ||
                            (name.includes("pflegeassistent") && name.includes("lg1") && name.includes("lg2"))
                          );
                        }
                        return true;
                      })
                      .map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))
                    }
                  </SelectContent>
                </Select>
                {formData.finanzierungsart === "bildungsgutschein" && (
                  <p className="text-xs text-blue-600 mt-1">Nur BG-fähige Maßnahmen werden angezeigt</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Kursstart</Label>
                <Input
                  type="date"
                  value={formData.kurs_start}
                  onChange={(e) => setFormData({...formData, kurs_start: e.target.value})}
                />
              </div>
              <div>
                <Label className="flex items-center justify-between">
                  <span>Kursende</span>
                  <button
                    type="button"
                    disabled={!formData.kurs_start || !formData.kurs}
                    onClick={async () => {
                      try {
                        const r = await axios.get(`${API}/kurs-tools/berechne-ende`, {
                          params: { start_datum: formData.kurs_start, kurs: formData.kurs }
                        });
                        setFormData({...formData, kurs_ende: r.data.ende_datum});
                        toast.success(`Ende berechnet: ${r.data.ende_datum} (${r.data.soll_unterrichtstage} Unterrichtstage${r.data.praktikum_tage ? ` + ${r.data.praktikum_tage} Praktikumstage = ${r.data.kurstage_gesamt} Tage` : ''}, ${r.data.wochen} Wochen, ${r.data.feiertage_uebersprungen.length} Feiertag(e) übersprungen)`, { duration: 6000 });
                      } catch (e) {
                        toast.error(e.response?.data?.detail || "Konnte Ende nicht berechnen — Kurs unbekannt?");
                      }
                    }}
                    className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title={!formData.kurs_start ? "Erst Kursstart wählen" : (!formData.kurs ? "Erst Kurs wählen" : "Auto-berechnen aus Soll-Tagen + NRW-Feiertage")}
                    data-testid="auto-ende-btn-1"
                  >
                    Auto
                  </button>
                </Label>
                <Input
                  type="date"
                  value={formData.kurs_ende}
                  onChange={(e) => setFormData({...formData, kurs_ende: e.target.value})}
                />
              </div>
            </div>

            {/* Praktikumsnachweis - nur bei Kursen die es erfordern */}
            {(() => {
              const selectedKurs = VERTRIEB_KURSE.find(k => k.name === formData.kurs || k.id === formData.kurs);
              const needsPraktikum = selectedKurs?.praktikum && selectedKurs?.praktikum.includes("Monat");
              if (needsPraktikum) {
                return (
                  <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="praktikum_nachweis"
                        checked={formData.praktikum_nachweis_erbracht}
                        onCheckedChange={(checked) => setFormData({...formData, praktikum_nachweis_erbracht: checked})}
                      />
                      <label htmlFor="praktikum_nachweis" className="text-sm cursor-pointer">
                        <span className="font-medium">Praktikumsnachweis erbracht</span>
                        <span className="text-purple-600 ml-2">({selectedKurs?.praktikum})</span>
                      </label>
                    </div>
                    <p className="text-xs text-purple-600 mt-1">
                      Zertifikat kann erst nach Praktikumsnachweis ausgestellt werden
                    </p>
                  </div>
                );
              }
              return null;
            })()}

            {/* Pflegeeinrichtung - mit Suchfunktion */}
            <div>
              <Label>
                Pflegeeinrichtung {formData.finanzierungsart === "einrichtung" ? "*" : "(optional)"}
              </Label>
              <div className="relative">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Input
                      placeholder="Einrichtung suchen..."
                      value={pflegeeinrichtungSearch || (formData.pflegeeinrichtung_id ? einrichtungen.find(e => e.id === formData.pflegeeinrichtung_id)?.einrichtungsname || "" : "")}
                      onChange={(e) => {
                        setPflegeeinrichtungSearch(e.target.value);
                        if (!e.target.value) {
                          setFormData({...formData, pflegeeinrichtung_id: ""});
                        }
                      }}
                      onFocus={() => setPflegeeinrichtungSearch(pflegeeinrichtungSearch || "")}
                      data-testid="input-pflegeeinrichtung"
                      className="pr-8"
                    />
                    {formData.pflegeeinrichtung_id && (
                      <button
                        type="button"
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        onClick={() => {
                          setFormData({...formData, pflegeeinrichtung_id: ""});
                          setPflegeeinrichtungSearch("");
                        }}
                      >
                        <XCircle size={16} />
                      </button>
                    )}
                  </div>
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={() => {
                      setNewPeForm({
                        einrichtungsname: (pflegeeinrichtungSearch || "").trim(),
                        strasse: "", plz: "", ort: "",
                        ansprechpartner_name: "", ansprechpartner_email: "", ansprechpartner_telefon: "",
                      });
                      setNewPeDialogOpen(true);
                    }}
                    title="Neue Pflegeeinrichtung anlegen"
                    data-testid="new-pe-open-btn"
                  >
                    <Plus size={16} />
                  </Button>
                </div>
                {/* Suchergebnisse Dropdown */}
                {pflegeeinrichtungSearch !== null && pflegeeinrichtungSearch !== undefined && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <div 
                      className="p-2 hover:bg-gray-100 cursor-pointer text-gray-500 border-b"
                      onClick={() => {
                        setFormData({...formData, pflegeeinrichtung_id: ""});
                        setPflegeeinrichtungSearch(null);
                      }}
                    >
                      Keine Einrichtung
                    </div>
                    {einrichtungen
                      .filter(e => {
                        if (!pflegeeinrichtungSearch) return true;
                        const search = pflegeeinrichtungSearch.toLowerCase();
                        return e.einrichtungsname?.toLowerCase().includes(search) || 
                               e.ort?.toLowerCase().includes(search) ||
                               e.plz?.includes(search);
                      })
                      .slice(0, 15)
                      .map(e => (
                        <div
                          key={e.id}
                          className={`p-2 hover:bg-emerald-50 cursor-pointer ${formData.pflegeeinrichtung_id === e.id ? 'bg-emerald-100' : ''}`}
                          onClick={() => {
                            setFormData({...formData, pflegeeinrichtung_id: e.id});
                            setPflegeeinrichtungSearch(null);
                          }}
                        >
                          <div className="font-medium text-sm">{e.einrichtungsname}</div>
                          {e.ort && <div className="text-xs text-gray-500">{e.plz} {e.ort}</div>}
                        </div>
                      ))
                    }
                    {pflegeeinrichtungSearch && einrichtungen.filter(e => 
                      e.einrichtungsname?.toLowerCase().includes(pflegeeinrichtungSearch.toLowerCase()) || 
                      e.ort?.toLowerCase().includes(pflegeeinrichtungSearch.toLowerCase())
                    ).length === 0 && (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        Keine Einrichtung gefunden
                      </div>
                    )}
                  </div>
                )}
              </div>
              {formData.pflegeeinrichtung_id && (
                <p className="text-xs text-emerald-600 mt-1">
                  ✓ {einrichtungen.find(e => e.id === formData.pflegeeinrichtung_id)?.einrichtungsname}
                </p>
              )}
            </div>

            {/* Bildungsgutschein Felder */}
            {formData.finanzierungsart === "bildungsgutschein" && (
              <div className="border rounded-lg p-4 bg-amber-50/50 space-y-4">
                <h4 className="font-medium text-sm flex items-center gap-2">
                  <Ticket size={16} className="text-amber-600" />
                  Bildungsgutschein Details (AZAV-Pflicht)
                </h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Gutschein-Nummer *</Label>
                    <Input
                      value={formData.bildungsgutschein_nummer}
                      onChange={(e) => setFormData({...formData, bildungsgutschein_nummer: e.target.value})}
                      placeholder="z.B. BG-2025-12345"
                      data-testid="input-gutschein-nummer"
                    />
                  </div>
                  <div>
                    <Label>Ausstellende Stelle *</Label>
                    <Input
                      value={formData.bildungsgutschein_ausstellende_stelle}
                      onChange={(e) => setFormData({...formData, bildungsgutschein_ausstellende_stelle: e.target.value})}
                      placeholder="z.B. Agentur für Arbeit Berlin"
                      data-testid="input-gutschein-stelle"
                    />
                  </div>
                </div>
                
                {/* KD-Nr und Maßnahmenummer */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>KD-Nr (Kundennummer)</Label>
                    <Input
                      value={formData.kundennummer}
                      onChange={(e) => setFormData({...formData, kundennummer: e.target.value})}
                      placeholder="z.B. 123456789"
                      data-testid="input-kundennummer"
                    />
                  </div>
                  <div>
                    <Label>Maßnahmenummer</Label>
                    <Input
                      value={formData.massnahmenummer}
                      onChange={(e) => setFormData({...formData, massnahmenummer: e.target.value})}
                      placeholder="z.B. 364 1013 2026"
                      data-testid="input-massnahmenummer"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Ausstellungsdatum</Label>
                    <Input
                      type="date"
                      value={formData.bildungsgutschein_ausstellungsdatum}
                      onChange={(e) => setFormData({...formData, bildungsgutschein_ausstellungsdatum: e.target.value})}
                      data-testid="input-gutschein-ausstellung"
                    />
                  </div>
                  <div>
                    <Label>Ablaufdatum *</Label>
                    <Input
                      type="date"
                      value={formData.bildungsgutschein_ablaufdatum}
                      onChange={(e) => setFormData({...formData, bildungsgutschein_ablaufdatum: e.target.value})}
                      data-testid="input-gutschein-ablauf"
                    />
                  </div>
                </div>
                <p className="text-xs text-[#78716c]">
                  Bei Ablauf in 30 Tagen wird automatisch eine Wiedervorlage erstellt. AZAV-Sperre: Gutschein muss am Kursstart gültig sein.
                </p>
                
                <div>
                  <Label>Scan Bildungsgutschein *</Label>
                  <div className="flex gap-2 items-center">
                    <Input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="hidden"
                      data-testid="input-gutschein-file"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      className="flex-1"
                    >
                      <Upload size={16} className="mr-2" />
                      {formData.bildungsgutschein_scan || editItem?.bildungsgutschein_scan || "Datei auswählen"}
                    </Button>
                  </div>
                  {(formData.bildungsgutschein_scan || editItem?.bildungsgutschein_scan) && (
                    <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                      <FileText size={12} />
                      {formData.bildungsgutschein_scan || editItem?.bildungsgutschein_scan}
                    </p>
                  )}
                  <p className="text-xs text-red-600 mt-1">
                    AZAV-Sperre: Kein Kursstart ohne Scan möglich.
                  </p>
                </div>
              </div>
            )}

            {/* Maßnahme/Kurs Zuordnung */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold mb-3 block">Kurs/Maßnahme Zuordnung</Label>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <Label>Maßnahme auswählen</Label>
                  <Select 
                    value={formData.massnahme_id || "none"} 
                    onValueChange={(v) => {
                      const massnahme = massnahmen.find(m => m.id === v);
                      setFormData({
                        ...formData, 
                        massnahme_id: v === "none" ? "" : v,
                        kurs: massnahme ? massnahme.name : formData.kurs,
                        kurs_start: massnahme?.startdatum || formData.kurs_start,
                        kurs_ende: massnahme?.enddatum || formData.kurs_ende
                      });
                    }}
                  >
                    <SelectTrigger data-testid="input-massnahme">
                      <SelectValue placeholder="Maßnahme wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Keine Maßnahme</SelectItem>
                      {massnahmen.map(m => (
                        <SelectItem key={m.id} value={m.id}>
                          {m.name} {m.ist_azav && <span className="text-amber-600">(AZAV)</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Kursstart</Label>
                    <Input 
                      type="date"
                      value={formData.kurs_start || ""}
                      onChange={(e) => setFormData({...formData, kurs_start: e.target.value})}
                      data-testid="input-kurs-start"
                    />
                  </div>
                  <div>
                    <Label className="flex items-center justify-between">
                      <span>Kursende</span>
                      <button
                        type="button"
                        disabled={!formData.kurs_start || !formData.kurs}
                        onClick={async () => {
                          try {
                            const r = await axios.get(`${API}/kurs-tools/berechne-ende`, {
                              params: { start_datum: formData.kurs_start, kurs: formData.kurs }
                            });
                            setFormData({...formData, kurs_ende: r.data.ende_datum});
                            toast.success(`Ende berechnet: ${r.data.ende_datum} (${r.data.soll_unterrichtstage} Tage${r.data.praktikum_tage ? ` + ${r.data.praktikum_tage} Praktikum = ${r.data.kurstage_gesamt}` : ''}, ${r.data.wochen} Wochen, ${r.data.feiertage_uebersprungen.length} Feiertag(e) übersprungen)`, { duration: 6000 });
                          } catch (e) {
                            toast.error(e.response?.data?.detail || "Konnte Ende nicht berechnen — Kurs unbekannt?");
                          }
                        }}
                        className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="auto-ende-btn-2"
                      >
                        Auto
                      </button>
                    </Label>
                    <Input 
                      type="date"
                      value={formData.kurs_ende || ""}
                      onChange={(e) => setFormData({...formData, kurs_ende: e.target.value})}
                      data-testid="input-kurs-ende"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Label>Interne Notizen</Label>
              <Textarea
                value={formData.interne_notizen}
                onChange={(e) => setFormData({...formData, interne_notizen: e.target.value})}
                rows={3}
                data-testid="input-teilnehmer-notizen"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={handleSubmit} data-testid="submit-teilnehmer">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Neue Pflegeeinrichtung anlegen (statt window.prompt — mit Adresse + Ansprechpartner) */}
      <Dialog open={newPeDialogOpen} onOpenChange={setNewPeDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="new-pe-dialog">
          <DialogHeader>
            <DialogTitle>Neue Pflegeeinrichtung anlegen</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Einrichtungsname *</Label>
              <Input value={newPeForm.einrichtungsname}
                     onChange={(e) => setNewPeForm({...newPeForm, einrichtungsname: e.target.value})}
                     data-testid="new-pe-name-input" placeholder="z.B. Pflegedienst Musterstadt GmbH" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-3"><Label>Straße & Nr.</Label>
                <Input value={newPeForm.strasse}
                       onChange={(e) => setNewPeForm({...newPeForm, strasse: e.target.value})}
                       data-testid="new-pe-strasse-input" placeholder="Musterstraße 12" /></div>
              <div><Label>PLZ</Label>
                <Input value={newPeForm.plz}
                       onChange={(e) => setNewPeForm({...newPeForm, plz: e.target.value})}
                       data-testid="new-pe-plz-input" /></div>
              <div className="col-span-2"><Label>Ort</Label>
                <Input value={newPeForm.ort}
                       onChange={(e) => setNewPeForm({...newPeForm, ort: e.target.value})}
                       data-testid="new-pe-ort-input" /></div>
            </div>
            <div className="border-t pt-3">
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Ansprechpartner (optional)</div>
              <div className="space-y-2">
                <div><Label>Name</Label>
                  <Input value={newPeForm.ansprechpartner_name}
                         onChange={(e) => setNewPeForm({...newPeForm, ansprechpartner_name: e.target.value})}
                         data-testid="new-pe-ap-name-input" placeholder="Frau Müller" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>E-Mail</Label>
                    <Input type="email" value={newPeForm.ansprechpartner_email}
                           onChange={(e) => setNewPeForm({...newPeForm, ansprechpartner_email: e.target.value})}
                           data-testid="new-pe-ap-email-input" /></div>
                  <div><Label>Telefon</Label>
                    <Input value={newPeForm.ansprechpartner_telefon}
                           onChange={(e) => setNewPeForm({...newPeForm, ansprechpartner_telefon: e.target.value})}
                           data-testid="new-pe-ap-tel-input" /></div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewPeDialogOpen(false)} disabled={newPeSaving}>Abbrechen</Button>
            <Button
              className="bg-[#D9775A] hover:bg-[#C2603F] text-white"
              disabled={newPeSaving || !newPeForm.einrichtungsname.trim()}
              onClick={async () => {
                setNewPeSaving(true);
                try {
                  const payload = {
                    ...newPeForm,
                    einrichtungsname: newPeForm.einrichtungsname.trim(),
                    status: "aktiv",
                    akquise_status: "neu",
                  };
                  const res = await axios.post(`${API}/pflegeeinrichtungen`, payload);
                  toast.success(`${payload.einrichtungsname} wurde angelegt`);
                  setEinrichtungen(prev => [...prev, { id: res.data.id, ...payload }]);
                  setFormData({...formData, pflegeeinrichtung_id: res.data.id});
                  setPflegeeinrichtungSearch("");
                  setNewPeDialogOpen(false);
                  fetchData();
                } catch (err) {
                  toast.error("Fehler beim Anlegen", { description: err?.response?.data?.detail || "" });
                } finally {
                  setNewPeSaving(false);
                }
              }}
              data-testid="new-pe-save-btn"
            >
              {newPeSaving ? "Speichere…" : "Anlegen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Kursdaten synchronisieren (leere Top-Level `kurs`/`kurs_start` aus `kurse[]` befüllen) */}
      <Dialog open={syncKurseOpen} onOpenChange={setSyncKurseOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="sync-kurse-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Kursdaten in Übersicht synchronisieren
            </DialogTitle>
            <DialogDescription>
              Übernimmt für Teilnehmer mit leerer <code>Kurs / Start</code>-Spalte automatisch die Daten aus dem
              Kurse-Array (z.&nbsp;B. der aktive Kurs im Detail). Bestehende Werte werden <b>niemals überschrieben</b>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {syncKurseLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 size={16} className="animate-spin" /> Lade Vorschau…
              </div>
            )}

            {!syncKurseLoading && syncKurseResult && (
              <>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="rounded-lg border p-3 bg-gray-50">
                    <p className="text-xs text-gray-500">Kandidaten (mit kurse[])</p>
                    <p className="text-2xl font-semibold">{syncKurseResult.total_candidates}</p>
                  </div>
                  <div className="rounded-lg border p-3 bg-emerald-50">
                    <p className="text-xs text-emerald-700">Würden aktualisiert</p>
                    <p className="text-2xl font-semibold text-emerald-700">
                      {syncKurseResult.would_update_count || 0}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3 bg-gray-50">
                    <p className="text-xs text-gray-500">Unverändert</p>
                    <p className="text-2xl font-semibold">{syncKurseResult.unchanged_count}</p>
                  </div>
                </div>

                {syncKurseResult.changes && syncKurseResult.changes.length > 0 ? (
                  <div className="rounded-lg border max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs text-gray-600 sticky top-0">
                        <tr>
                          <th className="px-3 py-2">Teilnehmer</th>
                          <th className="px-3 py-2">Änderungen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {syncKurseResult.changes.map((c) => (
                          <tr key={c.id} className="border-t align-top">
                            <td className="px-3 py-2 font-medium">{c.name || '—'}</td>
                            <td className="px-3 py-2 text-xs text-gray-700">
                              {c.changes.map((ch, idx) => (
                                <div key={idx}>
                                  <span className="font-mono text-gray-500">{ch.field}</span>:{' '}
                                  <span className="text-red-500 line-through">{String(ch.before ?? '∅')}</span>{' → '}
                                  <span className="text-emerald-700 font-medium">{String(ch.after ?? '∅')}</span>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-4 text-center text-sm text-gray-500">
                    Alles synchron — keine Änderungen nötig.
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Nur leere Felder werden gefüllt. Ein Aktivitätslog-Eintrag pro Teilnehmer wird geschrieben.
                </p>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSyncKurseOpen(false)}
              disabled={syncKurseApplying}
              data-testid="sync-kurse-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              onClick={runSyncKursePreview}
              variant="ghost"
              disabled={syncKurseLoading || syncKurseApplying}
              data-testid="sync-kurse-refresh-btn"
            >
              <RefreshCw size={14} className="mr-1.5" /> Neu prüfen
            </Button>
            <Button
              onClick={applySyncKurse}
              disabled={syncKurseApplying || syncKurseLoading || !syncKurseResult || (syncKurseResult?.would_update_count || 0) === 0}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="sync-kurse-apply-btn"
            >
              {syncKurseApplying ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Übernehme…</> : `Jetzt übernehmen (${syncKurseResult?.would_update_count || 0})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Legacy-Zertifikatsnotizen bereinigen (Ninox-Migrationsrest) */}
      <Dialog open={zertCleanupOpen} onOpenChange={setZertCleanupOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="zert-cleanup-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Legacy-Zertifikatsnotizen bereinigen
            </DialogTitle>
            <DialogDescription>
              Entfernt Zeilen wie <code>„Zertifikat: 2025/600"</code> aus den <b>Internen Notizen</b> —
              diese Nummern stammen aus dem Ninox-Import und wurden vom neuen System <b>nie vergeben</b>.
              Andere Notizen bleiben unverändert.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {zertCleanupLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 size={16} className="animate-spin" /> Lade Vorschau…
              </div>
            )}

            {!zertCleanupLoading && zertCleanupResult && (
              <>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg border p-3 bg-gray-50">
                    <p className="text-xs text-gray-500">Gefundene TN mit Muster</p>
                    <p className="text-2xl font-semibold">{zertCleanupResult.total_candidates}</p>
                  </div>
                  <div className="rounded-lg border p-3 bg-amber-50">
                    <p className="text-xs text-amber-700">Würden bereinigt</p>
                    <p className="text-2xl font-semibold text-amber-700">
                      {zertCleanupResult.would_update_count || 0}
                    </p>
                  </div>
                </div>

                {zertCleanupResult.changes && zertCleanupResult.changes.length > 0 ? (
                  <div className="rounded-lg border max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs text-gray-600 sticky top-0">
                        <tr>
                          <th className="px-3 py-2">Teilnehmer</th>
                          <th className="px-3 py-2">Entfernte Zeilen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zertCleanupResult.changes.map((c) => (
                          <tr key={c.id} className="border-t align-top">
                            <td className="px-3 py-2 font-medium">{c.name || '—'}</td>
                            <td className="px-3 py-2 text-xs">
                              {(c.removed || []).map((line, idx) => (
                                <div key={idx} className="text-red-600 line-through">{line}</div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-4 text-center text-sm text-gray-500">
                    Keine Legacy-Zertifikatsnotizen gefunden — nichts zu tun.
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Nur passende Zeilen werden entfernt. Ein Aktivitätslog-Eintrag pro Teilnehmer wird geschrieben.
                </p>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setZertCleanupOpen(false)}
              disabled={zertCleanupApplying}
              data-testid="zert-cleanup-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              onClick={runZertCleanupPreview}
              variant="ghost"
              disabled={zertCleanupLoading || zertCleanupApplying}
              data-testid="zert-cleanup-refresh-btn"
            >
              <RefreshCw size={14} className="mr-1.5" /> Neu prüfen
            </Button>
            <Button
              onClick={applyZertCleanup}
              disabled={zertCleanupApplying || zertCleanupLoading || !zertCleanupResult || (zertCleanupResult?.would_update_count || 0) === 0}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="zert-cleanup-apply-btn"
            >
              {zertCleanupApplying ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Bereinige…</> : `Jetzt bereinigen (${zertCleanupResult?.would_update_count || 0})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Bestanden-Flag im kurse[]-Array spiegeln (Fix für Zertifikat-Druck-Fehler) */}
      <Dialog open={bestandenSyncOpen} onOpenChange={setBestandenSyncOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="bestanden-sync-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Bestanden-Flag reparieren
            </DialogTitle>
            <DialogDescription>
              Kopiert den <b>Bestanden</b>-Status aus dem Prüfungs-Datensatz (oder Top-Level-Feld) in den passenden
              Eintrag im <code>kurse[]</code>-Array. Nötig für alle Teilnehmer, deren Prüfung vor dem Fix (13.07.)
              erfasst wurde — bei ihnen scheitert der Zertifikat-Druck mit <i>„Kurs ist nicht als bestanden markiert"</i>.
              Bestehende <code>bestanden</code>-Werte werden <b>niemals überschrieben</b>.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {bestandenSyncLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 size={16} className="animate-spin" /> Lade Vorschau…
              </div>
            )}

            {!bestandenSyncLoading && bestandenSyncResult && (
              <>
                <div className="grid grid-cols-3 gap-3 text-sm">
                  <div className="rounded-lg border p-3 bg-gray-50">
                    <p className="text-xs text-gray-500">Geprüfte TN</p>
                    <p className="text-2xl font-semibold">{bestandenSyncResult.total_candidates}</p>
                  </div>
                  <div className="rounded-lg border p-3 bg-blue-50">
                    <p className="text-xs text-blue-700">Würden repariert</p>
                    <p className="text-2xl font-semibold text-blue-700">
                      {bestandenSyncResult.would_update_count || 0}
                    </p>
                  </div>
                  <div className="rounded-lg border p-3 bg-gray-50">
                    <p className="text-xs text-gray-500">Bereits ok</p>
                    <p className="text-2xl font-semibold">{bestandenSyncResult.unchanged_count}</p>
                  </div>
                </div>

                {bestandenSyncResult.changes && bestandenSyncResult.changes.length > 0 ? (
                  <div className="rounded-lg border max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs text-gray-600 sticky top-0">
                        <tr>
                          <th className="px-3 py-2">Teilnehmer</th>
                          <th className="px-3 py-2">Änderungen</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bestandenSyncResult.changes.map((c) => (
                          <tr key={c.id} className="border-t align-top">
                            <td className="px-3 py-2 font-medium">{c.name || '—'}</td>
                            <td className="px-3 py-2 text-xs text-gray-700">
                              {c.changes.map((ch, idx) => (
                                <div key={idx}>
                                  <span className="font-mono text-gray-500">{ch.kurs_name}</span>:{' '}
                                  <span className={ch.bestanden_new ? 'text-emerald-700 font-medium' : 'text-red-600 font-medium'}>
                                    {ch.bestanden_new ? '✓ bestanden' : '✗ nicht bestanden'}
                                  </span>
                                  <span className="text-gray-400 ml-1">({ch.quelle})</span>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-4 text-center text-sm text-gray-500">
                    Alles synchron — keine Reparatur nötig.
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setBestandenSyncOpen(false)}
              disabled={bestandenSyncApplying}
              data-testid="bestanden-sync-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              onClick={runBestandenSyncPreview}
              variant="ghost"
              disabled={bestandenSyncLoading || bestandenSyncApplying}
              data-testid="bestanden-sync-refresh-btn"
            >
              <RefreshCw size={14} className="mr-1.5" /> Neu prüfen
            </Button>
            <Button
              onClick={applyBestandenSync}
              disabled={bestandenSyncApplying || bestandenSyncLoading || !bestandenSyncResult || (bestandenSyncResult?.would_update_count || 0) === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="bestanden-sync-apply-btn"
            >
              {bestandenSyncApplying ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Repariere…</> : `Jetzt reparieren (${bestandenSyncResult?.would_update_count || 0})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Kurs-ID/Name-Mismatch fixen (z.B. Wundtherapeut mit kurs_id=wundexperte) */}
      <Dialog open={kursIdFixOpen} onOpenChange={setKursIdFixOpen}>
        <DialogContent className="max-w-2xl bg-white" data-testid="kurs-id-fix-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              Kurs-ID Mismatch reparieren
            </DialogTitle>
            <DialogDescription>
              Findet Teilnehmer, bei denen die interne <code>kurs_id</code> nicht zum
              angezeigten <code>kurs_name</code> passt (z. B. Anzeige „Wundtherapeut",
              aber ID zeigt auf <code>wundexperte</code>). Solche TN erhalten falsche
              Modul-Inhalte in der Teilnahmebescheinigung.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {kursIdFixLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Loader2 size={16} className="animate-spin" /> Lade Vorschau…
              </div>
            )}

            {!kursIdFixLoading && kursIdFixResult && (
              <>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="rounded-lg border p-3 bg-gray-50">
                    <p className="text-xs text-gray-500">Geprüfte TN</p>
                    <p className="text-2xl font-semibold">{kursIdFixResult.total_candidates}</p>
                  </div>
                  <div className="rounded-lg border p-3 bg-purple-50">
                    <p className="text-xs text-purple-700">Würden korrigiert</p>
                    <p className="text-2xl font-semibold text-purple-700">
                      {kursIdFixResult.would_update_count || 0}
                    </p>
                  </div>
                </div>

                {kursIdFixResult.changes && kursIdFixResult.changes.length > 0 ? (
                  <div className="rounded-lg border max-h-72 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 text-left text-xs text-gray-600 sticky top-0">
                        <tr>
                          <th className="px-3 py-2">Teilnehmer</th>
                          <th className="px-3 py-2">Kursname → ID-Korrektur</th>
                        </tr>
                      </thead>
                      <tbody>
                        {kursIdFixResult.changes.map((c) => (
                          <tr key={c.id} className="border-t align-top">
                            <td className="px-3 py-2 font-medium">{c.name || '—'}</td>
                            <td className="px-3 py-2 text-xs text-gray-700">
                              {c.changes.map((ch, idx) => (
                                <div key={idx}>
                                  <span className="font-medium">{ch.kurs_name}</span>:{' '}
                                  <span className="text-red-500 font-mono line-through">{ch.ist_id}</span>{' → '}
                                  <span className="text-emerald-700 font-mono font-medium">{ch.soll_id}</span>
                                </div>
                              ))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rounded-lg border border-dashed p-4 text-center text-sm text-gray-500">
                    Alle Kurs-IDs passen zum Kurs-Namen — nichts zu tun.
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setKursIdFixOpen(false)}
              disabled={kursIdFixApplying}
              data-testid="kurs-id-fix-cancel-btn"
            >
              Abbrechen
            </Button>
            <Button
              onClick={runKursIdFixPreview}
              variant="ghost"
              disabled={kursIdFixLoading || kursIdFixApplying}
              data-testid="kurs-id-fix-refresh-btn"
            >
              <RefreshCw size={14} className="mr-1.5" /> Neu prüfen
            </Button>
            <Button
              onClick={applyKursIdFix}
              disabled={kursIdFixApplying || kursIdFixLoading || !kursIdFixResult || (kursIdFixResult?.would_update_count || 0) === 0}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="kurs-id-fix-apply-btn"
            >
              {kursIdFixApplying ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Korrigiere…</> : `Jetzt korrigieren (${kursIdFixResult?.would_update_count || 0})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== BILDUNGSGUTSCHEINE ====================
const Bildungsgutscheine = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("kurs_start");
  const [sortDir, setSortDir] = useState("desc");
  const navigate = useNavigate();
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const openDetail = (id) => {
    console.log("Opening detail for:", id);
    setSelectedId(id);
    setDetailOpen(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/teilnehmer/bildungsgutscheine`);
        setData(response.data);
      } catch (error) {
        toast.error("Fehler beim Laden");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const isExpiringSoon = (date) => {
    if (!date) return false;
    const ablauf = new Date(date);
    const heute = new Date();
    const diff = (ablauf - heute) / (1000 * 60 * 60 * 24);
    return diff <= 30 && diff >= 0;
  };

  const isExpired = (date) => {
    if (!date) return false;
    return new Date(date) < new Date();
  };

  // Gefilterte und sortierte Daten
  const getFilteredData = () => {
    let filtered = [...data];
    
    // Suche
    if (search.trim()) {
      const s = search.toLowerCase();
      filtered = filtered.filter(item => 
        `${item.vorname} ${item.nachname}`.toLowerCase().includes(s) ||
        (item.bildungsgutschein_nummer || "").toLowerCase().includes(s)
      );
    }
    
    // Status-Filter
    if (statusFilter === "expired") {
      filtered = filtered.filter(item => isExpired(item.bildungsgutschein_ablaufdatum));
    } else if (statusFilter === "expiring") {
      filtered = filtered.filter(item => isExpiringSoon(item.bildungsgutschein_ablaufdatum));
    } else if (statusFilter === "valid") {
      filtered = filtered.filter(item => !isExpired(item.bildungsgutschein_ablaufdatum) && !isExpiringSoon(item.bildungsgutschein_ablaufdatum));
    } else if (statusFilter === "no_scan") {
      filtered = filtered.filter(item => !item.bildungsgutschein_scan);
    }
    
    // Sortierung
    filtered.sort((a, b) => {
      let valA, valB;
      if (sortField === "nachname") {
        valA = `${a.nachname || ""} ${a.vorname || ""}`.toLowerCase();
        valB = `${b.nachname || ""} ${b.vorname || ""}`.toLowerCase();
      } else if (sortField === "ablaufdatum") {
        valA = a.bildungsgutschein_ablaufdatum || "9999";
        valB = b.bildungsgutschein_ablaufdatum || "9999";
      } else if (sortField === "nummer") {
        valA = a.bildungsgutschein_nummer || "";
        valB = b.bildungsgutschein_nummer || "";
      } else if (sortField === "kurs_start") {
        valA = a.kurs_start || "0000";
        valB = b.kurs_start || "0000";
      } else {
        valA = a[sortField] || "";
        valB = b[sortField] || "";
      }
      
      if (sortDir === "asc") {
        return valA < valB ? -1 : valA > valB ? 1 : 0;
      } else {
        return valA > valB ? -1 : valA < valB ? 1 : 0;
      }
    });
    
    return filtered;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir("asc");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="ml-1 opacity-30" />;
    return sortDir === "asc" ? 
      <ChevronUp size={14} className="ml-1" /> : 
      <ChevronDown size={14} className="ml-1" />;
  };

  return (
    <div className="page-container" data-testid="bildungsgutscheine-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Bildungsgutscheine</h1>
        <p className="text-sm text-gray-500">{getFilteredData().length} von {data.length} Einträgen</p>
      </div>

      {/* Filter */}
      <Card className="card-custom mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716c]" size={18} />
              <Input
                placeholder="Name oder Gutschein-Nr. suchen..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle</SelectItem>
                <SelectItem value="valid">Gültig</SelectItem>
                <SelectItem value="expiring">Läuft bald ab</SelectItem>
                <SelectItem value="expired">Abgelaufen</SelectItem>
                <SelectItem value="no_scan">Ohne Scan</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
            </div>
          ) : getFilteredData().length === 0 ? (
            <div className="empty-state">
              <Ticket className="empty-state-icon mx-auto" />
              <p>{search ? "Keine Treffer gefunden" : "Keine Teilnehmer mit Bildungsgutschein"}</p>
              {search && (
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setSearch("")}>
                  Suche zurücksetzen
                </Button>
              )}
            </div>
          ) : (
            <Table data-testid="gutscheine-table">
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort("nachname")}>
                    <div className="flex items-center">Teilnehmer <SortIcon field="nachname" /></div>
                  </TableHead>
                  <TableHead>Maßnahme</TableHead>
                  <TableHead>Kurs-Start</TableHead>
                  <TableHead>Kurs-Ende</TableHead>
                  <TableHead className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort("nummer")}>
                    <div className="flex items-center">Gutschein-Nr. <SortIcon field="nummer" /></div>
                  </TableHead>
                  <TableHead className="cursor-pointer hover:bg-gray-100" onClick={() => handleSort("ablaufdatum")}>
                    <div className="flex items-center">BG Ablaufdatum <SortIcon field="ablaufdatum" /></div>
                  </TableHead>
                  <TableHead>Scan</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {getFilteredData().map((item) => (
                  <TableRow 
                    key={item.id} 
                    className={`cursor-pointer hover:bg-[#FFFFFF] ${isExpired(item.bildungsgutschein_ablaufdatum) ? "bg-red-50" : 
                              isExpiringSoon(item.bildungsgutschein_ablaufdatum) ? "bg-amber-50" : ""}`}
                    onClick={() => openDetail(item.id)}
                  >
                    <TableCell className="font-medium">
                      <span className="text-blue-600 hover:underline">{item.vorname || ""} {item.nachname || ""}</span>
                    </TableCell>
                    <TableCell className="text-sm truncate max-w-[200px]">{item.kurs || "-"}</TableCell>
                    <TableCell>{item.kurs_start || "-"}</TableCell>
                    <TableCell>{item.kurs_ende || "-"}</TableCell>
                    <TableCell className="font-mono text-sm">{item.bildungsgutschein_nummer || "-"}</TableCell>
                    <TableCell>{item.bildungsgutschein_ablaufdatum || "-"}</TableCell>
                    <TableCell>
                      {item.bildungsgutschein_scan ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <FileText size={12} className="mr-1" />
                          Vorhanden
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                          Fehlt
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {isExpired(item.bildungsgutschein_ablaufdatum) ? (
                        <Badge className="bg-red-100 text-red-700">Abgelaufen</Badge>
                      ) : isExpiringSoon(item.bildungsgutschein_ablaufdatum) ? (
                        <Badge className="bg-amber-100 text-amber-700">Läuft bald ab</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700">Gültig</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      {/* Teilnehmer Detail Modal */}
      {selectedId && (
        <TeilnehmerDetail
          teilnehmerId={selectedId}
          isOpen={detailOpen}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
};

// ==================== BG-ANGEBOTE (Bildungsgutschein für Agentur für Arbeit) ====================
const BGAngebote = () => {
  const [teilnehmerName, setTeilnehmerName] = useState("");
  const [teilnehmerEmail, setTeilnehmerEmail] = useState("");
  const [kursTyp, setKursTyp] = useState("pflegeassistent_lg1_lg2");
  const [kostentraeger, setKostentraeger] = useState("Agentur für Arbeit");
  const [startTermin, setStartTermin] = useState("");
  const [loading, setLoading] = useState(false);
  const [kurse, setKurse] = useState([]);
  const [erstellteAngebote, setErstellteAngebote] = useState([]);
  const [gespeicherteAngebote, setGespeicherteAngebote] = useState([]);
  const [loadingAngebote, setLoadingAngebote] = useState(true);
  // BG-Status inline editing
  const [bgStatusDialog, setBgStatusDialog] = useState(null); // {id, ...} oder null
  // Nachfrage-Dialog
  const [nachfrageDialog, setNachfrageDialog] = useState(null); // {id, teilnehmer_email, ...}
  const [nachfrageTo, setNachfrageTo] = useState("");
  const [nachfrageSubject, setNachfrageSubject] = useState("");
  const [nachfrageBody, setNachfrageBody] = useState("");
  const [nachfrageAttach, setNachfrageAttach] = useState(true);
  const [nachfrageSending, setNachfrageSending] = useState(false);

  // Lade verfügbare Kurse und gespeicherte Angebote beim Start
  useEffect(() => {
    const loadData = async () => {
      try {
        const [kurseRes, angeboteRes] = await Promise.all([
          axios.get(`${API}/bg-angebote/kurse`),
          axios.get(`${API}/angebote`)
        ]);
        setKurse(kurseRes.data.kurse);
        setGespeicherteAngebote(angeboteRes.data);
      } catch (error) {
        console.error("Fehler beim Laden:", error);
        // Fallback für Kurse
        setKurse([
          { key: "pflegeassistent_lg1_lg2", name: "Pflegeassistent LG1 & LG2", umfang: "208 UE", dauer: "6,5 Wochen" },
          { key: "pflegehilfskraft_betreuungsassistent", name: "Pflegehilfskraft & Betreuungsassistent", umfang: "429 UE", dauer: "Nach Maßnahmeplanung" },
          { key: "ambulanter_pflegeassistent", name: "Ambulanter Pflegeassistent", umfang: "300 UE", dauer: "Nach Maßnahmeplanung" }
        ]);
      } finally {
        setLoadingAngebote(false);
      }
    };
    loadData();
  }, []);

  const loadGespeicherteAngebote = async () => {
    try {
      const res = await axios.get(`${API}/angebote`);
      setGespeicherteAngebote(res.data);
    } catch (error) {
      console.error("Fehler beim Laden der Angebote:", error);
    }
  };

  const handleDownloadAngebot = async (angebotId) => {
    try {
      const res = await axios.get(`${API}/angebote/${angebotId}`);
      const byteCharacters = atob(res.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', res.data.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Fehler beim Herunterladen");
    }
  };

  const handleDeleteAngebot = async (angebotId) => {
    if (!window.confirm("Angebot wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/angebote/${angebotId}`);
      toast.success("Angebot gelöscht");
      loadGespeicherteAngebote();
    } catch (error) {
      toast.error("Fehler beim Löschen");
    }
  };

  const handleErstelleAngebot = async () => {
    if (!teilnehmerName.trim()) {
      toast.error("Bitte Teilnehmername eingeben");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/bg-angebote/erstellen`, {
        teilnehmer_name: teilnehmerName.trim(),
        teilnehmer_email: teilnehmerEmail.trim(),
        kurs_typ: kursTyp,
        kostentraeger: kostentraeger,
        start_termin: startTermin || null
      });

      // PDF herunterladen
      const byteCharacters = atob(response.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.data.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Zur Historie hinzufügen
      const kursInfo = kurse.find(k => k.key === kursTyp);
      setErstellteAngebote(prev => [{
        teilnehmer: teilnehmerName,
        kurs: kursInfo?.name || kursTyp,
        datum: new Date().toLocaleString('de-DE'),
        filename: response.data.filename
      }, ...prev]);

      toast.success("BG-Angebot erstellt und heruntergeladen");
      setTeilnehmerName("");
      setTeilnehmerEmail("");
      setStartTermin("");
      loadGespeicherteAngebote();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen des Angebots");
    } finally {
      setLoading(false);
    }
  };

  const handleVorschau = async () => {
    if (!teilnehmerName.trim()) {
      toast.error("Bitte Teilnehmername eingeben");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/bg-angebote/erstellen`, {
        teilnehmer_name: teilnehmerName.trim(),
        teilnehmer_email: teilnehmerEmail.trim(),
        kurs_typ: kursTyp,
        kostentraeger: kostentraeger,
        start_termin: startTermin || null
      });

      // PDF in neuem Tab öffnen
      const byteCharacters = atob(response.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen der Vorschau");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container" data-testid="bg-angebote-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="page-title">BG-Angebote</h1>
          <p className="text-sm text-gray-500">Angebote für die Agentur für Arbeit (Bildungsgutschein)</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Formular */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="text-[#6d665b]" size={20} />
            Neues Angebot erstellen
          </h2>
          
          <div className="space-y-4">
            {/* Teilnehmername */}
            <div>
              <label className="block text-sm font-medium mb-1">Teilnehmer/in Name *</label>
              <Input
                value={teilnehmerName}
                onChange={(e) => setTeilnehmerName(e.target.value)}
                placeholder="Vor- und Nachname eingeben"
                data-testid="teilnehmer-name-input"
              />
            </div>

            {/* Teilnehmer-Email (optional, für Nachfrage) */}
            <div>
              <label className="block text-sm font-medium mb-1">
                E-Mail <span className="text-xs text-stone-500 font-normal">(optional — für spätere Nachfrage)</span>
              </label>
              <Input
                type="email"
                value={teilnehmerEmail}
                onChange={(e) => setTeilnehmerEmail(e.target.value)}
                placeholder="name@example.com"
                data-testid="teilnehmer-email-input"
              />
            </div>

            {/* Kursauswahl */}
            <div>
              <label className="block text-sm font-medium mb-1">Kurs / Maßnahme *</label>
              <Select value={kursTyp} onValueChange={setKursTyp}>
                <SelectTrigger data-testid="kurs-select">
                  <SelectValue placeholder="Kurs auswählen" />
                </SelectTrigger>
                <SelectContent>
                  {kurse.map(kurs => (
                    <SelectItem key={kurs.key} value={kurs.key}>
                      <div className="flex flex-col">
                        <span>{kurs.name}</span>
                        <span className="text-xs text-gray-500">{kurs.umfang} • {kurs.dauer}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Kostenträger */}
            <div>
              <label className="block text-sm font-medium mb-1">Kostenträger</label>
              <Input
                value={kostentraeger}
                onChange={(e) => setKostentraeger(e.target.value)}
                placeholder="z.B. Agentur für Arbeit"
              />
            </div>

            {/* Starttermin */}
            <div>
              <label className="block text-sm font-medium mb-1">
                Starttermin <span className="text-xs text-stone-500 font-normal">(optional — wenn leer: „Starttermin nach Absprache")</span>
              </label>
              <Input
                type="date"
                value={startTermin}
                onChange={(e) => setStartTermin(e.target.value)}
                data-testid="bg-angebot-starttermin"
              />
            </div>

            {/* Kurs-Info */}
            {kursTyp && kurse.length > 0 && (() => {
              const selectedKurs = kurse.find(k => k.key === kursTyp);
              return selectedKurs ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <p className="font-medium text-amber-800">
                    {selectedKurs.name}
                  </p>
                  <p className="text-amber-600">
                    Umfang: {selectedKurs.umfang} • 
                    Dauer: {selectedKurs.dauer}
                  </p>
                  {selectedKurs.massnahmenummer && (
                    <p className="text-amber-700 font-medium mt-1">
                      Maßnahmenummer: {selectedKurs.massnahmenummer}
                    </p>
                  )}
                  {selectedKurs.preis && (
                    <p className="text-amber-700 mt-1">
                      Maßnahmepreis: {selectedKurs.preis.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}
                    </p>
                  )}
                </div>
              ) : null;
            })()}

            {/* Buttons */}
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={handleVorschau}
                disabled={loading || !teilnehmerName.trim()}
                className="flex-1"
              >
                <Eye size={16} className="mr-2" />
                Vorschau
              </Button>
              <Button
                onClick={handleErstelleAngebot}
                disabled={loading || !teilnehmerName.trim()}
                className="flex-1 bg-[#6d665b] hover:bg-[#5a544a] text-white"
                data-testid="erstelle-angebot-btn"
              >
                {loading ? (
                  <RefreshCw size={16} className="mr-2 animate-spin" />
                ) : (
                  <Download size={16} className="mr-2" />
                )}
                Angebot erstellen
              </Button>
            </div>
          </div>
        </Card>

        {/* Info & Historie */}
        <div className="space-y-4">
          {/* Info-Box */}
          <Card className="p-4 bg-blue-50 border-blue-200">
            <h3 className="font-medium text-blue-800 mb-2">ℹ Hinweis</h3>
            <p className="text-sm text-blue-700">
              Diese Angebote sind für Interessenten mit Bildungsgutschein gedacht. 
              Das PDF enthält alle relevanten Informationen für die Agentur für Arbeit:
            </p>
            <ul className="text-sm text-blue-700 mt-2 list-disc list-inside">
              <li>Maßnahmendetails und Umfang</li>
              <li>Qualifizierungsziele und Inhalte</li>
              <li>Teilnahmevoraussetzungen</li>
              <li>Unterrichtsform und Dauer</li>
            </ul>
          </Card>

          {/* Letzte erstellte Angebote */}
          {erstellteAngebote.length > 0 && (
            <Card className="p-4">
              <h3 className="font-medium mb-3">Zuletzt erstellt (diese Sitzung)</h3>
              <div className="space-y-2">
                {erstellteAngebote.slice(0, 5).map((angebot, index) => (
                  <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded text-sm">
                    <div>
                      <p className="font-medium">{angebot.teilnehmer}</p>
                      <p className="text-xs text-gray-500">{angebot.kurs}</p>
                    </div>
                    <span className="text-xs text-gray-400">{angebot.datum}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Verfügbare Kurse */}
          <Card className="p-4">
            <h3 className="font-medium mb-3">Verfügbare BG-Kurse</h3>
            <div className="space-y-2">
              {kurse.map(kurs => (
                <div key={kurs.key} className="p-2 bg-gray-50 rounded text-sm">
                  <p className="font-medium">{kurs.name}</p>
                  <p className="text-xs text-gray-500">{kurs.umfang} • {kurs.dauer}</p>
                  {kurs.massnahmenummer && (
                    <p className="text-xs text-gray-600 font-medium">Maßnahmenummer: {kurs.massnahmenummer}</p>
                  )}
                  {kurs.preis && (
                    <p className="text-xs text-green-600 font-medium">Preis: {kurs.preis.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })}</p>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>

      {/* Gespeicherte Angebote */}
      <Card className="mt-6 p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FileText className="text-[#6d665b]" size={20} />
          Alle erstellten Angebote ({gespeicherteAngebote.length})
        </h2>
        
        {loadingAngebote ? (
          <div className="text-center py-8 text-gray-500">Lade Angebote...</div>
        ) : gespeicherteAngebote.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Noch keine Angebote erstellt. Erstellen Sie Ihr erstes Angebot oben.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Teilnehmer</TableHead>
                <TableHead>Kurs</TableHead>
                <TableHead>Kostenträger</TableHead>
                <TableHead className="text-center">BG erhalten?</TableHead>
                <TableHead>Nachfragen</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {gespeicherteAngebote.map(angebot => (
                <TableRow key={angebot.id} className={angebot.bg_erhalten ? "hover:bg-emerald-50/40 bg-emerald-50/20" : "hover:bg-gray-50"}>
                  <TableCell className="font-mono text-sm">{angebot.datum}</TableCell>
                  <TableCell className="font-medium">
                    {angebot.teilnehmer_name}
                    {angebot.teilnehmer_email && (
                      <div className="text-xs text-gray-500">{angebot.teilnehmer_email}</div>
                    )}
                  </TableCell>
                  <TableCell>{angebot.kurs_name}</TableCell>
                  <TableCell className="text-gray-600">{angebot.kostentraeger}</TableCell>
                  <TableCell className="text-center">
                    <button
                      type="button"
                      onClick={() => setBgStatusDialog({
                        id: angebot.id,
                        teilnehmer_name: angebot.teilnehmer_name,
                        bg_erhalten: !!angebot.bg_erhalten,
                        bg_erhalten_am: angebot.bg_erhalten_am || new Date().toISOString().slice(0, 10),
                        bg_nummer: angebot.bg_nummer || "",
                        bg_notiz: angebot.bg_notiz || "",
                      })}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium border transition-colors"
                      style={
                        angebot.bg_erhalten
                          ? { background: "#10b981", color: "white", borderColor: "#059669" }
                          : { background: "white", color: "#6b7280", borderColor: "#d1d5db" }
                      }
                      data-testid={`bg-status-btn-${angebot.id}`}
                    >
                      {angebot.bg_erhalten ? (
                        <>✓ erhalten {angebot.bg_erhalten_am ? `(${angebot.bg_erhalten_am})` : ""}</>
                      ) : (
                        <>offen</>
                      )}
                    </button>
                  </TableCell>
                  <TableCell>
                    {(angebot.nachfragen?.length || 0) > 0 ? (
                      <span className="text-xs">
                        {angebot.nachfragen.length}×
                        {angebot.letzte_nachfrage_am && (
                          <span className="text-gray-500"> · zuletzt {new Date(angebot.letzte_nachfrage_am).toLocaleDateString("de-DE")}</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setNachfrageDialog(angebot);
                          setNachfrageTo(angebot.teilnehmer_email || "");
                          setNachfrageSubject(`Nachfrage zu Ihrem Angebot: ${angebot.kurs_name || ""}`);
                          setNachfrageBody(
                            `Sehr geehrte/r ${angebot.teilnehmer_name},\n\n` +
                            `wir hatten Ihnen ein Angebot für die Maßnahme „${angebot.kurs_name || ""}" gesendet.\n\n` +
                            `Wir wollten uns kurz erkundigen, ob die Beantragung des Bildungsgutscheins bei der ` +
                            `Agentur für Arbeit erfolgreich war und ob wir Sie bei den nächsten Schritten unterstützen können.\n\n` +
                            `Für Rückfragen stehen wir Ihnen jederzeit zur Verfügung.\n\n` +
                            `Herzliche Grüße\nIhr Team von Dickies Campus`
                          );
                          setNachfrageAttach(true);
                        }}
                        data-testid={`nachfrage-btn-${angebot.id}`}
                      >
                        <Mail size={14} className="mr-1" /> Nachfrage
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadAngebot(angebot.id)}
                      >
                        <Download size={14} className="mr-1" /> PDF
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteAngebot(angebot.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* === Dialog: BG-Status (erhalten?) === */}
      <Dialog open={!!bgStatusDialog} onOpenChange={(o) => !o && setBgStatusDialog(null)}>
        <DialogContent data-testid="bg-status-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bildungsgutschein-Status</DialogTitle>
            <DialogDescription>
              {bgStatusDialog?.teilnehmer_name} — Hat dieser Teilnehmer einen Bildungsgutschein erhalten?
            </DialogDescription>
          </DialogHeader>
          {bgStatusDialog && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  id="bg-erhalten-cb"
                  checked={!!bgStatusDialog.bg_erhalten}
                  onCheckedChange={(v) => setBgStatusDialog({ ...bgStatusDialog, bg_erhalten: !!v })}
                  data-testid="bg-erhalten-cb"
                />
                <label htmlFor="bg-erhalten-cb" className="text-sm font-medium cursor-pointer">
                  Bildungsgutschein wurde ausgestellt
                </label>
              </div>
              {bgStatusDialog.bg_erhalten && (
                <>
                  <div>
                    <label className="block text-sm font-medium mb-1">Ausstellungsdatum</label>
                    <Input
                      type="date"
                      value={bgStatusDialog.bg_erhalten_am || ""}
                      onChange={(e) => setBgStatusDialog({ ...bgStatusDialog, bg_erhalten_am: e.target.value })}
                      data-testid="bg-erhalten-am-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">BG-Nummer (optional)</label>
                    <Input
                      value={bgStatusDialog.bg_nummer || ""}
                      onChange={(e) => setBgStatusDialog({ ...bgStatusDialog, bg_nummer: e.target.value })}
                      placeholder="z. B. BG-2026-0123"
                      data-testid="bg-nummer-input"
                    />
                  </div>
                </>
              )}
              <div>
                <label className="block text-sm font-medium mb-1">Notiz (optional)</label>
                <textarea
                  className="w-full rounded border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  rows={3}
                  value={bgStatusDialog.bg_notiz || ""}
                  onChange={(e) => setBgStatusDialog({ ...bgStatusDialog, bg_notiz: e.target.value })}
                  placeholder="z. B. Ablaufdatum, Auflagen…"
                  data-testid="bg-notiz-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBgStatusDialog(null)}>Abbrechen</Button>
            <Button
              onClick={async () => {
                try {
                  await axios.patch(`${API}/angebote/${bgStatusDialog.id}/bg-status`, {
                    bg_erhalten: bgStatusDialog.bg_erhalten,
                    bg_erhalten_am: bgStatusDialog.bg_erhalten ? bgStatusDialog.bg_erhalten_am : null,
                    bg_nummer: bgStatusDialog.bg_nummer || "",
                    bg_notiz: bgStatusDialog.bg_notiz || "",
                  });
                  toast.success("BG-Status gespeichert");
                  setBgStatusDialog(null);
                  loadGespeicherteAngebote();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
                }
              }}
              data-testid="bg-status-save"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Dialog: Nachfrage-Email === */}
      <Dialog open={!!nachfrageDialog} onOpenChange={(o) => !o && setNachfrageDialog(null)}>
        <DialogContent data-testid="nachfrage-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Nachfrage-Email senden</DialogTitle>
            <DialogDescription>
              An {nachfrageDialog?.teilnehmer_name} — {nachfrageDialog?.kurs_name}
            </DialogDescription>
          </DialogHeader>
          {nachfrageDialog && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">An (E-Mail) *</label>
                <Input
                  type="email"
                  value={nachfrageTo}
                  onChange={(e) => setNachfrageTo(e.target.value)}
                  placeholder="empfaenger@example.com"
                  data-testid="nachfrage-to"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Betreff</label>
                <Input
                  value={nachfrageSubject}
                  onChange={(e) => setNachfrageSubject(e.target.value)}
                  data-testid="nachfrage-subject"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Nachricht</label>
                <textarea
                  className="w-full rounded border border-gray-300 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  rows={10}
                  value={nachfrageBody}
                  onChange={(e) => setNachfrageBody(e.target.value)}
                  data-testid="nachfrage-body"
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="nachfrage-attach"
                  checked={nachfrageAttach}
                  onCheckedChange={(v) => setNachfrageAttach(!!v)}
                  data-testid="nachfrage-attach"
                />
                <label htmlFor="nachfrage-attach" className="text-sm cursor-pointer">
                  Original-Angebot als PDF anhängen ({nachfrageDialog.filename})
                </label>
              </div>
              {(nachfrageDialog.nachfragen?.length || 0) > 0 && (
                <div className="text-xs text-gray-500 border-t pt-2">
                  Bereits {nachfrageDialog.nachfragen.length} Nachfrage(n) gesendet
                  {nachfrageDialog.letzte_nachfrage_am && (
                    <> · zuletzt am {new Date(nachfrageDialog.letzte_nachfrage_am).toLocaleString("de-DE")}</>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNachfrageDialog(null)} disabled={nachfrageSending}>
              Abbrechen
            </Button>
            <Button
              disabled={!nachfrageTo || nachfrageSending}
              onClick={async () => {
                setNachfrageSending(true);
                try {
                  await axios.post(`${API}/angebote/${nachfrageDialog.id}/nachfrage-email`, {
                    to_email: nachfrageTo.trim(),
                    subject: nachfrageSubject,
                    body: nachfrageBody,
                    attach_pdf: nachfrageAttach,
                  });
                  toast.success(`Nachfrage an ${nachfrageTo} gesendet`);
                  setNachfrageDialog(null);
                  loadGespeicherteAngebote();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
                } finally {
                  setNachfrageSending(false);
                }
              }}
              data-testid="nachfrage-send"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {nachfrageSending ? <RefreshCw size={14} className="mr-2 animate-spin" /> : <Mail size={14} className="mr-2" />}
              Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TnSeitenLeiste = ({ seite, seitenGesamt, gesamt, proSeite, onSeite, onProSeite }) => (
  <div className="flex flex-wrap items-center justify-between gap-3 pt-4 text-sm text-stone-600" data-testid="tn-seiten-leiste">
    <div className="flex items-center gap-2">
      <span>Pro Seite</span>
      {[25, 50, 100].map((n) => (
        <button key={n} onClick={() => onProSeite(n)} data-testid={`tn-pro-seite-${n}`}
          className={`px-2 py-0.5 rounded border transition-colors ${n === proSeite ? "bg-[#3D3835] text-white border-[#3D3835]" : "border-stone-300 hover:bg-stone-100"}`}>{n}</button>
      ))}
    </div>
    <span data-testid="tn-seiten-info">{(seite - 1) * proSeite + 1}–{Math.min(seite * proSeite, gesamt)} von {gesamt}</span>
    <div className="flex items-center gap-1">
      <button disabled={seite <= 1} onClick={() => onSeite(seite - 1)} data-testid="tn-seite-zurueck"
        className="px-3 py-1 rounded border border-stone-300 disabled:opacity-40 hover:bg-stone-100 transition-colors">Zurück</button>
      <span className="px-2" data-testid="tn-seite-nr">Seite {seite} / {seitenGesamt}</span>
      <button disabled={seite >= seitenGesamt} onClick={() => onSeite(seite + 1)} data-testid="tn-seite-weiter"
        className="px-3 py-1 rounded border border-stone-300 disabled:opacity-40 hover:bg-stone-100 transition-colors">Weiter</button>
    </div>
  </div>
);

// ==================== RATENZAHLUNG ====================
const Ratenzahlung = () => {
  const [data, setData] = useState([]);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTeilnehmer, setSelectedTeilnehmer] = useState("");
  const [gesamtbetrag, setGesamtbetrag] = useState("");
  const [teilnehmerSearch, setTeilnehmerSearch] = useState("");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== "all" ? `?zahlungsstatus=${statusFilter}` : "";
      const [ratenzahlungenRes, teilnehmerRes] = await Promise.all([
        axios.get(`${API}/ratenzahlungen${params}`),
        axios.get(`${API}/teilnehmer`)
      ]);
      setData(ratenzahlungenRes.data);
      // Nur Selbstzahler und Pflegeeinrichtung ohne aktive Ratenzahlung
      const verfuegbareTeilnehmer = teilnehmerRes.data.filter(
        tn => tn.finanzierungsart !== "bildungsgutschein" && !tn.ratenzahlung_aktiviert
      );
      setTeilnehmer(verfuegbareTeilnehmer);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreateRatenzahlung = async () => {
    if (!selectedTeilnehmer) {
      toast.error("Bitte Teilnehmer auswählen");
      return;
    }
    if (!gesamtbetrag || parseFloat(gesamtbetrag) <= 0) {
      toast.error("Bitte Gesamtbetrag eingeben");
      return;
    }
    try {
      await axios.post(`${API}/ratenzahlungen`, {
        teilnehmer_id: selectedTeilnehmer,
        gesamtbetrag: parseFloat(gesamtbetrag),
        rate_1_bezahlt: false,
        rate_2_bezahlt: false,
        rate_3_bezahlt: false,
        rate_4_bezahlt: false
      });
      toast.success("Ratenzahlung erstellt");
      setDialogOpen(false);
      setSelectedTeilnehmer("");
      setGesamtbetrag("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
  };

  const handleToggleRate = async (id, rateNr, currentValue) => {
    try {
      await axios.put(`${API}/ratenzahlungen/${id}/rate/${rateNr}?bezahlt=${!currentValue}`);
      toast.success(`Rate ${rateNr} ${!currentValue ? 'bezahlt' : 'storniert'}`);
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Ratenzahlung wirklich löschen?")) {
      try {
        await axios.delete(`${API}/ratenzahlungen/${id}`);
        toast.success("Ratenzahlung gelöscht");
        fetchData();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const getBezahlteRaten = (item) => {
    return [item.rate_1_bezahlt, item.rate_2_bezahlt, item.rate_3_bezahlt, item.rate_4_bezahlt].filter(Boolean).length;
  };

  return (
    <div className="page-container" data-testid="ratenzahlung-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="page-title">Ratenzahlung</h1>
        <div className="sm:mr-40">
          <Button className="btn-accent" onClick={() => setDialogOpen(true)} data-testid="add-ratenzahlung-btn">
            <Plus size={16} />
            Neue Ratenzahlung
          </Button>
        </div>
      </div>

      <Card className="card-custom mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4 items-center">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[220px]" data-testid="status-filter">
                <SelectValue placeholder="Zahlungsstatus" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="offen">Offen</SelectItem>
                <SelectItem value="teilweise">Teilweise bezahlt</SelectItem>
                <SelectItem value="vollstaendig">Vollständig bezahlt</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-[#78716c]">
              Nur für Selbstzahler und Pflegeeinrichtungen (nicht bei Bildungsgutschein)
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
            </div>
          ) : data.length === 0 ? (
            <div className="empty-state">
              <CreditCard className="empty-state-icon mx-auto" />
              <p>Keine Ratenzahlungen gefunden</p>
              <p className="text-sm mt-2 mb-4">Ratenzahlungen für Selbstzahler und Pflegeeinrichtungen erstellen.</p>
              <Button 
                className="bg-[#D9775A] hover:bg-[#C2603F] text-white"
                onClick={() => setDialogOpen(true)}
              >
                <Plus size={16} className="mr-2" />
                Neue Ratenzahlung erstellen
              </Button>
            </div>
          ) : (
            <Table data-testid="ratenzahlung-table">
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead>Teilnehmer / Rechnung</TableHead>
                  <TableHead>Finanzierung</TableHead>
                  <TableHead>Gesamtbetrag</TableHead>
                  <TableHead>Raten</TableHead>
                  <TableHead>Fortschritt</TableHead>
                  <TableHead>Letzte Zahlung</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((item) => {
                  const anzahlRaten = item.anzahl_raten || 4;
                  return (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">
                      {item.teilnehmer_name || item.empfaenger_name || "-"}
                      {item.rechnungsnummer && (
                        <span className="text-xs text-blue-600 block">{item.rechnungsnummer}</span>
                      )}
                      {item.quelle === "rechnung" && (
                        <Badge variant="outline" className="text-xs mt-1">Rechnung</Badge>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={item.finanzierungsart} /></TableCell>
                    <TableCell className="font-mono">
                      {item.gesamtbetrag ? `€${item.gesamtbetrag.toLocaleString('de-DE')}` : "-"}
                      {item.gesamtbetrag > 0 && (
                        <span className="text-xs text-[#78716c] block">
                          (à €{(item.gesamtbetrag / anzahlRaten).toLocaleString('de-DE')})
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {Array.from({length: anzahlRaten}, (_, i) => i + 1).map(nr => {
                          const bezahlt = item[`rate_${nr}_bezahlt`];
                          return (
                            <button
                              key={nr}
                              onClick={() => handleToggleRate(item.id, nr, bezahlt)}
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                                bezahlt 
                                  ? 'bg-emerald-500 text-white hover:bg-emerald-600' 
                                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                              }`}
                              title={`Rate ${nr} ${bezahlt ? 'bezahlt' : 'offen'}`}
                              data-testid={`rate-${item.id}-${nr}`}
                            >
                              {nr}
                            </button>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full transition-all ${
                              getBezahlteRaten(item) === anzahlRaten ? 'bg-emerald-500' :
                              getBezahlteRaten(item) > 0 ? 'bg-amber-500' : 'bg-gray-300'
                            }`}
                            style={{ width: `${(getBezahlteRaten(item) / anzahlRaten) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-[#78716c]">{getBezahlteRaten(item)}/{anzahlRaten}</span>
                      </div>
                    </TableCell>
                    <TableCell>{item.datum_letzte_zahlung || "-"}</TableCell>
                    <TableCell><StatusBadge status={item.zahlungsstatus} /></TableCell>
                    <TableCell className="text-right">
                      {item.quelle !== "rechnung" && (
                        <button className="action-btn delete" onClick={() => handleDelete(item.id)}>
                          <Trash2 size={16} />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>Neue Ratenzahlung</DialogTitle>
            <DialogDescription>
              Ratenzahlung für Selbstzahler oder Pflegeeinrichtung aktivieren. 4 Raten werden automatisch angelegt.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Teilnehmer auswählen</Label>
            <Input 
              placeholder="Name eingeben zum Suchen..." 
              value={teilnehmerSearch}
              onChange={(e) => setTeilnehmerSearch(e.target.value)}
              className="mb-2"
            />
            <Select value={selectedTeilnehmer} onValueChange={setSelectedTeilnehmer}>
              <SelectTrigger data-testid="select-teilnehmer">
                <SelectValue placeholder="Teilnehmer wählen" />
              </SelectTrigger>
              <SelectContent>
                {teilnehmer.length === 0 ? (
                  <SelectItem value="none" disabled>Keine verfügbaren Teilnehmer</SelectItem>
                ) : (
                  teilnehmer
                    .filter(tn => {
                      if (!teilnehmerSearch) return true;
                      const search = teilnehmerSearch.toLowerCase();
                      return `${tn.vorname} ${tn.nachname}`.toLowerCase().includes(search);
                    })
                    .map(tn => (
                      <SelectItem key={tn.id} value={tn.id}>
                        {tn.vorname} {tn.nachname} ({tn.finanzierungsart === "selbstzahler" ? "Selbstzahler" : "Einrichtung"})
                      </SelectItem>
                    ))
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#78716c] mt-2">
              Nur Teilnehmer ohne aktive Ratenzahlung und ohne Bildungsgutschein werden angezeigt.
            </p>
          </div>
          <div>
            <Label>Gesamtbetrag (€) *</Label>
            <Input 
              type="number" 
              step="0.01"
              placeholder="z.B. 2000"
              value={gesamtbetrag}
              onChange={(e) => setGesamtbetrag(e.target.value)}
            />
            {gesamtbetrag && parseFloat(gesamtbetrag) > 0 && (
              <p className="text-xs text-emerald-600 mt-1">
                4 Raten à €{(parseFloat(gesamtbetrag) / 4).toLocaleString('de-DE', {minimumFractionDigits: 2})}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={handleCreateRatenzahlung} data-testid="submit-ratenzahlung">
              Erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== MASSNAHMEN (vereinfacht - ersetzt separate Kurse) ====================
const Massnahmen = () => {
  const [data, setData] = useState([]);
  const [vorlagen, setVorlagen] = useState([]);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, azav, nicht-azav
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    umfang_ue: "",
    preis_euro: "",
    bg_faehig: false,
    azav_relevant: false,
    praktikum_erforderlich: false,
    praktikum_wochen: "",
    beschreibung: "",
    startdatum: "",
    enddatum: "",
    pruefungsdatum: "",
    status: "geplant",
    teilnehmer_ids: [],
    massnahmennummer: "",
    durchfuehrungsform: "",
    mindestanwesenheit_prozent: 80
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [massnahmenRes, vorlagenRes, tnRes] = await Promise.all([
        axios.get(`${API}/massnahmen`),
        axios.get(`${API}/massnahmen/vorlagen`),
        axios.get(`${API}/teilnehmer`)
      ]);
      setData(massnahmenRes.data);
      setVorlagen(vorlagenRes.data);
      setTeilnehmer(tnRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredData = data.filter(m => {
    if (filter === "azav" && !m.bg_faehig) return false;
    if (filter === "nicht-azav" && m.bg_faehig) return false;
    if (statusFilter !== "all" && m.status !== statusFilter) return false;
    return true;
  });

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        umfang_ue: parseInt(formData.umfang_ue) || 0,
        preis_euro: formData.preis_euro ? parseFloat(formData.preis_euro) : null,
        praktikum_wochen: formData.praktikum_erforderlich ? (parseInt(formData.praktikum_wochen) || null) : null,
        mindestanwesenheit_prozent: parseInt(formData.mindestanwesenheit_prozent) || 80
      };
      
      if (editItem) {
        await axios.put(`${API}/massnahmen/${editItem.id}`, payload);
        toast.success("Maßnahme aktualisiert");
      } else {
        await axios.post(`${API}/massnahmen`, payload);
        toast.success("Maßnahme erstellt");
      }
      setDialogOpen(false);
      setEditItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      const det = error.response?.data?.detail;
      toast.error(typeof det === "string" ? det : "Fehler beim Speichern – bitte Eingaben prüfen");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/massnahmen/${id}`);
        toast.success("Maßnahme gelöscht");
        fetchData();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      name: item.name || "",
      umfang_ue: item.umfang_ue || "",
      preis_euro: item.preis_euro || "",
      bg_faehig: item.bg_faehig || false,
      azav_relevant: item.azav_relevant || false,
      praktikum_erforderlich: item.praktikum_erforderlich || false,
      praktikum_wochen: item.praktikum_wochen || "",
      beschreibung: item.beschreibung || "",
      startdatum: item.startdatum || "",
      enddatum: item.enddatum || "",
      pruefungsdatum: item.pruefungsdatum || "",
      status: item.status || "geplant",
      teilnehmer_ids: item.teilnehmer_ids || [],
      massnahmennummer: item.massnahmennummer || "",
      durchfuehrungsform: item.durchfuehrungsform || "",
      mindestanwesenheit_prozent: item.mindestanwesenheit_prozent || 80
    });
    setDialogOpen(true);
  };

  const handleCreateFromVorlage = (vorlage) => {
    setEditItem(null);
    setFormData({
      name: vorlage.name || "",
      umfang_ue: vorlage.umfang_ue || "",
      preis_euro: vorlage.preis_euro || "",
      bg_faehig: vorlage.bg_faehig || false,
      azav_relevant: vorlage.azav_relevant || false,
      praktikum_erforderlich: vorlage.praktikum_erforderlich || false,
      praktikum_wochen: vorlage.praktikum_wochen || "",
      beschreibung: vorlage.beschreibung || "",
      startdatum: "",
      enddatum: "",
      pruefungsdatum: "",
      status: "geplant",
      teilnehmer_ids: [],
      massnahmennummer: "",
      durchfuehrungsform: "",
      mindestanwesenheit_prozent: 80
    });
    setDialogOpen(true);
  };

  const handleStatusAction = async (item, action) => {
    try {
      if (action === "start") {
        await axios.post(`${API}/massnahmen/${item.id}/start`);
        toast.success("Maßnahme gestartet");
      } else if (action === "abschliessen") {
        await axios.post(`${API}/massnahmen/${item.id}/abschliessen`);
        toast.success("Maßnahme abgeschlossen");
      }
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'object' && detail.fehler) {
        toast.error(detail.fehler.join('\n'));
      } else {
        toast.error(detail || "Fehler bei Status-Änderung");
      }
    }
  };

  const handleExportAkte = async (id) => {
    try {
      const response = await axios.get(`${API}/azav/export/massnahmeakte/${id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `massnahmeakte_${id.slice(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Export");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      umfang_ue: "",
      preis_euro: "",
      bg_faehig: false,
      azav_relevant: false,
      praktikum_erforderlich: false,
      praktikum_wochen: "",
      beschreibung: "",
      startdatum: "",
      enddatum: "",
      pruefungsdatum: "",
      status: "geplant",
      teilnehmer_ids: [],
      massnahmennummer: "",
      durchfuehrungsform: "",
      mindestanwesenheit_prozent: 80
    });
  };

  const azavCount = data.filter(m => m.bg_faehig).length;
  const nichtAzavCount = data.filter(m => !m.bg_faehig).length;
  const laufendCount = data.filter(m => m.status === "laufend").length;

  return (
    <div className="page-container" data-testid="massnahmen-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Maßnahmen</h1>
          <p className="text-sm text-[#78716c] mt-1">Alle Weiterbildungsmaßnahmen verwalten</p>
        </div>
      </div>
      
      {/* Action Buttons */}
      <div className="flex gap-2 mb-4">
        <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-massnahme-btn">
          <Plus size={16} className="mr-2" />
          Neue Maßnahme
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <BookOpen size={16} className="mr-2" />
              Aus Vorlage
              <ChevronDown size={14} className="ml-2" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-72">
            {vorlagen.map((v, idx) => (
              <DropdownMenuItem key={idx} onClick={() => handleCreateFromVorlage(v)}>
                <div className="flex justify-between w-full">
                  <div>
                    <p className="font-medium">{v.name}</p>
                    <p className="text-xs text-[#78716c]">{v.umfang_ue} UE {v.bg_faehig ? "• AZAV" : ""}</p>
                  </div>
                  {v.preis_euro && (
                    <span className="text-sm font-medium text-emerald-600">{v.preis_euro.toLocaleString('de-DE')} €</span>
                  )}
                </div>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className={`card-custom cursor-pointer hover:shadow-md ${filter === 'all' && statusFilter === 'all' ? 'ring-2 ring-[#6d665b]' : ''}`} 
              onClick={() => { setFilter("all"); setStatusFilter("all"); }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#d2c9c1] flex items-center justify-center">
                <GraduationCap className="text-[#6d665b]" size={20} />
              </div>
              <div>
                <p className="text-2xl font-semibold">{data.length}</p>
                <p className="text-sm text-[#78716c]">Alle</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`card-custom cursor-pointer hover:shadow-md ${statusFilter === 'laufend' ? 'ring-2 ring-blue-400' : ''}`} 
              onClick={() => { setFilter("all"); setStatusFilter("laufend"); }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Play className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-blue-600">{laufendCount}</p>
                <p className="text-sm text-blue-700">Laufend</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`card-custom cursor-pointer hover:shadow-md ${filter === 'azav' ? 'ring-2 ring-amber-400' : ''}`} 
              onClick={() => { setFilter("azav"); setStatusFilter("all"); }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <ShieldCheck className="text-amber-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-amber-600">{azavCount}</p>
                <p className="text-sm text-amber-700">AZAV</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={`card-custom cursor-pointer hover:shadow-md ${filter === 'nicht-azav' ? 'ring-2 ring-emerald-400' : ''}`} 
              onClick={() => { setFilter("nicht-azav"); setStatusFilter("all"); }}>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <BookOpen className="text-emerald-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-semibold text-emerald-600">{nichtAzavCount}</p>
                <p className="text-sm text-emerald-700">Nicht-AZAV</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="empty-state">
              <GraduationCap className="empty-state-icon mx-auto" />
              <p>Keine Maßnahmen gefunden</p>
            </div>
          ) : (
            <Table data-testid="massnahmen-table">
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead>Maßnahme</TableHead>
                  <TableHead>Preis</TableHead>
                  <TableHead>Zeitraum</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Teilnehmer</TableHead>
                  <TableHead>Typ</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-xs text-[#78716c]">{item.umfang_ue} UE</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.preis_euro ? (
                        <span className="font-medium text-emerald-700">{item.preis_euro.toLocaleString('de-DE')} €</span>
                      ) : (
                        <span className="text-[#78716c]">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.startdatum ? (
                        <div className="text-sm">
                          <p>{item.startdatum}</p>
                          <p className="text-[#78716c]">bis {item.enddatum || "offen"}</p>
                        </div>
                      ) : (
                        <span className="text-[#78716c]">Nicht geplant</span>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={item.status} /></TableCell>
                    <TableCell>
                      <Badge variant="outline">{item.teilnehmer_ids?.length || 0} TN</Badge>
                    </TableCell>
                    <TableCell>
                      {item.bg_faehig ? (
                        <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                          <ShieldCheck size={12} className="mr-1" />
                          AZAV
                        </Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Nicht-AZAV
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.status === "geplant" && (
                        <button 
                          className="action-btn" 
                          onClick={() => handleStatusAction(item, "start")}
                          title="Starten"
                        >
                          <Play size={16} />
                        </button>
                      )}
                      {item.status === "laufend" && (
                        <button 
                          className="action-btn" 
                          onClick={() => handleStatusAction(item, "abschliessen")}
                          title="Abschließen"
                        >
                          <CheckCircle size={16} />
                        </button>
                      )}
                      <button 
                        className="action-btn" 
                        onClick={() => handleExportAkte(item.id)}
                        title="PDF Export"
                      >
                        <FileText size={16} />
                      </button>
                      <button className="action-btn" onClick={() => handleEdit(item)}>
                        <Pencil size={16} />
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={16} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>
              {editItem ? "Maßnahme bearbeiten" : "Neue Maßnahme"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="z.B. LG1 & LG2"
                  data-testid="input-massnahme-name"
                />
              </div>
              <div>
                <Label>Umfang (UE) *</Label>
                <Input
                  type="number"
                  value={formData.umfang_ue}
                  onChange={(e) => setFormData({...formData, umfang_ue: e.target.value})}
                  placeholder="208"
                  data-testid="input-umfang-ue"
                />
              </div>
              <div>
                <Label>Preis (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.preis_euro}
                  onChange={(e) => setFormData({...formData, preis_euro: e.target.value})}
                  placeholder="1360.00"
                  data-testid="input-preis"
                />
              </div>
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={formData.beschreibung}
                onChange={(e) => setFormData({...formData, beschreibung: e.target.value})}
                placeholder="Kurzbeschreibung der Maßnahme"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Startdatum</Label>
                <Input
                  type="date"
                  value={formData.startdatum}
                  onChange={(e) => setFormData({...formData, startdatum: e.target.value})}
                />
              </div>
              <div>
                <Label>Enddatum</Label>
                <Input
                  type="date"
                  value={formData.enddatum}
                  onChange={(e) => setFormData({...formData, enddatum: e.target.value})}
                />
              </div>
              <div>
                <Label>Prüfungsdatum</Label>
                <Input
                  type="date"
                  value={formData.pruefungsdatum}
                  onChange={(e) => setFormData({...formData, pruefungsdatum: e.target.value})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="bg_faehig"
                    checked={formData.bg_faehig}
                    onCheckedChange={(checked) => setFormData({...formData, bg_faehig: checked, azav_relevant: checked})}
                  />
                  <label htmlFor="bg_faehig" className="text-sm">Bildungsgutschein-fähig (AZAV)</label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="praktikum"
                    checked={formData.praktikum_erforderlich}
                    onCheckedChange={(checked) => setFormData({...formData, praktikum_erforderlich: checked})}
                  />
                  <label htmlFor="praktikum" className="text-sm">Praktikum erforderlich</label>
                </div>
              </div>
              {formData.praktikum_erforderlich && (
                <div>
                  <Label>Praktikum (Wochen)</Label>
                  <Input
                    type="number"
                    value={formData.praktikum_wochen}
                    onChange={(e) => setFormData({...formData, praktikum_wochen: e.target.value})}
                    placeholder="1"
                  />
                </div>
              )}
            </div>
            {formData.bg_faehig && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-amber-700 mb-3">AZAV-Pflichtfelder</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Maßnahmennummer</Label>
                      <Input
                        value={formData.massnahmennummer}
                        onChange={(e) => setFormData({...formData, massnahmennummer: e.target.value})}
                        placeholder="z.B. 123/456/2024"
                      />
                    </div>
                    <div>
                      <Label>Durchführungsform</Label>
                      <Select value={formData.durchfuehrungsform} onValueChange={(v) => setFormData({...formData, durchfuehrungsform: v})}>
                        <SelectTrigger>
                          <SelectValue placeholder="Wählen..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="praesenz">Präsenz</SelectItem>
                          <SelectItem value="online">Online</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Mindestanwesenheit (%)</Label>
                      <Input
                        type="number"
                        value={formData.mindestanwesenheit_prozent}
                        onChange={(e) => setFormData({...formData, mindestanwesenheit_prozent: e.target.value})}
                        placeholder="80"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            <div>
              <Label>Teilnehmer</Label>
              <div className="border rounded-md p-3 max-h-40 overflow-y-auto">
                {teilnehmer.length === 0 ? (
                  <p className="text-sm text-[#78716c]">Keine Teilnehmer vorhanden</p>
                ) : (
                  <div className="space-y-2">
                    {teilnehmer.map(tn => (
                      <div key={tn.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`tn-${tn.id}`}
                          checked={formData.teilnehmer_ids.includes(tn.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({...formData, teilnehmer_ids: [...formData.teilnehmer_ids, tn.id]});
                            } else {
                              setFormData({...formData, teilnehmer_ids: formData.teilnehmer_ids.filter(id => id !== tn.id)});
                            }
                          }}
                        />
                        <label htmlFor={`tn-${tn.id}`} className="text-sm">
                          {tn.vorname} {tn.nachname}
                          <span className="ml-2 text-xs text-[#78716c]">
                            ({tn.finanzierungsart === "bildungsgutschein" ? "BG" : tn.finanzierungsart === "selbstzahler" ? "SZ" : "PE"})
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={handleSubmit} data-testid="submit-massnahme">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== WEITERBILDUNGEN ====================
const Weiterbildungen = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    beschreibung: "",
    gesamtdauer: "",
    zertifikat_erforderlich: true
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = search ? `?search=${search}` : "";
      const response = await axios.get(`${API}/weiterbildungen${params}`);
      setData(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async () => {
    try {
      if (editItem) {
        await axios.put(`${API}/weiterbildungen/${editItem.id}`, formData);
        toast.success("Weiterbildung aktualisiert");
      } else {
        await axios.post(`${API}/weiterbildungen`, formData);
        toast.success("Weiterbildung erstellt");
      }
      setDialogOpen(false);
      setEditItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/weiterbildungen/${id}`);
        toast.success("Weiterbildung gelöscht");
        fetchData();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      name: item.name,
      beschreibung: item.beschreibung,
      gesamtdauer: item.gesamtdauer,
      zertifikat_erforderlich: item.zertifikat_erforderlich
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      beschreibung: "",
      gesamtdauer: "",
      zertifikat_erforderlich: true
    });
  };

  return (
    <div className="page-container" data-testid="weiterbildungen-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Weiterbildungen</h1>
        <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-weiterbildung-btn">
          <Plus size={16} className="mr-2" />
          Neue Weiterbildung
        </Button>
      </div>

      <Card className="card-custom mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716c]" size={18} />
            <Input
              placeholder="Suchen..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-weiterbildung"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
            </div>
          ) : data.length === 0 ? (
            <div className="empty-state">
              <GraduationCap className="empty-state-icon mx-auto" />
              <p>Keine Weiterbildungen gefunden</p>
            </div>
          ) : (
            <Table data-testid="weiterbildungen-table">
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead>Name</TableHead>
                  <TableHead>Beschreibung</TableHead>
                  <TableHead>Dauer</TableHead>
                  <TableHead>Zertifikat</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell className="max-w-xs truncate">{item.beschreibung}</TableCell>
                    <TableCell>{item.gesamtdauer}</TableCell>
                    <TableCell>
                      <Badge variant={item.zertifikat_erforderlich ? "default" : "secondary"}>
                        {item.zertifikat_erforderlich ? "Ja" : "Nein"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <button className="action-btn" onClick={() => handleEdit(item)}>
                        <Pencil size={16} />
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={16} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>
              {editItem ? "Weiterbildung bearbeiten" : "Neue Weiterbildung"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                data-testid="input-wb-name"
              />
            </div>
            <div>
              <Label>Beschreibung</Label>
              <Textarea
                value={formData.beschreibung}
                onChange={(e) => setFormData({...formData, beschreibung: e.target.value})}
                rows={3}
                data-testid="input-wb-beschreibung"
              />
            </div>
            <div>
              <Label>Gesamtdauer *</Label>
              <Input
                value={formData.gesamtdauer}
                onChange={(e) => setFormData({...formData, gesamtdauer: e.target.value})}
                placeholder="z.B. 6 Monate"
                data-testid="input-wb-dauer"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="zertifikat"
                checked={formData.zertifikat_erforderlich}
                onChange={(e) => setFormData({...formData, zertifikat_erforderlich: e.target.checked})}
                className="w-4 h-4"
                data-testid="input-wb-zertifikat"
              />
              <Label htmlFor="zertifikat">Zertifikat erforderlich</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={handleSubmit} data-testid="submit-weiterbildung">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// ==================== ANWESENHEIT (AZAV) ====================
const Anwesenheit = () => {
  const [massnahmen, setMassnahmen] = useState([]);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [anwesenheiten, setAnwesenheiten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMassnahme, setSelectedMassnahme] = useState("");
  const [selectedDatum, setSelectedDatum] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [massnahmenRes, tnRes] = await Promise.all([
          axios.get(`${API}/massnahmen`),
          axios.get(`${API}/teilnehmer`)
        ]);
        // Filtern nach AZAV-relevanten Maßnahmen
        const relevantMassnahmen = massnahmenRes.data.filter(m => 
          m.ist_azav || m.azav_relevant || m.bg_faehig || m.status === 'laufend' || m.status === 'aktiv'
        );
        setMassnahmen(relevantMassnahmen);
        setTeilnehmer(tnRes.data);
        if (relevantMassnahmen.length > 0) {
          setSelectedMassnahme(relevantMassnahmen[0].id);
        }
      } catch (error) {
        toast.error("Fehler beim Laden");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedMassnahme && selectedDatum) {
      loadAnwesenheit();
    }
  }, [selectedMassnahme, selectedDatum]);

  const loadAnwesenheit = async () => {
    try {
      const response = await axios.get(`${API}/anwesenheit?massnahme_id=${selectedMassnahme}&datum=${selectedDatum}`);
      setAnwesenheiten(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Anwesenheit");
    }
  };

  const getMassnahmeTeilnehmer = () => {
    const massnahme = massnahmen.find(m => m.id === selectedMassnahme);
    if (!massnahme) return [];
    // Filtere Teilnehmer nach massnahme_id oder passendem Kursnamen
    return teilnehmer.filter(tn => 
      tn.massnahme_id === selectedMassnahme || 
      tn.kurs === massnahme.name ||
      massnahme.teilnehmer_ids?.includes(tn.id)
    );
  };

  const getAnwesenheitStatus = (teilnehmerId) => {
    return anwesenheiten.find(a => a.teilnehmer_id === teilnehmerId);
  };

  const handleToggleAnwesenheit = async (teilnehmerId, currentStatus) => {
    const existing = getAnwesenheitStatus(teilnehmerId);
    const tn = teilnehmer.find(t => t.id === teilnehmerId);
    const isBildungsgutschein = tn?.finanzierungsart === "bildungsgutschein";
    
    if (existing) {
      // Update
      const newAnwesend = !existing.anwesend;
      let fehlgrund = existing.fehlgrund;
      
      if (!newAnwesend && isBildungsgutschein && !fehlgrund) {
        fehlgrund = prompt("AZAV: Bitte Fehlgrund angeben:");
        if (!fehlgrund) {
          toast.error("Bei Bildungsgutschein ist der Fehlgrund Pflicht");
          return;
        }
      }
      
      try {
        await axios.put(`${API}/anwesenheit/${existing.id}`, {
          teilnehmer_id: teilnehmerId,
          massnahme_id: selectedMassnahme,
          datum: selectedDatum,
          anwesend: newAnwesend,
          fehlgrund: newAnwesend ? null : fehlgrund
        });
        loadAnwesenheit();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Fehler beim Aktualisieren");
      }
    } else {
      // Create new
      let fehlgrund = null;
      if (!currentStatus && isBildungsgutschein) {
        fehlgrund = prompt("AZAV: Bitte Fehlgrund angeben:");
        if (!fehlgrund) {
          toast.error("Bei Bildungsgutschein ist der Fehlgrund Pflicht");
          return;
        }
      }
      
      try {
        await axios.post(`${API}/anwesenheit`, {
          teilnehmer_id: teilnehmerId,
          massnahme_id: selectedMassnahme,
          datum: selectedDatum,
          anwesend: currentStatus !== false,
          fehlgrund
        });
        toast.success("Anwesenheit erfasst");
        loadAnwesenheit();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Fehler beim Speichern");
      }
    }
  };

  const selectedMassnahmeData = massnahmen.find(m => m.id === selectedMassnahme);

  return (
    <div className="page-container" data-testid="anwesenheit-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Anwesenheit</h1>
      </div>

      <Card className="card-custom mb-6">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Maßnahme</Label>
              <Select value={selectedMassnahme} onValueChange={setSelectedMassnahme}>
                <SelectTrigger data-testid="select-massnahme">
                  <SelectValue placeholder="Maßnahme wählen" />
                </SelectTrigger>
                <SelectContent>
                  {massnahmen.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Datum</Label>
              <Input
                type="date"
                value={selectedDatum}
                onChange={(e) => setSelectedDatum(e.target.value)}
                data-testid="select-datum"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {selectedMassnahme && (
        <Card className="card-custom">
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>{selectedMassnahmeData?.name} - {selectedDatum}</span>
              {selectedMassnahmeData?.massnahmennummer && (
                <Badge variant="outline" className="bg-blue-50 text-blue-700">
                  Nr.: {selectedMassnahmeData.massnahmennummer}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="loading-spinner"></div>
              </div>
            ) : getMassnahmeTeilnehmer().length === 0 ? (
              <p className="text-[#78716c] text-center py-8">Keine Teilnehmer in dieser Maßnahme</p>
            ) : (
              <div className="space-y-2">
                {getMassnahmeTeilnehmer().map(tn => {
                  const status = getAnwesenheitStatus(tn.id);
                  const isAnwesend = status?.anwesend;
                  const isBildungsgutschein = tn.finanzierungsart === "bildungsgutschein";
                  
                  return (
                    <div 
                      key={tn.id} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        status 
                          ? (isAnwesend ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200')
                          : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleToggleAnwesenheit(tn.id, !isAnwesend)}
                          className={`w-6 h-6 rounded-full flex items-center justify-center ${
                            status
                              ? (isAnwesend ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white')
                              : 'bg-gray-300'
                          }`}
                          data-testid={`toggle-${tn.id}`}
                        >
                          {status && (isAnwesend ? <CheckCircle size={14} /> : <X size={14} />)}
                        </button>
                        <div>
                          <p className="font-medium text-sm">{tn.vorname} {tn.nachname}</p>
                          {isBildungsgutschein && (
                            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                              AZAV-Pflicht
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        {status && !isAnwesend && status.fehlgrund && (
                          <p className="text-xs text-red-600">Grund: {status.fehlgrund}</p>
                        )}
                        {!status && (
                          <span className="text-xs text-[#78716c]">Nicht erfasst</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ==================== PRÜFUNGEN ====================
const Pruefungen = () => {
  const [data, setData] = useState([]);
  const [massnahmen, setMassnahmen] = useState([]);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ergebnisFilter, setErgebnisFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    massnahme_id: "",
    pruefungsdatum: "",
    pruefungsdatum_2: "",
    pruefungsart: "",
    teilnehmer_id: "",
    ergebnis: "offen",
    pruefer: "",
    bemerkung: "",
    gesamtpunkte: "",
    max_punkte: "",
    zertifikatsnummer: ""
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = ergebnisFilter !== "all" ? `?ergebnis=${ergebnisFilter}` : "";
      const [pruefungenRes, massnahmenRes, tnRes] = await Promise.all([
        axios.get(`${API}/pruefungen${params}`),
        axios.get(`${API}/massnahmen`),
        axios.get(`${API}/teilnehmer`)
      ]);
      setData(pruefungenRes.data);
      setMassnahmen(massnahmenRes.data);
      setTeilnehmer(tnRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [ergebnisFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getMassnahmeName = (id) => {
    const m = massnahmen.find(m => m.id === id);
    return m ? m.name : "-";
  };

  const getTeilnehmerName = (id) => {
    const tn = teilnehmer.find(t => t.id === id);
    return tn ? `${tn.vorname} ${tn.nachname}` : "-";
  };

  const tnAusLink = useRef(new URLSearchParams(window.location.search).get("tn"));
  useEffect(() => {
    const tid = tnAusLink.current;
    if (!tid || loading) return;
    tnAusLink.current = null;
    const item = data.find((p) => p.teilnehmer_id === tid);
    if (item) handleEdit(item);
    else { resetForm(); setEditItem(null); setFormData((d) => ({ ...d, teilnehmer_id: tid })); setDialogOpen(true); }
  }, [data, loading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    const payload = { ...formData };
    ["gesamtpunkte", "max_punkte", "punkte_schriftlich", "punkte_muendlich", "punkte_facharbeit"].forEach((k) => {
      if (payload[k] === "" || payload[k] === undefined) payload[k] = null;
    });
    try {
      if (editItem) {
        await axios.put(`${API}/pruefungen/${editItem.id}`, payload);
        toast.success("Prüfung aktualisiert");
      } else {
        await axios.post(`${API}/pruefungen`, payload);
        toast.success("Prüfung erstellt");
      }
      setDialogOpen(false);
      setEditItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/pruefungen/${id}`);
        toast.success("Prüfung gelöscht");
        fetchData();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      massnahme_id: item.massnahme_id,
      pruefungsdatum: item.pruefungsdatum || "",
      pruefungsdatum_2: item.pruefungsdatum_2 || "",
      pruefungsart: item.pruefungsart,
      teilnehmer_id: item.teilnehmer_id,
      ergebnis: item.ergebnis,
      pruefer: item.pruefer || "",
      pruefer_2: item.pruefer_2 || "",
      ergebnis_2: item.ergebnis_2 || "",
      unterlage_anhang_id: item.unterlage_anhang_id || "",
      unterlage_filename: item.unterlage_filename || "",
      punkte_schriftlich: item.punkte_schriftlich ?? "",
      punkte_muendlich: item.punkte_muendlich ?? "",
      punkte_facharbeit: item.punkte_facharbeit ?? "",
      bemerkung: item.bemerkung || "",
      gesamtpunkte: item.gesamtpunkte || "",
      max_punkte: item.max_punkte || "",
      zertifikatsnummer: item.zertifikatsnummer || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      massnahme_id: "",
      pruefungsdatum: "",
      pruefungsdatum_2: "",
      pruefungsart: "",
      teilnehmer_id: "",
      ergebnis: "offen",
      pruefer: "",
      pruefer_2: "",
      ergebnis_2: "",
      unterlage_anhang_id: "",
      unterlage_filename: "",
      bemerkung: "",
      gesamtpunkte: "",
      max_punkte: "",
      zertifikatsnummer: ""
    });
  };

  const unterlageHochladen = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!formData.teilnehmer_id) { toast.error("Bitte zuerst Teilnehmer/in wählen"); return; }
    const fd = new FormData();
    fd.append("file", f);
    fd.append("kategorie", "PR");
    try {
      const { data: a } = await axios.post(`${API}/teilnehmer/${formData.teilnehmer_id}/anhaenge`, fd);
      setFormData((d) => ({ ...d, unterlage_anhang_id: a.id, unterlage_filename: a.filename }));
      toast.success("Prüfungsunterlage in der Akte abgelegt");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hochladen fehlgeschlagen");
    } finally { e.target.value = ""; }
  };

  return (
    <div className="page-container" data-testid="pruefungen-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Prüfungen</h1>
        <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-pruefung-btn">
          <Plus size={16} className="mr-2" />
          Neue Prüfung
        </Button>
      </div>

      <BestandenOhnePruefung />
      <ZertifikatBelegCheck />
      <AzavReparaturen />
      <EinrichtungBelegstatus />
      <PraktikumNachweise />

      <Card className="card-custom mb-6">
        <CardContent className="p-4">
          <Select value={ergebnisFilter} onValueChange={setErgebnisFilter}>
            <SelectTrigger className="w-[200px]" data-testid="ergebnis-filter">
              <SelectValue placeholder="Ergebnis" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Ergebnisse</SelectItem>
              <SelectItem value="offen">Offen</SelectItem>
              <SelectItem value="bestanden">Bestanden</SelectItem>
              <SelectItem value="nicht_bestanden">Nicht bestanden</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
            </div>
          ) : data.length === 0 ? (
            <div className="empty-state">
              <ClipboardCheck className="empty-state-icon mx-auto" />
              <p>Keine Prüfungen gefunden</p>
            </div>
          ) : (
            <Table data-testid="pruefungen-table">
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead>Maßnahme</TableHead>
                  <TableHead>Teilnehmer</TableHead>
                  <TableHead>Prüfungsart</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Punkte</TableHead>
                  <TableHead>Ergebnis</TableHead>
                  <TableHead>Zertifikat</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{getMassnahmeName(item.massnahme_id)}</TableCell>
                    <TableCell>{getTeilnehmerName(item.teilnehmer_id)}</TableCell>
                    <TableCell>{item.pruefungsart}</TableCell>
                    <TableCell>{item.pruefungsdatum}</TableCell>
                    <TableCell>
                      {item.gesamtpunkte ? (
                        <span className="font-mono">
                          {item.gesamtpunkte}{item.max_punkte ? `/${item.max_punkte}` : ''}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell><StatusBadge status={item.ergebnis} /></TableCell>
                    <TableCell>
                      {item.zertifikatsnummer ? (
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                          {item.zertifikatsnummer}
                        </Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <button className="action-btn" onClick={() => handleEdit(item)}>
                        <Pencil size={16} />
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={16} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>
              {editItem ? "Prüfung bearbeiten" : "Neue Prüfung"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Maßnahme *</Label>
              <Select value={formData.massnahme_id} onValueChange={(v) => setFormData({...formData, massnahme_id: v})}>
                <SelectTrigger data-testid="input-pruefung-massnahme">
                  <SelectValue placeholder="Maßnahme wählen" />
                </SelectTrigger>
                <SelectContent>
                  {massnahmen.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Teilnehmer *</Label>
              <Select value={formData.teilnehmer_id} onValueChange={(v) => setFormData({...formData, teilnehmer_id: v})}>
                <SelectTrigger data-testid="input-pruefung-teilnehmer">
                  <SelectValue placeholder="Teilnehmer wählen" />
                </SelectTrigger>
                <SelectContent>
                  {teilnehmer.map(tn => (
                    <SelectItem key={tn.id} value={tn.id}>{tn.vorname} {tn.nachname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Prüfungsart *</Label>
              <Input
                value={formData.pruefungsart}
                onChange={(e) => setFormData({...formData, pruefungsart: e.target.value})}
                placeholder="z.B. Schriftlich, Praktisch"
                data-testid="input-pruefungsart"
              />
            </div>
            <div>
              <Label>Prüfungsdatum *</Label>
              <Input
                type="date"
                value={formData.pruefungsdatum}
                onChange={(e) => setFormData({...formData, pruefungsdatum: e.target.value})}
                data-testid="input-pruefungsdatum"
              />
            </div>
            <div>
              <Label>2. Prüfungsdatum (Nachprüfung)</Label>
              <div className="flex gap-2">
                <Input
                  type="date"
                  value={formData.pruefungsdatum_2 || ""}
                  onChange={(e) => setFormData({...formData, pruefungsdatum_2: e.target.value || null})}
                  className="flex-1"
                />
                {formData.pruefungsdatum_2 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setFormData({...formData, pruefungsdatum_2: null})}
                    className="text-red-500 hover:text-red-700"
                  >
                    <X size={14} />
                  </Button>
                )}
              </div>
            </div>
            <div>
              <Label>Ergebnis</Label>
              <Select value={formData.ergebnis} onValueChange={(v) => setFormData({...formData, ergebnis: v})}>
                <SelectTrigger data-testid="input-ergebnis">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="offen">Offen</SelectItem>
                  <SelectItem value="bestanden">Bestanden</SelectItem>
                  <SelectItem value="nicht_bestanden">Nicht bestanden</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {/* Zusätzliche Felder */}
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <Label>Gesamtpunkte</Label>
              <Input
                type="number"
                step="0.5"
                value={formData.gesamtpunkte || ""}
                onChange={(e) => setFormData({...formData, gesamtpunkte: e.target.value ? parseFloat(e.target.value) : null})}
                placeholder="z.B. 85"
              />
            </div>
            <div>
              <Label>Max. Punkte</Label>
              <Input
                type="number"
                value={formData.max_punkte || ""}
                onChange={(e) => setFormData({...formData, max_punkte: e.target.value ? parseFloat(e.target.value) : null})}
                placeholder="z.B. 100"
              />
            </div>
            <div>
              <Label>Zertifikatsnummer</Label>
              <Input
                value={formData.zertifikatsnummer || ""}
                onChange={(e) => setFormData({...formData, zertifikatsnummer: e.target.value})}
                placeholder="z.B. ZERT-2026-001"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-3 gap-4 mt-4">
            {[["punkte_schriftlich", "Punkte schriftlich"], ["punkte_muendlich", "Punkte mündlich"], ["punkte_facharbeit", "Punkte Facharbeit"]].map(([k, l]) => (
              <div key={k}>
                <Label>{l}</Label>
                <Input type="number" step="0.01" value={formData[k] ?? ""} data-testid={`input-${k}`}
                  onChange={(e) => setFormData({...formData, [k]: e.target.value ? parseFloat(e.target.value) : null})} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <Label>Prüfer/in 1 *</Label>
              <Input value={formData.pruefer || ""} onChange={(e) => setFormData({...formData, pruefer: e.target.value})} data-testid="input-pruefer-1" />
            </div>
            <div>
              <Label>Prüfer/in 2 *</Label>
              <Input value={formData.pruefer_2 || ""} onChange={(e) => setFormData({...formData, pruefer_2: e.target.value})} data-testid="input-pruefer-2" />
            </div>
            <div>
              <Label>Ergebnis Wiederholung</Label>
              <Select value={formData.ergebnis_2 || "keine"} onValueChange={(v) => setFormData({...formData, ergebnis_2: v === "keine" ? "" : v})}>
                <SelectTrigger data-testid="input-ergebnis-2"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="keine">keine Wiederholung</SelectItem>
                  <SelectItem value="bestanden">Bestanden</SelectItem>
                  <SelectItem value="nicht_bestanden">Nicht bestanden</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3 border rounded-xl px-3 py-2" data-testid="pruefung-unterlage">
            <span className="text-sm">Prüfungsunterlage: {formData.unterlage_filename
              ? <span className="text-emerald-700 font-medium">{formData.unterlage_filename}</span>
              : <span className="text-orange-700">noch nicht hochgeladen</span>}</span>
            <label className="cursor-pointer text-xs font-semibold px-3 py-1.5 rounded-full border border-[#141312] hover:bg-[#141312] hover:text-white">
              {formData.unterlage_filename ? "Ersetzen" : "Hochladen"}
              <input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={unterlageHochladen} data-testid="input-pruefung-unterlage" />
            </label>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" data-testid="submit-pruefung"
              onClick={() => {
                if (!formData.pruefer?.trim() || !formData.pruefer_2?.trim()) { toast.error("Bitte beide Prüfer/innen eintragen"); return; }
                handleSubmit();
              }}>Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== ZERTIFIKATE ====================
const Zertifikate = () => {
  const [data, setData] = useState([]);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [massnahmen, setMassnahmen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [teilnehmerSearch, setTeilnehmerSearch] = useState("");
  const [formData, setFormData] = useState({
    teilnehmer_id: "",
    massnahme_id: "",
    zertifikatstyp: ""
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [zertRes, tnRes, massnahmenRes] = await Promise.all([
        axios.get(`${API}/zertifikate`),
        axios.get(`${API}/teilnehmer`),
        axios.get(`${API}/massnahmen`)
      ]);
      setData(zertRes.data);
      setTeilnehmer(tnRes.data);
      setMassnahmen(massnahmenRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getTeilnehmerName = (id) => {
    const tn = teilnehmer.find(t => t.id === id);
    return tn ? `${tn.vorname} ${tn.nachname}` : "-";
  };

  const getMassnahmeName = (id) => {
    const m = massnahmen.find(m => m.id === id);
    return m ? m.name : "-";
  };

  // Maßnahmen für Teilnehmer filtern
  const getFilteredMassnahmen = () => {
    if (!formData.teilnehmer_id) return massnahmen;
    const tn = teilnehmer.find(t => t.id === formData.teilnehmer_id);
    if (!tn) return massnahmen;
    
    // Filter: Zeige die Maßnahme, die dem Teilnehmer zugewiesen ist
    if (tn.massnahme_id) {
      const assignedMassnahme = massnahmen.filter(m => m.id === tn.massnahme_id);
      if (assignedMassnahme.length > 0) return assignedMassnahme;
    }
    
    // Fallback: Zeige alle Maßnahmen wenn keine zugewiesen
    return massnahmen;
  };

  // Teilnehmer filtern: nur mit Maßnahme UND bestanden, plus Suchfilter
  const getFilteredTeilnehmer = () => {
    let filtered = teilnehmer.filter(tn => 
      tn.id && tn.massnahme_id && tn.bestanden === true
    );
    
    if (teilnehmerSearch.trim()) {
      const search = teilnehmerSearch.toLowerCase();
      filtered = filtered.filter(tn => 
        `${tn.vorname} ${tn.nachname}`.toLowerCase().includes(search)
      );
    }
    
    return filtered;
  };

  const handleMassnahmeChange = (massnahmeId) => {
    const m = massnahmen.find(m => m.id === massnahmeId);
    setFormData(prev => ({
      ...prev,
      massnahme_id: massnahmeId,
      zertifikatstyp: m ? m.name : prev.zertifikatstyp
    }));
  };

  const handleSubmit = async () => {
    try {
      await axios.post(`${API}/zertifikate`, formData);
      toast.success("Zertifikat erstellt");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen. Prüfung bestanden?");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/zertifikate/${id}`);
        toast.success("Zertifikat gelöscht");
        fetchData();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await axios.get(`${API}/zertifikate/${id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `zertifikat_${id.slice(0,8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF heruntergeladen");
    } catch (error) {
      toast.error("Fehler beim Download");
    }
  };

  const resetForm = () => {
    setFormData({
      teilnehmer_id: "",
      massnahme_id: "",
      zertifikatstyp: ""
    });
  };

  return (
    <div className="page-container" data-testid="zertifikate-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Zertifikate</h1>
        <Button 
          className="btn-accent" 
          onClick={() => { resetForm(); setDialogOpen(true); }} 
          data-testid="add-zertifikat-btn"
        >
          <Plus size={16} />
          Neues Zertifikat
        </Button>
      </div>
      <ZertifikatDruckliste />

      <OriginalDruckOffen />

      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
            </div>
          ) : data.length === 0 ? (
            <div className="empty-state">
              <Award className="empty-state-icon mx-auto" />
              <p>Keine Zertifikate gefunden</p>
              <p className="text-sm mt-2 mb-4">Zertifikate können nur bei bestandener Prüfung erstellt werden.</p>
              <Button 
                className="bg-[#D9775A] hover:bg-[#C2603F] text-white"
                onClick={() => { resetForm(); setDialogOpen(true); }} 
              >
                <Plus size={16} className="mr-2" />
                Neues Zertifikat erstellen
              </Button>
            </div>
          ) : (
            <Table data-testid="zertifikate-table">
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead>Zertifikat-Nr.</TableHead>
                  <TableHead>Teilnehmer</TableHead>
                  <TableHead>Maßnahme</TableHead>
                  <TableHead>Ausstellungsdatum</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="mono-text font-medium">{item.id.slice(0,8).toUpperCase()}</TableCell>
                    <TableCell>{getTeilnehmerName(item.teilnehmer_id)}</TableCell>
                    <TableCell>{getMassnahmeName(item.massnahme_id)}</TableCell>
                    <TableCell>{item.ausstellungsdatum}</TableCell>
                    <TableCell className="text-right">
                      <button 
                        className="action-btn" 
                        onClick={() => handleDownloadPDF(item.id)}
                        title="PDF herunterladen"
                        data-testid={`download-pdf-${item.id}`}
                      >
                        <FileText size={16} />
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={16} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={(open) => {
        setDialogOpen(open);
        if (!open) {
          setTeilnehmerSearch("");
          setFormData({ teilnehmer_id: "", massnahme_id: "", zertifikatstyp: "" });
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>Neues Zertifikat</DialogTitle>
            <DialogDescription>
              Zertifikate können nur für Teilnehmer mit bestandener Prüfung erstellt werden.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Teilnehmer suchen *</Label>
              <Input
                placeholder="Name eingeben zum Suchen..."
                value={teilnehmerSearch}
                onChange={(e) => setTeilnehmerSearch(e.target.value)}
                className="mb-2"
              />
              <Select value={formData.teilnehmer_id} onValueChange={(v) => setFormData({...formData, teilnehmer_id: v, massnahme_id: ""})}>
                <SelectTrigger data-testid="input-zert-teilnehmer">
                  <SelectValue placeholder="Teilnehmer wählen" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {getFilteredTeilnehmer().length === 0 ? (
                    <SelectItem value="none" disabled>
                      {teilnehmerSearch ? "Kein Teilnehmer gefunden" : "Keine Teilnehmer mit bestandener Prüfung"}
                    </SelectItem>
                  ) : (
                    getFilteredTeilnehmer().map(tn => (
                      <SelectItem key={tn.id} value={tn.id}>
                        {tn.vorname} {tn.nachname}
                        <span className="ml-2 text-xs text-[#78716c]">
                          ({tn.finanzierungsart === "bildungsgutschein" ? "BG" : "SZ"})
                        </span>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {getFilteredTeilnehmer().length} Teilnehmer mit bestandener Prüfung verfügbar
              </p>
            </div>
            <div>
              <Label>Maßnahme *</Label>
              <Select 
                value={formData.massnahme_id} 
                onValueChange={handleMassnahmeChange}
                disabled={!formData.teilnehmer_id}
              >
                <SelectTrigger data-testid="input-zert-massnahme">
                  <SelectValue placeholder={formData.teilnehmer_id ? "Maßnahme wählen" : "Erst Teilnehmer wählen"} />
                </SelectTrigger>
                <SelectContent>
                  {getFilteredMassnahmen().length === 0 ? (
                    <SelectItem value="none" disabled>Keine Maßnahmen für diesen Teilnehmer</SelectItem>
                  ) : (
                    getFilteredMassnahmen().map(m => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name} ({m.umfang_ue} UE)
                        {m.bg_faehig && " "}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              {formData.teilnehmer_id && (
                <p className="text-xs text-[#78716c] mt-1">
                  Nur Maßnahmen, in denen der Teilnehmer eingeschrieben ist
                </p>
              )}
            </div>
            <div>
              <Label>Zertifikatstyp</Label>
              <Input
                value={formData.zertifikatstyp}
                onChange={(e) => setFormData({...formData, zertifikatstyp: e.target.value})}
                placeholder="Wird automatisch aus Maßnahme übernommen"
                data-testid="input-zertifikatstyp"
              />
              <p className="text-xs text-[#78716c] mt-1">Wird automatisch aus der gewählten Maßnahme gesetzt</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={handleSubmit} data-testid="submit-zertifikat">Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== RECHNUNGEN ====================
// Sub-Component: E-Mail-Historie zu einer Rechnung (Rechnungs- + Mahnungs-Versand)
const RechnungEmailHistorie = ({ rechnungId }) => {
  const [events, setEvents] = React.useState(null);
  React.useEffect(() => {
    if (!rechnungId) return;
    let alive = true;
    axios.get(`${API}/rechnungen/${rechnungId}/email-log`)
      .then(res => { if (alive) setEvents(res.data.events || []); })
      .catch(() => { if (alive) setEvents([]); });
    return () => { alive = false; };
  }, [rechnungId]);

  if (events === null) {
    return <div className="text-xs text-[#8E7B71] italic">E-Mail-Historie wird geladen…</div>;
  }
  if (events.length === 0) {
    return (
      <div className="rounded-md border border-[#E0D6CA] p-3 bg-[#FBF7F1]">
        <div className="text-xs uppercase tracking-[0.18em] text-[#8E7B71] font-semibold mb-1">E-Mail-Historie</div>
        <div className="text-xs text-[#8E7B71]">Noch keine E-Mail zu dieser Rechnung versendet.</div>
      </div>
    );
  }

  const typLabel = (typ) => {
    if (typ === "rechnung") return { label: "Rechnung", color: "#3D3835", bg: "#F1E8DF" };
    if (typ === "mahnung_stufe_0") return { label: "Zahlungserinnerung", color: "#6E866F", bg: "#E9EFE7" };
    if (typ === "mahnung_stufe_1") return { label: "1. Mahnung", color: "#E07856", bg: "#FBEBE2" };
    if (typ === "mahnung_stufe_2") return { label: "2. Mahnung · Letzte Frist", color: "#C2452F", bg: "#F6E4DF" };
    return { label: typ, color: "#3D3835", bg: "#F1E8DF" };
  };

  return (
    <div className="rounded-md border border-[#E0D6CA] p-3 bg-[#FBF7F1]" data-testid={`email-log-${rechnungId}`}>
      <div className="text-xs uppercase tracking-[0.18em] text-[#8E7B71] font-semibold mb-2">E-Mail-Historie</div>
      <div className="space-y-2">
        {events.map((e) => {
          const t = typLabel(e.typ);
          const dt = e.gesendet_am ? new Date(e.gesendet_am) : null;
          const dtStr = dt ? dt.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
          return (
            <div key={e.id} className="flex items-start gap-2 text-sm">
              <span
                className="text-[10px] px-2 py-0.5 rounded font-semibold uppercase tracking-wider shrink-0 mt-0.5"
                style={{ color: t.color, background: t.bg, border: `0.5px solid ${t.color}33` }}
              >
                {t.label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-[#3D3835] truncate">{e.subject}</div>
                <div className="text-[11px] text-[#8E7B71]">
                  {dtStr} · an <span className="font-medium">{e.to_email}</span>
                  {e.bcc && e.bcc.length > 0 && <span> · BCC {e.bcc.join(", ")}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


const Rechnungen = () => {
  const [data, setData] = useState([]);
  const [statistik, setStatistik] = useState({});
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [teilnehmerAlle, setTeilnehmerAlle] = useState([]); // AfA-Rechnungen: inkl. Bildungsgutschein
  const [einrichtungen, setEinrichtungen] = useState([]);
  const [massnahmen, setMassnahmen] = useState([]);
  const [vertriebsmitarbeiter, setVertriebsmitarbeiter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, offen, ueberfaellig, raten
  const [searchTerm, setSearchTerm] = useState(""); // NEU: Suchbegriff für Rechnungen
  
  // === Zahlung zuordnen Dialog ===
  const [zahlungZuordnenOpen, setZahlungZuordnenOpen] = useState(false);
  const [zuordnungBetrag, setZuordnungBetrag] = useState("");
  const [zuordnungVzweck, setZuordnungVzweck] = useState("");
  const [zuordnungKandidaten, setZuordnungKandidaten] = useState([]);
  const [zuordnungLoading, setZuordnungLoading] = useState(false);
  
  const searchKandidaten = async () => {
    if (!zuordnungBetrag && !zuordnungVzweck.trim()) {
      toast.error("Bitte Betrag oder Verwendungszweck eingeben");
      return;
    }
    setZuordnungLoading(true);
    try {
      const r = await axios.post(`${API}/rechnungen/zahlung-zuordnen`, {
        betrag: zuordnungBetrag ? parseFloat(zuordnungBetrag.toString().replace(",", ".")) : null,
        verwendungszweck: zuordnungVzweck.trim(),
      });
      setZuordnungKandidaten(r.data?.candidates || []);
      if ((r.data?.candidates || []).length === 0) {
        toast("Keine passende offene Rechnung gefunden", { icon: "" });
      }
    } catch (e) {
      toast.error("Suche fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    } finally {
      setZuordnungLoading(false);
    }
  };
  
  const markAlsBezahlt = async (rechnungsId) => {
    if (!window.confirm("Diese Rechnung als VOLLSTÄNDIG BEZAHLT markieren?")) return;
    try {
      await axios.put(`${API}/rechnungen/${rechnungsId}`, {
        zahlungsstatus: "bezahlt",
        bezahlt_am: new Date().toISOString().split("T")[0],
      });
      toast.success("Rechnung als bezahlt markiert");
      setZahlungZuordnenOpen(false);
      fetchData();
    } catch (e) {
      toast.error("Fehlgeschlagen: " + (e.response?.data?.detail || e.message));
    }
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  // Lokales State: pro Rate (key=rate_nr) das geplante Zahlungs-Datum vor dem Klick auf „Bezahlt markieren"
  const [rateZahlDatum, setRateZahlDatum] = useState({});
  // Lokales State: pro Rate der tatsächlich eingegangene Betrag (überschreibt vereinbarte rate.betrag)
  const [rateZahlBetrag, setRateZahlBetrag] = useState({});
  const [editItem, setEditItem] = useState(null); // NEU: Für Bearbeitung
  const [editOnlineDialog, setEditOnlineDialog] = useState(null); // NEU: Für WooCommerce-Bearbeitung
  const [onlineZahlung, setOnlineZahlung] = useState(null);
  const [editRatenDialog, setEditRatenDialog] = useState(null); // NEU: Für einfache Raten-Bearbeitung bei normalen Rechnungen
  const [zzDialog, setZzDialog] = useState(null); // Zahlungsziel-Verlängerung Dialog
  const [zzNeuesDatum, setZzNeuesDatum] = useState("");
  const [zzGrund, setZzGrund] = useState("");
  const [zzMahnungReset, setZzMahnungReset] = useState(true);
  // Manuelle Mahnungs-Versand-Erfassung (z.B. wenn extern per eigener E-Mail verschickt)
  const [mahnungManuellDialog, setMahnungManuellDialog] = useState(null); // { rechnung, stufe }
  const [mahnungManuellDatum, setMahnungManuellDatum] = useState("");
  const [mahnungManuellEmpfaenger, setMahnungManuellEmpfaenger] = useState("");
  const [mahnungManuellWeg, setMahnungManuellWeg] = useState("extern");
  const [mahnungManuellNotiz, setMahnungManuellNotiz] = useState("");

  // NEU: Volle manuelle Mahnung — alle Werte selbst eingeben
  const [mahnErstellDialog, setMahnErstellDialog] = useState(null); // rechnung
  const [mahnStufe, setMahnStufe] = useState(1);
  const [mahnBetrag, setMahnBetrag] = useState("");
  const [mahnDatum, setMahnDatum] = useState("");
  const [mahnFrist, setMahnFrist] = useState("");
  const [mahnGebuehr, setMahnGebuehr] = useState("0");
  const [mahnZinsen, setMahnZinsen] = useState("0");
  const [mahnBeschreibung, setMahnBeschreibung] = useState("");
  const [mahnNotizIntern, setMahnNotizIntern] = useState("");
  const [mahnGenerating, setMahnGenerating] = useState(false);
  
  // NEU: Erweiterte formData für mehrere Positionen
  const [formData, setFormData] = useState({
    empfaenger_typ: "teilnehmer",
    teilnehmer_id: "",
    pflegeeinrichtung_id: "",
    // NEU: Empfängerdaten manuell überschreibbar
    empfaenger_name: "",
    empfaenger_adresse: "",
    // NEU: Positionen (statt einzelne Maßnahme)
    positionen: [],
    // Legacy für Abwärtskompatibilität
    massnahme_id: "",
    rabatt_art: "keiner",
    rabatt_hoehe: 0,
    zahlungsart: "einmalzahlung",
    zahlungsziel: "",
    // NEU: Flexible Ratenanzahl
    anzahl_raten: 1,
    vertriebsmitarbeiter_id: "",
    interne_notiz: "",
    // NEU: Leistungszeitraum (§ 14 UStG Pflichtangabe)
    leistungszeitraum_von: "",
    leistungszeitraum_bis: ""
  });

  // Kurse mit Preisen für schnelle Auswahl (vordefiniert)
  const KURS_VORLAGEN = [
    // Standard-Preise (für Pflegeeinrichtungen & Selbstzahler)
    { name: "LG1 & LG2", preis: 1360 },
    { name: "LG1 & LG2 (BG)", preis: 1495.52, massnahmenummer: "364 1013 2026" },
    { name: "Pflegehilfskraft & Betreuungsassistent", preis: 3114.54 },
    { name: "Pflegehilfskraft (BG)", preis: 3114.54, massnahmenummer: "364 1232 2024" },
    { name: "Palliativ Care", preis: 3490 },
    { name: "Wundexperte", preis: 1690 },
    { name: "Fachtherapeut Wunde nach HKP-Richtlinien", preis: 2890 },
    { name: "Pflegetherapeut Wunde nach HKP-Richtlinien", preis: 1450 },
    { name: "Expertenkurs Außerklinische Intensivpflege", preis: 1990 },
    { name: "Chron. Wunden (Pflegefachkräfte)", preis: 1250 },
    { name: "LG3 & LG4", preis: 490 },
    { name: "Ambulanter Pflegeassistent", preis: 2178 },
    { name: "Ambulanter Pflegeassistent (BG)", preis: 2178, massnahmenummer: "364 1045 2026" },
    { name: "Alltagsbegleiter / Betreuungskraft", preis: 990 },
    { name: "Gerontopsychiatrie", preis: 0 },
    { name: "Pflegedienstleitung PDL", preis: 4690 },
    { name: "Praxisanleitung", preis: 2499 },
    { name: "Basiskurs Intensivpflege §132l", preis: 0 },
    { name: "Pflegeberater §7a SGB XI", preis: 0 },
    { name: "Qualitätsbeauftragter in der Pflege", preis: 0 },
    { name: "Rezertifizierung Praxisanleiter", preis: 620 },
    { name: "Rezertifizierung Betreuungskraft", preis: 250 },
    { name: "Berufsspezifisches Deutsch", preis: 1900 },
    { name: "Inklusionsassistenz", preis: 944 },
    { name: "Fortbildungspaket klein", preis: 1200 },
    { name: "Fortbildungspaket groß", preis: 1900 },
  ];

  const [onlineBestellungen, setOnlineBestellungen] = useState([]);
  
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [rechnungenRes, statistikRes, tnRes, einrRes, massRes, vmRes, onlineRes] = await Promise.all([
        axios.get(`${API}/rechnungen`),
        axios.get(`${API}/rechnungen/statistik`),
        axios.get(`${API}/teilnehmer`),
        axios.get(`${API}/pflegeeinrichtungen`),
        axios.get(`${API}/massnahmen`),
        axios.get(`${API}/vertriebsmitarbeiter`),
        axios.get(`${API}/online-bestellungen`)
      ]);
      setData(rechnungenRes.data);
      setOnlineBestellungen(onlineRes.data);
      setStatistik(statistikRes.data);
      // Nur Teilnehmer ohne Bildungsgutschein
      setTeilnehmer(tnRes.data.filter(t => t.finanzierungsart !== "bildungsgutschein"));
      setTeilnehmerAlle(tnRes.data);
      setEinrichtungen(einrRes.data);
      // Nur Maßnahmen mit Preis
      setMassnahmen(massRes.data.filter(m => m.preis_euro));
      setVertriebsmitarbeiter(vmRes.data.filter(v => v.aktiv));
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Online-Bestellungen zu Rechnungs-Format konvertieren für gemeinsame Anzeige
  const onlineAlsRechnungen = onlineBestellungen.map(b => {
    // Zahlungsstatus berechnen
    let zahlungsstatus = "offen";
    if (b.zahlungsart === "ratenzahlung" && b.raten && b.raten.length > 0) {
      const alleBezahlt = b.raten.every(r => r.bezahlt);
      const teilBezahlt = b.raten.some(r => r.bezahlt);
      zahlungsstatus = alleBezahlt ? "bezahlt" : (teilBezahlt ? "teilbezahlt" : "offen");
    } else {
      zahlungsstatus = b.bereits_bezahlt ? "bezahlt" : "offen";
    }
    
    return {
      ...b,
      id: b.id,
      rechnungsnummer: b.externe_rechnungsnummer ? `WOO-${b.externe_rechnungsnummer}` : `ONLINE-${b.id?.slice(0,6)}`,
      rechnungsdatum: b.datum || b.bestelldatum,
      empfaenger_name: b.kunde_name,
      empfaenger_typ: "online",
      massnahme_name: b.massnahme_oder_kurs || b.kurs_name,
      endbetrag: b.umsatzbetrag,
      zahlungsstatus: zahlungsstatus,
      zahlungsart: b.zahlungsart || "einmalzahlung",
      anzahl_raten: b.anzahl_raten || 1,
      raten: b.raten || [],
      ist_online_bestellung: true, // Marker für visuelle Unterscheidung
      notiz: b.notiz,
      // Mahnungsfelder übernehmen
      mahnung_stufe: b.mahnung_stufe || 0,
      mahnung_1_datum: b.mahnung_1_datum,
      mahnung_2_datum: b.mahnung_2_datum
    };
  });

  // Kombinierte Daten: Rechnungen + Online-Bestellungen
  const alleEintraege = [...data, ...onlineAlsRechnungen];

  const filteredData = alleEintraege.filter(r => {
    // Erst nach Status filtern
    let matchesFilter = true;
    if (filter === "offen") matchesFilter = r.zahlungsstatus === "offen";
    else if (filter === "ueberfaellig") matchesFilter = r.ist_ueberfaellig;
    else if (filter === "afa_ueberfaellig") matchesFilter = r.ist_ueberfaellig && r.ist_afa;
    // Ratenzahlung-Filter: zahlungsart ODER anzahl_raten > 1
    else if (filter === "raten") matchesFilter = (r.zahlungsart === "ratenzahlung" || (r.anzahl_raten && r.anzahl_raten > 1)) && r.zahlungsstatus !== "bezahlt";
    else if (filter === "online") matchesFilter = r.ist_online_bestellung === true;
    else if (filter === "bezahlt") matchesFilter = r.zahlungsstatus === "bezahlt";
    else if (filter === "storniert") matchesFilter = r.zahlungsstatus === "storniert";
    
    if (!matchesFilter) return false;
    
    // Dann nach Suchbegriff filtern
    if (!searchTerm.trim()) return true;
    
    const search = searchTerm.toLowerCase().trim();
    
    // Suche in Empfänger-Name (Teilnehmer, Einrichtung, manuell)
    if (r.empfaenger_name?.toLowerCase().includes(search)) return true;
    
    // Suche in Rechnungsnummer
    if (r.rechnungsnummer?.toLowerCase().includes(search)) return true;
    
    // Suche in Maßnahme/Kurs-Name
    if (r.massnahme_name?.toLowerCase().includes(search)) return true;
    
    // Suche in Positionen (falls vorhanden)
    if (r.positionen?.some(p => p.beschreibung?.toLowerCase().includes(search))) return true;
    
    // Suche nach Einrichtung-ID -> Einrichtungsname
    if (r.pflegeeinrichtung_id) {
      const einrichtung = einrichtungen.find(e => e.id === r.pflegeeinrichtung_id);
      if (einrichtung?.name?.toLowerCase().includes(search)) return true;
    }
    
    // Suche nach Teilnehmer-ID -> Teilnehmername und BG-Nummer
    if (r.teilnehmer_id) {
      const tn = teilnehmer.find(t => t.id === r.teilnehmer_id);
      const tnName = `${tn?.vorname || ''} ${tn?.nachname || ''}`.toLowerCase();
      if (tnName.includes(search)) return true;
      // NEU: Suche nach BG-Nummer
      if (tn?.bildungsgutschein_nummer?.toLowerCase().includes(search)) return true;
    }
    
    // NEU: Suche nach BG-Nummer (vom Backend geliefert)
    if (r.bg_nummer?.toLowerCase().includes(search)) return true;
    
    // NEU: Suche nach Teilnehmername (vom Backend geliefert)
    if (r.teilnehmer_name?.toLowerCase().includes(search)) return true;
    
    // NEU: Suche in internen Notizen (für BG-Nummer etc.)
    if (r.interne_notizen?.toLowerCase().includes(search)) return true;
    if (r.notizen?.toLowerCase().includes(search)) return true;
    
    // NEU: Volltextsuche für AfA-Geldeingang-Zuordnung
    if (r.empfaenger_adresse?.toLowerCase().includes(search)) return true;
    if (r.interne_notiz?.toLowerCase().includes(search)) return true;
    if (r.einleitungstext?.toLowerCase().includes(search)) return true;
    if (r.massnahme_id?.toLowerCase().includes(search)) return true;
    if (r.kurs?.toLowerCase().includes(search)) return true;
    // Beträge (sowohl mit Punkt als auch mit Komma)
    if (r.endbetrag != null) {
      const sNum = r.endbetrag.toFixed(2);
      if (sNum.includes(search) || sNum.replace(".", ",").includes(search)) return true;
      if (String(Math.round(r.endbetrag)).includes(search)) return true;
    }
    if (r.grundpreis != null && String(Math.round(r.grundpreis)).includes(search)) return true;
    // Raten-Verwendungszweck / Notiz
    if (r.raten?.some(ra => ra.verwendungszweck?.toLowerCase().includes(search) || ra.notiz?.toLowerCase().includes(search))) return true;
    
    // Suche in "Agentur für Arbeit" oder manuellem Empfänger-Typ
    if (r.empfaenger_typ === "afa" && "agentur für arbeit".includes(search)) return true;
    if (r.empfaenger_typ === "afa" && r.empfaenger_name?.toLowerCase().includes(search)) return true;
    
    return false;
  });

  const handleSubmit = async () => {
    try {
      // Validierung für manuellen Empfänger
      if (formData.empfaenger_typ === "manuell" || formData.empfaenger_typ === "afa") {
        if (!formData.empfaenger_name) {
          toast.error(formData.empfaenger_typ === "afa" ? "Bitte Agentur für Arbeit eingeben" : "Bitte Empfänger-Name eingeben");
          return;
        }
        if (formData.empfaenger_typ === "afa" && !editItem && !formData.teilnehmer_id) {
          toast.error("Bitte Teilnehmer/in auswählen – nötig für den erwarteten Zahlungseingang ab Kursstart");
          return;
        }
      } else if (formData.empfaenger_typ === "teilnehmer" && !formData.teilnehmer_id) {
        toast.error("Bitte Teilnehmer auswählen");
        return;
      } else if (formData.empfaenger_typ === "pflegeeinrichtung" && !formData.pflegeeinrichtung_id) {
        toast.error("Bitte Pflegeeinrichtung auswählen");
        return;
      }
      // NEU: Entweder Positionen oder Maßnahme erforderlich
      if (formData.positionen.length === 0 && !formData.massnahme_id) {
        toast.error("Bitte mindestens eine Position hinzufügen");
        return;
      }
      if (!formData.zahlungsziel) {
        toast.error("Bitte Zahlungsziel angeben");
        return;
      }

      // Daten für API vorbereiten
      const submitData = {
        empfaenger_typ: formData.empfaenger_typ,
        teilnehmer_id: formData.teilnehmer_id || null,
        pflegeeinrichtung_id: formData.pflegeeinrichtung_id || null,
        empfaenger_name: formData.empfaenger_name || null,
        empfaenger_adresse: formData.empfaenger_adresse || null,
        positionen: formData.positionen.map(p => ({
          beschreibung: p.beschreibung,
          menge: p.menge,
          einzelpreis: p.einzelpreis
        })),
        massnahme_id: formData.positionen.length === 0 ? formData.massnahme_id : null,
        rabatt_art: formData.rabatt_art,
        rabatt_hoehe: formData.rabatt_hoehe,
        zahlungsart: formData.anzahl_raten > 1 ? "ratenzahlung" : formData.zahlungsart,
        zahlungsziel: formData.zahlungsziel,
        anzahl_raten: formData.anzahl_raten,
        vertriebsmitarbeiter_id: formData.vertriebsmitarbeiter_id || null,
        interne_notiz: formData.interne_notiz || "",
        einleitungstext: formData.einleitungstext || "",
        leistungszeitraum_von: formData.leistungszeitraum_von || null,
        leistungszeitraum_bis: formData.leistungszeitraum_bis || null
      };

      if (editItem) {
        // Bearbeiten
        await axios.put(`${API}/rechnungen/${editItem.id}`, submitData);
        toast.success("Rechnung aktualisiert");
      } else {
        // Neu erstellen
        await axios.post(`${API}/rechnungen`, submitData);
        toast.success("Rechnung erstellt");
      }
      setDialogOpen(false);
      setEditItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };
  
  // NEU: Rechnung bearbeiten
  const handleEdit = (rechnung) => {
    setEditItem(rechnung);
    setFormData({
      empfaenger_typ: rechnung.empfaenger_typ || "teilnehmer",
      teilnehmer_id: rechnung.teilnehmer_id || "",
      pflegeeinrichtung_id: rechnung.pflegeeinrichtung_id || "",
      empfaenger_name: rechnung.empfaenger_name || "",
      empfaenger_adresse: rechnung.empfaenger_adresse || "",
      positionen: rechnung.positionen || [],
      massnahme_id: "",
      rabatt_art: rechnung.rabatt_art || "keiner",
      rabatt_hoehe: rechnung.rabatt_hoehe || 0,
      zahlungsart: rechnung.zahlungsart || "einmalzahlung",
      zahlungsziel: rechnung.zahlungsziel || "",
      anzahl_raten: rechnung.anzahl_raten || 1,
      vertriebsmitarbeiter_id: rechnung.vertriebsmitarbeiter_id || "",
      interne_notiz: rechnung.interne_notiz || "",
      einleitungstext: rechnung.einleitungstext || "",
      leistungszeitraum_von: rechnung.leistungszeitraum_von || "",
      leistungszeitraum_bis: rechnung.leistungszeitraum_bis || ""
    });
    setDialogOpen(true);
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await axios.get(`${API}/rechnungen/${id}/pdf`, {
        responseType: 'blob'
      });
      const rechnung = data.find(r => r.id === id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rechnung_${rechnung?.rechnungsnummer || id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("PDF heruntergeladen");
      if (rechnung && !rechnung.festgeschrieben_am) fetchData();
    } catch (error) {
      toast.error("Fehler beim Download");
    }
  };

  const handleZahlungUpdate = async (id, zahlungsstatus) => {
    try {
      await axios.put(`${API}/rechnungen/${id}/zahlung?zahlungsstatus=${zahlungsstatus}`);
      toast.success("Zahlungsstatus aktualisiert");
      fetchData();
      setDetailDialog(null);
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleRateUpdate = async (id, rateNr, bezahlt, bezahltAm = null, bezahltBetrag = null) => {
    try {
      // Bei Bezahlt-Markierung: Datum + tatsächlicher Betrag als Query-Parameter mitgeben
      let url = `${API}/rechnungen/${id}/zahlung?rate_nr=${rateNr}&bezahlt=${bezahlt}`;
      if (bezahlt && bezahltAm) {
        url += `&bezahlt_am=${encodeURIComponent(bezahltAm)}`;
      }
      if (bezahlt && bezahltBetrag !== null && bezahltBetrag !== undefined && bezahltBetrag !== "") {
        url += `&bezahlt_betrag=${encodeURIComponent(bezahltBetrag)}`;
      }
      await axios.put(url);
      toast.success(`Rate ${rateNr} ${bezahlt ? `als bezahlt markiert (${bezahltBetrag ?? 'voller Betrag'})` : 'zurückgesetzt'}`);
      fetchData();
      // Detail-Dialog aktualisieren
      const updated = await axios.get(`${API}/rechnungen/${id}`);
      setDetailDialog(updated.data);
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleStorno = async (id) => {
    const rechnung = data.find(r => r.id === id);
    const nr = rechnung?.rechnungsnummer || "";
    if (!window.confirm(`Rechnung ${nr} wirklich stornieren?\n\nDies kann nicht rückgängig gemacht werden.`)) return;
    const grund = window.prompt(
      `Grund der Stornierung (wird auf der Stornorechnung vermerkt und protokolliert):\n\nz.B. "Kunde hat Buchung zurückgezogen", "Doppelbuchung", "Nach Absprache" …`,
      ""
    );
    if (grund === null) return; // Abbrechen im Prompt = kompletter Abbruch
    try {
      await axios.put(`${API}/rechnungen/${id}/storno`, { grund: grund.trim() });
      toast.success("Rechnung storniert" + (grund.trim() ? ` · Grund: ${grund.trim().slice(0, 60)}` : ""));
      fetchData();
      setDetailDialog(null);
    } catch (error) {
      toast.error("Fehler beim Stornieren");
    }
  };

  const handleDeleteRechnung = async (id) => {
    const rechnung = data.find(r => r.id === id);
    const nr = rechnung?.rechnungsnummer || id;
    if (window.confirm(`Rechnung ${nr} wirklich ENDGÜLTIG löschen?\n\nDies kann NICHT rückgängig gemacht werden!`)) {
      try {
        await axios.delete(`${API}/rechnungen/${id}`);
        toast.success(`Rechnung ${nr} gelöscht`);
        fetchData();
        setDetailDialog(null);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Fehler beim Löschen");
      }
    }
  };

  // Mahnungs-Status zurücksetzen
  const handleMahnungReset = async (id, istOnline = false) => {
    if (!window.confirm("Mahnungs-Status wirklich zurücksetzen?\n\nDies ermöglicht es, die 1. Mahnung erneut zu senden (z.B. nach fehlerhafter Mahnung).")) {
      return;
    }
    
    try {
      const endpoint = istOnline 
        ? `${API}/online-bestellungen/${id}/mahnung-reset`
        : `${API}/rechnungen/${id}/mahnung-reset`;
      
      await axios.put(endpoint);
      toast.success("Mahnungs-Status zurückgesetzt - Sie können jetzt die 1. Mahnung erneut senden");
      fetchData();
      
      // Dialog aktualisieren
      if (detailDialog?.id === id && !istOnline) {
        const updated = await axios.get(`${API}/rechnungen/${id}`);
        setDetailDialog(updated.data);
      }
      if (editOnlineDialog?.id === id && istOnline) {
        setEditOnlineDialog(null);
      }
    } catch (error) {
      toast.error("Fehler beim Zurücksetzen");
    }
  };

  // Mahnung Vorschau - öffnet PDF in neuem Tab ohne zu speichern
  const handleMahnungVorschau = async (id, stufe, istOnline = false) => {
    const eintrag = istOnline 
      ? onlineBestellungen.find(b => b.id === id)
      : data.find(r => r.id === id);
    const bezeichnung = istOnline ? "Bestellung" : "Rechnung";
    const nummer = istOnline 
      ? (eintrag?.externe_rechnungsnummer || `ONLINE-${id?.slice(0,6)}`)
      : eintrag?.rechnungsnummer;
    
    try {
      toast.info(`Lade Vorschau für ${stufe}. Mahnung...`);
      
      // API-Endpoint für Vorschau (ohne DB-Update)
      const endpoint = istOnline 
        ? `${API}/online-bestellungen/${id}/mahnung/${stufe}/vorschau`
        : `${API}/rechnungen/${id}/mahnung/${stufe}/vorschau`;
      
      const response = await axios.get(endpoint);
      
      // PDF aus Base64 in neuem Tab öffnen
      const byteCharacters = atob(response.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Laden der Vorschau");
    }
  };

  // Mahnung erstellen, als gesendet markieren und PDF herunterladen
  const handleMahnung = async (id, stufe, istOnline = false) => {
    // Suche in beiden Listen
    const eintrag = istOnline 
      ? onlineBestellungen.find(b => b.id === id)
      : data.find(r => r.id === id);
    const bezeichnung = istOnline ? "Bestellung" : "Rechnung";
    const nummer = istOnline 
      ? (eintrag?.externe_rechnungsnummer || `ONLINE-${id?.slice(0,6)}`)
      : eintrag?.rechnungsnummer;
    const mahnungText = stufe === 1 ? "1. Mahnung (Zahlungserinnerung)" : "2. Mahnung (mit Inkasso-Androhung)";
    
    if (!window.confirm(`${mahnungText} für ${bezeichnung} ${nummer} erstellen und als GESENDET markieren?`)) {
      return;
    }
    
    try {
      // Unterschiedliche API-Endpoints für Rechnungen vs. Online-Bestellungen
      const endpoint = istOnline 
        ? `${API}/online-bestellungen/${id}/mahnung/${stufe}`
        : `${API}/rechnungen/${id}/mahnung/${stufe}`;
      
      const response = await axios.post(endpoint);
      
      // PDF aus Base64 herunterladen
      const byteCharacters = atob(response.data.pdf_base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', response.data.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${stufe}. Mahnung erstellt und als gesendet markiert`);
      fetchData();
      if (detailDialog?.id === id) {
        // Detail-Dialog aktualisieren (nur für Rechnungen)
        if (!istOnline) {
          const updated = await axios.get(`${API}/rechnungen/${id}`);
          setDetailDialog(updated.data);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen der Mahnung");
    }
  };

  // Rechnung per E-Mail an Empfänger versenden
  const handleRechnungPerEmail = async (id) => {
    const rechnung = data.find(r => r.id === id);
    const nummer = rechnung?.rechnungsnummer;
    // Aufgelöste E-Mail aus Backend holen (Kaskade: Rechnung → Teilnehmer → Pflegeeinrichtung)
    let defaultEmail = rechnung?.empfaenger_email || "";
    let quelleHinweis = "";
    try {
      const res = await axios.get(`${API}/rechnungen/${id}/default-email?zweck=rechnung`);
      if (res.data?.postversand) {
        window.alert(`Rechnung ${nummer}: E-Mail-Versand gesperrt\n\n${res.data.postversand_hinweis}\n\nEinwilligung einholen unter Finanzen → E-Rechnung & Versand.`);
        return;
      }
      if (res.data?.erechnung) quelleHinweis = "\n\nGeht als E-Rechnung (ZUGFeRD-PDF) raus.";
      if (res.data?.email) {
        defaultEmail = res.data.email;
        quelleHinweis += `\n\nAdresse gefunden: ${res.data.email} (${res.data.quelle_label})`;
      } else {
        quelleHinweis += `\n\n${res.data.quelle_label}`;
      }
    } catch (_) {}
    const toEmail = window.prompt(
      `Rechnung ${nummer} per E-Mail versenden${quelleHinweis}\n\n` +
      `Empfänger-Adresse (BCC geht automatisch an info@dickies-helden.de):`,
      defaultEmail
    );
    if (!toEmail || !toEmail.trim()) return;
    const customMessage = window.prompt(
      "Optionale persönliche Nachricht (leer lassen für Standard-Text):",
      ""
    );
    if (customMessage === null) return;
    try {
      const response = await axios.post(
        `${API}/rechnungen/${id}/send-email`,
        { to_email: toEmail.trim(), custom_message: customMessage.trim() || null }
      );
      toast.success(`Rechnung gesendet an ${response.data.to_email}${response.data.versand_art === "zugferd" ? " (E-Rechnung ZUGFeRD)" : ""}`);
      fetchData();
      if (detailDialog?.id === id) {
        const updated = await axios.get(`${API}/rechnungen/${id}`);
        setDetailDialog(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim E-Mail-Versand");
    }
  };


  // Mahnung per E-Mail an Rechnungsempfänger versenden (mit vorgeschaltetem Dialog)
  const handleMahnungPerEmail = async (id, stufe) => {
    const rechnung = data.find(r => r.id === id);
    const nummer = rechnung?.rechnungsnummer;
    // Aufgelöste E-Mail aus Backend holen (Kaskade: Rechnung → Teilnehmer → Pflegeeinrichtung)
    let defaultEmail = rechnung?.empfaenger_email || "";
    let quelleHinweis = "";
    try {
      const res = await axios.get(`${API}/rechnungen/${id}/default-email`);
      if (res.data?.email) {
        defaultEmail = res.data.email;
        quelleHinweis = `\n\nAdresse gefunden: ${res.data.email} (${res.data.quelle_label})`;
      } else {
        quelleHinweis = `\n\n${res.data.quelle_label}`;
      }
    } catch (_) {}
    const stufenLabel = stufe === 0 ? "Zahlungserinnerung" : stufe === 1 ? "1. Mahnung" : "2. Mahnung (Letzte Frist)";
    try {
      const pr = await axios.get(`${API}/rechnungen/${id}/mahnung/${stufe}/versandpruefung`);
      if (!pr.data.ok && !window.confirm(`Achtung – ${stufenLabel} für ${nummer}:\n\n${pr.data.hinweise.join("\n")}\n\nTrotzdem jetzt versenden?`)) return;
    } catch (_) {}
    const toEmail = window.prompt(
      `${stufenLabel} für Rechnung ${nummer} per E-Mail versenden${quelleHinweis}\n\n` +
      `Empfänger-Adresse (BCC geht automatisch an info@dickies-helden.de):`,
      defaultEmail
    );
    if (!toEmail || !toEmail.trim()) return;
    const customMessage = window.prompt(
      "Optionale persönliche Nachricht (leer lassen für Standard-Text):",
      ""
    );
    if (customMessage === null) return;  // Abbrechen
    try {
      const response = await axios.post(
        `${API}/rechnungen/${id}/mahnung/${stufe}/send-email`,
        {
          to_email: toEmail.trim(),
          custom_message: customMessage.trim() || null,
        }
      );
      toast.success(`${stufenLabel} gesendet an ${response.data.to_email}`);
      fetchData();
      if (detailDialog?.id === id) {
        const updated = await axios.get(`${API}/rechnungen/${id}`);
        setDetailDialog(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim E-Mail-Versand");
    }
  };

  // Öffnet den "Manuell eintragen"-Dialog: Nutzer trägt einen Mahnungs-Versand ein,
  // den er selber über seine eigene E-Mail / Brief / Fax verschickt hat.
  const openMahnungManuellDialog = (rechnung, stufe) => {
    const heute = new Date().toISOString().split("T")[0];
    setMahnungManuellDatum(heute);
    setMahnungManuellEmpfaenger(rechnung?.empfaenger_email || "");
    setMahnungManuellWeg("extern");
    setMahnungManuellNotiz("");
    setMahnungManuellDialog({ rechnung, stufe });
  };

  const submitMahnungManuell = async () => {
    if (!mahnungManuellDialog) return;
    const { rechnung, stufe } = mahnungManuellDialog;
    try {
      const res = await axios.post(
        `${API}/rechnungen/${rechnung.id}/mahnung/${stufe}/markiere-versendet`,
        {
          datum: mahnungManuellDatum || null,
          empfaenger: mahnungManuellEmpfaenger.trim() || null,
          versand_weg: mahnungManuellWeg || "extern",
          notiz: mahnungManuellNotiz.trim() || null,
        }
      );
      toast.success(res.data?.message || "Als versendet vermerkt");
      setMahnungManuellDialog(null);
      fetchData();
      if (detailDialog?.id === rechnung.id) {
        const updated = await axios.get(`${API}/rechnungen/${rechnung.id}`);
        setDetailDialog(updated.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Vermerken");
    }
  };

  // NEU: Mahnung manuell erstellen (alle Werte vom Nutzer eingegeben)
  const openMahnErstellDialog = (rechnung) => {
    const heute = new Date().toISOString().split("T")[0];
    const inVierzehn = new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    setMahnStufe((rechnung.mahnung_stufe || 0) + 1);
    setMahnBetrag("");
    setMahnDatum(heute);
    setMahnFrist(inVierzehn);
    setMahnGebuehr("0");
    setMahnZinsen("0");
    setMahnBeschreibung("");
    setMahnNotizIntern("");
    setMahnErstellDialog(rechnung);
  };

  const submitMahnErstell = async () => {
    if (!mahnErstellDialog) return;
    const betrag = parseFloat(String(mahnBetrag).replace(",", "."));
    if (!betrag || betrag <= 0) {
      toast.error("Bitte einen Betrag > 0 € eingeben");
      return;
    }
    if (!mahnDatum) {
      toast.error("Bitte Mahndatum angeben");
      return;
    }
    setMahnGenerating(true);
    try {
      const res = await axios.post(
        `${API}/rechnungen/${mahnErstellDialog.id}/mahnung-manuell`,
        {
          stufe: parseInt(mahnStufe),
          betrag,
          mahndatum: mahnDatum,
          frist_datum: mahnFrist || null,
          mahngebuehr: parseFloat(String(mahnGebuehr).replace(",", ".")) || 0,
          verzugspauschale: parseFloat(String(mahnZinsen).replace(",", ".")) || 0,
          beschreibung: mahnBeschreibung.trim() || "",
          notiz_intern: mahnNotizIntern.trim() || "",
        }
      );
      // PDF-Download aus base64
      try {
        const b64 = res.data?.pdf_base64;
        if (b64) {
          const bin = atob(b64);
          const bytes = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
          const blob = new Blob([bytes], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = res.data.filename || "Mahnung.pdf";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } catch (e) { /* ignore */ }
      toast.success(res.data?.message || "Mahnung erstellt");
      setMahnErstellDialog(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Erstellen");
    }
    setMahnGenerating(false);
  };



  const resetForm = () => {
    const today = new Date();
    const zahlungsziel = new Date(today.getTime() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    setFormData({
      empfaenger_typ: "teilnehmer",
      teilnehmer_id: "",
      pflegeeinrichtung_id: "",
      empfaenger_name: "",
      empfaenger_adresse: "",
      positionen: [],
      massnahme_id: "",
      rabatt_art: "keiner",
      rabatt_hoehe: 0,
      zahlungsart: "einmalzahlung",
      zahlungsziel: zahlungsziel,
      anzahl_raten: 1,
      vertriebsmitarbeiter_id: "",
      interne_notiz: "",
      einleitungstext: "",  // Text für Agentur für Arbeit etc.
      leistungszeitraum_von: "",
      leistungszeitraum_bis: ""
    });
    setEditItem(null);
  };

  // NEU: Positionen-Verwaltung
  const addPosition = (beschreibung = "", menge = 1, einzelpreis = 0, ist_abzug = false) => {
    setFormData(prev => ({
      ...prev,
      positionen: [...prev.positionen, { beschreibung, menge, einzelpreis, ist_abzug }]
    }));
  };

  const isAbzug = (pos) => pos.ist_abzug || pos.einzelpreis < 0;

  const updatePosition = (index, field, value) => {
    setFormData(prev => {
      const newPositionen = [...prev.positionen];
      const cur = newPositionen[index];
      if (field === 'einzelpreis' && isAbzug(cur)) value = -Math.abs(value);
      newPositionen[index] = { ...cur, ...(isAbzug(cur) ? { ist_abzug: true } : {}), [field]: value };
      return { ...prev, positionen: newPositionen };
    });
  };

  const removePosition = (index) => {
    setFormData(prev => ({
      ...prev,
      positionen: prev.positionen.filter((_, i) => i !== index)
    }));
  };

  const addKursVorlage = (kurs) => {
    addPosition(kurs.name, 1, kurs.preis);
  };

  // NEU: Berechnung für mehrere Positionen
  const zwischensumme = formData.positionen.reduce((sum, p) => sum + (p.menge * p.einzelpreis), 0);
  const rabattBetrag = formData.rabatt_art === "prozent" 
    ? zwischensumme * formData.rabatt_hoehe / 100 
    : (formData.rabatt_art === "fixbetrag" ? formData.rabatt_hoehe : 0);
  const endbetrag = Math.max(0, zwischensumme - rabattBetrag);

  // Legacy: Für alte Maßnahmen-Auswahl (wenn keine Positionen)
  const selectedMassnahme = massnahmen.find(m => m.id === formData.massnahme_id);
  const grundpreis = formData.positionen.length > 0 ? zwischensumme : (selectedMassnahme?.preis_euro || 0);
  const legacyRabattBetrag = formData.rabatt_art === "prozent" 
    ? grundpreis * formData.rabatt_hoehe / 100 
    : (formData.rabatt_art === "fixbetrag" ? formData.rabatt_hoehe : 0);
  const legacyEndbetrag = Math.max(0, grundpreis - legacyRabattBetrag);
  const finalEndbetrag = formData.positionen.length > 0 ? endbetrag : legacyEndbetrag;

  return (
    <div className="page-container" data-testid="rechnungen-page">
      {/* Header — Landingpage-Style Magazin-Anmutung */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-12">
        <div>
          <div className="dh-section-label" data-testid="rechnungen-section-label">Rechnungen & Finanzen</div>
          <h1 className="dh-h1">Zahlungsübersicht</h1>
          <p className="text-sm mt-2" style={{color: 'var(--dh-warm-gray)', fontWeight: 500}}>Nur für Selbstzahler & Pflegeeinrichtungen</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button
            variant="outline"
            className="border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
            data-testid="export-kunden-csv-btn"
            title="Lädt eine CSV mit allen Kunden, die mindestens eine Rechnung haben (inkl. Umsatz, Bezahlt, Offen)"
            onClick={async () => {
              const t = toast.loading("CSV wird erstellt...");
              try {
                const res = await axios.get(`${API}/export/kunden-mit-bestellungen`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
                const link = document.createElement('a');
                link.href = url;
                const today = new Date().toISOString().slice(0, 10);
                link.setAttribute('download', `kunden_mit_bestellungen_${today}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                toast.success("CSV heruntergeladen", { id: t });
              } catch (err) {
                toast.error("Fehler beim CSV-Export", { id: t });
              }
            }}
          >
            <Download size={16} className="mr-2" />
            Kunden-CSV
          </Button>
          <Button 
            variant="outline"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="btn-sync-zahlungsstatus"
            title="Korrigiert (a) den endgültigen Betrag aus Zwischensumme - Rabatt und (b) den Status aus Teilzahlungen — behebt falsche Beträge und 'Offen' trotz Zahlung"
            onClick={async () => {
              if (!window.confirm("Synchronisiert ALLE Rechnungen:\n\n1. Endbetrag aus Zwischensumme - Rabatt neu berechnen (korrigiert falsche Anzeige)\n2. Zahlungsstatus aus Teilzahlungen ableiten\n\nFortfahren?")) return;
              const t = toast.loading("Beträge und Status werden synchronisiert...");
              try {
                const [endRes, statusRes] = await Promise.all([
                  axios.post(`${API}/rechnungen/sync-endbetrag`),
                  axios.post(`${API}/rechnungen/sync-zahlungsstatus`)
                ]);
                toast.success(
                  `Beträge: ${endRes.data.geandert} korrigiert · Status: ${statusRes.data.geandert} korrigiert`,
                  { id: t, duration: 6000 }
                );
                fetchData();
              } catch (err) {
                toast.error(err.response?.data?.detail || "Synchronisation fehlgeschlagen", { id: t });
              }
            }}
          >
            <RefreshCw size={16} className="mr-2" />
            Status synchronisieren
          </Button>
          <Button
            variant="outline"
            className="border-indigo-300 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
            onClick={() => { setZahlungZuordnenOpen(true); setZuordnungBetrag(""); setZuordnungVzweck(""); setZuordnungKandidaten([]); }}
            data-testid="zahlung-zuordnen-btn"
            title="AfA- oder andere Zahlungseingänge per Betrag + Verwendungszweck einer Rechnung zuordnen"
          >
            <Search size={16} className="mr-2" />
            Zahlung zuordnen
          </Button>
          <Button 
            className="btn-accent" 
            onClick={() => { resetForm(); setDialogOpen(true); }} 
            data-testid="add-rechnung-btn"
          >
            <Plus size={16} />
            Neue Rechnung
          </Button>
        </div>
      </div>

      {/* NEU: Suchleiste — Volltextsuche über alle Rechnungsfelder */}
      <div className="mb-6">
        <div className="relative max-w-3xl">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#78716c]" size={18} />
          <Input
            type="text"
            placeholder="Volltextsuche: Nr · Name · Adresse · BG-Nummer · Betrag · Notiz · Verwendungszweck · Kurs · Positionen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white border-[#d2c9c1]"
            data-testid="rechnungen-search-input"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#78716c] hover:text-[#44403c]"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <p className="text-xs text-[#78716c] mt-1 ml-1">
          Tipp für Geldeingang-Zuordnung: Kopiere den Verwendungszweck aus dem Bank-Konto-Auszug hier rein
        </p>
        {searchTerm && (
          <p className="text-sm text-[#78716c] mt-2">
            {filteredData.length} Treffer für "{searchTerm}"
          </p>
        )}
      </div>

      {/* Statistik-Cards — Magazin-Layout mit Hero-KPI (Offene Summe) */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-10">
        {/* Hero: Offene Summe — 2 Spalten breit */}
        <div className="dh-kpi-hero md:col-span-2 lg:col-span-2" data-testid="kpi-card-summe">
          <div className="dh-kpi-hero-icon">
            <Euro size={80} />
          </div>
          <div className="dh-kpi-hero-label">— Offene Summe</div>
          <div>
            <div className="dh-kpi-hero-value">{(statistik.offene_summe || 0).toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 0})} €</div>
            <div className="dh-kpi-hero-sub">{statistik.ueberfaellig || 0} Rechnungen überfällig · {statistik.offen || 0} offen</div>
            <button type="button" onClick={(e) => { e.stopPropagation(); setFilter(filter === "afa_ueberfaellig" ? "all" : "afa_ueberfaellig"); }}
              className={`mt-2 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${filter === "afa_ueberfaellig" ? "bg-white text-[#8A2F1F]" : "bg-white/20 text-white hover:bg-white/30"}`}
              data-testid="filter-afa-ueberfaellig">
              AfA überfällig: {statistik.afa_ueberfaellig || 0}× · {(statistik.afa_ueberfaellig_summe || 0).toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 0})} €
            </button>
            <div className="dh-kpi-hero-sub" data-testid="kpi-afa-erwartet-14">AfA erwartet in 14 Tagen: {(statistik.afa_erwartet_14_tage || 0).toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 0})} € ({statistik.afa_erwartet_14_tage_anzahl || 0}×)</div>
          </div>
        </div>
        <div className={`dh-kpi-standard ${filter === 'all' ? 'dh-kpi-standard--active' : ''}`} onClick={() => setFilter("all")} data-testid="kpi-card-all">
          <div className="dh-kpi-standard-label">— Gesamt</div>
          <div>
            <div className="dh-kpi-standard-value">{statistik.total || 0}</div>
            <div className="dh-kpi-standard-sub">Rechnungen</div>
          </div>
        </div>
        <div className={`dh-kpi-standard ${filter === 'offen' ? 'dh-kpi-standard--active' : ''}`} onClick={() => setFilter("offen")} data-testid="kpi-card-offen">
          <div className="dh-kpi-standard-label">— Offen</div>
          <div>
            <div className="dh-kpi-standard-value">{statistik.offen || 0}</div>
            <div className="dh-kpi-standard-sub">Nicht bezahlt</div>
          </div>
        </div>
        <div className={`dh-kpi-standard ${filter === 'ueberfaellig' ? 'dh-kpi-standard--active' : ''}`} onClick={() => setFilter("ueberfaellig")} data-testid="kpi-card-ueberfaellig">
          <div className="dh-kpi-standard-label">— Überfällig</div>
          <div>
            <div className="dh-kpi-standard-value dh-red">{statistik.ueberfaellig || 0}</div>
            <div className="dh-kpi-standard-sub">Mahnstufe möglich</div>
          </div>
        </div>
        <div className={`dh-kpi-standard ${filter === 'raten' ? 'dh-kpi-standard--active' : ''}`} onClick={() => setFilter("raten")} data-testid="kpi-card-raten">
          <div className="dh-kpi-standard-label">— Raten offen</div>
          <div>
            <div className="dh-kpi-standard-value">{statistik.teilweise_bezahlt || 0}</div>
            <div className="dh-kpi-standard-sub">Teilzahlung</div>
          </div>
        </div>
        <div className={`dh-kpi-standard ${filter === 'online' ? 'dh-kpi-standard--active' : ''}`} onClick={() => setFilter("online")} data-testid="kpi-card-online">
          <div className="dh-kpi-standard-label">— Online-Shop</div>
          <div>
            <div className="dh-kpi-standard-value">{onlineBestellungen.length || 0}</div>
            <div className="dh-kpi-standard-sub">Automatik</div>
          </div>
        </div>
        <div className={`dh-kpi-standard ${filter === 'bezahlt' ? 'dh-kpi-standard--active' : ''}`} onClick={() => setFilter("bezahlt")} data-testid="kpi-card-bezahlt">
          <div className="dh-kpi-standard-label">— Bezahlt</div>
          <div>
            <div className="dh-kpi-standard-value dh-green">{statistik.bezahlt || 0}</div>
            <div className="dh-kpi-standard-sub">Abgeschlossen</div>
          </div>
        </div>
      </div>

      {/* Rechnungen Tabelle - zeigt jetzt ALLE inkl. Online-Bestellungen */}
      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
            </div>
          ) : filteredData.length === 0 ? (
            <div className="empty-state">
              {filter === "online" ? (
                <>
                  <ShoppingCart className="empty-state-icon mx-auto" />
                  <p>Keine Online-Bestellungen vorhanden</p>
                </>
              ) : filter === "raten" ? (
                <>
                  <CreditCard className="empty-state-icon mx-auto" />
                  <p>Keine offenen Ratenzahlungen</p>
                </>
              ) : (
                <>
                  <FileText className="empty-state-icon mx-auto" />
                  <p>Keine Rechnungen gefunden</p>
                </>
              )}
            </div>
          ) : (
            <Table data-testid="rechnungen-table">
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead>Rechnungsnr.</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead>Empfänger</TableHead>
                  <TableHead>Maßnahme</TableHead>
                  <TableHead>Betrag</TableHead>
                  <TableHead>Zahlungsart</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.map((item) => (
                  <TableRow 
                    key={item.id} 
                    className={
                      item.ist_online_bestellung ? 'bg-purple-50 border-l-4 border-purple-400' : 
                      item.ist_ueberfaellig && item.zahlungsstatus !== "storniert" ? 'bg-red-50' : ''
                    }
                  >
                    <TableCell className="font-mono font-medium">
                      <div className="flex items-center gap-2">
                        {item.ist_online_bestellung && (
                          <ShoppingCart size={14} className="text-purple-600" title="Online-Shop Bestellung" />
                        )}
                        {item.rechnungsnummer}
                        {item.zahlungsziel_historie && item.zahlungsziel_historie.length > 0 && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 border border-indigo-300"
                            title={`Zahlungsziel ${item.zahlungsziel_historie.length}× verlängert${item.zahlungsziel_historie.slice(-1)[0]?.grund ? ` · ${item.zahlungsziel_historie.slice(-1)[0].grund}` : ''}`}
                            data-testid={`badge-zz-verlaengert-${item.id}`}
                          >
                            <CalendarIcon size={10} /> {item.zahlungsziel_historie.length}×
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{item.rechnungsdatum}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-sm">{item.empfaenger_name}</p>
                        {/* NEU: Teilnehmername und BG-Nummer anzeigen */}
                        {item.teilnehmer_name && item.empfaenger_typ === "afa" && (
                          <p className="text-xs text-blue-600 font-medium">{item.teilnehmer_name}</p>
                        )}
                        {item.bg_nummer && (
                          <p className="text-xs text-purple-600 font-mono">{item.bg_nummer}</p>
                        )}
                        <p className="text-xs text-[#78716c]">
                          {item.ist_online_bestellung ? (
                            <span className="text-purple-600 font-medium">Online-Shop</span>
                          ) : item.empfaenger_typ === "afa" ? (
                            <span className="text-amber-600 font-medium" data-testid={`afa-kursstart-${item.id}`}>AfA {item.afa_kursstart ? `(Kursstart ${item.afa_kursstart.slice(8, 10)}.${item.afa_kursstart.slice(5, 7)}.)` : "(ab Rechnungsdatum)"}</span>
                          ) : item.empfaenger_typ === "teilnehmer" ? "Teilnehmer" : "Einrichtung"}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{item.massnahme_name}</TableCell>
                    <TableCell>
                      {/* Zeige Restbetrag bei Teilzahlung, sonst Gesamtbetrag */}
                      {item.offen_betrag > 0 && item.offen_betrag < (item.endbetrag || 0) - 0.005 ? (
                        <div data-testid={`betrag-offen-${item.id}`}>
                          <span className="font-medium text-amber-600">
                            {item.offen_betrag.toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} € offen
                          </span>
                          <p className="text-xs text-gray-500">
                            von {item.endbetrag?.toLocaleString('de-DE')} €
                          </p>
                          <p className="text-xs text-emerald-600">
                            {((item.endbetrag || 0) - item.offen_betrag - (item.teilstorno_betrag || 0)).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} € bezahlt
                          </p>
                          {item.teilstorno_betrag > 0 && (
                            <p className="text-xs text-red-600">{Number(item.teilstorno_betrag).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} € storniert</p>
                          )}
                        </div>
                      ) : (
                        <>
                          <span className="font-medium">{item.endbetrag?.toLocaleString('de-DE')} €</span>
                          {item.rabatt_art && item.rabatt_art !== "keiner" && !item.ist_online_bestellung && (
                            <p className="text-xs text-emerald-600">inkl. Rabatt</p>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.ist_online_bestellung ? (
                        item.zahlungsart === "ratenzahlung" || item.anzahl_raten > 1 ? (
                          <Badge variant="outline" className="bg-blue-100 text-blue-700">
                            {item.anzahl_raten} Raten
                            {item.raten && item.raten.length > 0 && (
                              <span className="ml-1 text-xs">
                                ({item.raten.filter(r => r.bezahlt).length}/{item.raten.length})
                              </span>
                            )}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-purple-100 text-purple-700">WooCommerce</Badge>
                        )
                      ) : (
                        <Badge variant="outline" className={(item.zahlungsart === "ratenzahlung" || item.anzahl_raten > 1) ? "bg-blue-50 text-blue-700" : item.zahlungsart === "barzahlung" ? "bg-green-50 text-green-700" : ""}>
                          {(item.zahlungsart === "ratenzahlung" || item.anzahl_raten > 1) ? `${item.anzahl_raten || item.raten?.length || 4} Raten` : item.zahlungsart === "barzahlung" ? "Bar" : "Einmal"}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {/* Status mit Teilzahlung-Anzeige */}
                      {item.bereits_bezahlt > 0 && item.zahlungsstatus !== "bezahlt" ? (
                        <Badge className="bg-amber-100 text-amber-700">Teilweise</Badge>
                      ) : (
                        <StatusBadge status={item.zahlungsstatus} />
                      )}
                      {(item.teilstornos || []).map((t) => (
                        <button key={t.id} type="button" title={`Teilstorno: ${t.grund}`} data-testid={`teilstorno-pdf-${t.id}`}
                          className="ml-1 inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700 hover:bg-red-100"
                          onClick={async (e) => {
                            e.stopPropagation();
                            try {
                              const res = await axios.get(`${API}/rechnungen/${item.id}/teilstorno-pdf/${t.id}`, { responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', `Stornorechnung_${t.nr}.pdf`);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                            } catch (err) { toast.error('Teilstorno-PDF konnte nicht erstellt werden'); }
                          }}>
                          <FileText size={11} /> Teilstorno −{Number(t.betrag).toLocaleString('de-DE', {minimumFractionDigits: 2})} €
                        </button>
                      ))}
                      {item.festgeschrieben_am && !item.ist_storno && (
                        <Badge className="ml-1 bg-stone-200 text-stone-700 text-xs" title={`Festgeschrieben seit ${String(item.festgeschrieben_am).slice(0, 10)} – Änderung nur per Storno`} data-testid={`badge-festgeschrieben-${item.id}`}>Gesperrt</Badge>
                      )}
                      {item.nichtantritt_hinweis && (
                        <Badge className="ml-1 bg-red-100 text-red-800 text-xs" title="Teilnehmer/in hat den Kurs nicht angetreten – Storno prüfen" data-testid={`badge-nichtantritt-${item.id}`}>Nichtantritt</Badge>
                      )}
                      {item.mahnsperre && (
                        <Badge className="ml-1 bg-amber-100 text-amber-800 text-xs" title={item.mahnsperre_grund || ""} data-testid={`badge-mahnsperre-${item.id}`}>Mahnsperre</Badge>
                      )}
                      {item.gesamtfaellig && (
                        <Badge className="ml-1 bg-red-600 text-white text-xs" data-testid={`badge-gesamtfaellig-${item.id}`}>Gesamtfällig</Badge>
                      )}
                      {item.forderungsausfall && item.zahlungsstatus !== "storniert" && (
                        <Badge className="ml-1 bg-stone-200 text-stone-700 text-xs" title={item.forderungsausfall_grund || ""} data-testid={`badge-ausfall-${item.id}`}>Ausfall – zählt nicht</Badge>
                      )}
                      {item.ist_ueberfaellig && item.zahlungsstatus !== "bezahlt" && item.zahlungsstatus !== "storniert" && (
                        <Badge className="ml-1 bg-red-100 text-red-700 text-xs">Überfällig</Badge>
                      )}
                      {!item.ist_ueberfaellig && item.afa_erwartet?.[0] && (
                        <Badge className="ml-1 bg-emerald-50 text-emerald-700 text-xs" data-testid={`badge-afa-erwartet-${item.id}`}>
                          AfA erwartet {item.afa_erwartet[0].von.slice(8, 10)}.{item.afa_erwartet[0].von.slice(5, 7)}.–{item.afa_erwartet[0].bis.slice(8, 10)}.{item.afa_erwartet[0].bis.slice(5, 7)}.
                        </Badge>
                      )}
                      {item.zahlungsart === "ratenzahlung" && Array.isArray(item.raten) && item.raten.some(r => !r.bezahlt) && (
                        <div className="mt-1 text-[11px] font-semibold text-blue-700" data-testid={`raten-offen-${item.id}`}>
                          {item.raten.filter(r => !r.bezahlt).length} Raten offen · {item.raten.filter(r => !r.bezahlt).reduce((s, r) => s + (Number(r.betrag) || 0), 0).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €
                        </div>
                      )}
                      {item.ist_online_bestellung && item.zahlungsstatus === 'bezahlt' && item.bezahlt_am && (
                        <div className="mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded inline-block bg-[#E9EFE7] text-[#3F5540] border border-[#BFD0BF]"
                          title={item.zahlung_notiz || ""} data-testid={`badge-online-bezahlt-${item.id}`}>
                          bezahlt am {item.bezahlt_am.split("-").reverse().join(".")}{item.zahlungsweg ? ` · ${item.zahlungsweg}` : ""}
                        </div>
                      )}
                      {/* Mahnungs-Vermerk: sichtbar in der Zeile */}
                      {item.mahnung_stufe > 0 && (() => {
                        const stufe = item.mahnung_stufe;
                        const datum = item[`mahnung_${stufe}_datum`];
                        const label = stufe === 1 ? "1. Mahnung" : stufe === 2 ? "2. Mahnung" : "Zahlungserinnerung";
                        const weg = item[`mahnung_${stufe}_versand_weg`];
                        const emailSystem = item[`mahnung_${stufe}_email_gesendet_am`];
                        const wegLabel = weg === "extern" ? "eigene E-Mail"
                                        : weg === "brief" ? "Brief"
                                        : weg === "fax" ? "Fax"
                                        : weg === "anderer" ? "anderer Weg"
                                        : emailSystem ? "System-E-Mail"
                                        : null;
                        const istVersendet = !!wegLabel;
                        const datumFmt = datum ? new Date(datum).toLocaleDateString("de-DE") : "—";
                        // Farbton: versendet = stufengetreu, nur PDF = neutrales Warn-Grau
                        const tone = !istVersendet
                          ? "bg-[#F5EFE6] text-[#8A6D3B] border border-[#D9C9A8]"
                          : stufe === 2
                          ? "bg-[#F6E4DF] text-[#8A2F1F] border border-[#E3B6AB]"
                          : stufe === 1
                          ? "bg-[#FBEBE2] text-[#8A3B22] border border-[#EBBBA6]"
                          : "bg-[#E9EFE7] text-[#3F5540] border border-[#BFD0BF]";
                        const haupttext = istVersendet
                          ? `${label} gesendet am ${datumFmt}`
                          : `${label} · nur PDF erstellt am ${datumFmt} — nicht versendet`;
                        return (
                          <div
                            className={`mt-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded ${tone}`}
                            title={istVersendet ? `${label} am ${datumFmt} · Versand: ${wegLabel}` : `${label} am ${datumFmt} · PDF wurde erstellt, aber noch NICHT versendet. Bitte per E-Mail-Button versenden oder manuell eintragen.`}
                            data-testid={`badge-mahnung-versendet-${item.id}`}
                          >
                            <AlertTriangle size={10} />
                            <span>{haupttext}</span>
                            {istVersendet && <span className="opacity-70">· {wegLabel}</span>}
                          </div>
                        );
                      })()}
                    </TableCell>
                    <TableCell className="text-right rechnung-actions-cell">
                      <div className="rechnung-actions-wrap">
                      {item.ist_online_bestellung ? (
                        <>
                          {/* Online-Bestellung bearbeiten */}
                          <button 
                            className="action-btn text-blue-500 hover:text-blue-700"
                            onClick={() => setEditOnlineDialog(item)}
                            title="Bearbeiten"
                          >
                            <Pencil size={16} />
                          </button>
                          <button
                            className="action-btn text-indigo-500 hover:text-indigo-700"
                            onClick={() => {
                              setZzDialog(item);
                              setZzNeuesDatum(item.zahlungsziel || "");
                              setZzGrund("");
                              setZzMahnungReset(true);
                            }}
                            title="Zahlungsziel verlängern"
                            data-testid={`btn-zahlungsziel-verlaengern-online-${item.id}`}
                          >
                            <CalendarIcon size={16} />
                          </button>
                          {/* Online-Bestellung: Bezahlt-Status togglen */}
                          <button 
                            className={`action-btn ${item.zahlungsstatus === 'bezahlt' ? 'text-emerald-600' : 'text-amber-600'}`}
                            data-testid={`btn-online-bezahlt-${item.id}`}
                            onClick={async () => {
                              if (item.zahlungsstatus !== 'bezahlt') { setOnlineZahlung(item); return; }
                              if (!window.confirm("Zahlung zurücknehmen und wieder als offen markieren?")) return;
                              try {
                                await axios.post(`${API}/online-bestellungen/${item.id}/zahlung`, { bezahlt: false });
                                toast.success("Als offen markiert");
                                fetchData();
                              } catch (err) {
                                toast.error("Fehler");
                              }
                            }}
                            title={item.zahlungsstatus === 'bezahlt' ? "Als offen markieren" : "Als bezahlt markieren"}
                          >
                            {item.zahlungsstatus === 'bezahlt' ? <XCircle size={16} /> : <Check size={16} />}
                          </button>
                          {/* Mahnung-Buttons für offene Online-Bestellungen */}
                          {item.zahlungsstatus !== "bezahlt" && (
                            <>
                              {(!item.mahnung_stufe || item.mahnung_stufe === 0) && (
                                <button 
                                  className="action-btn text-amber-500 hover:text-amber-700" 
                                  onClick={() => handleMahnung(item.id, 1, true)}
                                  title="1. Mahnung erstellen"
                                >
                                  <AlertTriangle size={16} />
                                </button>
                              )}
                              {item.mahnung_stufe === 1 && (
                                <button 
                                  className="action-btn text-red-500 hover:text-red-700" 
                                  onClick={() => handleMahnung(item.id, 2, true)}
                                  title="2. Mahnung (Inkasso) erstellen"
                                >
                                  <AlertTriangle size={16} />
                                </button>
                              )}
                              {item.mahnung_stufe === 2 && (
                                <span className="text-red-600 text-xs ml-1 font-bold">INKASSO</span>
                              )}
                              {/* Mahnungs-Status zurücksetzen Button */}
                              {item.mahnung_stufe > 0 && (
                                <button 
                                  className="action-btn text-orange-500 hover:text-orange-700" 
                                  onClick={() => handleMahnungReset(item.id, true)}
                                  title="Mahnungs-Status zurücksetzen"
                                >
                                  <RefreshCcw size={16} />
                                </button>
                              )}
                            </>
                          )}
                          {/* Online-Bestellung löschen */}
                          <button 
                            className="action-btn text-red-500 hover:text-red-700"
                            onClick={async () => {
                              if (window.confirm(`Online-Bestellung ${item.rechnungsnummer} löschen?`)) {
                                try {
                                  await axios.delete(`${API}/online-bestellungen/${item.id}`);
                                  toast.success("Gelöscht");
                                  fetchData();
                                } catch (err) {
                                  toast.error("Fehler beim Löschen");
                                }
                              }
                            }}
                            title="Löschen"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button className="action-btn" onClick={() => setDetailDialog(item)} title="Details">
                            <Eye size={16} />
                          </button>
                          <button className="action-btn" onClick={() => handleEdit(item)} title="Bearbeiten">
                            <Pencil size={16} />
                          </button>
                          <button className="action-btn text-blue-500 hover:text-blue-700" onClick={() => setEditRatenDialog(item)} title="Zahlungsart & Raten">
                            <CreditCard size={16} />
                          </button>
                          <button
                            className="action-btn text-indigo-500 hover:text-indigo-700"
                            onClick={() => {
                              setZzDialog(item);
                              setZzNeuesDatum(item.zahlungsziel || "");
                              setZzGrund("");
                              setZzMahnungReset(true);
                            }}
                            title="Zahlungsziel verlängern (verhindert falsche Mahnung)"
                            data-testid={`btn-zahlungsziel-verlaengern-${item.id}`}
                          >
                            <CalendarIcon size={16} />
                          </button>
                          <button className="action-btn" onClick={() => handleDownloadPDF(item.id)} title="PDF">
                            <Download size={16} />
                          </button>
                          <button
                            className="action-btn text-blue-500 hover:text-blue-700"
                            onClick={() => handleRechnungPerEmail(item.id)}
                            title={item.rechnung_email_gesendet_am ? `Rechnung zuletzt per E-Mail gesendet am ${new Date(item.rechnung_email_gesendet_am).toLocaleString("de-DE")} an ${item.rechnung_email_an || ""}${item.rechnung_email_art === "zugferd" ? " (E-Rechnung)" : ""}` : "Rechnung per E-Mail senden"}
                            data-testid={`rechnung-email-${item.id}`}
                          >
                            <Mail size={16} className={item.rechnung_email_gesendet_am ? "text-emerald-600" : ""} />
                          </button>
                          <span className="inline-block w-8 text-[10px] text-emerald-700 whitespace-nowrap self-center" data-testid={`rechnung-email-gesendet-${item.id}`} title={item.rechnung_email_gesendet_am ? `Per E-Mail gesendet am ${new Date(item.rechnung_email_gesendet_am).toLocaleString("de-DE")}` : ""}>
                            {item.rechnung_email_gesendet_am ? new Date(item.rechnung_email_gesendet_am).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" }) : ""}
                          </span>
                          {/* Mahnung-Button für offene/überfällige Rechnungen (manuelle Eingabe) */}
                          {item.zahlungsstatus !== "bezahlt" && item.zahlungsstatus !== "storniert" && (
                            <>
                              <button
                                className="action-btn text-amber-600 hover:text-amber-800"
                                onClick={() => openMahnErstellDialog(item)}
                                title="Mahnung erstellen (manuell: Betrag, Datum, Stufe selber eingeben)"
                                data-testid={`mahnung-erstellen-${item.id}`}
                              >
                                <AlertTriangle size={16} />
                              </button>
                              {/* Mahnungs-Status zurücksetzen Button */}
                              {item.mahnung_stufe > 0 && (
                                <button
                                  className="action-btn text-orange-500 hover:text-orange-700"
                                  onClick={() => handleMahnungReset(item.id, false)}
                                  title="Mahnungs-Status zurücksetzen"
                                >
                                  <RefreshCcw size={16} />
                                </button>
                              )}
                            </>
                          )}
                          {false && item.zahlungsstatus !== "bezahlt" && item.zahlungsstatus !== "storniert" && (() => {
                            // (alte automatische Mahn-Logik — deaktiviert, Nutzer wollte alles manuell)
                            const s = item.mahnung_stufe || 0;
                            const versendet = (n) => !!(item[`mahnung_${n}_email_gesendet_am`] || item[`mahnung_${n}_versand_weg`]);
                            // Wenn aktuelle Stufe noch NICHT versendet wurde → gleiche Stufe versenden;
                            // sonst nächste Stufe anbieten (0 → 1, 1 → 2, 2 → INKASSO).
                            const aktion = s === 0
                              ? { stufe: 1, offen: false, farbeText: "text-amber-500", farbeHover: "hover:text-amber-700", farbeMail: "text-amber-600", farbeMailHover: "hover:text-amber-800", farbeMan: "text-amber-700", farbeManHover: "hover:text-amber-900", label: "1. Mahnung", tone: "amber" }
                              : !versendet(s)
                              ? { stufe: s, offen: true,  farbeText: s === 2 ? "text-red-500" : "text-amber-500", farbeHover: s === 2 ? "hover:text-red-700" : "hover:text-amber-700", farbeMail: s === 2 ? "text-red-600" : "text-amber-600", farbeMailHover: s === 2 ? "hover:text-red-800" : "hover:text-amber-800", farbeMan: s === 2 ? "text-red-700" : "text-amber-700", farbeManHover: s === 2 ? "hover:text-red-900" : "hover:text-amber-900", label: s === 2 ? "2. Mahnung (Inkasso)" : "1. Mahnung", tone: s === 2 ? "red" : "amber" }
                              : s === 1
                              ? { stufe: 2, offen: false, farbeText: "text-red-500", farbeHover: "hover:text-red-700", farbeMail: "text-red-600", farbeMailHover: "hover:text-red-800", farbeMan: "text-red-700", farbeManHover: "hover:text-red-900", label: "2. Mahnung (Inkasso)", tone: "red" }
                              : null; // Stufe 2 versendet → INKASSO
                            return (
                              <>
                                {aktion ? (
                                  <>
                                    {/* PDF erstellen — nur wenn aktuelle Stufe noch nicht mal als PDF existiert */}
                                    {!aktion.offen && (
                                      <button
                                        className={`action-btn ${aktion.farbeText} ${aktion.farbeHover}`}
                                        onClick={() => handleMahnung(item.id, aktion.stufe)}
                                        title={`${aktion.label} erstellen (PDF, ohne Versand)`}
                                        data-testid={`mahnung-stufe${aktion.stufe}-pdf-${item.id}`}
                                      >
                                        <AlertTriangle size={16} />
                                      </button>
                                    )}
                                    {/* Per System-E-Mail versenden — immer verfügbar, auch wenn PDF schon existiert */}
                                    <button
                                      className={`action-btn ${aktion.farbeMail} ${aktion.farbeMailHover}`}
                                      onClick={() => handleMahnungPerEmail(item.id, aktion.stufe)}
                                      title={aktion.offen ? `${aktion.label} jetzt per System-E-Mail versenden (PDF ist bereits erstellt)` : `${aktion.label} per System-E-Mail versenden`}
                                      data-testid={`mahnung-stufe${aktion.stufe}-email-${item.id}`}
                                    >
                                      <Mail size={16} />
                                    </button>
                                    {/* Manuell eintragen — wenn selbst per eigener E-Mail / Brief verschickt */}
                                    <button
                                      className={`action-btn ${aktion.farbeMan} ${aktion.farbeManHover}`}
                                      onClick={() => openMahnungManuellDialog(item, aktion.stufe)}
                                      title={`${aktion.label} manuell eintragen (z.B. eigene E-Mail / Brief)`}
                                      data-testid={`mahnung-stufe${aktion.stufe}-manuell-${item.id}`}
                                    >
                                      <ClipboardCheck size={16} />
                                    </button>
                                  </>
                                ) : (
                                  <span className="text-red-600 text-xs ml-1 font-bold">INKASSO</span>
                                )}
                                {/* Mahnungs-Status zurücksetzen Button */}
                                {item.mahnung_stufe > 0 && (
                                  <button 
                                    className="action-btn text-orange-500 hover:text-orange-700" 
                                    onClick={() => handleMahnungReset(item.id, false)}
                                    title="Mahnungs-Status zurücksetzen"
                                  >
                                    <RefreshCcw size={16} />
                                  </button>
                                )}
                              </>
                            );
                          })()}
                          {item.zahlungsstatus !== "storniert" && (
                            <button 
                              className="action-btn text-red-500 hover:text-red-700" 
                              onClick={async () => {
                                if (!window.confirm(`Rechnung ${item.rechnungsnummer} wirklich stornieren?`)) return;
                                const grund = window.prompt(
                                  `Grund der Stornierung (wird auf der Stornorechnung vermerkt und protokolliert):\n\nz.B. "Kunde hat Buchung zurückgezogen", "Doppelbuchung", "Nach Absprache" …`,
                                  ""
                                );
                                if (grund === null) return;
                                try {
                                  await axios.put(`${API}/rechnungen/${item.id}/storno`, { grund: grund.trim() });
                                  toast.success(`Rechnung ${item.rechnungsnummer} storniert` + (grund.trim() ? ` · Grund: ${grund.trim().slice(0, 60)}` : ""));
                                  fetchData();
                                } catch (err) {
                                  toast.error("Fehler beim Stornieren");
                                }
                              }} 
                              title="Stornieren"
                            >
                              <XCircle size={16} />
                            </button>
                          )}
                          {item.zahlungsstatus === "storniert" && (
                            <>
                            <button 
                              className="action-btn text-red-600 hover:text-red-800" 
                              onClick={async (e) => {
                                e.stopPropagation();
                                try {
                                  toast.info('Stornorechnung wird erstellt...');
                                  const res = await axios.get(`${API}/rechnungen/${item.id}/storno-pdf`, { responseType: 'blob' });
                                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                                  const link = document.createElement('a');
                                  link.href = url;
                                  link.setAttribute('download', `Stornorechnung_${item.rechnungsnummer}.pdf`);
                                  document.body.appendChild(link);
                                  link.click();
                                  link.remove();
                                  toast.success('Stornorechnung heruntergeladen');
                                } catch (err) {
                                  toast.error('Fehler beim Erstellen der Stornorechnung');
                                }
                              }}
                              title="Stornorechnung herunterladen"
                              data-testid={`storno-pdf-btn-${item.id}`}
                            >
                              <FileText size={16} />
                            </button>
                            <button 
                              className="action-btn text-green-600 hover:text-green-800" 
                              onClick={async () => {
                                if (window.confirm(`Stornierung von ${item.rechnungsnummer} rückgängig machen?`)) {
                                  try {
                                    await axios.put(`${API}/rechnungen/${item.id}/storno-rueckgaengig`);
                                    toast.success(`Stornierung aufgehoben`);
                                    fetchData();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || "Fehler");
                                  }
                                }
                              }} 
                              title="Stornierung aufheben"
                            >
                              ↩
                            </button>
                            </>
                          )}
                          {/* Löschen-Button */}
                          <button 
                            className="action-btn text-red-400 hover:text-red-600" 
                            onClick={(e) => { e.stopPropagation(); handleDeleteRechnung(item.id); }}
                            title="Rechnung löschen"
                            data-testid={`delete-rechnung-${item.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* === Zahlung zuordnen Dialog (AfA-Geldeingang-Matching) === */}
      <Dialog open={zahlungZuordnenOpen} onOpenChange={setZahlungZuordnenOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="zahlung-zuordnen-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{fontFamily: 'Manrope, sans-serif'}}>
              <Search size={20} className="text-indigo-600" />
              Zahlung zuordnen
            </DialogTitle>
            <DialogDescription>
              Trage Betrag + Verwendungszweck aus dem Kontoauszug ein. Das System findet automatisch
              die wahrscheinlichste offene Rechnung — auch wenn die AfA nur mit Teilnehmer-Name
              oder BG-Nummer überweist.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Eingegangener Betrag (€)
                </label>
                <Input
                  type="text"
                  placeholder="z.B. 1250,00"
                  value={zuordnungBetrag}
                  onChange={(e) => setZuordnungBetrag(e.target.value)}
                  data-testid="zuordnung-betrag-input"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700 block mb-1">
                  Verwendungszweck aus Kontoauszug
                </label>
                <Input
                  type="text"
                  placeholder="z.B. Müller 1250,00 BG87654321 Pflegeassistent"
                  value={zuordnungVzweck}
                  onChange={(e) => setZuordnungVzweck(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') searchKandidaten(); }}
                  data-testid="zuordnung-vzweck-input"
                />
              </div>
            </div>
            
            <Button
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={searchKandidaten}
              disabled={zuordnungLoading || (!zuordnungBetrag && !zuordnungVzweck.trim())}
              data-testid="zuordnung-search-btn"
            >
              {zuordnungLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Search size={16} className="mr-2" />}
              Passende Rechnungen suchen
            </Button>
            
            {/* Ergebnisliste */}
            {zuordnungKandidaten.length > 0 && (
              <div className="space-y-2 mt-4">
                <div className="text-sm font-semibold text-gray-700">
                  {zuordnungKandidaten.length} Kandidat(en) gefunden — sortiert nach Wahrscheinlichkeit:
                </div>
                {zuordnungKandidaten.map((c, idx) => {
                  const r = c.rechnung;
                  const scoreColor = c.score >= 60 ? 'border-emerald-300 bg-emerald-50' :
                                     c.score >= 30 ? 'border-amber-300 bg-amber-50' :
                                     'border-gray-200 bg-gray-50';
                  const scoreBadge = c.score >= 60 ? 'bg-emerald-600' :
                                     c.score >= 30 ? 'bg-amber-500' : 'bg-gray-400';
                  return (
                    <div key={r.id} className={`p-3 rounded border-2 ${scoreColor}`} data-testid={`zuordnung-candidate-${idx}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center justify-center min-w-[44px] h-7 px-2 text-xs font-bold text-white ${scoreBadge} rounded-full`}>
                              {c.score}%
                            </span>
                            <span className="font-semibold text-base">
                              {r.rechnungsnummer}
                            </span>
                            <Badge className={`${r.zahlungsstatus === 'offen' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'} border-0 text-xs`}>
                              {r.zahlungsstatus}
                            </Badge>
                            {r.empfaenger_typ === 'afa' && (
                              <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">AfA</Badge>
                            )}
                          </div>
                          <div className="mt-1 text-sm">
                            <span className="font-medium">{r.empfaenger_name}</span>
                            {r.teilnehmer_name && r.teilnehmer_name !== r.empfaenger_name && (
                              <span className="text-gray-600"> · TN: {r.teilnehmer_name}</span>
                            )}
                            {r.bg_nummer && <span className="text-gray-600"> · BG: <span className="font-mono">{r.bg_nummer}</span></span>}
                          </div>
                          <div className="mt-0.5 text-xs text-gray-600">
                            {r.kurs} · Datum: {r.rechnungsdatum} · Fällig: {r.zahlungsziel}
                          </div>
                          {c.reasons?.length > 0 && (
                            <div className="mt-1.5 text-xs text-emerald-700">
                              ✓ Matches: {c.reasons.join(' · ')}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-xl font-bold text-gray-900">
                            {Number(r.endbetrag || 0).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                          </div>
                          <Button
                            size="sm"
                            className="mt-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                            onClick={() => markAlsBezahlt(r.id)}
                            data-testid={`zuordnung-bezahlt-${idx}`}
                          >
                            ✓ Als bezahlt
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setZahlungZuordnenOpen(false)} data-testid="zuordnung-close-btn">
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Neue Rechnung Dialog - KOMPLETT ÜBERARBEITET */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) { setEditItem(null); } setDialogOpen(open); }}>
        <DialogContent className="max-w-4xl max-h-[95vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>{editItem ? `Rechnung ${editItem.rechnungsnummer} bearbeiten` : 'Neue Rechnung erstellen'}</DialogTitle>
            <DialogDescription>
              {editItem ? 'Bearbeiten Sie die Rechnungsdaten.' : 'Fügen Sie beliebig viele Positionen hinzu. Alle Felder sind manuell editierbar.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {/* Empfänger-Typ & Auswahl in einer Zeile */}
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Empfänger-Typ *</Label>
                <Select value={formData.empfaenger_typ} onValueChange={(v) => setFormData({...formData, empfaenger_typ: v, teilnehmer_id: "", pflegeeinrichtung_id: "", empfaenger_name: "", empfaenger_adresse: ""})}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="teilnehmer">Teilnehmer (SZ)</SelectItem>
                    <SelectItem value="pflegeeinrichtung">Pflegeeinrichtung</SelectItem>
                    <SelectItem value="afa">AfA (Agentur für Arbeit)</SelectItem>
                    <SelectItem value="manuell">Manuell (freie Eingabe)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                {formData.empfaenger_typ === "afa" ? (
                  <>
                    <Label className="text-xs">Agentur für Arbeit *</Label>
                    <Input
                      value={formData.empfaenger_name}
                      onChange={(e) => setFormData({...formData, empfaenger_name: e.target.value})}
                      placeholder="z.B. Agentur für Arbeit Köln"
                      className="h-9"
                    />
                    <p className="text-xs text-purple-600 mt-1">Zahlungseingang ca. 1 Monat nach Kursstart</p>
                    <Label className="text-xs mt-2 block">Teilnehmer/in (für Kursstart) *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="h-9 w-full justify-between font-normal" data-testid="afa-teilnehmer-picker">
                          {formData.teilnehmer_id
                            ? `${teilnehmerAlle.find(t => t.id === formData.teilnehmer_id)?.vorname || ''} ${teilnehmerAlle.find(t => t.id === formData.teilnehmer_id)?.nachname || ''}`
                            : "Teilnehmer suchen..."}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Name eingeben..." />
                          <CommandList>
                            <CommandEmpty>Kein Teilnehmer gefunden</CommandEmpty>
                            <CommandGroup>
                              {teilnehmerAlle.filter(tn => tn.vorname || tn.nachname).map(tn => (
                                <CommandItem key={tn.id} value={`${tn.vorname} ${tn.nachname} ${tn.id}`}
                                  onSelect={() => setFormData({...formData, teilnehmer_id: tn.id})}
                                  data-testid={`afa-teilnehmer-option-${tn.id}`}>
                                  <Check className={`mr-2 h-4 w-4 ${formData.teilnehmer_id === tn.id ? "opacity-100" : "opacity-0"}`} />
                                  {tn.vorname} {tn.nachname}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </>
                ) : formData.empfaenger_typ === "manuell" ? (
                  <>
                    <Label className="text-xs">Empfänger-Name *</Label>
                    <Input
                      value={formData.empfaenger_name}
                      onChange={(e) => setFormData({...formData, empfaenger_name: e.target.value})}
                      placeholder="Name eingeben..."
                      className="h-9"
                    />
                  </>
                ) : formData.empfaenger_typ === "teilnehmer" ? (
                  <>
                    <Label className="text-xs">Teilnehmer *</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="h-9 w-full justify-between font-normal">
                          {formData.teilnehmer_id 
                            ? teilnehmer.find(t => t.id === formData.teilnehmer_id)?.vorname + ' ' + teilnehmer.find(t => t.id === formData.teilnehmer_id)?.nachname
                            : "Teilnehmer suchen..."}
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Name eingeben..." />
                          <CommandList>
                            <CommandEmpty>Kein Teilnehmer gefunden</CommandEmpty>
                            <CommandGroup>
                              {teilnehmer.filter(tn => tn.vorname || tn.nachname).map(tn => (
                                <CommandItem
                                  key={tn.id}
                                  value={`${tn.vorname} ${tn.nachname} ${tn.id}`}
                                  onSelect={() => {
                                    setFormData({
                                      ...formData, 
                                      teilnehmer_id: tn.id,
                                      empfaenger_name: `${tn.vorname || ''} ${tn.nachname || ''}`.trim(),
                                      empfaenger_adresse: `${tn.strasse || ''}\n${tn.plz || ''} ${tn.ort || ''}`.trim()
                                    });
                                  }}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${formData.teilnehmer_id === tn.id ? "opacity-100" : "opacity-0"}`} />
                                  {tn.vorname} {tn.nachname}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </>
                ) : formData.empfaenger_typ === "pflegeeinrichtung" ? (
                  <>
                    <Label className="text-xs">Pflegeeinrichtung * <button type="button" onClick={fetchData} className="text-blue-500 hover:text-blue-700 ml-1" title="Liste aktualisieren">↻</button></Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" role="combobox" className="h-9 w-full justify-between font-normal text-left">
                          <span className="truncate">
                            {formData.pflegeeinrichtung_id 
                              ? (einrichtungen.find(e => e.id === formData.pflegeeinrichtung_id)?.einrichtungsname || einrichtungen.find(e => e.id === formData.pflegeeinrichtung_id)?.name || "")
                              : "Einrichtung suchen..."}
                          </span>
                          <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[400px] p-0" align="start">
                        <Command>
                          <CommandInput placeholder="Name oder Ort eingeben..." />
                          <CommandList>
                            <CommandEmpty>Keine Einrichtung gefunden - <button type="button" onClick={fetchData} className="text-blue-500 underline">Liste aktualisieren</button></CommandEmpty>
                            <CommandGroup>
                              {einrichtungen.map(e => (
                                <CommandItem
                                  key={e.id}
                                  value={`${e.einrichtungsname || e.name || ''} ${e.ort || ''} ${e.plz || ''}`}
                                  onSelect={() => {
                                    setFormData({
                                      ...formData, 
                                      pflegeeinrichtung_id: e.id,
                                      empfaenger_name: e.einrichtungsname || e.name || '',
                                      empfaenger_adresse: `${e.strasse || ''}\n${e.plz || ''} ${e.ort || ''}`.trim()
                                    });
                                  }}
                                >
                                  <Check className={`mr-2 h-4 w-4 ${formData.pflegeeinrichtung_id === e.id ? "opacity-100" : "opacity-0"}`} />
                                  <div className="flex flex-col">
                                    <span className="font-medium">{e.einrichtungsname || e.name}</span>
                                    <span className="text-xs text-gray-500">{e.plz} {e.ort}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </>
                ) : null}
              </div>

              <div>
                <Label className="text-xs">Name (editierbar)</Label>
                <Input
                  className="h-9"
                  value={formData.empfaenger_name}
                  onChange={(e) => setFormData({...formData, empfaenger_name: e.target.value})}
                  placeholder="Name"
                />
              </div>
              <div>
                <Label className="text-xs">Adresse (editierbar)</Label>
                <Input
                  className="h-9"
                  value={formData.empfaenger_adresse.replace(/\n/g, ', ')}
                  onChange={(e) => setFormData({...formData, empfaenger_adresse: e.target.value})}
                  placeholder="Straße, PLZ Ort"
                />
              </div>
            </div>

            {/* POSITIONEN - Kompakter */}
            <Card className="border-2 border-dashed border-blue-200 bg-blue-50/30">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-semibold flex items-center gap-2">
                    <FileText size={16} className="text-blue-600" />
                    Rechnungspositionen
                  </Label>
                  <div className="flex gap-2">
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline"
                      onClick={() => addPosition("", 1, 0)}
                      className="h-7 text-xs text-blue-600 border-blue-300 hover:bg-blue-50"
                    >
                      <Plus size={12} className="mr-1" /> Neue Position
                    </Button>
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline"
                      onClick={() => addPosition("abzgl. Guthaben aus Rechnung Nr. ", 1, 0, true)}
                      className="h-7 text-xs text-green-600 border-green-300 hover:bg-green-50"
                      data-testid="rechnung-add-guthaben-btn"
                    >
                      <Plus size={12} className="mr-1" /> abzgl. Guthaben
                    </Button>
                  </div>
                </div>

                {/* Schnellauswahl - kompakter */}
                <div className="mb-3 p-2 bg-white rounded border">
                  <p className="text-xs text-gray-500 mb-1">Schnellauswahl (Klick = hinzufügen):</p>
                  <div className="flex flex-wrap gap-1">
                    {KURS_VORLAGEN.map((kurs, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => addKursVorlage(kurs)}
                        className="text-xs px-2 py-0.5 bg-gray-50 border border-gray-200 rounded hover:bg-blue-50 hover:border-blue-300 transition-colors"
                      >
                        {kurs.name} <span className="font-medium text-blue-600">({kurs.preis}€)</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Positionen-Liste */}
                {formData.positionen.length === 0 ? (
                  <div className="text-center py-4 text-gray-400 border border-dashed rounded bg-white">
                    <p className="text-sm">Noch keine Positionen - bitte oben auswählen oder hinzufügen</p>
                  </div>
                ) : (
                  <div className="space-y-1">
                    {/* Header */}
                    <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1">
                      <div className="col-span-5">Beschreibung</div>
                      <div className="col-span-2 text-center">Menge</div>
                      <div className="col-span-2 text-right">Einzelpreis €</div>
                      <div className="col-span-2 text-right">Gesamt €</div>
                      <div className="col-span-1"></div>
                    </div>
                    
                    {formData.positionen.map((pos, index) => (
                      <div key={index} data-testid={`rechnung-position-${index}`} className={`grid grid-cols-12 gap-2 items-center p-1.5 rounded border ${isAbzug(pos) ? 'bg-green-50 border-green-200' : 'bg-white'}`}>
                        <div className="col-span-5">
                          <Input
                            value={pos.beschreibung}
                            onChange={(e) => updatePosition(index, 'beschreibung', e.target.value)}
                            placeholder={isAbzug(pos) ? "z. B. abzgl. Guthaben aus Rechnung Nr. …" : "Beschreibung"}
                            className="h-8 text-sm"
                            data-testid={`rechnung-position-beschreibung-${index}`}
                          />
                          {isAbzug(pos) && <p className="text-[10px] text-green-700 mt-0.5">Betrag positiv eingeben – wird automatisch abgezogen</p>}
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            min="1"
                            value={pos.menge}
                            onChange={(e) => updatePosition(index, 'menge', parseInt(e.target.value) || 1)}
                            className="h-8 text-sm text-center"
                          />
                        </div>
                        <div className="col-span-2">
                          <Input
                            type="number"
                            step="0.01"
                            min={isAbzug(pos) ? "0" : undefined}
                            value={isAbzug(pos) ? (pos.einzelpreis ? Math.abs(pos.einzelpreis) : '') : pos.einzelpreis}
                            placeholder={isAbzug(pos) ? "Betrag" : undefined}
                            onChange={(e) => updatePosition(index, 'einzelpreis', parseFloat(e.target.value) || 0)}
                            className={`h-8 text-sm text-right ${isAbzug(pos) ? 'text-green-600 font-medium' : ''}`}
                            data-testid={`rechnung-position-preis-${index}`}
                          />
                        </div>
                        <div className={`col-span-2 text-right font-medium text-sm pr-1 ${isAbzug(pos) ? 'text-green-600' : ''}`}>
                          {(pos.menge * pos.einzelpreis).toLocaleString('de-DE')}
                        </div>
                        <div className="col-span-1 text-center">
                          <button 
                            type="button"
                            onClick={() => removePosition(index)}
                            className="text-red-500 hover:text-red-700 p-0.5"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                    
                    {/* Summenzeile direkt unter Positionen */}
                    <div className="grid grid-cols-12 gap-2 items-center pt-2 border-t mt-2">
                      <div className="col-span-9 text-right font-medium text-sm">
                        {formData.positionen.length} Position(en) - Zwischensumme:
                      </div>
                      <div className="col-span-2 text-right font-bold text-base text-emerald-700">
                        {zwischensumme.toLocaleString('de-DE')} €
                      </div>
                      <div className="col-span-1"></div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Rabatt & Zahlung - kompakt in einer Zeile */}
            <div className="grid grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">Rabattart</Label>
                <Select value={formData.rabatt_art} onValueChange={(v) => setFormData({...formData, rabatt_art: v, rabatt_hoehe: 0})}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="keiner">Kein Rabatt</SelectItem>
                    <SelectItem value="prozent">Prozent</SelectItem>
                    <SelectItem value="fixbetrag">Fixbetrag</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {formData.rabatt_art !== "keiner" ? (
                <div>
                  <Label className="text-xs">Rabatt {formData.rabatt_art === "prozent" ? "% (max 20%)" : "€"}</Label>
                  <Input
                    type="number"
                    className="h-9"
                    step={formData.rabatt_art === "prozent" ? "1" : "0.01"}
                    max={formData.rabatt_art === "prozent" ? 20 : undefined}
                    value={formData.rabatt_hoehe}
                    onChange={(e) => {
                      let val = parseFloat(e.target.value) || 0;
                      if (formData.rabatt_art === "prozent" && val > 20) {
                        toast.error("Rabatt darf maximal 20% sein (Provision = 20%)");
                        val = 20;
                      }
                      setFormData({...formData, rabatt_hoehe: val});
                    }}
                  />
                </div>
              ) : <div></div>}
              
              <div>
                <Label className="text-xs">Zahlungsart</Label>
                <Select 
                  value={formData.anzahl_raten > 1 ? "ratenzahlung" : formData.zahlungsart} 
                  onValueChange={(v) => {
                    // Bei Ratenzahlung: Zahlungsziel auf HEUTE setzen (1. Rate sofort fällig).
                    // Bei Einmalzahlung/Barzahlung: Standard-Zahlungsziel von 14 Tagen verwenden.
                    const heute = new Date().toISOString().split('T')[0];
                    const heutePlus14 = new Date(Date.now() + 14*24*60*60*1000).toISOString().split('T')[0];
                    if (v === "einmalzahlung" || v === "barzahlung") {
                      setFormData({
                        ...formData,
                        zahlungsart: v,
                        anzahl_raten: 1,
                        zahlungsziel: formData.zahlungsziel || heutePlus14,
                      });
                    } else {
                      setFormData({
                        ...formData,
                        zahlungsart: v,
                        anzahl_raten: formData.anzahl_raten > 1 ? formData.anzahl_raten : 2,
                        zahlungsziel: heute,
                      });
                    }
                  }}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="einmalzahlung">Einmalzahlung</SelectItem>
                    <SelectItem value="barzahlung">Barzahlung</SelectItem>
                    <SelectItem value="ratenzahlung">Ratenzahlung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {(formData.zahlungsart === "ratenzahlung" || formData.anzahl_raten > 1) && (
                <div>
                  <Label className="text-xs">Anzahl Raten</Label>
                  <Select 
                    value={formData.anzahl_raten.toString()} 
                    onValueChange={(v) => setFormData({...formData, anzahl_raten: parseInt(v)})}
                  >
                    <SelectTrigger className="h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2">2 Raten</SelectItem>
                      <SelectItem value="3">3 Raten</SelectItem>
                      <SelectItem value="4">4 Raten</SelectItem>
                      <SelectItem value="5">5 Raten</SelectItem>
                      <SelectItem value="6">6 Raten</SelectItem>
                      <SelectItem value="7">7 Raten</SelectItem>
                      <SelectItem value="8">8 Raten</SelectItem>
                      <SelectItem value="9">9 Raten</SelectItem>
                      <SelectItem value="10">10 Raten</SelectItem>
                      <SelectItem value="11">11 Raten</SelectItem>
                      <SelectItem value="12">12 Raten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label className="text-xs">Zahlungsziel *</Label>
                <Input
                  type="date"
                  className="h-9"
                  value={formData.zahlungsziel}
                  onChange={(e) => setFormData({...formData, zahlungsziel: e.target.value})}
                />
              </div>
            </div>

            {/* Gesamtbetrag Box - nur wenn Positionen vorhanden */}
            {formData.positionen.length > 0 && (
              <div className="flex items-center justify-between p-3 bg-gradient-to-r from-emerald-50 to-blue-50 rounded-lg border border-emerald-200">
                <div className="text-sm">
                  {rabattBetrag > 0 && (
                    <span className="text-emerald-600 mr-3">Rabatt: -{rabattBetrag.toLocaleString('de-DE')} €</span>
                  )}
                  {formData.anzahl_raten > 1 && (
                    <span className="text-blue-600">{formData.anzahl_raten} Raten à {(finalEndbetrag / formData.anzahl_raten).toLocaleString('de-DE', {maximumFractionDigits: 2})} €</span>
                  )}
                </div>
                <div className="text-right">
                  <span className="text-sm text-gray-500 mr-2">Gesamtbetrag:</span>
                  <span className="text-2xl font-bold" style={{ color: "#10b981" }} data-testid="rechnung-form-gesamtbetrag">{finalEndbetrag.toLocaleString('de-DE')} €</span>
                </div>
              </div>
            )}

            {/* Optionale Felder - ganz unten */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Vertriebsmitarbeiter (optional)</Label>
                <Select value={formData.vertriebsmitarbeiter_id || "none"} onValueChange={(v) => setFormData({...formData, vertriebsmitarbeiter_id: v === "none" ? "" : v})}>
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Nicht zugewiesen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nicht zugewiesen</SelectItem>
                    {vertriebsmitarbeiter.filter(vm => vm.id).map(vm => (
                      <SelectItem key={vm.id} value={vm.id}>{vm.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* NEU: Leistungszeitraum (§ 14 UStG Pflichtangabe) */}
              <div className="col-span-2 grid grid-cols-2 gap-3 p-3 rounded-md" style={{backgroundColor: "#FBEBE2"}}>
                <div className="col-span-2">
                  <Label className="text-xs font-semibold" style={{color: "#3D3835"}}>
                    Leistungszeitraum <span className="text-[10px] font-normal opacity-70">(§ 14 UStG Pflichtangabe)</span>
                  </Label>
                  <p className="text-[10px] opacity-70 mt-0.5" style={{color: "#8E7B71"}}>Wird automatisch aus dem Teilnehmer-Kurs übernommen, wenn leer gelassen.</p>
                </div>
                <div>
                  <Label className="text-xs">Von</Label>
                  <Input
                    type="date"
                    data-testid="rechnung-lz-von-input"
                    className="h-9"
                    value={formData.leistungszeitraum_von || ""}
                    onChange={(e) => setFormData({...formData, leistungszeitraum_von: e.target.value})}
                  />
                </div>
                <div>
                  <Label className="text-xs">Bis</Label>
                  <Input
                    type="date"
                    data-testid="rechnung-lz-bis-input"
                    className="h-9"
                    value={formData.leistungszeitraum_bis || ""}
                    onChange={(e) => setFormData({...formData, leistungszeitraum_bis: e.target.value})}
                  />
                </div>
              </div>

              {/* NEU: Einleitungstext für PDF (z.B. Bildungsgutschein-Info) */}
              <div className="col-span-2">
                <Label className="text-xs flex items-center gap-1">
                  Einleitungstext für PDF (optional)
                  <span className="text-gray-400 text-xs font-normal">- erscheint im PDF vor den Positionen</span>
                </Label>
                <textarea
                  className="w-full p-2 border rounded-md text-sm min-h-[80px] resize-y"
                  value={formData.einleitungstext || ""}
                  onChange={(e) => setFormData({...formData, einleitungstext: e.target.value})}
                  placeholder="z.B. für Agentur für Arbeit:&#10;Bildungsgutschein-Nr.: BGS-123456&#10;Teilnehmer: Max Mustermann&#10;Maßnahme: Pflegedienstleitung"
                />
              </div>
              
              <div>
                <Label className="text-xs">Interne Notiz (optional)</Label>
                <Input
                  className="h-9"
                  value={formData.interne_notiz}
                  onChange={(e) => setFormData({...formData, interne_notiz: e.target.value})}
                  placeholder="Interne Bemerkungen..."
                />
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <p className="text-xs text-gray-400 flex-1">USt-befreit gem. § 4 Nr. 21 UStG</p>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button 
              className="bg-[#D9775A] hover:bg-[#C2603F] text-white" 
              onClick={handleSubmit}
              disabled={formData.positionen.length === 0}
            >
              {editItem ? 'Änderungen speichern' : 'Rechnung erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail-Dialog - AKTUALISIERT für flexible Raten und Positionen */}
      <Dialog open={!!detailDialog} onOpenChange={() => setDetailDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {detailDialog && (
            <>
              <DialogHeader>
                <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>
                  Rechnung {detailDialog.rechnungsnummer}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                {/* Bearbeitbare Rechnungsnummer und Datum */}
                <div className="flex gap-2 items-end bg-amber-50 p-3 rounded-lg border border-amber-200">
                  <div className="flex-1">
                    <Label className="text-xs text-[#78716c]">Rechnungsnr.</Label>
                    <Input
                      defaultValue={detailDialog.rechnungsnummer}
                      id="edit-rechnungsnummer"
                      className="h-8 text-sm"
                      data-testid="edit-rechnungsnummer"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-[#78716c]">Rechnungsdatum</Label>
                    <Input
                      type="date"
                      defaultValue={detailDialog.rechnungsdatum}
                      id="edit-rechnungsdatum"
                      className="h-8 text-sm"
                      data-testid="edit-rechnungsdatum"
                    />
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                    data-testid="save-meta-btn"
                    onClick={async () => {
                      const nr = document.getElementById('edit-rechnungsnummer').value;
                      const datum = document.getElementById('edit-rechnungsdatum').value;
                      const payload = {};
                      if (nr !== detailDialog.rechnungsnummer) payload.rechnungsnummer = nr;
                      if (datum !== detailDialog.rechnungsdatum) payload.rechnungsdatum = datum;
                      if (Object.keys(payload).length === 0) { toast.info('Keine Änderungen'); return; }
                      try {
                        await axios.put(`${API}/rechnungen/${detailDialog.id}/meta`, payload);
                        toast.success('Rechnungsdaten aktualisiert + PDF neu generiert');
                        setDetailDialog(null);
                        fetchData();
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
                      }
                    }}
                  >
                    Speichern
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#78716c]">Zahlungsziel</p>
                    <p className="font-medium">{detailDialog.zahlungsziel}</p>
                  </div>
                  <div>
                    <p className="text-[#78716c]">Empfänger</p>
                    <p className="font-medium">{detailDialog.empfaenger_name}</p>
                  </div>
                  <div>
                    <p className="text-[#78716c]">Adresse</p>
                    <p className="font-medium whitespace-pre-line text-xs">{detailDialog.empfaenger_adresse}</p>
                  </div>
                </div>

                {/* AfA Checkbox für +1 Monat Zahlungseingang */}
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <input
                    type="checkbox"
                    id="afa-checkbox"
                    checked={detailDialog.empfaenger_typ === "afa"}
                    onChange={async (e) => {
                      const isAfa = e.target.checked;
                      try {
                        await axios.put(`${API}/rechnungen/${detailDialog.id}`, {
                          ...detailDialog,
                          empfaenger_typ: isAfa ? "afa" : (detailDialog.teilnehmer_id ? "teilnehmer" : detailDialog.pflegeeinrichtung_id ? "pflegeeinrichtung" : "manuell")
                        });
                        setDetailDialog(prev => ({...prev, empfaenger_typ: isAfa ? "afa" : prev.empfaenger_typ}));
                        fetchData();
                        toast.success(isAfa ? "Als AfA markiert (+1 Monat)" : "AfA-Markierung entfernt");
                      } catch (error) {
                        toast.error("Fehler beim Speichern");
                      }
                    }}
                    className="w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                  />
                  <label htmlFor="afa-checkbox" className="flex-1 cursor-pointer">
                    <span className="font-medium text-purple-700">AfA (Agentur für Arbeit)</span>
                    <p className="text-xs text-purple-600">Zahlungseingang ca. 1 Monat nach Kursstart (Raten: zur Fälligkeit)</p>
                  </label>
                </div>

                {detailDialog.empfaenger_typ === "afa" && (
                  <div className="flex items-center gap-3 p-3 rounded-lg border border-stone-200" data-testid="detail-teilnehmer-verknuepfung">
                    <div className="flex-1 text-sm">
                      <span className="text-stone-500">Teilnehmer/in: </span>
                      <span className="font-medium" data-testid="detail-teilnehmer-name">{detailDialog.teilnehmer_name || "nicht verknüpft"}</span>
                    </div>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" data-testid="detail-teilnehmer-verknuepfen-btn">{detailDialog.teilnehmer_id ? "Ändern" : "Verknüpfen"}</Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[300px] p-0" align="end">
                        <Command>
                          <CommandInput placeholder="Name eingeben..." />
                          <CommandList>
                            <CommandEmpty>Kein Teilnehmer gefunden</CommandEmpty>
                            <CommandGroup>
                              {teilnehmer.map(tn => (
                                <CommandItem key={tn.id} value={`${tn.vorname} ${tn.nachname}`}
                                  data-testid={`detail-teilnehmer-option-${tn.id}`}
                                  onSelect={async () => {
                                    try {
                                      const res = await axios.post(`${API}/admin/link-rechnung-tn`, {rechnung_id: detailDialog.id, teilnehmer_id: tn.id, also_mark_bg: false});
                                      if (res.data?.error) throw new Error(res.data.error);
                                      setDetailDialog(prev => ({...prev, teilnehmer_id: tn.id, teilnehmer_name: `${tn.vorname || ''} ${tn.nachname || ''}`.trim()}));
                                      fetchData();
                                      toast.success("Teilnehmer verknüpft");
                                    } catch (error) {
                                      toast.error("Verknüpfen fehlgeschlagen");
                                    }
                                  }}>
                                  {tn.vorname} {tn.nachname}
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}

                {/* NEU: Positionen anzeigen */}
                {detailDialog.positionen && detailDialog.positionen.length > 0 ? (
                  <Card className="bg-[#FFFFFF]">
                    <CardContent className="p-4">
                      <p className="text-sm font-medium mb-2">Positionen</p>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left text-gray-500">
                            <th className="pb-2">Beschreibung</th>
                            <th className="pb-2 text-center">Menge</th>
                            <th className="pb-2 text-right">Einzelpreis</th>
                            <th className="pb-2 text-right">Gesamt</th>
                          </tr>
                        </thead>
                        <tbody>
                          {detailDialog.positionen.map((pos, i) => (
                            <tr key={i} className="border-b border-gray-100">
                              <td className="py-1">{pos.beschreibung}</td>
                              <td className="py-1 text-center">{pos.menge}</td>
                              <td className="py-1 text-right">{pos.einzelpreis?.toLocaleString('de-DE')} €</td>
                              <td className="py-1 text-right font-medium">{pos.gesamtpreis?.toLocaleString('de-DE')} €</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mt-3 pt-2 border-t space-y-1">
                        <div className="flex justify-between">
                          <span>Zwischensumme:</span>
                          <span>{(detailDialog.zwischensumme || detailDialog.grundpreis)?.toLocaleString('de-DE')} €</span>
                        </div>
                        {detailDialog.rabatt_art !== "keiner" && detailDialog.rabatt_hoehe > 0 && (
                          <div className="flex justify-between text-emerald-600">
                            <span>Rabatt ({detailDialog.rabatt_art === "prozent" ? `${detailDialog.rabatt_hoehe}%` : "Fixbetrag"}):</span>
                            <span>- {(detailDialog.rabatt_art === "prozent" 
                              ? (detailDialog.zwischensumme || detailDialog.grundpreis) * detailDialog.rabatt_hoehe / 100 
                              : detailDialog.rabatt_hoehe).toLocaleString('de-DE')} €</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-lg pt-1 border-t">
                          <span>Gesamtbetrag:</span>
                          <span>{(() => {
                            // Berechne korrekten Gesamtbetrag MIT Rabattabzug
                            const zwischensumme = detailDialog.zwischensumme || detailDialog.grundpreis || 0;
                            let rabattBetrag = 0;
                            if (detailDialog.rabatt_art === "prozent" && detailDialog.rabatt_hoehe > 0) {
                              rabattBetrag = zwischensumme * detailDialog.rabatt_hoehe / 100;
                            } else if (detailDialog.rabatt_art === "fixbetrag" && detailDialog.rabatt_hoehe > 0) {
                              rabattBetrag = detailDialog.rabatt_hoehe;
                            }
                            const korrekterBetrag = Math.max(0, zwischensumme - rabattBetrag);
                            return korrekterBetrag.toLocaleString('de-DE');
                          })()} €</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  /* Legacy: Einzelne Maßnahme */
                  <Card className="bg-[#FFFFFF]">
                    <CardContent className="p-4 space-y-2">
                      <div>
                        <p className="text-[#78716c] text-sm">Maßnahme</p>
                        <p className="font-medium">{detailDialog.massnahme_name}</p>
                      </div>
                      <div className="flex justify-between">
                        <span>Grundpreis:</span>
                        <span>{detailDialog.grundpreis?.toLocaleString('de-DE')} €</span>
                      </div>
                      {detailDialog.rabatt_art !== "keiner" && (
                        <div className="flex justify-between text-emerald-600">
                          <span>Rabatt ({detailDialog.rabatt_art === "prozent" ? `${detailDialog.rabatt_hoehe}%` : "Fixbetrag"}):</span>
                          <span>- {(detailDialog.rabatt_art === "prozent" ? detailDialog.grundpreis * detailDialog.rabatt_hoehe / 100 : detailDialog.rabatt_hoehe).toLocaleString('de-DE')} €</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold border-t pt-2">
                        <span>Endbetrag:</span>
                        <span>{(() => {
                          // Berechne korrekten Endbetrag MIT Rabattabzug
                          const grundpreis = detailDialog.grundpreis || 0;
                          let rabattBetrag = 0;
                          if (detailDialog.rabatt_art === "prozent" && detailDialog.rabatt_hoehe > 0) {
                            rabattBetrag = grundpreis * detailDialog.rabatt_hoehe / 100;
                          } else if (detailDialog.rabatt_art === "fixbetrag" && detailDialog.rabatt_hoehe > 0) {
                            rabattBetrag = detailDialog.rabatt_hoehe;
                          }
                          const korrekterBetrag = Math.max(0, grundpreis - rabattBetrag);
                          return korrekterBetrag.toLocaleString('de-DE');
                        })()} €</span>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Zahlungsstatus - NEU: Flexible Raten unterstützen */}
                <div>
                  <p className="text-sm text-[#78716c] mb-2">Zahlungsstatus</p>
                  {detailDialog.zahlungsart === "ratenzahlung" ? (
                    <div className="space-y-2">
                      {/* NEU: Flexible Raten (1-6) */}
                      {detailDialog.raten && detailDialog.raten.length > 0 ? (
                        /* Neues Raten-Format */
                        detailDialog.raten.map((rate) => {
                          const today = new Date().toISOString().slice(0,10);
                          const dKey = `${detailDialog.id}_${rate.rate_nr}`;
                          const inputDate = rateZahlDatum[dKey] || rate.bezahlt_am || today;
                          const inputBetrag = rateZahlBetrag[dKey] ?? (rate.betrag != null ? String(rate.betrag) : "");
                          const abweichung = rate.bezahlt && rate.bezahlt_betrag != null && Math.abs(Number(rate.bezahlt_betrag) - Number(rate.betrag||0)) > 0.005;
                          return (
                          <div key={rate.rate_nr} className="flex items-center justify-between p-2 bg-gray-50 rounded gap-2 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium">Rate {rate.rate_nr}: <span className="text-gray-700">{rate.betrag?.toLocaleString('de-DE')} €</span></div>
                              <div className="text-xs text-gray-500">
                                {rate.faellig_am && <span>Fällig: {rate.faellig_am}</span>}
                                {rate.bezahlt && rate.bezahlt_am && (
                                  <span className="ml-2 text-emerald-700">· Bezahlt am: {rate.bezahlt_am}</span>
                                )}
                                {rate.bezahlt && rate.bezahlt_betrag != null && (
                                  <span className={`ml-2 ${abweichung ? "text-amber-700 font-medium" : "text-emerald-700"}`}>
                                    · Eingang: {Number(rate.bezahlt_betrag).toLocaleString('de-DE')} €{abweichung ? " " : ""}
                                  </span>
                                )}
                              </div>
                            </div>
                            {!rate.bezahlt && (
                              <>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={inputBetrag}
                                  onChange={(e) => setRateZahlBetrag({...rateZahlBetrag, [dKey]: e.target.value})}
                                  className="border border-gray-300 rounded px-2 py-1 text-xs w-24"
                                  title="Tatsächlich eingegangener Betrag (€)"
                                  placeholder="Betrag €"
                                  data-testid={`rate-betrag-${rate.rate_nr}`}
                                />
                                <input
                                  type="date"
                                  value={inputDate}
                                  onChange={(e) => setRateZahlDatum({...rateZahlDatum, [dKey]: e.target.value})}
                                  className="border border-gray-300 rounded px-2 py-1 text-xs"
                                  title="Zahlungseingang-Datum"
                                  data-testid={`rate-datum-${rate.rate_nr}`}
                                />
                              </>
                            )}
                            <button
                              onClick={() => handleRateUpdate(detailDialog.id, rate.rate_nr, !rate.bezahlt, !rate.bezahlt ? inputDate : null, !rate.bezahlt ? inputBetrag : null)}
                              className={`px-3 py-1 rounded text-sm whitespace-nowrap ${
                                rate.bezahlt 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                              disabled={detailDialog.zahlungsstatus === "storniert"}
                              data-testid={`rate-toggle-${rate.rate_nr}`}
                            >
                              {rate.bezahlt ? '✓ Bezahlt' : 'Bezahlt markieren'}
                            </button>
                          </div>
                          );
                        })
                      ) : (
                        /* Legacy: Feste 4 Raten */
                        Array.from({length: detailDialog.anzahl_raten || 4}, (_, i) => i + 1).map(nr => {
                          const today = new Date().toISOString().slice(0,10);
                          const bezahlt = !!detailDialog[`rate_${nr}_bezahlt`];
                          const bezahltAmStored = detailDialog[`rate_${nr}_bezahlt_am`];
                          const bezahltBetragStored = detailDialog[`rate_${nr}_bezahlt_betrag`];
                          const rateDefault = (detailDialog.endbetrag || 0) / (detailDialog.anzahl_raten || 4);
                          const dKey = `${detailDialog.id}_${nr}`;
                          const inputDate = rateZahlDatum[dKey] || bezahltAmStored || today;
                          const inputBetrag = rateZahlBetrag[dKey] ?? (rateDefault ? rateDefault.toFixed(2) : "");
                          const abweichung = bezahlt && bezahltBetragStored != null && Math.abs(Number(bezahltBetragStored) - rateDefault) > 0.005;
                          return (
                          <div key={nr} className="flex items-center justify-between p-2 bg-gray-50 rounded gap-2 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="text-sm">Rate {nr}: {rateDefault.toLocaleString('de-DE')} €</div>
                              {bezahlt && (
                                <div className="text-xs text-emerald-700">
                                  {bezahltAmStored && <>Bezahlt am: {bezahltAmStored}</>}
                                  {bezahltBetragStored != null && (
                                    <span className={`ml-2 ${abweichung ? "text-amber-700 font-medium" : ""}`}>
                                      · Eingang: {Number(bezahltBetragStored).toLocaleString('de-DE')} €{abweichung ? " " : ""}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            {!bezahlt && (
                              <>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={inputBetrag}
                                  onChange={(e) => setRateZahlBetrag({...rateZahlBetrag, [dKey]: e.target.value})}
                                  className="border border-gray-300 rounded px-2 py-1 text-xs w-24"
                                  title="Tatsächlich eingegangener Betrag (€)"
                                  placeholder="Betrag €"
                                  data-testid={`rate-legacy-betrag-${nr}`}
                                />
                                <input
                                  type="date"
                                  value={inputDate}
                                  onChange={(e) => setRateZahlDatum({...rateZahlDatum, [dKey]: e.target.value})}
                                  className="border border-gray-300 rounded px-2 py-1 text-xs"
                                  title="Zahlungseingang-Datum"
                                  data-testid={`rate-legacy-datum-${nr}`}
                                />
                              </>
                            )}
                            <button
                              onClick={() => handleRateUpdate(detailDialog.id, nr, !bezahlt, !bezahlt ? inputDate : null, !bezahlt ? inputBetrag : null)}
                              className={`px-3 py-1 rounded text-sm whitespace-nowrap ${
                                bezahlt 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                              disabled={detailDialog.zahlungsstatus === "storniert"}
                              data-testid={`rate-legacy-toggle-${nr}`}
                            >
                              {bezahlt ? '✓ Bezahlt' : 'Bezahlt markieren'}
                            </button>
                          </div>
                          );
                        })
                      )}
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        variant={detailDialog.zahlungsstatus === "offen" ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleZahlungUpdate(detailDialog.id, "offen")}
                        disabled={detailDialog.zahlungsstatus === "storniert"}
                      >
                        Offen
                      </Button>
                      <Button 
                        variant={detailDialog.zahlungsstatus === "bezahlt" ? "default" : "outline"}
                        size="sm"
                        onClick={() => handleZahlungUpdate(detailDialog.id, "bezahlt")}
                        disabled={detailDialog.zahlungsstatus === "storniert"}
                        className={detailDialog.zahlungsstatus === "bezahlt" ? "bg-emerald-600" : ""}
                      >
                        Bezahlt
                      </Button>
                    </div>
                  )}
                </div>

                {/* NEU: Teilzahlungen mit Datum (mehrere möglich, für Fälle wo Kunde ohne Ratenzahlung teilweise zahlt) */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium mb-2">Teilzahlungen (mit Datum)</p>

                  {/* Liste bestehender Teilzahlungen */}
                  {(detailDialog.teilzahlungen && detailDialog.teilzahlungen.length > 0) ? (
                    <div className="space-y-1 mb-3">
                      {detailDialog.teilzahlungen.map((tz) => (
                        <div key={tz.id} className="flex items-center gap-2 text-sm bg-white border border-blue-100 rounded px-2 py-1">
                          <span className="text-stone-500 text-xs w-24">{tz.datum}</span>
                          <span className="font-semibold text-emerald-700 w-24">{(tz.betrag || 0).toLocaleString('de-DE', {minimumFractionDigits: 2, maximumFractionDigits: 2})} €</span>
                          <span className="text-xs text-stone-500 flex-1 truncate italic">{tz.notiz || ""}</span>
                          <button
                            onClick={async () => {
                              if (!window.confirm(`Teilzahlung ${(tz.betrag || 0).toLocaleString('de-DE')} € vom ${tz.datum} löschen?`)) return;
                              try {
                                const isOnline = detailDialog.ist_online_bestellung;
                                if (isOnline) {
                                  toast.info("Löschen einzelner Teilzahlungen für Online-Bestellungen noch nicht verfügbar");
                                  return;
                                }
                                const res = await axios.delete(`${API}/rechnungen/${detailDialog.id}/teilzahlungen/${tz.id}`);
                                setDetailDialog(prev => ({...prev, ...res.data}));
                                fetchData();
                                toast.success("Teilzahlung gelöscht");
                              } catch (err) {
                                toast.error("Fehler beim Löschen: " + (err.response?.data?.detail || err.message));
                              }
                            }}
                            className="text-red-500 hover:text-red-700 text-xs"
                            data-testid={`btn-delete-tz-${tz.id}`}
                            title="Löschen"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    (detailDialog.bereits_bezahlt > 0) && (
                      <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 flex items-center justify-between">
                        <span>Historische Teilzahlung ohne Datum: {(detailDialog.bereits_bezahlt || 0).toLocaleString('de-DE')} €</span>
                        <button
                          onClick={async () => {
                            try {
                              const res = await axios.post(`${API}/rechnungen/${detailDialog.id}/teilzahlungen/backfill-from-lump-sum`);
                              if (!res.data.skipped) {
                                setDetailDialog(prev => ({...prev, ...res.data}));
                                fetchData();
                                toast.success("Als Teilzahlung mit heutigem Datum übernommen");
                              }
                            } catch (err) {
                              toast.error("Fehler: " + (err.response?.data?.detail || err.message));
                            }
                          }}
                          className="text-blue-700 underline text-xs"
                          data-testid="btn-backfill-tz"
                        >
                          Als Teilzahlung übernehmen
                        </button>
                      </div>
                    )
                  )}

                  {/* Neue Teilzahlung hinzufügen */}
                  <div className="grid grid-cols-[110px_120px_1fr_auto] gap-2 items-center">
                    <input
                      type="date"
                      id="tz-datum-input"
                      defaultValue={new Date().toISOString().slice(0, 10)}
                      className="px-2 py-1 border rounded text-sm"
                      data-testid="input-tz-datum"
                    />
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      placeholder="Betrag €"
                      id="tz-betrag-input"
                      className="px-2 py-1 border rounded text-right text-sm"
                      data-testid="input-tz-betrag"
                    />
                    <input
                      type="text"
                      placeholder="Notiz (optional, z.B. 'Überweisung Sparkasse')"
                      id="tz-notiz-input"
                      className="px-2 py-1 border rounded text-sm"
                      data-testid="input-tz-notiz"
                    />
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={async () => {
                        const datum = document.getElementById('tz-datum-input')?.value;
                        const betrag = parseFloat(document.getElementById('tz-betrag-input')?.value);
                        const notiz = document.getElementById('tz-notiz-input')?.value || "";
                        if (!datum) { toast.error("Datum ist Pflicht"); return; }
                        if (!betrag || betrag <= 0) { toast.error("Betrag muss > 0 sein"); return; }
                        try {
                          const isOnline = detailDialog.ist_online_bestellung;
                          if (isOnline) {
                            // Fallback: alte Legacy-API für online-bestellungen (Einzelsumme)
                            const currentSum = (detailDialog.bereits_bezahlt || 0) + betrag;
                            await axios.put(`${API}/online-bestellungen/${detailDialog.id}/teilzahlung`, {bereits_bezahlt: currentSum});
                            setDetailDialog(prev => ({...prev, bereits_bezahlt: currentSum}));
                            fetchData();
                            toast.success(`+${betrag.toLocaleString('de-DE')} € erfasst (Online-Bestellung: nur Gesamtsumme)`);
                          } else {
                            const res = await axios.post(`${API}/rechnungen/${detailDialog.id}/teilzahlungen`, {
                              betrag, datum, notiz
                            });
                            setDetailDialog(prev => ({...prev, ...res.data}));
                            fetchData();
                            toast.success(`Teilzahlung ${betrag.toLocaleString('de-DE')} € am ${datum} erfasst`);
                          }
                          document.getElementById('tz-betrag-input').value = "";
                          document.getElementById('tz-notiz-input').value = "";
                        } catch (err) {
                          toast.error("Fehler: " + (err.response?.data?.detail || err.message));
                        }
                      }}
                      data-testid="btn-add-tz"
                    >
                      + Erfassen
                    </Button>
                  </div>

                  <div className="flex items-center gap-2 text-sm mt-3">
                    <span className="text-gray-600 w-32">Noch offen:</span>
                    <span className="font-bold text-red-600" data-testid="text-noch-offen">
                      {(() => {
                        const zwischensumme = detailDialog.zwischensumme || detailDialog.grundpreis || detailDialog.endbetrag || 0;
                        let rabattBetrag = 0;
                        if (detailDialog.rabatt_art === "prozent" && detailDialog.rabatt_hoehe > 0) {
                          rabattBetrag = zwischensumme * detailDialog.rabatt_hoehe / 100;
                        } else if (detailDialog.rabatt_art === "fixbetrag" && detailDialog.rabatt_hoehe > 0) {
                          rabattBetrag = detailDialog.rabatt_hoehe;
                        }
                        const gesamtBetrag = Math.max(0, zwischensumme - rabattBetrag);
                        const bereitsGezahlt = detailDialog.bereits_bezahlt || 0;
                        const nochOffen = Math.max(0, gesamtBetrag - bereitsGezahlt - (detailDialog.teilstorno_betrag || 0));
                        return nochOffen.toLocaleString('de-DE');
                      })()} €
                    </span>
                  </div>
                </div>

                {detailDialog.interne_notiz && (
                  <div>
                    <p className="text-sm text-[#78716c]">Interne Notiz</p>
                    <p className="text-sm">{detailDialog.interne_notiz}</p>
                  </div>
                )}
                
                {/* Mahnungs-Info und Buttons */}
                {detailDialog.zahlungsstatus !== "bezahlt" && detailDialog.zahlungsstatus !== "storniert" && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm font-medium mb-2">Mahnwesen</p>
                    <div className="space-y-3">
                      {/* Status-Anzeige mit Versandprotokoll pro Stufe */}
                      <div className="text-sm space-y-1">
                        {!detailDialog.mahnung_stufe || detailDialog.mahnung_stufe === 0 ? (
                          <span className="text-gray-600">Noch keine Mahnung versendet</span>
                        ) : (
                          <>
                            {[1, 2].filter(s => detailDialog.mahnung_stufe >= s).map((s) => {
                              const datum = detailDialog[`mahnung_${s}_datum`];
                              const weg = detailDialog[`mahnung_${s}_versand_weg`];
                              const empf = detailDialog[`mahnung_${s}_versand_empfaenger`] || detailDialog[`mahnung_${s}_email_an`];
                              const notiz = detailDialog[`mahnung_${s}_versand_notiz`];
                              const emailSystem = detailDialog[`mahnung_${s}_email_gesendet_am`];
                              const wegLabel = weg === "extern" ? "eigene E-Mail" : weg === "brief" ? "Brief/Post" : weg === "fax" ? "Fax" : weg === "anderer" ? "anderer Weg" : emailSystem ? "System-E-Mail" : null;
                              const istVersendet = !!wegLabel;
                              const dot = !istVersendet ? "bg-[#B79A5C]" : s === 2 ? "bg-red-500" : "bg-amber-500";
                              const txt = !istVersendet ? "text-[#8A6D3B]" : s === 2 ? "text-red-600 font-semibold" : "text-amber-700";
                              return (
                                <div key={s} className={`flex items-start gap-2 ${txt}`}>
                                  <span className={`inline-block w-2 h-2 rounded-full mt-1.5 ${dot}`}></span>
                                  <div className="flex-1">
                                    <div>
                                      {s === 1 ? "1. Mahnung" : "2. Mahnung (Inkasso)"}
                                      {istVersendet
                                        ? <> gesendet am {datum ? new Date(datum).toLocaleDateString('de-DE') : '—'}<span className="ml-2 text-xs opacity-80">· Versand: {wegLabel}</span></>
                                        : <> · nur PDF erstellt am {datum ? new Date(datum).toLocaleDateString('de-DE') : '—'} <span className="ml-1 text-xs opacity-80">— noch NICHT versendet</span></>
                                      }
                                    </div>
                                    {empf && <div className="text-xs text-[#78716c]">an: {empf}</div>}
                                    {notiz && <div className="text-xs text-[#78716c] italic">„{notiz}"</div>}
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        )}
                      </div>
                      
                      {/* Buttons: Vorschau + Senden */}
                      <div className="flex flex-wrap gap-2">
                        {(!detailDialog.mahnung_stufe || detailDialog.mahnung_stufe === 0) && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                              onClick={() => handleMahnungVorschau(detailDialog.id, 1)}
                            >
                              <Eye size={14} className="mr-1" />
                              Vorschau 1. Mahnung
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="bg-amber-100 hover:bg-amber-200 text-amber-700"
                              onClick={() => handleMahnung(detailDialog.id, 1)}
                            >
                              <AlertTriangle size={14} className="mr-1" />
                              1. Mahnung senden
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-amber-50 hover:bg-amber-100 text-amber-800 border-amber-300"
                              onClick={() => openMahnungManuellDialog(detailDialog, 1)}
                              data-testid="detail-mahnung-stufe1-manuell"
                            >
                              <ClipboardCheck size={14} className="mr-1" />
                              Manuell eintragen
                            </Button>
                          </>
                        )}
                        {detailDialog.mahnung_stufe === 1 && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                              onClick={() => handleMahnungVorschau(detailDialog.id, 1)}
                            >
                              <Eye size={14} className="mr-1" />
                              1. Mahnung erneut
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                              onClick={() => handleMahnungVorschau(detailDialog.id, 2)}
                            >
                              <Eye size={14} className="mr-1" />
                              Vorschau 2. Mahnung
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="bg-red-100 hover:bg-red-200 text-red-700"
                              onClick={() => handleMahnung(detailDialog.id, 2)}
                            >
                              <AlertTriangle size={14} className="mr-1" />
                              2. Mahnung senden
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-red-50 hover:bg-red-100 text-red-800 border-red-300"
                              onClick={() => openMahnungManuellDialog(detailDialog, 2)}
                              data-testid="detail-mahnung-stufe2-manuell"
                            >
                              <ClipboardCheck size={14} className="mr-1" />
                              Manuell eintragen
                            </Button>
                          </>
                        )}
                        {detailDialog.mahnung_stufe === 2 && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                              onClick={() => handleMahnungVorschau(detailDialog.id, 1)}
                            >
                              <Eye size={14} className="mr-1" />
                              1. Mahnung erneut
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="bg-gray-100 hover:bg-gray-200 text-gray-700"
                              onClick={() => handleMahnungVorschau(detailDialog.id, 2)}
                            >
                              <Eye size={14} className="mr-1" />
                              2. Mahnung erneut
                            </Button>
                            <span className="text-red-600 text-xs self-center">Alle Mahnungen versendet - Inkasso vorbereiten</span>

                            {/* Paywise Inkasso-Paket Download */}
                            <Button
                              data-testid="paywise-paket-btn"
                              size="sm"
                              className="bg-rose-600 hover:bg-rose-700 text-white"
                              onClick={async () => {
                                try {
                                  toast.info("Erstelle Paywise-Paket …");
                                  const res = await axios.post(
                                    `${API}/rechnungen/${detailDialog.id}/paywise-paket`,
                                    {},
                                    { responseType: "blob" }
                                  );
                                  const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
                                  const a = document.createElement("a");
                                  a.href = url;
                                  a.download = `Paywise-Inkasso-Paket_${detailDialog.rechnungsnummer || detailDialog.id}.zip`;
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  toast.success("Paywise-Paket heruntergeladen");
                                } catch (e) {
                                  toast.error(e?.response?.data?.detail || "Fehler beim Erstellen des Pakets");
                                }
                              }}
                            >
                              <Download size={14} className="mr-1" /> Paywise-Paket herunterladen
                            </Button>

                            {/* An Paywise übergeben markieren */}
                            {!detailDialog.paywise_uebergeben_am && (
                              <Button
                                data-testid="paywise-uebergeben-btn"
                                size="sm"
                                variant="outline"
                                className="border-rose-500 text-rose-700 hover:bg-rose-50"
                                onClick={async () => {
                                  if (!window.confirm("Diese Rechnung als an Paywise übergeben markieren? Der Status wechselt auf 'Inkasso'.")) return;
                                  try {
                                    const res = await axios.post(`${API}/rechnungen/${detailDialog.id}/paywise-uebergeben`, {
                                      unterzeichner: "Buchhaltung",
                                    });
                                    toast.success("An Paywise übergeben markiert");
                                    setDetailDialog({...detailDialog, paywise_uebergeben_am: res.data.paywise_uebergeben_am, zahlungsstatus: "inkasso"});
                                  } catch (e) {
                                    toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
                                  }
                                }}
                              >
                                <CheckCircle size={14} className="mr-1" /> An Paywise übergeben
                              </Button>
                            )}
                            {detailDialog.paywise_uebergeben_am && (
                              <span className="text-rose-700 text-xs self-center font-medium" data-testid="paywise-uebergeben-info">
                                Inkasso übergeben am {new Date(detailDialog.paywise_uebergeben_am).toLocaleDateString('de-DE')}
                              </span>
                            )}
                          </>
                        )}
                        
                        {/* Zurücksetzen-Button wenn mindestens eine Mahnung gesendet wurde */}
                        {detailDialog.mahnung_stufe > 0 && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="bg-orange-100 hover:bg-orange-200 text-orange-700 border-orange-300"
                            onClick={() => handleMahnungReset(detailDialog.id)}
                          >
                            <RefreshCcw size={14} className="mr-1" />
                            Status zurücksetzen
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Stornierungs-Grund anzeigen (nur bei stornierten Rechnungen) */}
                {detailDialog.zahlungsstatus === "storniert" && detailDialog.storno_grund && (
                  <div className="mt-3 p-3 rounded-md border border-red-200 bg-red-50" data-testid="storno-grund-info">
                    <div className="text-xs uppercase tracking-wider text-red-700 mb-1">Stornierungsgrund</div>
                    <div className="text-sm text-red-900 whitespace-pre-wrap">{detailDialog.storno_grund}</div>
                  </div>
                )}
                {!detailDialog.ist_storno && detailDialog.zahlungsstatus !== "storniert" && <ERechnungButtons rechnung={detailDialog} />}
                <TeilstornoListe rechnung={detailDialog} />
                {detailDialog.zahlungsstatus !== "storniert" && !detailDialog.ist_storno && Number(detailDialog.offen_betrag || 0) > 0 && (
                  <div className="mt-3"><TeilstornoButton rechnung={detailDialog} onDone={() => { setDetailDialog(null); fetchData(); }} /></div>
                )}
                {detailDialog.zahlungsstatus !== "storniert" && detailDialog.zahlungsstatus !== "bezahlt" && !detailDialog.ist_storno && !detailDialog.forderungsausfall && (
                  <MahnsperreBox rechnung={detailDialog} onDone={() => { setDetailDialog(null); fetchData(); }} />
                )}
                {/* E-Mail-Historie zu dieser Rechnung */}
                <RechnungEmailHistorie rechnungId={detailDialog.id} />
              </div>
              <DialogFooter className="flex justify-between">
                <div className="flex gap-2">
                  {detailDialog.zahlungsstatus === "storniert" ? (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-red-500 text-red-600 hover:bg-red-50"
                        onClick={async () => {
                          try {
                            toast.info('Stornorechnung wird erstellt...');
                            const res = await axios.get(`${API}/rechnungen/${detailDialog.id}/storno-pdf`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', `Stornorechnung_${detailDialog.rechnungsnummer}.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                            toast.success('Stornorechnung heruntergeladen');
                          } catch (err) {
                            toast.error('Fehler beim Erstellen der Stornorechnung');
                          }
                        }}
                        data-testid="storno-pdf-detail-btn"
                      >
                        <FileText size={14} className="mr-1" /> Stornorechnung
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-green-500 text-green-600 hover:bg-green-50"
                        onClick={async () => {
                          if (!window.confirm("Stornierung wirklich rückgängig machen? Die Rechnung wird wieder auf 'offen' gesetzt.")) return;
                          try {
                            await axios.put(`${API}/rechnungen/${detailDialog.id}/storno-rueckgaengig`);
                            toast.success("Stornierung rückgängig gemacht");
                            setDetailDialog(null);
                            fetchData();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || "Fehler");
                          }
                        }}
                      >
                        Stornierung aufheben
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      variant="destructive" 
                      size="sm"
                      onClick={() => handleStorno(detailDialog.id)}
                    >
                      Stornieren
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => handleDownloadPDF(detailDialog.id)}>
                    <Download size={16} className="mr-2" />
                    PDF
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-red-300 text-red-500 hover:bg-red-50 hover:text-red-700"
                    onClick={() => handleDeleteRechnung(detailDialog.id)}
                    data-testid="delete-rechnung-detail-btn"
                  >
                    <Trash2 size={16} className="mr-1" /> Löschen
                  </Button>
                  <Button onClick={() => setDetailDialog(null)}>Schließen</Button>
                </div>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* NEU: WooCommerce-Bearbeitungs-Dialog mit Raten-Checkboxen */}
      <OnlineZahlungDialog item={onlineZahlung} onClose={() => setOnlineZahlung(null)} onSaved={fetchData} />
      <Dialog open={!!editOnlineDialog} onOpenChange={() => setEditOnlineDialog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {editOnlineDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ShoppingCart className="text-purple-600" size={20} />
                  WooCommerce-Bestellung bearbeiten
                </DialogTitle>
                <DialogDescription>
                  {editOnlineDialog.rechnungsnummer} - {editOnlineDialog.empfaenger_name}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Grunddaten */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Kundenname</Label>
                    <Input
                      value={editOnlineDialog.empfaenger_name || ""}
                      onChange={(e) => setEditOnlineDialog({...editOnlineDialog, empfaenger_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Kurs/Produkt</Label>
                    <Input
                      value={editOnlineDialog.massnahme_name || ""}
                      onChange={(e) => setEditOnlineDialog({...editOnlineDialog, massnahme_name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Betrag (€)</Label>
                    <Input
                      type="number"
                      value={editOnlineDialog.endbetrag || 0}
                      onChange={(e) => setEditOnlineDialog({...editOnlineDialog, endbetrag: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Bestelldatum</Label>
                    <Input
                      type="date"
                      value={editOnlineDialog.rechnungsdatum || ""}
                      onChange={(e) => setEditOnlineDialog({...editOnlineDialog, rechnungsdatum: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Externe Rechnungsnr.</Label>
                    <Input
                      value={editOnlineDialog.externe_rechnungsnummer || editOnlineDialog.rechnungsnummer || ""}
                      onChange={(e) => setEditOnlineDialog({...editOnlineDialog, externe_rechnungsnummer: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Notiz</Label>
                    <Input
                      value={editOnlineDialog.notiz || ""}
                      onChange={(e) => setEditOnlineDialog({...editOnlineDialog, notiz: e.target.value})}
                    />
                  </div>
                </div>

                {/* Zahlungsart */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="font-medium mb-2 block">Zahlungsart</Label>
                  <div className="flex gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={editOnlineDialog.zahlungsart !== "ratenzahlung"}
                        onChange={() => setEditOnlineDialog({...editOnlineDialog, zahlungsart: "einmalzahlung", anzahl_raten: 1})}
                      />
                      <span>Einmalzahlung</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={editOnlineDialog.zahlungsart === "ratenzahlung"}
                        onChange={() => setEditOnlineDialog({...editOnlineDialog, zahlungsart: "ratenzahlung", anzahl_raten: editOnlineDialog.anzahl_raten || 2})}
                      />
                      <span>Ratenzahlung</span>
                    </label>
                  </div>
                  
                  {editOnlineDialog.zahlungsart === "ratenzahlung" && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Anzahl Raten:</Label>
                        <Select
                          value={String(editOnlineDialog.anzahl_raten || 2)}
                          onValueChange={(v) => setEditOnlineDialog({...editOnlineDialog, anzahl_raten: parseInt(v)})}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2">2</SelectItem>
                            <SelectItem value="3">3</SelectItem>
                            <SelectItem value="4">4</SelectItem>
                            <SelectItem value="5">5</SelectItem>
                            <SelectItem value="6">6</SelectItem>
                            <SelectItem value="7">7</SelectItem>
                            <SelectItem value="8">8</SelectItem>
                            <SelectItem value="9">9</SelectItem>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="11">11</SelectItem>
                            <SelectItem value="12">12</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Raten-Übersicht mit Checkboxen */}
                      <div className="bg-white rounded-lg p-3 space-y-2">
                        <p className="font-medium text-sm">Raten-Status:</p>
                        {editOnlineDialog.raten && editOnlineDialog.raten.length > 0 ? (
                          // Bestehende Raten
                          editOnlineDialog.raten.map((rate, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <div>
                                <span className="text-sm font-medium">
                                  Rate {rate.nummer || index + 1}: €{(rate.betrag || (editOnlineDialog.endbetrag / (editOnlineDialog.anzahl_raten || 2))).toFixed(2)}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/online-bestellungen/${editOnlineDialog.id}/rate/${index + 1}`, null, {
                                      params: { bezahlt: !rate.bezahlt }
                                    });
                                    // Update local state
                                    const updatedRaten = [...editOnlineDialog.raten];
                                    updatedRaten[index] = { ...rate, bezahlt: !rate.bezahlt };
                                    setEditOnlineDialog({...editOnlineDialog, raten: updatedRaten});
                                    fetchData();
                                    toast.success(rate.bezahlt ? "Rate als offen markiert" : "Rate als bezahlt markiert");
                                  } catch (err) {
                                    toast.error("Fehler beim Aktualisieren der Rate");
                                  }
                                }}
                                className={`px-3 py-1 rounded text-sm ${
                                  rate.bezahlt 
                                    ? 'bg-emerald-100 text-emerald-700' 
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                              >
                                {rate.bezahlt ? '✓ Bezahlt' : 'Offen'}
                              </button>
                            </div>
                          ))
                        ) : (
                          // Neue Raten (noch nicht gespeichert)
                          Array.from({length: editOnlineDialog.anzahl_raten || 2}).map((_, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm">
                                Rate {i + 1}: €{(editOnlineDialog.endbetrag / (editOnlineDialog.anzahl_raten || 2)).toFixed(2)}
                              </span>
                              <span className="text-xs text-gray-500">Wird nach Speichern erstellt</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Bezahlt-Status (nur bei Einmalzahlung) */}
                {editOnlineDialog.zahlungsart !== "ratenzahlung" && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="bereits_bezahlt"
                      checked={editOnlineDialog.zahlungsstatus === "bezahlt"}
                      onCheckedChange={(checked) => setEditOnlineDialog({...editOnlineDialog, zahlungsstatus: checked ? "bezahlt" : "offen"})}
                    />
                    <label htmlFor="bereits_bezahlt" className="text-sm cursor-pointer">
                      Bereits bezahlt
                    </label>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setEditOnlineDialog(null)}>
                  Abbrechen
                </Button>
                <Button 
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/online-bestellungen/${editOnlineDialog.id}`, {
                        kunde_name: editOnlineDialog.empfaenger_name,
                        kurs_name: editOnlineDialog.massnahme_name,
                        umsatzbetrag: editOnlineDialog.endbetrag,
                        bestelldatum: editOnlineDialog.rechnungsdatum,
                        externe_rechnungsnummer: editOnlineDialog.externe_rechnungsnummer || editOnlineDialog.rechnungsnummer,
                        notiz: editOnlineDialog.notiz,
                        bereits_bezahlt: editOnlineDialog.zahlungsstatus === "bezahlt",
                        zahlungsart: editOnlineDialog.zahlungsart || "einmalzahlung",
                        anzahl_raten: editOnlineDialog.anzahl_raten || 1,
                        anzahl_teilnehmer: editOnlineDialog.anzahl_teilnehmer || 1,
                        vertriebsmitarbeiter_id: editOnlineDialog.vertriebsmitarbeiter_id || null
                      });
                      toast.success("WooCommerce-Bestellung aktualisiert");
                      setEditOnlineDialog(null);
                      fetchData();
                    } catch (err) {
                      toast.error("Fehler beim Speichern");
                    }
                  }}
                >
                  <Save size={16} className="mr-2" />
                  Speichern
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>


      {/* NEU: Zahlungsziel verlängern Dialog */}
      <Dialog open={!!zzDialog} onOpenChange={(o) => { if (!o) setZzDialog(null); }}>
        <DialogContent className="max-w-md">
          {zzDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CalendarIcon className="text-indigo-600" size={20} />
                  Zahlungsziel verlängern
                </DialogTitle>
                <DialogDescription>
                  {zzDialog.rechnungsnummer} · {zzDialog.empfaenger_name} · Aktuell fällig: <strong>{zzDialog.zahlungsziel}</strong>
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label htmlFor="zz-neues-datum">Neues Zahlungsziel</Label>
                  <Input
                    id="zz-neues-datum"
                    type="date"
                    value={zzNeuesDatum}
                    onChange={(e) => setZzNeuesDatum(e.target.value)}
                    data-testid="zz-neues-datum"
                  />
                  <p className="text-xs text-gray-500 mt-1">Die Rechnung ist bis zu diesem Datum <strong>nicht</strong> überfällig.</p>
                </div>
                <div>
                  <Label htmlFor="zz-grund">Grund / Absprache (optional)</Label>
                  <Input
                    id="zz-grund"
                    type="text"
                    placeholder="z.B. Telefonisch vereinbart mit Kunde am 24.02."
                    value={zzGrund}
                    onChange={(e) => setZzGrund(e.target.value)}
                    data-testid="zz-grund"
                  />
                </div>
                {zzDialog.mahnung_stufe > 0 && (
                  <label className="flex items-start gap-2 text-sm p-3 bg-amber-50 border border-amber-200 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={zzMahnungReset}
                      onChange={(e) => setZzMahnungReset(e.target.checked)}
                      className="mt-0.5"
                      data-testid="zz-mahnung-reset"
                    />
                    <span>
                      <strong>Mahnung Stufe {zzDialog.mahnung_stufe} zurücksetzen</strong>
                      <br />
                      <span className="text-xs text-gray-600">Weil Zahlung neu vereinbart wurde. Mahnung wird auf 0 gesetzt.</span>
                    </span>
                  </label>
                )}
                {zzDialog.zahlungsziel_historie?.length > 0 && (
                  <div className="text-xs bg-gray-50 rounded p-2 max-h-24 overflow-y-auto">
                    <div className="font-medium mb-1">Historie:</div>
                    {zzDialog.zahlungsziel_historie.map((h, i) => (
                      <div key={i} className="text-gray-600">
                        {h.altes_ziel} → {h.neues_ziel} {h.grund && `· ${h.grund}`}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setZzDialog(null)} data-testid="zz-abbrechen">Abbrechen</Button>
                <Button
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="zz-speichern"
                  onClick={async () => {
                    if (!zzNeuesDatum) { toast.error("Bitte Datum wählen"); return; }
                    try {
                      const isOnline = zzDialog.ist_online_bestellung;
                      const url = isOnline
                        ? `${API}/online-bestellungen/${zzDialog.id}/zahlungsziel-verlaengern`
                        : `${API}/rechnungen/${zzDialog.id}/zahlungsziel-verlaengern`;
                      await axios.put(url, {
                        neues_zahlungsziel: zzNeuesDatum,
                        grund: zzGrund,
                        mahnung_zuruecksetzen: zzMahnungReset,
                      });
                      toast.success("Zahlungsziel verlängert", {
                        description: `Neu fällig: ${new Date(zzNeuesDatum).toLocaleDateString('de-DE')}`,
                      });
                      setZzDialog(null);
                      fetchData();
                    } catch (err) {
                      toast.error("Fehler", { description: err?.response?.data?.detail || err.message });
                    }
                  }}
                >
                  <Save size={16} className="mr-2" /> Verlängern
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>


      {/* NEU: Mahnung erstellen — alle Felder manuell */}
      <Dialog open={!!mahnErstellDialog} onOpenChange={(o) => { if (!o) setMahnErstellDialog(null); }}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
          {mahnErstellDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="text-amber-600" size={20} />
                  Mahnung erstellen
                </DialogTitle>
                <DialogDescription>
                  <b>{mahnErstellDialog.rechnungsnummer}</b> · {mahnErstellDialog.empfaenger_name}
                  <br />
                  <span className="text-xs text-[#78716c]">Alle Werte selbst eingeben. Z.B. nur eine einzelne Rate anmahnen — nicht den Gesamtbetrag.</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {/* Stufe + Datum */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Mahnstufe *</Label>
                    <select
                      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                      value={mahnStufe}
                      onChange={(e) => setMahnStufe(parseInt(e.target.value))}
                      data-testid="mahn-erstell-stufe"
                    >
                      <option value={0}>Zahlungserinnerung</option>
                      <option value={1}>1. Mahnung</option>
                      <option value={2}>2. Mahnung</option>
                      <option value={3}>3. Mahnung (Inkasso)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Mahndatum *</Label>
                    <Input
                      type="date"
                      value={mahnDatum}
                      onChange={(e) => setMahnDatum(e.target.value)}
                      className="mt-1"
                      data-testid="mahn-erstell-datum"
                    />
                  </div>
                </div>

                {/* Betrag + Fälligkeit */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Zu mahnender Betrag * (€)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="z.B. 1000,00"
                      value={mahnBetrag}
                      onChange={(e) => setMahnBetrag(e.target.value)}
                      className="mt-1 font-mono"
                      data-testid="mahn-erstell-betrag"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">
                      Rechnung gesamt: {Number(mahnErstellDialog.endbetrag || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
                    </p>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Neue Fälligkeit</Label>
                    <Input
                      type="date"
                      value={mahnFrist}
                      onChange={(e) => setMahnFrist(e.target.value)}
                      className="mt-1"
                      data-testid="mahn-erstell-frist"
                    />
                  </div>
                </div>

                {/* Optional: Mahngebühr + Zinsen */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-sm font-medium">Mahngebühr (€)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={mahnGebuehr}
                      onChange={(e) => setMahnGebuehr(e.target.value)}
                      className="mt-1 font-mono"
                      data-testid="mahn-erstell-gebuehr"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Verzugszinsen (€)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={mahnZinsen}
                      onChange={(e) => setMahnZinsen(e.target.value)}
                      className="mt-1 font-mono"
                      data-testid="mahn-erstell-zinsen"
                    />
                  </div>
                </div>

                {/* Beschreibung */}
                <div>
                  <Label className="text-sm font-medium">Betrifft / Beschreibung (erscheint im PDF)</Label>
                  <Input
                    type="text"
                    placeholder='z.B. "2. Rate (November 2025)"'
                    value={mahnBeschreibung}
                    onChange={(e) => setMahnBeschreibung(e.target.value)}
                    className="mt-1"
                    data-testid="mahn-erstell-beschreibung"
                  />
                </div>

                {/* Interne Notiz */}
                <div>
                  <Label className="text-sm font-medium">Interne Notiz (nicht im PDF)</Label>
                  <textarea
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[60px]"
                    placeholder="z.B. telefonisch angekündigt am …"
                    value={mahnNotizIntern}
                    onChange={(e) => setMahnNotizIntern(e.target.value)}
                    data-testid="mahn-erstell-notiz"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setMahnErstellDialog(null)} data-testid="mahn-erstell-abbrechen">
                  Abbrechen
                </Button>
                <Button
                  onClick={submitMahnErstell}
                  disabled={mahnGenerating}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="mahn-erstell-submit"
                >
                  {mahnGenerating ? "Erstelle…" : "PDF erzeugen & speichern"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>


      {/* Manuell eintragen: Mahnungs-Versand außerhalb des Systems (eigene E-Mail, Brief, Fax) */}
      <Dialog open={!!mahnungManuellDialog} onOpenChange={(o) => { if (!o) setMahnungManuellDialog(null); }}>
        <DialogContent className="max-w-md">
          {mahnungManuellDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardCheck className="text-amber-700" size={20} />
                  {mahnungManuellDialog.stufe === 0 ? "Zahlungserinnerung" : `${mahnungManuellDialog.stufe}. Mahnung`} manuell eintragen
                </DialogTitle>
                <DialogDescription>
                  {mahnungManuellDialog.rechnung.rechnungsnummer} · {mahnungManuellDialog.rechnung.empfaenger_name}
                  <br />
                  <span className="text-xs text-[#78716c]">Wenn Sie die Mahnung selber verschickt haben (eigene E-Mail, Brief, Fax), tragen Sie den Versand hier ein — so ist er in der Rechnungszeile und im Versandprotokoll sichtbar.</span>
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-sm font-medium">Versanddatum</Label>
                  <Input
                    type="date"
                    value={mahnungManuellDatum}
                    onChange={(e) => setMahnungManuellDatum(e.target.value)}
                    className="mt-1"
                    data-testid="mahnung-manuell-datum"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium">Versandweg</Label>
                  <select
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                    value={mahnungManuellWeg}
                    onChange={(e) => setMahnungManuellWeg(e.target.value)}
                    data-testid="mahnung-manuell-weg"
                  >
                    <option value="extern">Eigene E-Mail (extern)</option>
                    <option value="brief">Brief / Post</option>
                    <option value="fax">Fax</option>
                    <option value="anderer">Anderer Weg</option>
                  </select>
                </div>

                <div>
                  <Label className="text-sm font-medium">Empfänger (E-Mail / Name / Adresse)</Label>
                  <Input
                    type="text"
                    value={mahnungManuellEmpfaenger}
                    onChange={(e) => setMahnungManuellEmpfaenger(e.target.value)}
                    placeholder="z.B. buchhaltung@kunde.de oder Herr Müller"
                    className="mt-1"
                    data-testid="mahnung-manuell-empfaenger"
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium">Notiz (optional)</Label>
                  <textarea
                    className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[60px]"
                    value={mahnungManuellNotiz}
                    onChange={(e) => setMahnungManuellNotiz(e.target.value)}
                    placeholder="z.B. Betreff der E-Mail, Referenz, telefonische Rücksprache …"
                    data-testid="mahnung-manuell-notiz"
                  />
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setMahnungManuellDialog(null)} data-testid="mahnung-manuell-abbrechen">
                  Abbrechen
                </Button>
                <Button
                  onClick={submitMahnungManuell}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="mahnung-manuell-speichern"
                >
                  <Save size={16} className="mr-2" /> Eintrag speichern
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>


      {/* NEU: Einfacher Raten-Dialog für normale Rechnungen */}
      <Dialog open={!!editRatenDialog} onOpenChange={() => setEditRatenDialog(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {editRatenDialog && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CreditCard className="text-blue-600" size={20} />
                  Zahlungsart & Raten bearbeiten
                </DialogTitle>
                <DialogDescription>
                  {editRatenDialog.rechnungsnummer} - {editRatenDialog.empfaenger_name}
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Betrag anzeigen */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Rechnungsbetrag:</span>
                    <span className="font-bold text-lg">{editRatenDialog.endbetrag?.toLocaleString('de-DE')} €</span>
                  </div>
                </div>

                {/* Zahlungsart */}
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="font-medium mb-2 block">Zahlungsart</Label>
                  <div className="flex gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={editRatenDialog.zahlungsart !== "ratenzahlung"}
                        onChange={() => setEditRatenDialog({...editRatenDialog, zahlungsart: "einmalzahlung", anzahl_raten: 1})}
                      />
                      <span>Einmalzahlung</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={editRatenDialog.zahlungsart === "ratenzahlung"}
                        onChange={() => setEditRatenDialog({...editRatenDialog, zahlungsart: "ratenzahlung", anzahl_raten: editRatenDialog.anzahl_raten || 2})}
                      />
                      <span>Ratenzahlung</span>
                    </label>
                  </div>
                  
                  {editRatenDialog.zahlungsart === "ratenzahlung" && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm">Anzahl Raten:</Label>
                        <Select
                          value={String(editRatenDialog.anzahl_raten || 2)}
                          onValueChange={async (v) => {
                            const neueAnzahl = parseInt(v);
                            // Speichere neue Anzahl direkt in DB
                            try {
                              await axios.put(`${API}/rechnungen/${editRatenDialog.id}/zahlungsart`, {
                                zahlungsart: "ratenzahlung",
                                anzahl_raten: neueAnzahl
                              });
                              // Aktualisiere Dialog mit neuen Daten
                              const updated = await axios.get(`${API}/rechnungen/${editRatenDialog.id}`);
                              setEditRatenDialog(updated.data);
                              fetchData();
                              toast.success(`Auf ${neueAnzahl} Raten geändert`);
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || "Fehler beim Ändern der Ratenanzahl");
                            }
                          }}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="2">2</SelectItem>
                            <SelectItem value="3">3</SelectItem>
                            <SelectItem value="4">4</SelectItem>
                            <SelectItem value="5">5</SelectItem>
                            <SelectItem value="6">6</SelectItem>
                            <SelectItem value="7">7</SelectItem>
                            <SelectItem value="8">8</SelectItem>
                            <SelectItem value="9">9</SelectItem>
                            <SelectItem value="10">10</SelectItem>
                            <SelectItem value="11">11</SelectItem>
                            <SelectItem value="12">12</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Raten-Übersicht mit Checkboxen */}
                      <div className="bg-white rounded-lg p-3 space-y-2">
                        <p className="font-medium text-sm">Raten-Status:</p>
                        {editRatenDialog.raten && editRatenDialog.raten.length > 0 ? (
                          // Vorhandene Raten anzeigen
                          editRatenDialog.raten.map((rate, index) => {
                            const rNr = rate.rate_nr || index + 1;
                            const today = new Date().toISOString().slice(0, 10);
                            const dKey = `${editRatenDialog.id}_${rNr}`;
                            const inputDate = rateZahlDatum[dKey] || rate.bezahlt_am || today;
                            const inputBetrag = rateZahlBetrag[dKey] ?? (rate.betrag != null ? String(rate.betrag) : "");
                            const abweichung = rate.bezahlt && rate.bezahlt_betrag != null && Math.abs(Number(rate.bezahlt_betrag) - Number(rate.betrag || 0)) > 0.005;
                            return (
                              <div key={index} className="flex items-center justify-between gap-2 p-2 bg-gray-50 rounded flex-wrap">
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium">
                                    Rate {rNr}: <span className="text-gray-700">€{(rate.betrag || (editRatenDialog.endbetrag / (editRatenDialog.anzahl_raten || 2))).toFixed(2)}</span>
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {rate.faellig_am && <span>Fällig: {rate.faellig_am}</span>}
                                    {rate.bezahlt && rate.bezahlt_am && (
                                      <span className="ml-2 text-emerald-700">· Bezahlt am: {rate.bezahlt_am}</span>
                                    )}
                                    {rate.bezahlt && rate.bezahlt_betrag != null && (
                                      <span className={`ml-2 ${abweichung ? "text-amber-700 font-medium" : "text-emerald-700"}`}>
                                        · Eingang: €{Number(rate.bezahlt_betrag).toLocaleString('de-DE')}{abweichung ? " " : ""}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {!rate.bezahlt && (
                                  <>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={inputBetrag}
                                      onChange={(e) => setRateZahlBetrag({ ...rateZahlBetrag, [dKey]: e.target.value })}
                                      className="border border-gray-300 rounded px-2 py-1 text-xs w-24"
                                      placeholder="Betrag €"
                                      title="Tatsächlich eingegangener Betrag"
                                      data-testid={`erd-rate-betrag-${rNr}`}
                                    />
                                    <input
                                      type="date"
                                      value={inputDate}
                                      onChange={(e) => setRateZahlDatum({ ...rateZahlDatum, [dKey]: e.target.value })}
                                      className="border border-gray-300 rounded px-2 py-1 text-xs"
                                      title="Zahlungseingangsdatum"
                                      data-testid={`erd-rate-datum-${rNr}`}
                                    />
                                  </>
                                )}
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      const params = new URLSearchParams();
                                      params.set("rate_nr", String(rNr));
                                      params.set("bezahlt", String(!rate.bezahlt));
                                      if (!rate.bezahlt) {
                                        if (inputDate) params.set("bezahlt_am", inputDate);
                                        if (inputBetrag) params.set("bezahlt_betrag", inputBetrag);
                                      }
                                      await axios.put(`${API}/rechnungen/${editRatenDialog.id}/zahlung?${params.toString()}`);
                                      const updated = await axios.get(`${API}/rechnungen/${editRatenDialog.id}`);
                                      setEditRatenDialog(updated.data);
                                      fetchData();
                                      toast.success(rate.bezahlt ? "Rate als offen markiert" : `Rate ${rNr} bezahlt${inputDate ? ` am ${inputDate}` : ""}${inputBetrag ? ` (${inputBetrag}€)` : ""}`);
                                    } catch (err) {
                                      toast.error("Fehler beim Aktualisieren der Rate");
                                    }
                                  }}
                                  className={`px-3 py-1 rounded text-sm whitespace-nowrap ${rate.bezahlt ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                                  data-testid={`erd-rate-toggle-${rNr}`}
                                >
                                  {rate.bezahlt ? '✓ Bezahlt' : 'Bezahlt markieren'}
                                </button>
                              </div>
                            );
                          })
                        ) : (
                          // Neue Raten werden nach dem Speichern erstellt
                          Array.from({length: editRatenDialog.anzahl_raten || 2}).map((_, i) => (
                            <div key={i} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                              <span className="text-sm">
                                Rate {i + 1}: €{(editRatenDialog.endbetrag / (editRatenDialog.anzahl_raten || 2)).toFixed(2)}
                              </span>
                              <span className="text-xs text-gray-500">Speichern um Raten zu erstellen</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Bezahlt-Status (nur bei Einmalzahlung) */}
                {editRatenDialog.zahlungsart !== "ratenzahlung" && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="rechnung_bezahlt"
                      checked={editRatenDialog.zahlungsstatus === "bezahlt"}
                      onCheckedChange={async (checked) => {
                        try {
                          await axios.put(`${API}/rechnungen/${editRatenDialog.id}/status`, {
                            zahlungsstatus: checked ? "bezahlt" : "offen"
                          });
                          setEditRatenDialog({...editRatenDialog, zahlungsstatus: checked ? "bezahlt" : "offen"});
                          fetchData();
                          toast.success(checked ? "Als bezahlt markiert" : "Als offen markiert");
                        } catch (err) {
                          toast.error("Fehler beim Aktualisieren");
                        }
                      }}
                    />
                    <label htmlFor="rechnung_bezahlt" className="text-sm cursor-pointer">
                      Bereits bezahlt
                    </label>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setEditRatenDialog(null)}>
                  Schließen
                </Button>
                {editRatenDialog.zahlungsart === "ratenzahlung" && (!editRatenDialog.raten || editRatenDialog.raten.length === 0) && (
                  <Button 
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={async () => {
                      try {
                        await axios.put(`${API}/rechnungen/${editRatenDialog.id}/zahlungsart`, {
                          zahlungsart: "ratenzahlung",
                          anzahl_raten: editRatenDialog.anzahl_raten || 2
                        });
                        const updated = await axios.get(`${API}/rechnungen/${editRatenDialog.id}`);
                        setEditRatenDialog(updated.data);
                        fetchData();
                        toast.success("Ratenzahlung aktiviert");
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Fehler beim Speichern");
                      }
                    }}
                  >
                    <Save size={16} className="mr-2" />
                    Ratenzahlung speichern
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== ARCHIVIERTE KLASSENBÜCHER (READ-ONLY) ====================
const ArchiviertesKlassenbuchArchiv = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/archiv/klassenbuecher`);
      setData(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der archivierten Klassenbücher");
    } finally {
      setLoading(false);
    }
  };

  const filtered = data.filter(kb => 
    kb.kursbezeichnung?.toLowerCase().includes(filter.toLowerCase()) ||
    kb.dozent?.toLowerCase().includes(filter.toLowerCase()) ||
    kb.massnahme_name?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="page-container" data-testid="archiv-klassenbuecher-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Archivierte Klassenbücher</h1>
          <p className="text-sm text-[#78716c] mt-1">Historische Nachweise aus Altsystem (Ninox) - Nur Ansicht</p>
        </div>
        <Badge className="bg-amber-100 text-amber-800">
          <AlertTriangle size={14} className="mr-1" />
          Archiv - Read-Only
        </Badge>
      </div>

      {/* Info Banner */}
      <Card className="mb-6 bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-600 mt-0.5" size={20} />
            <div>
              <p className="font-medium text-amber-800">Archivierte Daten aus Ninox</p>
              <p className="text-sm text-amber-700">
                Diese Klassenbücher stammen aus dem Altsystem und dienen ausschließlich der historischen Nachweisführung.
                Sie werden nicht in die aktive AZAV-Logik einbezogen und können nicht bearbeitet werden.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filter */}
      <Card className="card-custom mb-6">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716c]" size={18} />
            <Input
              placeholder="Suchen nach Kurs, Dozent, Maßnahme..."
              className="pl-10"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-[#78716c]">Lade archivierte Klassenbücher...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-[#78716c]">Keine archivierten Klassenbücher gefunden</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead className="w-[200px]">Kursbezeichnung</TableHead>
                  <TableHead className="w-[150px]">Maßnahme</TableHead>
                  <TableHead className="w-[180px]">Zeitraum</TableHead>
                  <TableHead className="w-[120px]">Dozent</TableHead>
                  <TableHead className="w-[100px]">Unterschrift</TableHead>
                  <TableHead className="w-[100px]">Archiviert am</TableHead>
                  <TableHead className="w-[80px]">Dokument</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((kb) => (
                  <TableRow key={kb.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{kb.kursbezeichnung}</p>
                        <p className="text-xs text-[#78716c]">Quelle: {kb.quelle}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{kb.massnahme_name || "-"}</TableCell>
                    <TableCell className="text-sm">
                      {kb.zeitraum_von} - {kb.zeitraum_bis}
                    </TableCell>
                    <TableCell className="text-sm">{kb.dozent || "-"}</TableCell>
                    <TableCell>
                      {kb.enthaelt_unterschrift ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Ja</Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-600">Nein</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{kb.archiviert_am}</TableCell>
                    <TableCell>
                      {kb.klassenbuch_datei ? (
                        <Button variant="ghost" size="sm">
                          <FileDown size={16} />
                        </Button>
                      ) : (
                        <span className="text-[#78716c] text-xs">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== AKTIVITÄTEN / HISTORIE ====================
const Aktivitaeten = () => {
  const [aktivitaeten, setAktivitaeten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchAktivitaeten();
  }, []);

  const fetchAktivitaeten = async () => {
    try {
      setLoading(true);
      // Nur Notizen-Änderungen laden (vereinfacht)
      const response = await axios.get(`${API}/aktivitaeten/notizen?limit=100`);
      setAktivitaeten(response.data);
    } catch (error) {
      console.error("Fehler beim Laden der Aktivitäten");
      // Fallback: alle Aktivitäten laden und filtern
      try {
        const allResponse = await axios.get(`${API}/aktivitaeten?limit=200`);
        const notizenOnly = allResponse.data.filter(a => 
          (a.details || "").toLowerCase().includes("notiz") ||
          (a.bereich || "").toLowerCase().includes("notiz")
        );
        setAktivitaeten(notizenOnly);
      } catch (e) {
        setAktivitaeten([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredAktivitaeten = aktivitaeten.filter(a => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (a.details || "").toLowerCase().includes(term) ||
      (a.benutzer || "").toLowerCase().includes(term) ||
      (a.pflegeeinrichtung_name || "").toLowerCase().includes(term) ||
      (a.teilnehmer_name || "").toLowerCase().includes(term)
    );
  });

  // Gruppiere nach Datum
  const groupedByDate = filteredAktivitaeten.reduce((groups, aktivitaet) => {
    const date = aktivitaet.datum?.split('T')[0] || 'Unbekannt';
    const dateFormatted = date !== 'Unbekannt' 
      ? new Date(date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
      : 'Unbekannt';
    if (!groups[dateFormatted]) {
      groups[dateFormatted] = [];
    }
    groups[dateFormatted].push(aktivitaet);
    return groups;
  }, {});

  return (
    <div className="page-container" data-testid="aktivitaeten-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>
            Notizen-Historie
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Änderungen an internen Notizen bei Teilnehmern und Pflegeeinrichtungen
          </p>
        </div>
        <Button variant="outline" onClick={fetchAktivitaeten} disabled={loading}>
          <RefreshCcw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </Button>
      </div>

      {/* Suche */}
      <Card className="card-custom mb-6">
        <CardContent className="pt-4">
          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Suchen nach Name, Benutzer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="text-sm text-gray-500">
              {filteredAktivitaeten.length} Notiz-Änderungen
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Aktivitäten-Liste */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCcw size={32} className="animate-spin text-gray-400" />
        </div>
      ) : filteredAktivitaeten.length === 0 ? (
        <Card className="card-custom">
          <CardContent className="py-12 text-center">
            <FileText size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">Keine Notiz-Änderungen gefunden</p>
            <p className="text-xs text-gray-400 mt-2">
              Hier werden nur Änderungen an internen Notizen angezeigt
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByDate).map(([date, items]) => (
            <div key={date}>
              <h3 className="text-sm font-medium text-gray-500 mb-3 flex items-center gap-2">
                <Calendar size={14} />
                {date}
              </h3>
              <Card className="card-custom">
                <CardContent className="p-0">
                  <div className="divide-y">
                    {items.map((aktivitaet, idx) => (
                      <div key={aktivitaet.id || idx} className="p-4 hover:bg-gray-50">
                        <div className="flex items-start gap-4">
                          {/* Zeit */}
                          <div className="text-xs text-gray-400 w-12 pt-1">
                            {aktivitaet.datum_formatiert?.split(' ')[1] || ''}
                          </div>
                          
                          {/* Icon */}
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-100">
                            <FileText size={14} className="text-amber-600" />
                          </div>
                          
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {aktivitaet.benutzer && (
                                <span className="text-sm font-medium text-gray-700">
                                  {aktivitaet.benutzer}
                                </span>
                              )}
                              {aktivitaet.teilnehmer_name && (
                                <span 
                                  className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 cursor-pointer hover:bg-blue-200"
                                  onClick={() => navigate(`/teilnehmer?id=${aktivitaet.teilnehmer_id}`)}
                                >
                                  <Users size={10} className="inline mr-1" />
                                  {aktivitaet.teilnehmer_name}
                                </span>
                              )}
                              {aktivitaet.pflegeeinrichtung_name && (
                                <span 
                                  className="text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 cursor-pointer hover:bg-orange-200"
                                  onClick={() => navigate(`/pflegeeinrichtungen?id=${aktivitaet.pflegeeinrichtung_id}`)}
                                >
                                  <Building2 size={10} className="inline mr-1" />
                                  {aktivitaet.pflegeeinrichtung_name}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              {aktivitaet.details || "Notizen geändert"}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== NINOX SYNC (DEAKTIVIERT) ====================
const NinoxSync = () => {
  return (
    <div className="page-container" data-testid="ninox-sync-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold" style={{fontFamily: 'Manrope, sans-serif'}}>Synchronisierung</h1>
      </div>

      <Card className="mb-6 border-amber-300 bg-amber-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle size={24} />
            Synchronisierung deaktiviert
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-amber-700 mb-4">
            Die automatische Synchronisierung mit Notion und Ninox wurde deaktiviert, 
            um versehentliches Überschreiben von Daten zu verhindern.
          </p>
          <p className="text-amber-700 mb-4">
            Alle Teilnehmer und Einrichtungen werden jetzt ausschließlich im CRM gepflegt.
          </p>
          <p className="text-sm text-amber-600">
            Bei Fragen wenden Sie sich bitte an den Administrator.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ==================== AUFGABEN ====================
const Aufgaben = () => {
  const [data, setData] = useState([]);
  const [einrichtungen, setEinrichtungen] = useState([]);
  const [teilnehmer, setTeilnehmer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [formData, setFormData] = useState({
    aufgabe: "",
    bezug_typ: "pflegeeinrichtung",
    bezug_id: "",
    faelligkeitsdatum: "",
    status: "offen",
    interne_notizen: ""
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const [aufgabenRes, einrichtungenRes, tnRes] = await Promise.all([
        axios.get(`${API}/aufgaben${params}`),
        axios.get(`${API}/pflegeeinrichtungen`),
        axios.get(`${API}/teilnehmer`)
      ]);
      setData(aufgabenRes.data);
      setEinrichtungen(einrichtungenRes.data);
      setTeilnehmer(tnRes.data);
    } catch (error) {
      toast.error("Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getBezugName = (typ, id) => {
    if (typ === "pflegeeinrichtung") {
      const e = einrichtungen.find(e => e.id === id);
      return e ? e.einrichtungsname : "-";
    } else {
      const tn = teilnehmer.find(t => t.id === id);
      return tn ? `${tn.vorname} ${tn.nachname}` : "-";
    }
  };

  const handleSubmit = async () => {
    try {
      if (editItem) {
        await axios.put(`${API}/aufgaben/${editItem.id}`, formData);
        toast.success("Aufgabe aktualisiert");
      } else {
        await axios.post(`${API}/aufgaben`, formData);
        toast.success("Aufgabe erstellt");
      }
      setDialogOpen(false);
      setEditItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Fehler beim Speichern");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Wirklich löschen?")) {
      try {
        await axios.delete(`${API}/aufgaben/${id}`);
        toast.success("Aufgabe gelöscht");
        fetchData();
      } catch (error) {
        toast.error("Fehler beim Löschen");
      }
    }
  };

  const handleEdit = (item) => {
    setEditItem(item);
    setFormData({
      aufgabe: item.aufgabe,
      bezug_typ: item.bezug_typ,
      bezug_id: item.bezug_id,
      faelligkeitsdatum: item.faelligkeitsdatum,
      status: item.status,
      interne_notizen: item.interne_notizen || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      aufgabe: "",
      bezug_typ: "pflegeeinrichtung",
      bezug_id: "",
      faelligkeitsdatum: "",
      status: "offen",
      interne_notizen: ""
    });
  };

  return (
    <div className="page-container" data-testid="aufgaben-page">
      <h1 className="text-3xl font-semibold mb-6" style={{fontFamily: 'Manrope, sans-serif'}}>Aufgaben</h1>
      <AutoTodos />
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-base text-stone-500">Manuelle Aufgaben & Wiedervorlagen</h2>
        <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={() => { resetForm(); setDialogOpen(true); }} data-testid="add-aufgabe-btn">
          <Plus size={16} className="mr-2" />
          Neue Aufgabe
        </Button>
      </div>

      <Card className="card-custom mb-6">
        <CardContent className="p-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px]" data-testid="aufgabe-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="offen">Offen</SelectItem>
              <SelectItem value="erledigt">Erledigt</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <Card className="card-custom">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner"></div>
            </div>
          ) : data.length === 0 ? (
            <div className="empty-state">
              <ListTodo className="empty-state-icon mx-auto" />
              <p>Keine Aufgaben gefunden</p>
            </div>
          ) : (
            <Table data-testid="aufgaben-table">
              <TableHeader>
                <TableRow className="bg-[#FFFFFF]">
                  <TableHead>Aufgabe</TableHead>
                  <TableHead>Bezug</TableHead>
                  <TableHead>Fälligkeitsdatum</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Aktionen</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-medium">{item.aufgabe}</TableCell>
                    <TableCell>
                      <div>
                        <span className="text-xs text-[#78716c]">{item.bezug_typ === "pflegeeinrichtung" ? "Einrichtung" : item.bezug_typ === "interessent" ? "Interessent" : "Teilnehmer"}</span>
                        <p className="text-sm">{item.bezug_name || getBezugName(item.bezug_typ, item.bezug_id)}</p>
                      </div>
                    </TableCell>
                    <TableCell>{item.faelligkeitsdatum}</TableCell>
                    <TableCell><StatusBadge status={item.status} /></TableCell>
                    <TableCell className="text-right">
                      <button className="action-btn" onClick={() => handleEdit(item)}>
                        <Pencil size={16} />
                      </button>
                      <button className="action-btn delete" onClick={() => handleDelete(item.id)}>
                        <Trash2 size={16} />
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{fontFamily: 'Manrope, sans-serif'}}>
              {editItem ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>Aufgabe *</Label>
              <Input
                value={formData.aufgabe}
                onChange={(e) => setFormData({...formData, aufgabe: e.target.value})}
                data-testid="input-aufgabe"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Bezugstyp</Label>
                <Select value={formData.bezug_typ} onValueChange={(v) => setFormData({...formData, bezug_typ: v, bezug_id: ""})}>
                  <SelectTrigger data-testid="input-bezug-typ">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pflegeeinrichtung">Pflegeeinrichtung</SelectItem>
                    <SelectItem value="teilnehmer">Teilnehmer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Bezug *</Label>
                <Select value={formData.bezug_id} onValueChange={(v) => setFormData({...formData, bezug_id: v})}>
                  <SelectTrigger data-testid="input-bezug-id">
                    <SelectValue placeholder="Auswählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.bezug_typ === "pflegeeinrichtung" ? (
                      einrichtungen.map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.einrichtungsname}</SelectItem>
                      ))
                    ) : (
                      teilnehmer.map(tn => (
                        <SelectItem key={tn.id} value={tn.id}>{tn.vorname} {tn.nachname}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Fälligkeitsdatum *</Label>
                <Input
                  type="date"
                  value={formData.faelligkeitsdatum}
                  onChange={(e) => setFormData({...formData, faelligkeitsdatum: e.target.value})}
                  data-testid="input-faellig"
                />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger data-testid="input-aufgabe-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="offen">Offen</SelectItem>
                    <SelectItem value="erledigt">Erledigt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Interne Notizen</Label>
              <Textarea
                value={formData.interne_notizen}
                onChange={(e) => setFormData({...formData, interne_notizen: e.target.value})}
                rows={3}
                data-testid="input-aufgabe-notizen"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button className="bg-[#D9775A] hover:bg-[#C2603F] text-white" onClick={handleSubmit} data-testid="submit-aufgabe">Speichern</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== MAIN APP ====================
function AppContent() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.innerWidth < 768);
  const { isAuthenticated, loading, user, logout, canViewAll, canViewVertrieb, canViewKlassenbuch, canViewTeilnehmer, canViewFinances } = useAuth();
  const location = useLocation();

  // Public-Routen (kein Auth erforderlich) — z.B. Online-Unterschrift via Token-Link
  if (location.pathname === "/termin" || location.pathname.startsWith("/termin/")) {
    return (
      <Routes>
        <Route path="/termin" element={<TerminBuchung />} />
        <Route path="/termin/storno/:token" element={<TerminStorno />} />
      </Routes>
    );
  }

  if (location.pathname.startsWith("/rechnungsadresse/") || location.pathname.startsWith("/einwilligung-rechnung/") || location.pathname.startsWith("/einwilligung-einrichtung/")) {
    return (
      <Routes>
        <Route path="/rechnungsadresse/:token" element={<RechnungsadresseFormular />} />
        <Route path="/einwilligung-einrichtung/:token" element={<EinwilligungEinrichtung />} />
        <Route path="/einwilligung-rechnung/:token" element={<EinwilligungRechnung />} />
      </Routes>
    );
  }

  if (location.pathname.startsWith("/sign/")) {
    return (
      <Routes>
        <Route path="/sign/:token" element={<SignPage />} />
      </Routes>
    );
  }

  // Zeige Ladeindikator
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f0eb]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#b8a99a]"></div>
      </div>
    );
  }

  // Login-Seite wenn nicht eingeloggt
  if (!isAuthenticated) {
    return <LoginPage />;
  }

  return (
    <div className="flex">
      <Sidebar collapsed={sidebarCollapsed} setCollapsed={setSidebarCollapsed} userRole={user?.role} />
      <main className="main-content">
        {/* User-Info Header — kompakter Avatar mit Klick-Dropdown nach unten */}
        <div className="fixed top-3 right-4 z-50">
          <details className="group relative">
            <summary
              className="list-none cursor-pointer w-10 h-10 rounded-full bg-gradient-to-br from-[#b8a99a] to-[#d1b198] flex items-center justify-center text-white text-sm font-semibold shadow-md hover:shadow-lg transition-shadow"
              data-testid="user-avatar-trigger"
              title={user?.name}
            >
              {user?.name?.split(' ').map(n => n[0]).join('')}
            </summary>
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-2 z-50">
              <div className="px-4 py-2 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-800">{user?.name}</p>
                <p className="text-xs text-gray-500">{{gf: 'Geschäftsführung', verwaltung: 'Verwaltung', dozent: 'Dozent', vertrieb: 'Vertrieb'}[user?.role] || user?.role}</p>
              </div>
              <button
                onClick={logout}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                data-testid="logout-btn"
              >
                <LogOut size={14} /> Abmelden
              </button>
            </div>
          </details>
        </div>
        
        <Routes>
          {/* Alle Rollen: Dashboard (angepasst je nach Rolle) */}
          <Route path="/" element={<Dashboard />} />
          
          {/* Admin/Verwaltung: Alles */}
          <Route path="/betriebsferien" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <Betriebsferien />
            </ProtectedRoute>
          } />
          <Route path="/klassenbuch-luecken" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <KlassenbuchLuecken />
            </ProtectedRoute>
          } />
          <Route path="/termine" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'vertrieb']}>
              <Termine />
            </ProtectedRoute>
          } />
          <Route path="/kalender" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'vertrieb', 'dozent']}>
              <Kalender />
            </ProtectedRoute>
          } />
          <Route path="/vertrieb" element={<Navigate to="/pflegeeinrichtungen" replace />} />
          <Route path="/pflegeeinrichtungen" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'vertrieb', 'dozent']}>
              <Pflegeeinrichtungen />
            </ProtectedRoute>
          } />
          <Route path="/pflegeeinrichtungen/:id" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'vertrieb', 'dozent']}>
              <PflegeeinrichtungDetail />
            </ProtectedRoute>
          } />
          
          {/* Teilnehmer: Admin, Verwaltung, Dozenten, Vertrieb */}
          <Route path="/dozenten" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <DozentenView />
            </ProtectedRoute>
          } />
          <Route path="/teilnehmer" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent', 'vertrieb']}>
              <Teilnehmer />
            </ProtectedRoute>
          } />
          <Route path="/interessenten" element={<Navigate to="/pflegeeinrichtungen?pipeline=interessent" replace />} />
          <Route path="/admin/rechnungen-tn-zuordnung" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <RechnungenTnZuordnung />
            </ProtectedRoute>
          } />
          <Route path="/vertriebs-cockpit" element={<Navigate to="/pflegeeinrichtungen?view=cockpit" replace />} />
          <Route path="/bildungsgutscheine" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'vertrieb']}>
              <Bildungsgutscheine />
            </ProtectedRoute>
          } />
          
          {/* Finanzen: Admin, Verwaltung, Vertrieb */}
          <Route path="/kontoauszuege" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <Kontoauszuege />
            </ProtectedRoute>
          } />
          <Route path="/erechnung-versand" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <ERechnungVersand />
            </ProtectedRoute>
          } />
          <Route path="/rechnungsbuecher" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <Rechnungsbuecher />
            </ProtectedRoute>
          } />
          <Route path="/eingangsrechnungen" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <Eingangsrechnungen />
            </ProtectedRoute>
          } />
          <Route path="/rechnungen" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'vertrieb']}>
              <Rechnungen />
            </ProtectedRoute>
          } />
          <Route path="/ratenzahlung" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'vertrieb']}>
              <Ratenzahlung />
            </ProtectedRoute>
          } />
          <Route path="/buchhaltung" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <Buchhaltung />
            </ProtectedRoute>
          } />
          <Route path="/bg-angebote" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'vertrieb']}>
              <BGAngebote />
            </ProtectedRoute>
          } />
          
          {/* Kurse & Klassenbuch: Admin, Verwaltung, Dozenten */}
          <Route path="/massnahmen" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <Massnahmen />
            </ProtectedRoute>
          } />
          <Route path="/weiterbildungen" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <Weiterbildungen />
            </ProtectedRoute>
          } />
          <Route path="/anwesenheit" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <AnwesenheitNeu />
            </ProtectedRoute>
          } />
          <Route path="/pruefungen" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <Pruefungen />
            </ProtectedRoute>
          } />
          <Route path="/zertifikate" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <Zertifikate />
            </ProtectedRoute>
          } />
          <Route path="/fertig-ohne-zertifikat" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <FertigOhneZertifikat />
            </ProtectedRoute>
          } />
          
          {/* Aufgaben: Admin, Verwaltung, Vertrieb */}
          <Route path="/aufgaben" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'vertrieb']}>
              <Aufgaben />
            </ProtectedRoute>
          } />
          
          {/* Aktivitäten - Für alle Rollen */}
          <Route path="/aktivitaeten" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent', 'vertrieb']}>
              <Aenderungsprotokoll />
            </ProtectedRoute>
          } />
          <Route path="/datensicherung-wartung" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <AZAVMappe modus="werkzeuge" />
            </ProtectedRoute>
          } />

          {/* Urlaubsplanung - für alle Mitarbeiter mit Login */}
          <Route path="/urlaub" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent', 'vertrieb']}>
              <Urlaubsplanung currentUser={user} />
            </ProtectedRoute>
          } />
          
          {/* Admin-Only */}
          <Route path="/archiv-klassenbuecher" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <ArchiviertesKlassenbuchArchiv />
            </ProtectedRoute>
          } />
          <Route path="/klassenbuch-neu" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <KlassenbuchNeu />
            </ProtectedRoute>
          } />
          <Route path="/finanzuebersicht" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <Finanzuebersicht />
            </ProtectedRoute>
          } />
          <Route path="/bg-kurs-einstellungen" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <BGKursEinstellungen />
            </ProtectedRoute>
          } />
          <Route path="/ninox-sync" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <NinoxSync />
            </ProtectedRoute>
          } />
          <Route path="/azav-mappe" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <AZAVMappe />
            </ProtectedRoute>
          } />
          <Route path="/qm-cockpit" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <QMCockpit />
            </ProtectedRoute>
          } />
          <Route path="/kurstermine" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <Kurstermine />
            </ProtectedRoute>
          } />
          <Route path="/vertrieb-auswertung" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'vertrieb']}>
              <VertriebAuswertung />
            </ProtectedRoute>
          } />
          <Route path="/azav-ordner" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <AZAVOrdner />
            </ProtectedRoute>
          } />
          <Route path="/azav-dokumente" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung', 'dozent']}>
              <AZAVDokumente />
            </ProtectedRoute>
          } />
          <Route path="/design-beispiele" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <DesignBeispiele />
            </ProtectedRoute>
          } />
          <Route path="/cockpit-vorschau" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <CockpitVorschau />
            </ProtectedRoute>
          } />
          <Route path="/azav-antrags-wizard" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <AZAVAntragsWizard />
            </ProtectedRoute>
          } />
          <Route path="/verbleibsbefragung" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <VerbleibsbefragungAuswertung />
            </ProtectedRoute>
          } />
          <Route path="/personalakten" element={
            <ProtectedRoute allowedRoles={['gf', 'verwaltung']}>
              <Personalakten />
            </ProtectedRoute>
          } />
          
          {/* Login Route */}
          <Route path="/login" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
